import "@/App.css";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import DOMPurify from "dompurify";
import { 
  Search, 
  Filter, 
  Flame, 
  X, 
  ChevronDown, 
  ChevronUp,
  ArrowUpDown,
  Activity, 
  Droplets, 
  Send,
  Menu,
  Mail,
  Phone,
  Building2,
  FileCheck,
  Package,
  MapPin,
  ShieldCheck,
  Upload,
  FileText,
  Check,
  Settings,
  Image,
  Trash2,
  Edit3,
  Save,
  ArrowLeft,
  Lock,
  LogOut,
  User,
  Globe,
  UserPlus,
  Leaf,
  CheckCircle,
  XCircle,
  Clock,
  ClipboardList,
  DollarSign,
  Edit2,
  Download,
  RefreshCw,
  ZoomIn,
  Plus,
  Users,
  MessageCircle,
  Share2,
  Copy,
  CheckCircle2,
  Bot,
  Minimize2,
  Instagram,
  BookmarkPlus,
  Bookmark,
  BarChart3,
  Award,
  ImageIcon,
  Table2,
  ShoppingCart,
  TrendingUp,
  Zap,
  Target,
  Layers,
  Minus,
  Crosshair,
  MapPinned,
  Binoculars,
  Sparkles,
  ArrowRight,
  BookOpen,
  AlertTriangle,
  Bell,
  HeartPulse,
  Eye
} from "lucide-react";

// Prevent right-click on images
const preventImageActions = (e) => {
  e.preventDefault();
  return false;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const InstagramColorIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="ig-grad-1" cx="30%" cy="107%" r="150%">
        <stop offset="0%" stopColor="#fdf497"/>
        <stop offset="5%" stopColor="#fdf497"/>
        <stop offset="45%" stopColor="#fd5949"/>
        <stop offset="60%" stopColor="#d6249f"/>
        <stop offset="90%" stopColor="#285AEB"/>
      </radialGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#ig-grad-1)"/>
    <circle cx="12" cy="12" r="4.5" stroke="white" strokeWidth="1.5" fill="none"/>
    <circle cx="17.5" cy="6.5" r="1.2" fill="white"/>
    <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#ig-grad-1)" strokeWidth="0" fill="none"/>
  </svg>
);

const FIREGOLD_LOGO = "https://customer-assets.emergentagent.com/job_reload-engine/artifacts/sx02pppj_ChatGPT%20Image%20Nov%2011%2C%202025%20at%2009_53_40%20AM.png";
const FIREGOLD_MARKETPLACE_LOGO = "https://customer-assets.emergentagent.com/job_ecd636ef-7568-464f-aeda-90fbfc69ca12/artifacts/0f25xj2d_firegold%20Marketplace.png";

// Canonical list of cannabis effects (alphabetical, merged with DB values)
const ALL_EFFECTS = [
  "NONE SPECIFIED",
  "Active Body Feeling",
  "Alertness",
  "Anti-inflammatory Relief",
  "Anxiety",
  "Anxiety Reduction",
  "Anxiety Relief",
  "Appetite",
  "Appetite Stimulant",
  "Appetite Support",
  "Appetite stimulation",
  "Blissful",
  "Body High",
  "Body Relax",
  "Body Relaxation",
  "Body Warmth",
  "Bonding",
  "Calm",
  "Calm Euphoria",
  "Cerebral",
  "Cerebral Calm",
  "Cheerful",
  "Confidence Boost",
  "Controlled Euphoria",
  "Couch-Lock",
  "Creative",
  "Creative Energy",
  "Deep Relax",
  "Deep relaxation",
  "Dreaminess",
  "Elevated Mood",
  "Emotional Balance",
  "Energetic",
  "Enhanced Creativity in Art/Music",
  "Enhanced Sensory Awareness",
  "Enhanced Visual Perception",
  "Euphoria",
  "Euphoric",
  "Euphoric Lift",
  "Euphoric Mood",
  "Floating Feeling",
  "Focus",
  "Focus Creative Energy",
  "Food Craving",
  "Grounded Euphoria",
  "Grounding",
  "Happiness",
  "Increased Appetite",
  "Inflammation",
  "Inspiration",
  "Introspection",
  "Mental Clarity",
  "Mental Elevation",
  "Mental Expansion",
  "Mental Stimulation",
  "Mild High",
  "Mood Elevates",
  "Mood Elevation",
  "Mood Enhance",
  "Mood Shift",
  "Mood Softening",
  "Motivation",
  "Muscle Relax",
  "Muscle Relaxation",
  "Muscle Tension",
  "Nausea Reduction",
  "Pain Relief",
  "Physical",
  "Reduce Social Inhibition",
  "Relax",
  "Relaxation",
  "Relaxed",
  "Relaxed Body",
  "Relaxed Euphoria",
  "Relaxing",
  "Sedating",
  "Sedation",
  "Sedative",
  "Sedative Relax",
  "Sleep",
  "Sleepy",
  "Social comfort",
  "Stimulating",
  "Stress",
  "Stress Reducer",
  "Stress Relief",
  "Thought Acceleration",
  "Uplift",
  "Uplifted"
];

const ALL_TERPENES = [
  "No TERPENES",
  "Alpha-bisabolol",
  "Alpha-humulene",
  "Alpha-pinene",
  "Beta-caryophyllene",
  "Beta-myrcene",
  "Beta-pinene",
  "Borneol",
  "Camphene",
  "Caryophyllene",
  "Caryophyllene oxide",
  "Delta-3-carene",
  "Eucalyptol",
  "Farnesene",
  "Fenchol",
  "Geraniol",
  "Guaiol",
  "Humulene",
  "Limonene",
  "Linalool",
  "Myrcene",
  "Nerolidol",
  "Ocimene",
  "P-cymene",
  "Phytol",
  "Pinene",
  "Sabinene",
  "Terpinene",
  "Terpineol",
  "Terpinolene",
  "Trans-nerolidol",
  "Valencene",
];

// Multi-select effects dropdown component
const EffectsMultiSelect = ({ selected = [], onChange, maxItems = 8, testId = "effects-select" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleEffect = (effect) => {
    if (selected.includes(effect)) {
      onChange(selected.filter(e => e !== effect));
    } else if (selected.length < maxItems) {
      onChange([...selected, effect]);
    }
  };

  const visibleEffects = search
    ? ALL_EFFECTS.filter(e => e.toLowerCase().includes(search.toLowerCase()))
    : ALL_EFFECTS;

  return (
    <div ref={dropRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 text-left flex items-center justify-between outline-none"
      >
        <span className={selected.length ? 'text-white text-sm' : 'text-zinc-500 text-sm'}>
          {selected.length ? `${selected.length} effect${selected.length > 1 ? 's' : ''} selected` : 'Select effects (max 8)...'}
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map(e => (
            <span key={e} className="bg-gold-500/15 border border-gold-500/30 text-gold-500 text-xs px-2 py-1 flex items-center gap-1">
              {e}
              <button type="button" onClick={() => toggleEffect(e)} className="hover:text-red-400">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 max-h-72 overflow-y-auto shadow-xl">
          <div className="sticky top-0 bg-zinc-900 p-2 border-b border-zinc-800">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search effects..."
              className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm h-8 px-3 outline-none focus:border-gold-500"
            />
          </div>
          <div className="p-1">
            {visibleEffects.map(effect => (
              <button
                key={effect}
                type="button"
                onClick={() => toggleEffect(effect)}
                disabled={!selected.includes(effect) && selected.length >= maxItems}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between ${
                  selected.includes(effect)
                    ? 'bg-gold-500/10 text-gold-500'
                    : selected.length >= maxItems
                      ? 'text-zinc-600 cursor-not-allowed'
                      : 'text-zinc-300 hover:bg-zinc-800'
                }`}
              >
                {effect}
                {selected.includes(effect) && <Check className="w-3 h-3" />}
              </button>
            ))}
            {visibleEffects.length === 0 && (
              <p className="text-zinc-600 text-xs px-3 py-2">No effects match your search</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Analytics tracking helper
const trackEvent = async (eventType, data = {}) => {
  const token = localStorage.getItem('userToken');
  if (!token) return;
  
  try {
    await axios.post(`${API}/analytics/track`, {
      event_type: eventType,
      ...data
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (err) {
    // Silently fail - don't interrupt user experience
    console.debug('Analytics tracking failed:', err);
  }
};

// Session tracking - time on page
let sessionStartTime = Date.now();
let sessionId = null;
let pagesVisited = [];
let strainsViewed = [];

const initSession = (sId) => {
  sessionId = sId || localStorage.getItem('sessionId');
  sessionStartTime = Date.now();
  pagesVisited = [];
  strainsViewed = [];
};

const updateSession = async () => {
  const token = localStorage.getItem('userToken');
  if (!token || !sessionId) return;
  
  const duration = Math.floor((Date.now() - sessionStartTime) / 1000);
  
  try {
    await axios.post(`${API}/analytics/session/update?session_id=${sessionId}&duration=${duration}`, null, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (err) {
    console.debug('Session update failed:', err);
  }
};

// Update session every 30 seconds and on page unload
if (typeof window !== 'undefined') {
  setInterval(updateSession, 30000);
  window.addEventListener('beforeunload', updateSession);
}

// Buyer-facing category label (stored value stays 'Dried Flower')
export const categoryLabel = (c) => (c === 'Dried Flower' ? 'Flower' : c);

// THC display helpers — always show a "%" sign
export const formatThc = (v) => {
  const t = String(v ?? '').trim();
  if (!t) return '-';
  if (t.includes('%')) return t;
  const m = t.match(/^(\d+(?:\.\d+)?)\s*(\+)?$/);
  return m ? `${m[1]}%${m[2] || ''}` : t;
};

const thcNum = (v) => parseFloat(String(v ?? '').replace(/[^\d.]/g, ''));

export const getStrainThc = (strain) => {
  const lots = (strain?.lots || []).filter(l => !isNaN(thcNum(l.thc_percentage)));
  if (lots.length === 0) return formatThc(strain?.thc_percentage);
  if (lots.length === 1) return formatThc(lots[0].thc_percentage);
  const vals = lots.map(l => thcNum(l.thc_percentage));
  const lo = Math.min(...vals), hi = Math.max(...vals);
  return lo === hi ? `${lo}%` : `${lo}\u2013${hi}%`;
};

export const strainQty = (strain) => (strain?.lots && strain.lots.length > 1 && strain.lots.some(l => l.lot_size))
  ? strain.lots.map((l, i) => `Lot ${i + 1}: ${l.lot_size || '-'}`).join(', ')
  : strain?.lot_size;

export const LotsList = ({ strain, compact }) => {
  if (!strain?.lots || strain.lots.length < 2) return null;
  return (
    <div data-testid={`strain-lots-${strain.id}`} className="flex flex-wrap gap-1.5 mt-2">
      {strain.lots.map((lot, i) => (
        <span key={`${strain.id}-lot-${i}`} data-testid={`strain-lot-${strain.id}-${i}`}
          className={`bg-black/60 border border-gold-500/30 text-zinc-300 font-mono ${compact ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'}`}>
          {`Lot ${i + 1}`}: <span className="text-gold-500">{formatThc(lot.thc_percentage)}</span>
          {!compact && lot.lot_size ? <span className="text-zinc-500"> · {lot.lot_size}</span> : null}
        </span>
      ))}
    </div>
  );
};

export const LotsEditor = ({ lots, onChange, testIdPrefix }) => {
  const list = lots || [];
  const update = (i, field, value) => onChange(list.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  return (
    <div data-testid={`${testIdPrefix}-lots-editor`} className="bg-zinc-900/50 border border-zinc-800 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs uppercase tracking-widest text-zinc-500">Lots in stock (optional)</label>
        <button type="button" data-testid={`${testIdPrefix}-add-lot-btn`}
          onClick={() => onChange([...list, { thc_percentage: '', lot_size: '' }])}
          className="text-gold-500 text-xs border border-gold-500/40 px-2 py-1 hover:bg-gold-500/10 transition-colors">+ Add Lot</button>
      </div>
      {list.length === 0 && <p className="text-zinc-600 text-xs">No lots added. Add 2 or more to show them on one card. Shown publicly as "Lot 1", "Lot 2" only.</p>}
      {list.map((lot, i) => (
        <div key={`${testIdPrefix}-lot-${i}`} className="flex items-center gap-2">
          <span className="text-zinc-400 text-xs font-mono w-12">Lot {i + 1}</span>
          <input data-testid={`${testIdPrefix}-lot-thc-${i}`} type="text" value={lot.thc_percentage || ''} placeholder="THC % e.g. 28.4"
            onChange={e => update(i, 'thc_percentage', e.target.value)}
            className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-gold-500 text-white h-9 px-3 text-sm outline-none" />
          <input data-testid={`${testIdPrefix}-lot-size-${i}`} type="text" value={lot.lot_size || ''} placeholder="Amount e.g. 30 kg"
            onChange={e => update(i, 'lot_size', e.target.value)}
            className="flex-1 bg-zinc-950 border border-zinc-800 focus:border-gold-500 text-white h-9 px-3 text-sm outline-none" />
          <button type="button" data-testid={`${testIdPrefix}-remove-lot-${i}`} onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            className="text-red-400 text-xs px-2 hover:text-red-300">Remove</button>
        </div>
      ))}
    </div>
  );
};

// Strain Card Component
const StrainCard = ({ strain, onSelect, isSelected, onViewDetails, onAddToCart, isInCart, isAlerted, onToggleAlert }) => {
  const getTypeColor = (type) => {
    const lowerType = type.toLowerCase();
    if (lowerType.includes('indica')) return 'text-purple-400';
    if (lowerType.includes('sativa')) return 'text-green-400';
    return 'text-gold-500';
  };

  return (
    <div 
      data-testid={`strain-card-${strain.id}`}
      className={`bg-charcoal border ${isSelected ? 'border-gold-500' : 'border-white/5'} hover:border-gold-500/50 transition-all duration-500 group relative overflow-hidden cursor-pointer`}
      onClick={() => onViewDetails(strain)}
    >
      <div className="aspect-[4/5] overflow-hidden relative">
        <img 
          src={strain.image_url || 'https://images.unsplash.com/photo-1721058454918-5582b68de13c'}
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1721058454918-5582b68de13c'; }}
          alt={strain.strain_name}
          className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105 select-none pointer-events-none"
          onContextMenu={preventImageActions}
          onDragStart={preventImageActions}
          draggable="false"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        
        {/* Growing Method Badge */}
        {strain.growing_method && (
          <div className="absolute top-4 right-4 w-20 bg-black/80 backdrop-blur-sm px-2 py-1 border border-white/20 text-center">
            <span className="text-zinc-300 font-mono text-[10px] font-bold uppercase">{strain.growing_method}</span>
          </div>
        )}

        {/* THC Badge */}
        <div data-testid={`strain-thc-badge-${strain.id}`} className={`absolute ${strain.growing_method ? 'top-12' : 'top-4'} right-4 w-20 bg-black/80 backdrop-blur-sm px-1 py-1 border border-gold-500/30 text-center`}>
          <span className="text-gold-500 font-mono text-[11px] font-bold whitespace-nowrap">{getStrainThc(strain)}</span>
        </div>

        {/* Category Badge */}
        {strain.category && (
          <div className={`absolute ${strain.growing_method ? 'top-[84px]' : 'top-14'} right-4 w-20 bg-gold-500/20 backdrop-blur-sm px-2 py-0.5 border border-gold-500/40 text-center`}>
            <span className="text-gold-500 font-mono text-[9px] font-bold uppercase leading-tight">{categoryLabel(strain.category)}</span>
          </div>
        )}
        
        {/* COA Badge */}
        {strain.coa_url && (
          <div className={`absolute ${strain.category && strain.growing_method ? 'top-[116px]' : strain.growing_method || strain.category ? 'top-[84px]' : 'top-14'} right-4 w-20 bg-cannabis-green/90 backdrop-blur-sm px-2 py-0.5 border border-cannabis-green text-center`}>
            <span className="text-white font-mono text-xs font-bold">COA</span>
          </div>
        )}

        {/* THC Non-Detect Badge */}
        {strain.thc_non_detect && (
          <div data-testid={`nd-thc-badge-${strain.id}`} className="absolute top-4 left-4 bg-red-600/90 backdrop-blur-sm px-3 py-1 border border-red-400/50">
            <span className="text-white font-mono text-xs font-bold tracking-wider">ND/THC</span>
          </div>
        )}

        {/* BC DD Badge */}
        {strain.bc_dd_compliant && (
          <div className="absolute top-4 left-12 bg-blue-600/90 backdrop-blur-sm px-2 py-0.5 border border-blue-400 text-center">
            <span className="text-white font-mono text-[9px] font-bold">BC DD</span>
          </div>
        )}
        
        {/* Selection checkbox */}
        <div 
          className="absolute top-4 left-4 flex gap-1.5"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            data-testid={`select-strain-${strain.id}`}
            onClick={() => onSelect(strain.id)}
            className={`w-6 h-6 border-2 ${isSelected ? 'bg-gold-500 border-gold-500' : 'border-white/30 hover:border-gold-500'} transition-all flex items-center justify-center`}
          >
            {isSelected && <span className="text-black text-sm">&#10003;</span>}
          </button>
          <button
            data-testid={`add-to-cart-${strain.id}`}
            onClick={() => onAddToCart(strain.id)}
            className={`w-6 h-6 border-2 flex items-center justify-center transition-all ${
              isInCart ? 'bg-emerald-500 border-emerald-500' : 'border-white/30 hover:border-emerald-400 hover:bg-emerald-500/20'
            }`}
            title={isInCart ? 'In inquiry cart' : 'Add to inquiry cart'}
          >
            <ShoppingCart className={`w-3 h-3 ${isInCart ? 'text-black' : 'text-white/60'}`} />
          </button>
          <button
            data-testid={`alert-strain-${strain.id}`}
            onClick={() => onToggleAlert && onToggleAlert(strain.strain_name)}
            className={`w-6 h-6 border-2 flex items-center justify-center transition-all ${
              isAlerted ? 'bg-amber-500 border-amber-500' : 'border-white/30 hover:border-amber-400 hover:bg-amber-500/20'
            }`}
            title={isAlerted ? 'Subscribed to alerts' : 'Get alerted when available'}
          >
            <Bell className={`w-3 h-3 ${isAlerted ? 'text-black' : 'text-white/60'}`} />
          </button>
        </div>
      </div>
      
      <div className="p-6 absolute bottom-0 w-full">
        <div className={`font-manrope text-xs uppercase tracking-widest ${getTypeColor(strain.type)} mb-2`}>
          {strain.type}
        </div>
        <h3 className="font-cinzel font-bold text-xl text-white mb-2 flex items-center gap-2">
          {strain.strain_name}
          {strain.bc_dd_compliant && (
            <span className="bg-blue-600/20 border border-blue-400/40 text-blue-300 px-1.5 py-0.5 text-[9px] font-mono font-bold tracking-widest">BC DD</span>
          )}
        </h3>
        <div className="flex items-center gap-4 text-zinc-400 text-sm">
          <span className="flex items-center gap-1">
            <Activity className="w-4 h-4 text-gold-500" />
            <span data-testid={`strain-card-thc-${strain.id}`}>THC {getStrainThc(strain)}</span>
          </span>
          {!(strain.lots && strain.lots.length > 1) && <span className="font-mono text-xs text-zinc-500">{strain.lot_size}</span>}
        </div>
        <LotsList strain={strain} compact />
        {(strain.grade_a_kg || strain.grade_b_kg || strain.grade_c_kg) && (
          <div className="flex gap-1.5 mt-2">
            {strain.grade_a_kg > 0 && <span className="bg-cannabis-green/20 text-cannabis-green text-[10px] font-bold px-1.5 py-0.5">A: {strain.grade_a_kg}kg</span>}
            {strain.grade_b_kg > 0 && <span className="bg-gold-500/20 text-gold-500 text-[10px] font-bold px-1.5 py-0.5">B: {strain.grade_b_kg}kg</span>}
            {strain.grade_c_kg > 0 && <span className="bg-zinc-600/30 text-zinc-400 text-[10px] font-bold px-1.5 py-0.5">C: {strain.grade_c_kg}kg</span>}
          </div>
        )}
      </div>
    </div>
  );
};


// Terpene Radar Chart Component
const TerpeneRadar = ({ terpenes, percentage }) => {
  if (!terpenes || terpenes.length === 0) return null;
  // Assign relative values — if we have a total percentage, distribute, otherwise equal weight
  const count = Math.min(terpenes.length, 6);
  const items = terpenes.slice(0, count);
  // Simple SVG radar chart
  const size = 200;
  const cx = size / 2, cy = size / 2, r = 70;
  const angleStep = (2 * Math.PI) / items.length;
  // Generate polygon points — use decreasing weights for each terpene (dominant first)
  const weights = items.map((_, i) => 1 - (i * 0.12));
  const points = items.map((_, i) => {
    const angle = (i * angleStep) - (Math.PI / 2);
    const pr = r * weights[i];
    return `${cx + pr * Math.cos(angle)},${cy + pr * Math.sin(angle)}`;
  }).join(' ');
  const axisPoints = items.map((_, i) => {
    const angle = (i * angleStep) - (Math.PI / 2);
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle), label: items[i] };
  });
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background rings */}
        {[0.33, 0.66, 1].map((s, i) => (
          <polygon key={i} points={axisPoints.map(p => `${cx + (p.x - cx) * s},${cy + (p.y - cy) * s}`).join(' ')} fill="none" stroke="#333" strokeWidth="0.5" />
        ))}
        {/* Axis lines */}
        {axisPoints.map((p, i) => <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#333" strokeWidth="0.5" />)}
        {/* Data polygon */}
        <polygon points={points} fill="rgba(212,175,55,0.15)" stroke="#D4AF37" strokeWidth="1.5" />
        {/* Data points */}
        {items.map((_, i) => {
          const angle = (i * angleStep) - (Math.PI / 2);
          const pr = r * weights[i];
          return <circle key={i} cx={cx + pr * Math.cos(angle)} cy={cy + pr * Math.sin(angle)} r="3" fill="#D4AF37" />;
        })}
        {/* Labels */}
        {axisPoints.map((p, i) => {
          const angle = (i * angleStep) - (Math.PI / 2);
          const lr = r + 22;
          const lx = cx + lr * Math.cos(angle);
          const ly = cy + lr * Math.sin(angle);
          return (
            <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fill="#888" fontSize="9" fontFamily="manrope">
              {items[i].length > 12 ? items[i].slice(0, 11) + '…' : items[i]}
            </text>
          );
        })}
      </svg>
      {percentage && <p className="text-zinc-500 text-xs mt-1">Total Terpenes: {percentage}%</p>}
    </div>
  );
};

// Strain Detail Modal
const StrainDetailModal = ({ strain, onClose, onAddToInquiry, isInInquiry, onRequestSample }) => {
  const [showCoa, setShowCoa] = useState(false);
  const [coaLoading, setCoaLoading] = useState(false);
  const [coaBlobUrl, setCoaBlobUrl] = useState(null);
  const [imageZoomed, setImageZoomed] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });
  const [detailView, setDetailView] = useState(() => sessionStorage.getItem('fg_detail_view') || 'image');

  if (!strain) return null;

  const handleViewCoa = async () => {
    setCoaLoading(true);
    try {
      const token = localStorage.getItem('userToken');
      trackEvent('coa_view', { strain_name: strain.strain_name }, null, strain.id, strain.strain_name);
      const response = await axios.get(`${API}/strains/${strain.id}/coa`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(response.data);
      setCoaBlobUrl(blobUrl);
      setShowCoa(true);
    } catch {
      // Fallback: try to open COA URL directly
      if (strain.coa_url) {
        window.open(strain.coa_url, '_blank');
      } else {
        toast.error('Unable to load COA');
      }
    } finally {
      setCoaLoading(false);
    }
  };

  const closeCoa = () => {
    setShowCoa(false);
    if (coaBlobUrl) {
      URL.revokeObjectURL(coaBlobUrl);
      setCoaBlobUrl(null);
    }
  };

  return (
    <div 
      data-testid="strain-detail-modal"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-0 md:p-4"
      onClick={onClose}
    >
      <div 
        className="bg-charcoal border border-white/10 max-w-4xl w-full h-full md:h-auto md:max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky back/close bar - always visible on mobile */}
        <div className="sticky top-0 z-10 bg-charcoal/95 backdrop-blur-md border-b border-white/10 px-4 py-3 flex items-center justify-between md:hidden">
          <button
            data-testid="mobile-back-btn"
            onClick={onClose}
            className="flex items-center gap-2 text-black bg-gold-500 px-3 py-1.5 active:bg-gold-400 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-manrope uppercase tracking-widest font-bold">Back</span>
          </button>
          <span className="text-gold-500 font-cinzel font-bold text-sm truncate max-w-[50%]">{strain.strain_name}</span>
        </div>

        {/* View Toggle */}
        <div className="hidden md:flex items-center justify-end px-4 py-2 border-b border-white/5">
          <div className="flex border border-zinc-800 overflow-hidden" data-testid="detail-view-toggle">
            <button
              data-testid="detail-view-image"
              onClick={() => { setDetailView('image'); sessionStorage.setItem('fg_detail_view', 'image'); }}
              className={`px-3 py-1 text-[10px] uppercase tracking-widest font-manrope font-bold transition-all ${
                detailView === 'image' ? 'bg-gold-500 text-black' : 'text-zinc-500 hover:text-white'
              }`}
            >
              Image
            </button>
            <button
              data-testid="detail-view-data"
              onClick={() => { setDetailView('data'); sessionStorage.setItem('fg_detail_view', 'data'); }}
              className={`px-3 py-1 text-[10px] uppercase tracking-widest font-manrope font-bold transition-all ${
                detailView === 'data' ? 'bg-gold-500 text-black' : 'text-zinc-500 hover:text-white'
              }`}
            >
              Data
            </button>
          </div>
        </div>

        {detailView === 'image' ? (
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Image with Zoom + Logo */}
          <div className="flex flex-col">
            <div 
              className="aspect-square relative overflow-hidden cursor-zoom-in group"
              data-testid="strain-image-zoom"
              onClick={() => setImageZoomed(true)}
            >
              <img 
                src={strain.image_url || 'https://images.unsplash.com/photo-1721058454918-5582b68de13c'}
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1721058454918-5582b68de13c'; }}
                alt={strain.strain_name}
                className="w-full h-full object-cover select-none pointer-events-none transition-transform duration-300 group-hover:scale-105"
                onContextMenu={preventImageActions}
                onDragStart={preventImageActions}
                draggable="false"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center">
                <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-80 transition-opacity drop-shadow-lg" />
              </div>
              <button 
                data-testid="close-modal-btn"
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                className="absolute top-4 right-4 w-10 h-10 bg-gold-500 text-black hidden md:flex items-center justify-center hover:bg-gold-400 transition-all z-10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Firegold Marketplace Logo */}
            <div className="bg-zinc-950 py-6 flex items-center justify-center border-t border-white/5">
              <img 
                src={FIREGOLD_MARKETPLACE_LOGO} 
                alt="Firegold Marketplace" 
                data-testid="strain-detail-marketplace-logo"
                className="w-[50%] h-auto" 
                style={{ mixBlendMode: 'lighten' }} 
              />
            </div>
          </div>
          
          {/* Details */}
          <div className="p-8 flex flex-col">
            <div className="text-gold-500 font-manrope text-xs uppercase tracking-widest mb-2 flex items-center gap-3">
              <span>{strain.type}</span>
              {strain.growing_method && (
                <span className="bg-white/10 border border-white/20 text-zinc-300 px-2 py-0.5 text-[10px] font-bold tracking-widest">{strain.growing_method}</span>
              )}
              {strain.bc_dd_compliant && (
                <span className="bg-blue-600/20 border border-blue-400/40 text-blue-300 px-2 py-0.5 text-[10px] font-bold tracking-widest">BC DD</span>
              )}
              {strain.thc_non_detect && (
                <span data-testid="detail-nd-thc-badge" className="bg-red-600/20 border border-red-400/40 text-red-300 px-2 py-0.5 text-[10px] font-bold tracking-widest">ND/THC</span>
              )}
            </div>
            <h2 className="font-cinzel font-bold text-3xl text-white mb-4">{strain.strain_name}</h2>
            
            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-zinc-900/50 p-4 border border-white/5">
                <div className="text-zinc-500 font-manrope text-xs uppercase tracking-widest mb-1">THC</div>
                <div data-testid="detail-thc-value" className="text-white font-mono text-lg">{getStrainThc(strain)}</div>
                <LotsList strain={strain} compact />
              </div>
              <div className="bg-zinc-900/50 p-4 border border-white/5">
                <div className="text-zinc-500 font-manrope text-xs uppercase tracking-widest mb-1">CBD</div>
                <div className="text-gold-500 font-mono text-lg">{strain.cbd_percentage ? formatThc(strain.cbd_percentage) : '-'}</div>
                {strain.target_price_per_gram && (
                  <div className="text-zinc-400 font-mono text-xs mt-1">Target: ${Number(strain.target_price_per_gram).toFixed(2)} / g</div>
                )}
              </div>
            </div>
            
            {/* Lineage */}
            {strain.thc_non_detect && (
              <div data-testid="detail-nd-thc-info" className="mb-6 bg-red-600/10 border border-red-500/30 p-4">
                <div className="flex items-center gap-2">
                  <span className="bg-red-600 text-white font-mono text-xs font-bold px-2 py-0.5">ND/THC</span>
                  <span className="text-red-300 font-manrope text-sm">THC Non-Detect — This concentrate tests below detectable THC levels</span>
                </div>
              </div>
            )}
            {strain.extract_purity && (
              <div data-testid="detail-extract-purity" className="mb-6 bg-zinc-900/50 p-4 border border-white/5">
                <div className="text-zinc-500 font-manrope text-xs uppercase tracking-widest mb-1">Total Extract Purity</div>
                <div className="text-gold-500 font-mono text-lg font-bold">{strain.extract_purity}%</div>
              </div>
            )}
            <div className="mb-6">
              <div className="text-zinc-500 font-manrope text-xs uppercase tracking-widest mb-2">Lineage</div>
              <div className="text-zinc-300 font-light">{strain.lineage}</div>
            </div>
            
            {/* Terpenes */}
            <div className="mb-6">
              <div className="text-zinc-500 font-manrope text-xs uppercase tracking-widest mb-2 flex items-center gap-2">
                <Droplets className="w-4 h-4" /> Terpene Profile
              </div>
              <div className="flex flex-wrap gap-2">
                {strain.terpene_profile.map((terp, idx) => (
                  <span key={idx} className="px-3 py-1 bg-zinc-900 border border-white/10 text-zinc-300 text-sm">
                    {terp}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Effects */}
            <div className="mb-6">
              <div className="text-zinc-500 font-manrope text-xs uppercase tracking-widest mb-2">Potential Terpene Effects</div>
              <div className="flex flex-wrap gap-1.5">
                {(Array.isArray(strain.effects) ? strain.effects : (typeof strain.effects === 'string' && strain.effects ? strain.effects.split(',').map(e => e.trim()) : [])).map((effect, i) => (
                  <span key={i} className="bg-gold-500/10 border border-gold-500/30 text-gold-500 text-xs px-2 py-1">{effect}</span>
                ))}
              </div>
              <p className="text-zinc-600 text-[10px] mt-2 leading-relaxed">Reported terpene-associated experiences may vary between individuals. FireGold does not make medical claims regarding cannabis products.</p>
            </div>
            
            {/* Lot Size */}
            <div className="mb-6">
              <div className="text-zinc-500 font-manrope text-xs uppercase tracking-widest mb-2">Available Quantity</div>
              <div data-testid="detail-available-qty" className="text-white font-mono">{strainQty(strain)}</div>
            </div>


            {/* Grade Breakdown */}
            {(strain.grade_a_kg || strain.grade_b_kg || strain.grade_c_kg) && (
              <div className="mb-6">
                <div className="text-zinc-500 font-manrope text-xs uppercase tracking-widest mb-3">Grade Breakdown</div>
                <div className="grid grid-cols-3 gap-2">
                  {strain.grade_a_kg > 0 && (
                    <div className="bg-cannabis-green/10 border border-cannabis-green/30 p-3 text-center">
                      <div className="text-cannabis-green font-bold text-lg font-mono">{strain.grade_a_kg}</div>
                      <div className="text-cannabis-green/70 text-[10px] uppercase tracking-widest">Grade A (Bigs) (kg)</div>
                    </div>
                  )}
                  {strain.grade_b_kg > 0 && (
                    <div className="bg-gold-500/10 border border-gold-500/30 p-3 text-center">
                      <div className="text-gold-500 font-bold text-lg font-mono">{strain.grade_b_kg}</div>
                      <div className="text-gold-500/70 text-[10px] uppercase tracking-widest">Grade B (Mids) (kg)</div>
                    </div>
                  )}
                  {strain.grade_c_kg > 0 && (
                    <div className="bg-zinc-700/30 border border-zinc-600/30 p-3 text-center">
                      <div className="text-zinc-400 font-bold text-lg font-mono">{strain.grade_c_kg}</div>
                      <div className="text-zinc-500 text-[10px] uppercase tracking-widest">Grade C (Smalls) (kg)</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* COA Link */}
            {strain.coa_url && (
              <div className="mb-6">
                <button
                  data-testid="view-coa-btn"
                  onClick={handleViewCoa}
                  disabled={coaLoading}
                  className="w-full flex items-center justify-center gap-3 bg-cannabis-green text-black hover:bg-cannabis-green/90 px-6 py-3 transition-all"
                >
                  <FileText className="w-5 h-5" />
                  <span className="font-bold uppercase tracking-widest text-sm">
                    {coaLoading ? 'Loading COA...' : 'View Certificate of Analysis'}
                  </span>
                </button>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="mt-auto space-y-3">
              <button 
                data-testid="request-sample-btn"
                onClick={() => onRequestSample(strain)}
                className="w-full border-2 border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black font-bold uppercase tracking-widest px-8 py-4 transition-all flex items-center justify-center gap-2"
              >
                <Package className="w-4 h-4" />
                Request Sample
              </button>
              <button 
                data-testid="add-to-inquiry-btn"
                onClick={() => onAddToInquiry(strain.id)}
                className={`w-full ${isInInquiry ? 'bg-cannabis-green' : 'bg-gold-500 hover:bg-gold-400'} text-black font-bold uppercase tracking-widest px-8 py-4 transition-all`}
              >
                {isInInquiry ? 'Added to Inquiry' : 'Add to Inquiry'}
              </button>
            </div>
          </div>
        </div>

        ) : (
        /* DATA VIEW */
        <div className="p-6 md:p-8" data-testid="strain-data-view">
          {/* Close button for data view */}
          <button 
            data-testid="close-modal-btn-data"
            onClick={onClose}
            className="absolute top-16 right-4 w-10 h-10 bg-gold-500 text-black hidden md:flex items-center justify-center hover:bg-gold-400 transition-all z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Hero Image */}
          <div className="aspect-video relative overflow-hidden rounded-lg mb-6 max-h-[280px]">
            {strain.image_url ? (
              <img
                src={strain.image_url}
                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1721058454918-5582b68de13c'; }}
                alt={strain.strain_name}
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <div className="w-full h-full bg-zinc-900 flex items-center justify-center border border-zinc-800 rounded-lg">
                <p className="text-zinc-600 text-sm">No Hero Image Available</p>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-4">
              <h2 className="text-white font-cinzel font-bold text-2xl">{strain.strain_name}</h2>
              <p className="text-zinc-300 text-sm">{strain.type} · {categoryLabel(strain.category)}</p>
            </div>
          </div>

          {/* Key Data Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-zinc-900/60 border border-zinc-800 p-3 text-center">
              <div className="text-zinc-500 text-[10px] uppercase tracking-widest">THC</div>
              <div data-testid="data-view-thc-value" className="text-gold-500 font-mono font-bold text-lg">{getStrainThc(strain)}</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 p-3 text-center">
              <div className="text-zinc-500 text-[10px] uppercase tracking-widest">CBD</div>
              <div className="text-white font-mono font-bold text-lg">{strain.cbd_percentage || '-'}</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 p-3 text-center">
              <div className="text-zinc-500 text-[10px] uppercase tracking-widest">Lot Size</div>
              <div className="text-white font-mono font-bold text-lg">{strainQty(strain)}</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 p-3 text-center">
              <div className="text-zinc-500 text-[10px] uppercase tracking-widest">Method</div>
              <div className="text-white font-mono font-bold text-sm">{strain.growing_method}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Terpene Profile Wheel */}
            <div className="bg-zinc-900/40 border border-zinc-800 p-4 rounded">
              <h3 className="text-zinc-500 text-xs uppercase tracking-widest font-manrope mb-3">Terpene Profile</h3>
              {Array.isArray(strain.terpene_profile) && strain.terpene_profile.length > 0 ? (
                <TerpeneRadar terpenes={strain.terpene_profile.slice(0, 6)} percentage={strain.terpene_percentage} />
              ) : (
                <p className="text-zinc-600 text-sm py-8 text-center">No terpene data available</p>
              )}
            </div>

            {/* Chemistry & Strain Data */}
            <div className="space-y-3">
              <div className="bg-zinc-900/40 border border-zinc-800 p-4 rounded">
                <h3 className="text-zinc-500 text-xs uppercase tracking-widest font-manrope mb-2">Details</h3>
                <div className="space-y-1.5 text-sm">
                  {strain.lineage && <div className="flex justify-between"><span className="text-zinc-500">Lineage</span><span className="text-white">{strain.lineage}</span></div>}
                  {strain.lots && strain.lots.length > 1 && strain.lots.map((lot, i) => (
                    <div key={`dv-lot-${i}`} className="flex justify-between"><span className="text-zinc-500">{`Lot ${i + 1}`}</span><span className="text-gold-500 font-mono">{formatThc(lot.thc_percentage)}{lot.lot_size ? ` · ${lot.lot_size}` : ''}</span></div>
                  ))}
                  {strain.target_price_per_gram && <div className="flex justify-between"><span className="text-zinc-500">Target Price</span><span className="text-gold-500 font-mono">${Number(strain.target_price_per_gram).toFixed(2)} / g</span></div>}
                  {strain.terpene_percentage && <div className="flex justify-between"><span className="text-zinc-500">Terpene %</span><span className="text-white">{strain.terpene_percentage}%</span></div>}
                </div>
              </div>

              {/* COA Thumbnail */}
              <div className="bg-zinc-900/40 border border-zinc-800 p-4 rounded">
                <h3 className="text-zinc-500 text-xs uppercase tracking-widest font-manrope mb-2">Certificate of Analysis</h3>
                {strain.coa_url ? (
                  <button
                    onClick={handleViewCoa}
                    disabled={coaLoading}
                    className="flex items-center gap-2 bg-cannabis-green/10 border border-cannabis-green/30 text-cannabis-green px-3 py-2 text-sm hover:bg-cannabis-green/20 transition-all w-full justify-center"
                    data-testid="coa-view-btn-data"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    {coaLoading ? 'Loading...' : 'COA Verified — View'}
                  </button>
                ) : (
                  <div className="text-zinc-600 text-sm flex items-center gap-2 py-2">
                    <FileText className="w-4 h-4" /> COA Not Uploaded
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Effects */}
          {strain.effects && (Array.isArray(strain.effects) ? strain.effects.length > 0 : strain.effects) && (
            <div className="mb-6">
              <h3 className="text-zinc-500 text-xs uppercase tracking-widest font-manrope mb-2">Potential Terpene Effects</h3>
              <div className="flex flex-wrap gap-1.5">
                {(Array.isArray(strain.effects) ? strain.effects : strain.effects.split(',').map(e => e.trim())).map((effect, i) => (
                  <span key={i} className="bg-gold-500/10 border border-gold-500/30 text-gold-500 text-xs px-2 py-1">{effect}</span>
                ))}
              </div>
              <p className="text-zinc-600 text-[10px] mt-2 leading-relaxed">Reported terpene-associated experiences may vary between individuals. FireGold does not make medical claims regarding cannabis products.</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => onAddToInquiry && onAddToInquiry(strain)}
              className={`flex-1 py-3 font-bold uppercase tracking-widest text-xs transition-all ${
                isInInquiry ? 'bg-zinc-800 text-zinc-500' : 'bg-gold-500 hover:bg-gold-400 text-black'
              }`}
            >
              {isInInquiry ? 'Added to Inquiry' : 'Send Inquiry'}
            </button>
            {strain.coa_url && !showCoa && (
              <button
                onClick={handleViewCoa}
                disabled={coaLoading}
                className="px-6 py-3 border border-gold-500/30 text-gold-500 hover:border-gold-500 font-bold uppercase tracking-widest text-xs transition-all"
              >
                View COA
              </button>
            )}
          </div>
        </div>
        )}

        {/* Full-screen Image Zoom Overlay */}
        {imageZoomed && (
          <div 
            data-testid="image-zoom-overlay"
            className="fixed inset-0 z-[60] bg-black/95 flex flex-col cursor-zoom-out"
            onClick={() => setImageZoomed(false)}
          >
            <div className="flex items-center justify-between px-6 py-3 border-b border-white/10 bg-charcoal/80">
              <span className="font-cinzel font-bold text-white text-sm">{strain.strain_name}</span>
              <button
                data-testid="close-zoom-btn"
                onClick={() => setImageZoomed(false)}
                className="w-10 h-10 bg-gold-500 text-black hover:bg-gold-400 flex items-center justify-center transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div 
              className="flex-1 flex items-center justify-center p-4 overflow-hidden"
              onMouseMove={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setZoomPos({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
              }}
            >
              <img 
                src={strain.image_url || 'https://images.unsplash.com/photo-1721058454918-5582b68de13c'}
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1721058454918-5582b68de13c'; }}
                alt={strain.strain_name}
                className="max-w-full max-h-full object-contain select-none pointer-events-none"
                style={{ transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`, transform: 'scale(1.8)' }}
                onContextMenu={preventImageActions}
                onDragStart={preventImageActions}
                draggable="false"
              />
            </div>
          </div>
        )}

        {/* COA PDF Viewer Overlay */}
        {showCoa && coaBlobUrl && (
          <div className="fixed inset-0 z-[60] bg-black/95 flex flex-col" data-testid="coa-viewer">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-charcoal">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-cannabis-green" />
                <h3 className="font-cinzel font-bold text-lg text-white">Certificate of Analysis</h3>
                <span className="text-zinc-500 text-sm">— {strain.strain_name}</span>
              </div>
              <button
                data-testid="close-coa-btn"
                onClick={closeCoa}
                className="w-10 h-10 bg-gold-500 text-black hover:bg-gold-400 flex items-center justify-center transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-4">
              <iframe
                src={coaBlobUrl}
                title={`COA - ${strain.strain_name}`}
                className="w-full h-full border border-white/10"
                style={{ minHeight: 'calc(100vh - 80px)' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Filter Sidebar Component
const FilterSidebar = ({ filters, setFilters, strains, coaFilterOptions }) => {
  const types = useMemo(() => {
    const allTypes = strains.map(s => s.type);
    return [...new Set(allTypes)];
  }, [strains]);

  const terpenes = useMemo(() => {
    const allTerps = strains.flatMap(s => s.terpene_profile).filter(t => t !== 'N/A');
    return [...new Set(allTerps)];
  }, [strains]);

  return (
    <div data-testid="filter-sidebar" className="space-y-8">
      <div>
        <h3 className="font-manrope font-bold text-sm uppercase tracking-widest text-gold-500 mb-4">Category</h3>
        <div className="space-y-2">
          {['Dried Flower', 'Fresh Frozen', 'Trim', 'Concentrates'].map((cat) => (
            <button
              key={cat}
              data-testid={`filter-category-${cat.toLowerCase().replace(' ', '-')}`}
              onClick={() => setFilters(prev => ({ ...prev, category: prev.category === cat ? '' : cat }))}
              className={`block w-full text-left px-4 py-2 border ${
                filters.category === cat
                  ? 'border-gold-500 text-gold-500 bg-gold-500/10'
                  : 'border-zinc-800 text-zinc-400 hover:border-gold-500/50'
              } transition-all`}
            >
              {categoryLabel(cat)}
            </button>
          ))}
          <button
            data-testid="filter-bc-dd"
            onClick={() => setFilters(prev => ({ ...prev, bcDd: !prev.bcDd }))}
            className={`block w-full text-left px-4 py-2 border ${
              filters.bcDd
                ? 'border-blue-400 text-blue-300 bg-blue-600/10'
                : 'border-zinc-800 text-zinc-400 hover:border-blue-400/50'
            } transition-all`}
          >
            <span className="font-bold text-xs">BC DD</span>
            <span className="text-zinc-500 text-xs ml-2">Direct Delivery</span>
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-manrope font-bold text-sm uppercase tracking-widest text-gold-500 mb-4">Growing Method</h3>
        <div className="space-y-2">
          {['Indoor', 'Greenhouse', 'Outdoor'].map((method) => (
            <button
              key={method}
              data-testid={`filter-method-${method.toLowerCase()}`}
              onClick={() => setFilters(prev => ({ ...prev, growingMethod: prev.growingMethod === method ? '' : method }))}
              className={`block w-full text-left px-4 py-2 border ${
                filters.growingMethod === method
                  ? 'border-gold-500 text-gold-500 bg-gold-500/10'
                  : 'border-zinc-800 text-zinc-400 hover:border-gold-500/50'
              } transition-all`}
            >
              {method}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-manrope font-bold text-sm uppercase tracking-widest text-gold-500 mb-4">Potential Terpene Effects</h3>
        <p className="text-zinc-600 text-[10px] mb-3 leading-relaxed">Reported terpene-associated experiences may vary between individuals. FireGold does not make medical claims regarding cannabis products.</p>
        <div className="space-y-1">
          {(filters.selectedEffects || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {filters.selectedEffects.map(e => (
                <span key={e} className="bg-gold-500/15 border border-gold-500/30 text-gold-500 text-[10px] px-1.5 py-0.5 flex items-center gap-1">
                  {e}
                  <button onClick={() => setFilters(prev => ({...prev, selectedEffects: prev.selectedEffects.filter(x => x !== e)}))}><X className="w-2.5 h-2.5" /></button>
                </span>
              ))}
            </div>
          )}
          {(filters.selectedEffects || []).length >= 8 ? (
            <p data-testid="filter-effects-max" className="text-amber-400 text-[10px] uppercase tracking-widest font-bold py-2">Maximum of 8 selections allowed</p>
          ) : (
            <select
              data-testid="filter-effects"
              value=""
              onChange={(e) => {
                const val = e.target.value;
                if (val && !(filters.selectedEffects || []).includes(val) && (filters.selectedEffects || []).length < 8) {
                  setFilters(prev => ({...prev, selectedEffects: [...(prev.selectedEffects || []), val]}));
                }
                e.target.value = '';
              }}
              className="w-full bg-zinc-900/50 border border-zinc-800 text-zinc-400 text-xs h-10 px-3 outline-none focus:border-gold-500"
            >
              <option value="">Add effect filter... ({(filters.selectedEffects || []).length}/8)</option>
              {ALL_EFFECTS.filter(e => !(filters.selectedEffects || []).includes(e)).map(e => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div>
        <h3 className="font-manrope font-bold text-sm uppercase tracking-widest text-gold-500 mb-4">Terpene Profile</h3>
        <div className="space-y-1">
          {(filters.selectedTerpenes || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {filters.selectedTerpenes.map(t => (
                <span key={t} className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[10px] px-1.5 py-0.5 flex items-center gap-1">
                  {t}
                  <button onClick={() => setFilters(prev => ({...prev, selectedTerpenes: prev.selectedTerpenes.filter(x => x !== t)}))}><X className="w-2.5 h-2.5" /></button>
                </span>
              ))}
            </div>
          )}
          {(filters.selectedTerpenes || []).length >= 8 ? (
            <p data-testid="filter-terpenes-max" className="text-amber-400 text-[10px] uppercase tracking-widest font-bold py-2">Maximum of 8 selections allowed</p>
          ) : (
            <select
              data-testid="filter-terpenes"
              value=""
              onChange={(e) => {
                const val = e.target.value;
                if (val && !(filters.selectedTerpenes || []).includes(val) && (filters.selectedTerpenes || []).length < 8) {
                  setFilters(prev => ({...prev, selectedTerpenes: [...(prev.selectedTerpenes || []), val]}));
                }
                e.target.value = '';
              }}
              className="w-full bg-zinc-900/50 border border-zinc-800 text-zinc-400 text-xs h-10 px-3 outline-none focus:border-emerald-500"
            >
              <option value="">Add terpene filter... ({(filters.selectedTerpenes || []).length}/8)</option>
              {ALL_TERPENES.filter(t => !(filters.selectedTerpenes || []).includes(t)).map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div>
        <h3 className="font-manrope font-bold text-sm uppercase tracking-widest text-gold-500 mb-4">Strain Type</h3>
        <div className="space-y-2">
          {['All', 'Indica', 'Sativa', 'Hybrid'].map((type) => (
            <button
              key={type}
              data-testid={`filter-type-${type.toLowerCase()}`}
              onClick={() => setFilters(prev => ({ ...prev, type: type === 'All' ? '' : type }))}
              className={`block w-full text-left px-4 py-2 border ${
                filters.type === type || (type === 'All' && !filters.type)
                  ? 'border-gold-500 text-gold-500'
                  : 'border-zinc-800 text-zinc-400 hover:border-gold-500/50 hover:text-zinc-300'
              } transition-all`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-manrope font-bold text-sm uppercase tracking-widest text-gold-500 mb-4">THC Range</h3>
        <div className="space-y-2">
          {[
            { label: '25%+', value: 25 },
            { label: '30%+', value: 30 },
            { label: '35%+', value: 35 }
          ].map((range) => (
            <button
              key={range.value}
              data-testid={`filter-thc-${range.value}`}
              onClick={() => setFilters(prev => ({ ...prev, minThc: prev.minThc === range.value ? null : range.value }))}
              className={`block w-full text-left px-4 py-2 border ${
                filters.minThc === range.value
                  ? 'border-gold-500 text-gold-500'
                  : 'border-zinc-800 text-zinc-400 hover:border-gold-500/50'
              } transition-all`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h3 className="font-manrope font-bold text-sm uppercase tracking-widest text-gold-500 mb-4">Flower Grade</h3>
        <div className="space-y-2">
          {['A', 'B', 'C'].map((grade) => (
            <button
              key={grade}
              data-testid={`filter-grade-${grade.toLowerCase()}`}
              onClick={() => setFilters(prev => ({ ...prev, grade: prev.grade === grade ? '' : grade }))}
              className={`block w-full text-left px-4 py-2 border ${
                filters.grade === grade
                  ? 'border-gold-500 text-gold-500 bg-gold-500/10'
                  : 'border-zinc-800 text-zinc-400 hover:border-gold-500/50'
              } transition-all`}
            >
              Grade {grade === 'A' ? 'A (Bigs)' : grade === 'B' ? 'B (Mids)' : 'C (Smalls)'}
            </button>
          ))}
        </div>
      </div>

      {/* COA Chemistry Filters removed from user-facing sidebar */}

      {filters.type || filters.priceTier || filters.minThc || filters.search || filters.grade || filters.growingMethod || filters.category || filters.bcDd || filters.coaMinThc || filters.coaMaxThc || filters.coaMinTerpenes || filters.coaDominantTerpene || filters.coaAvailability ? (
        <button
          data-testid="clear-filters-btn"
          onClick={() => setFilters({ type: '', priceTier: '', minThc: null, search: '', grade: '', growingMethod: '', category: '', bcDd: false, selectedEffects: [], selectedTerpenes: [], coaMinThc: '', coaMaxThc: '', coaMinTerpenes: '', coaDominantTerpene: '', coaAvailability: '' })}
          className="w-full px-4 py-2 border border-zinc-700 text-zinc-400 hover:border-red-500 hover:text-red-400 transition-all uppercase tracking-widest text-xs"
        >
          Clear All Filters
        </button>
      ) : null}
    </div>
  );
};

// Sample Request Modal Component
const SampleRequestModal = ({ isOpen, onClose, strain, userData, userToken }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    shipping_address: '',
    message: ''
  });
  const [healthCanadaFile, setHealthCanadaFile] = useState(null);
  const [craFile, setCraFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-fill form with logged-in user data when modal opens
  useEffect(() => {
    if (isOpen && userData) {
      setFormData(prev => ({
        ...prev,
        company_name: prev.company_name || userData.company || '',
        contact_name: prev.contact_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
        email: prev.email || userData.email || ''
      }));
    }
  }, [isOpen, userData]);

  const handleFileChange = (e, setFile) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Please upload a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('File size must be less than 10MB');
        return;
      }
      setFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!healthCanadaFile || !craFile) {
      toast.error('Please upload both Health Canada License and CRA License PDFs');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('company_name', formData.company_name);
      submitData.append('contact_name', formData.contact_name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone || '');
      submitData.append('strain_id', strain.id);
      submitData.append('shipping_address', formData.shipping_address);
      submitData.append('message', formData.message || '');
      submitData.append('health_canada_license', healthCanadaFile);
      submitData.append('cra_license', craFile);

      await axios.post(`${API}/sample-requests/upload`, submitData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${userToken}`
        }
      });
      
      toast.success('Sample request submitted! We will verify your licenses and contact you.');
      onClose();
      setFormData({ 
        company_name: '', contact_name: '', email: '', phone: '',
        shipping_address: '', message: '' 
      });
      setHealthCanadaFile(null);
      setCraFile(null);
    } catch (error) {
      toast.error('Failed to submit sample request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !strain) return null;

  return (
    <div 
      data-testid="sample-request-modal"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-charcoal border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-gold-500" />
                <h2 className="font-cinzel font-bold text-2xl text-white">Request Sample</h2>
              </div>
              <p className="text-zinc-400 text-sm">Sample request for <span className="text-gold-500">{strain.strain_name}</span></p>
            </div>
            <button 
              data-testid="close-sample-request-btn"
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"
            >
              <X className="w-5 h-5 text-black" />
            </button>
          </div>

          {/* License Requirements Notice */}
          <div className="bg-gold-500/10 border border-gold-500/30 p-4 mb-6">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-gold-500 font-semibold text-sm mb-1">License Verification Required</h4>
                <p className="text-zinc-400 text-xs">
                  Sample requests require valid Health Canada and CRA license documents (PDF). Your licenses will be verified before samples are shipped.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Company Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  <Building2 className="w-4 h-4 inline mr-2" />Company Name *
                </label>
                <input
                  data-testid="sample-company-input"
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                  placeholder="Licensed cannabis retailer"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Contact Name *</label>
                <input
                  data-testid="sample-contact-input"
                  type="text"
                  required
                  value={formData.contact_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                  placeholder="Your name"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />Email *
                </label>
                <input
                  data-testid="sample-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                  placeholder="email@company.com"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />Phone
                </label>
                <input
                  data-testid="sample-phone-input"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            {/* License Upload Section */}
            <div className="border-t border-white/5 pt-6">
              <h3 className="font-manrope text-sm uppercase tracking-widest text-gold-500 mb-4 flex items-center gap-2">
                <FileCheck className="w-4 h-4" /> License Documents (PDF)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Health Canada License Upload */}
                <div>
                  <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                    Health Canada License *
                  </label>
                  <div className="relative">
                    <input
                      data-testid="sample-hc-license-upload"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => handleFileChange(e, setHealthCanadaFile)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div className={`w-full border-2 border-dashed ${healthCanadaFile ? 'border-cannabis-green bg-cannabis-green/10' : 'border-zinc-700 hover:border-gold-500/50'} p-4 transition-colors`}>
                      {healthCanadaFile ? (
                        <div className="flex items-center gap-3 text-cannabis-green">
                          <Check className="w-5 h-5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{healthCanadaFile.name}</p>
                            <p className="text-xs text-zinc-500">{(healthCanadaFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-zinc-500">
                          <Upload className="w-6 h-6" />
                          <span className="text-xs">Click to upload PDF</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-zinc-600 text-xs mt-1">Cannabis license issued by Health Canada</p>
                </div>

                {/* CRA License Upload */}
                <div>
                  <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                    CRA Excise License *
                  </label>
                  <div className="relative">
                    <input
                      data-testid="sample-cra-license-upload"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => handleFileChange(e, setCraFile)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div className={`w-full border-2 border-dashed ${craFile ? 'border-cannabis-green bg-cannabis-green/10' : 'border-zinc-700 hover:border-gold-500/50'} p-4 transition-colors`}>
                      {craFile ? (
                        <div className="flex items-center gap-3 text-cannabis-green">
                          <Check className="w-5 h-5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{craFile.name}</p>
                            <p className="text-xs text-zinc-500">{(craFile.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-zinc-500">
                          <Upload className="w-6 h-6" />
                          <span className="text-xs">Click to upload PDF</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-zinc-600 text-xs mt-1">Cannabis excise license from CRA</p>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />Shipping Address *
              </label>
              <textarea
                data-testid="sample-address-input"
                required
                value={formData.shipping_address}
                onChange={(e) => setFormData(prev => ({ ...prev, shipping_address: e.target.value }))}
                rows={2}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 px-4 py-3 transition-colors outline-none resize-none"
                placeholder="Full shipping address for licensed facility"
              />
            </div>

            {/* Additional Notes */}
            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Additional Notes</label>
              <textarea
                data-testid="sample-message-input"
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={2}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 px-4 py-3 transition-colors outline-none resize-none"
                placeholder="Any specific requirements or questions..."
              />
            </div>

            <button
              data-testid="submit-sample-request-btn"
              type="submit"
              disabled={isSubmitting || !healthCanadaFile || !craFile}
              className="w-full bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 disabled:cursor-not-allowed text-black font-bold uppercase tracking-widest px-8 py-4 transition-all flex items-center justify-center gap-2"
            >
              <Package className="w-4 h-4" />
              {isSubmitting ? 'Submitting...' : 'Submit Sample Request'}
            </button>

            <p className="text-zinc-600 text-xs text-center">
              By submitting, you confirm that the license documents provided are accurate and valid.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

// Inquiry Form Modal
const InquiryModal = ({ isOpen, onClose, selectedStrains, strains, userData, userToken }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    market_of_interest: '',
    message: '',
    open_permit: '',
    permit_jurisdiction: ''
  });
  const [strainVolumes, setStrainVolumes] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-fill form with logged-in user data when modal opens
  useEffect(() => {
    if (isOpen && userData) {
      setFormData(prev => ({
        ...prev,
        company_name: prev.company_name || userData.company || '',
        contact_name: prev.contact_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
        email: prev.email || userData.email || ''
      }));
    }
  }, [isOpen, userData]);

  const selectedStrainDetails = strains.filter(s => selectedStrains.includes(s.id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        strain_ids: selectedStrains,
        strain_volumes: strainVolumes
      }, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      toast.success('Inquiry submitted successfully! We will contact you soon.');
      onClose();
      setFormData({ company_name: '', contact_name: '', email: '', phone: '', market_of_interest: '', message: '', open_permit: '', permit_jurisdiction: '' });
      setStrainVolumes({});
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      data-testid="inquiry-modal"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-charcoal border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Submit Inquiry</h2>
              <p className="text-zinc-400 text-sm">Complete the form below to request more information</p>
            </div>
            <button 
              data-testid="close-inquiry-btn"
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"
            >
              <X className="w-5 h-5 text-black" />
            </button>
          </div>

          {/* Selected Strains with Volume Input */}
          {selectedStrainDetails.length > 0 && (
            <div className="mb-8">
              <h3 className="font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-4">Selected Strains & Volume Required ({selectedStrainDetails.length})</h3>
              <div className="space-y-3">
                {selectedStrainDetails.map(strain => (
                  <div key={strain.id} className="flex items-center gap-3 bg-zinc-900/50 border border-white/5 p-3">
                    <span className="text-gold-500 font-bold text-sm flex-1 min-w-0 truncate">{strain.strain_name}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <input
                        data-testid={`inquiry-volume-${strain.id}`}
                        type="number"
                        min="0"
                        step="1"
                        placeholder="kg"
                        value={strainVolumes[strain.id] || ''}
                        onChange={(e) => setStrainVolumes(prev => ({ ...prev, [strain.id]: e.target.value }))}
                        className="w-24 bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white text-sm h-9 px-3 outline-none text-right"
                      />
                      <span className="text-zinc-500 text-xs font-mono">kg</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  <Building2 className="w-4 h-4 inline mr-2" />Company Name
                </label>
                <input
                  data-testid="inquiry-company-input"
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                  placeholder="Your company"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Contact Name</label>
                <input
                  data-testid="inquiry-contact-input"
                  type="text"
                  required
                  value={formData.contact_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                  placeholder="Your name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />Email
                </label>
                <input
                  data-testid="inquiry-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                  placeholder="email@company.com"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />Phone (Optional)
                </label>
                <input
                  data-testid="inquiry-phone-input"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <Globe className="w-4 h-4 inline mr-2" />Market of Interest
              </label>
              <input
                data-testid="inquiry-market-input"
                type="text"
                value={formData.market_of_interest}
                onChange={(e) => setFormData(prev => ({ ...prev, market_of_interest: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                placeholder="e.g., Canada, Germany, Australia, Israel"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Do you have an Open Permit?</label>
                <select
                  data-testid="inquiry-open-permit"
                  value={formData.open_permit}
                  onChange={(e) => setFormData(prev => ({ ...prev, open_permit: e.target.value, permit_jurisdiction: e.target.value !== 'Yes' ? '' : prev.permit_jurisdiction }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                >
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Permit Jurisdiction</label>
                <select
                  data-testid="inquiry-jurisdiction"
                  value={formData.permit_jurisdiction}
                  onChange={(e) => setFormData(prev => ({ ...prev, permit_jurisdiction: e.target.value }))}
                  className={`w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none ${formData.open_permit !== 'Yes' ? 'opacity-50' : ''}`}
                  disabled={formData.open_permit !== 'Yes'}
                >
                  <option value="">Select jurisdiction...</option>
                  <option value="Domestic (Canada)">Domestic (Canada)</option>
                  <option value="Australia">Australia</option>
                  <option value="Czech Republic">Czech Republic</option>
                  <option value="Germany">Germany</option>
                  <option value="Israel">Israel</option>
                  <option value="Portugal">Portugal</option>
                  <option value="South Africa">South Africa</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Message</label>
              <textarea
                data-testid="inquiry-message-input"
                required
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={4}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 px-4 py-3 transition-colors outline-none resize-none"
                placeholder="Tell us about your requirements, quantities needed, or any questions..."
              />
            </div>

            <button
              data-testid="submit-inquiry-btn"
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 text-black font-bold uppercase tracking-widest px-8 py-4 transition-all flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// Grower Submission Page (Public)
const GrowerSubmitPage = () => {
  // Separate company/grower info from product-specific info
  const COMPANY_FIELDS = ['grower_name', 'company_name', 'email', 'phone', 'license_number'];
  const savedCompanyInfo = (() => {
    try {
      const stored = localStorage.getItem('firegold_submitter_info');
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  })();

  const [formData, setFormData] = useState({
    grower_name: savedCompanyInfo.grower_name || '',
    company_name: savedCompanyInfo.company_name || '',
    email: savedCompanyInfo.email || '',
    phone: savedCompanyInfo.phone || '',
    license_number: savedCompanyInfo.license_number || '',
    strain_name: '',
    strain_type: 'Balanced Hybrid',
    category: 'Dried Flower',
    extract_type: '',
    extraction_method: '',
    thc_percentage: '',
    cbd_percentage: '',
    terpene_profile: '',
    lineage: '',
    no_specific_lineage: false,
    lot_size_kg: '',
    effects: [],
    growing_method: 'Indoor',
    /* export_country removed */
    open_permit: '',
    is_irradiated: '',
    willing_to_irradiate: '',
    gacp_compliant: '',
    gmp_compliant: '',
    bc_dd_compliant: false,
    thc_non_detect: false,
    terpene_percentage: '',
    extract_purity: '',
    grade_a_kg: '',
    grade_b_kg: '',
    grade_c_kg: '',
    harvest_date: '',
    additional_notes: '',
    target_price_per_gram: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  const [lotError, setLotError] = useState('');
  const [strainImage, setStrainImage] = useState(null);
  const [coaFile, setCoaFile] = useState(null);
  const [additionalCoas, setAdditionalCoas] = useState([]);
  const [hcLicenseFile, setHcLicenseFile] = useState(null);
  const [craLicenseFile, setCraLicenseFile] = useState(null);
  const [ddcFile, setDdcFile] = useState(null);
  const [licensesOnFile, setLicensesOnFile] = useState(null);
  const [checkingLicenses, setCheckingLicenses] = useState(false);

  // Debounced license check when company name changes
  useEffect(() => {
    const timer = setTimeout(async () => {
      const name = formData.company_name.trim();
      if (name.length >= 2) {
        setCheckingLicenses(true);
        try {
          const res = await axios.get(`${API}/grower/check-licenses?company_name=${encodeURIComponent(name)}`);
          if (res.data.recognized) {
            setLicensesOnFile(res.data);
          } else {
            setLicensesOnFile(null);
          }
        } catch { setLicensesOnFile(null); }
        setCheckingLicenses(false);
      } else {
        setLicensesOnFile(null);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [formData.company_name]);

  const handleLotSizeChange = (val) => {
    setFormData(prev => ({ ...prev, lot_size_kg: val }));
    const num = parseFloat(val);
    if (val && isNaN(num)) {
      setLotError('Please enter a valid number');
    } else {
      setLotError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const lotKg = parseFloat(formData.lot_size_kg);
    if (isNaN(lotKg) || lotKg <= 0) {
      setLotError('Please enter a valid lot size');
      return;
    }
    setSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('grower_name', formData.grower_name);
      submitData.append('company_name', formData.company_name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('license_number', formData.license_number || '');
      submitData.append('strain_name', formData.strain_name);
      submitData.append('strain_type', formData.strain_type);
      submitData.append('category', formData.category);
      if (formData.category === 'Concentrates') {
        submitData.append('extract_type', formData.extract_type || '');
        submitData.append('extraction_method', formData.extraction_method || '');
      }
      submitData.append('thc_percentage', formData.thc_percentage);
      submitData.append('cbd_percentage', formData.cbd_percentage || '');
      submitData.append('terpene_profile', formData.terpene_profile);
      submitData.append('lineage', formData.lineage);
      submitData.append('lot_size_kg', lotKg);
      submitData.append('effects', Array.isArray(formData.effects) ? formData.effects.join(', ') : formData.effects);
      submitData.append('growing_method', formData.growing_method || '');
      submitData.append('open_permit', formData.open_permit || '');
      submitData.append('is_irradiated', formData.is_irradiated || '');
      submitData.append('willing_to_irradiate', formData.willing_to_irradiate || '');
      submitData.append('gacp_compliant', formData.gacp_compliant || '');
      submitData.append('gmp_compliant', formData.gmp_compliant || '');
      submitData.append('bc_dd_compliant', formData.bc_dd_compliant ? 'Yes' : 'No');
      submitData.append('thc_non_detect', formData.thc_non_detect ? 'Yes' : '');
      submitData.append('terpene_percentage', formData.terpene_percentage || '');
      submitData.append('extract_purity', formData.extract_purity || '');
      submitData.append('additional_notes', formData.additional_notes || '');
      submitData.append('target_price_per_gram', formData.target_price_per_gram || '');
      submitData.append('grade_a_kg', parseFloat(formData.grade_a_kg) || 0);
      submitData.append('grade_b_kg', parseFloat(formData.grade_b_kg) || 0);
      submitData.append('grade_c_kg', parseFloat(formData.grade_c_kg) || 0);
      submitData.append('harvest_date', formData.harvest_date || '');
      if (strainImage) {
        submitData.append('strain_image', strainImage);
      }
      if (coaFile) {
        submitData.append('coa_file', coaFile);
      }
      additionalCoas.forEach((file) => {
        submitData.append('extra_coa_files', file);
      });
      if (hcLicenseFile) {
        submitData.append('health_canada_license', hcLicenseFile);
      }
      if (craLicenseFile) {
        submitData.append('cra_license', craLicenseFile);
      }
      if (ddcFile) {
        submitData.append('ddc_file', ddcFile);
      }

      const response = await axios.post(`${API}/grower/submit`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSubmissionId(response.data.submission_id);
      setSubmitted(true);
      // Remember company/grower info for future submissions
      const companyInfo = {};
      COMPANY_FIELDS.forEach(f => { companyInfo[f] = formData[f]; });
      localStorage.setItem('firegold_submitter_info', JSON.stringify(companyInfo));
      toast.success('Strain submitted for review!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Submission failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
        <div className="bg-charcoal border border-white/10 p-8 max-w-lg w-full text-center">
          <div className="w-16 h-16 bg-cannabis-green/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-cannabis-green" />
          </div>
          <h2 className="font-cinzel font-bold text-2xl text-white mb-4">Submission Received!</h2>
          <p className="text-zinc-400 mb-4">
            Thank you for submitting <span className="text-gold-500">{formData.strain_name}</span>
          </p>
          <p className="text-zinc-500 text-sm mb-6">
            Our team will review your submission and contact you at {formData.email}
          </p>
          <div className="bg-zinc-900 border border-white/5 p-3 mb-6">
            <p className="text-zinc-500 text-xs">Reference ID</p>
            <p className="text-gold-500 font-mono">{submissionId}</p>
          </div>
          <div className="bg-gold-500/10 border border-gold-500/30 p-3 mb-6">
            <p className="text-gold-500 text-xs uppercase tracking-widest mb-1">Status</p>
            <p className="text-gold-500 font-bold">Pending Admin Authorization</p>
          </div>

          {/* License Download Section */}
          <div className="bg-zinc-900 border border-gold-500/30 p-5 mb-6 text-left">
            <div className="flex items-center gap-2 mb-3">
              <Download className="w-5 h-5 text-gold-500" />
              <h3 className="font-manrope font-bold text-sm uppercase tracking-widest text-gold-500">Complete Your Onboarding</h3>
            </div>
            <p className="text-zinc-400 text-sm mb-4">
              Download our Health Canada and CRA licenses to complete the mutual LP verification process.
            </p>
            <div className="space-y-2">
              <a 
                href="/firegold_hc_license.pdf" 
                download="Firegold_Health_Canada_License.pdf"
                data-testid="download-hc-license"
                className="flex items-center justify-between w-full bg-gold-500/10 hover:bg-gold-500/20 border border-gold-500/30 text-gold-500 px-4 py-3 transition-colors"
              >
                <span className="flex items-center gap-2 text-sm font-bold">
                  <FileText className="w-4 h-4" />
                  Firegold — Health Canada License
                </span>
                <Download className="w-4 h-4" />
              </a>
              <a 
                href="/firegold_cra_license.pdf" 
                download="Firegold_CRA_License.pdf"
                data-testid="download-cra-license"
                className="flex items-center justify-between w-full bg-gold-500/10 hover:bg-gold-500/20 border border-gold-500/30 text-gold-500 px-4 py-3 transition-colors"
              >
                <span className="flex items-center gap-2 text-sm font-bold">
                  <FileText className="w-4 h-4" />
                  Firegold — CRA License
                </span>
                <Download className="w-4 h-4" />
              </a>
            </div>
          </div>

          <a href="/" className="block w-full bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest px-8 py-4 transition-all text-center mb-3">
            Back to Catalog
          </a>
          <button
            data-testid="add-another-product-btn"
            onClick={() => {
              // Reset product-specific fields only, keep company/grower info
              setFormData(prev => ({
                ...prev,
                strain_name: '', strain_type: 'Balanced Hybrid', category: 'Dried Flower',
                extract_type: '', extraction_method: '', thc_percentage: '', cbd_percentage: '',
                terpene_profile: '', lineage: '', no_specific_lineage: false, lot_size_kg: '', effects: [],
                growing_method: 'Indoor', open_permit: '', is_irradiated: '',
                willing_to_irradiate: '', gacp_compliant: '', gmp_compliant: '',
                bc_dd_compliant: false, thc_non_detect: false, terpene_percentage: '', extract_purity: '', grade_a_kg: '',
                grade_b_kg: '', grade_c_kg: '', harvest_date: '', additional_notes: '',
                target_price_per_gram: ''
              }));
              setStrainImage(null);
              setCoaFile(null);
              setAdditionalCoas([]);
              setSubmitted(false);
              setSubmissionId('');
              setLotError('');
            }}
            className="block w-full border border-gold-500/40 hover:border-gold-500 text-gold-500 hover:text-gold-400 font-bold uppercase tracking-widest px-8 py-4 transition-all text-center"
          >
            Add Another Product
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-md border-b border-white/5 h-20 flex items-center justify-between px-6 md:px-12">
        <a href="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Back to Catalog</span>
        </a>
        <img src={FIREGOLD_LOGO} alt="Firegold Brands" className="h-28 w-auto" />
        <div></div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-cannabis-green/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Leaf className="w-8 h-8 text-cannabis-green" />
          </div>
          <h1 className="font-cinzel font-bold text-3xl text-white mb-4">Submit Your Product</h1>
          <p className="text-zinc-400 max-w-xl mx-auto">
            Are you a licensed grower with premium cannabis products? Submit your product for consideration in our B2B catalog.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-charcoal border border-white/10 p-8 space-y-8" data-testid="grower-submit-form">
          {/* Grower Information */}
          <div>
            <h3 className="font-cinzel font-bold text-lg text-gold-500 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" /> Grower Information
            </h3>
            {savedCompanyInfo.company_name && (
              <div className="bg-cannabis-green/10 border border-cannabis-green/30 p-3 mb-4">
                <p className="text-cannabis-green text-xs uppercase tracking-widest font-bold">Welcome back, {savedCompanyInfo.grower_name || savedCompanyInfo.company_name}</p>
                <p className="text-zinc-400 text-xs mt-1">Your company information has been prefilled. Edit any field if needed.</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Contact Name *</label>
                <input
                  data-testid="grower-name-input"
                  type="text"
                  required
                  value={formData.grower_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, grower_name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Company / LP Name *</label>
                <input
                  data-testid="grower-company-input"
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                  placeholder="Licensed Producer name"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Email *</label>
                <input
                  data-testid="grower-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                  placeholder="email@company.com"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Phone *</label>
                <input
                  data-testid="grower-phone-input"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Health Canada License # (optional)</label>
                <div className="flex gap-2">
                  <input
                    data-testid="grower-license-input"
                    type="text"
                    value={formData.license_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, license_number: e.target.value }))}
                    className="flex-1 bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                    placeholder="LIC-XXXXX"
                  />
                  {formData.license_number && (
                    <button
                      type="button"
                      data-testid="verify-license-btn"
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/grower/verify-license/${formData.license_number}`);
                          if (res.data.verified) {
                            toast.success(`License verified — ${res.data.company}`);
                          } else {
                            toast.info(res.data.message);
                          }
                        } catch { toast.error('Could not verify license'); }
                      }}
                      className="bg-gold-500/20 border border-gold-500/40 text-gold-500 px-4 h-12 font-bold uppercase tracking-widest text-xs hover:bg-gold-500/30 transition-colors whitespace-nowrap"
                    >
                      Verify
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Strain Information */}
          <div>
            <h3 className="font-cinzel font-bold text-lg text-gold-500 mb-4 flex items-center gap-2">
              <Leaf className="w-5 h-5" /> Strain Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Strain Name *</label>
                <input
                  data-testid="grower-strain-name-input"
                  type="text"
                  required
                  value={formData.strain_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, strain_name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                  placeholder="e.g., Blue Dream"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Type *</label>
                <select
                  data-testid="grower-strain-type-select"
                  value={formData.strain_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, strain_type: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                >
                  <option value="No Specific Strain">No Specific Strain</option>
                  <option value="Balanced Hybrid">Balanced Hybrid</option>
                  <option value="Hybrid, Indica Leaning">Hybrid, Indica Leaning</option>
                  <option value="Hybrid, Sativa Leaning">Hybrid, Sativa Leaning</option>
                  <option value="Indica Dominant">Indica Dominant</option>
                  <option value="Sativa Dominant">Sativa Dominant</option>
                </select>
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Category *</label>
                <select
                  data-testid="grower-category-select"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                >
                  <option value="Dried Flower">Dried Flower</option>
                  <option value="Fresh Frozen">Fresh Frozen</option>
                  <option value="Trim">Trim</option>
                  <option value="Concentrates">Concentrates</option>
                </select>
              </div>

              {/* Concentrates-specific fields */}
              {formData.category === 'Concentrates' && (
                <>
                  <div>
                    <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Extracts *</label>
                    <select
                      data-testid="grower-extract-type"
                      value={formData.extract_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, extract_type: e.target.value }))}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                    >
                      <option value="">Select extract type...</option>
                      <option value="Bubble Hash">Bubble Hash</option>
                      <option value="CBC Isolate">CBC Isolate</option>
                      <option value="CBD Distillate">CBD Distillate</option>
                      <option value="CBD Isolate">CBD Isolate</option>
                      <option value="CBD Live Resin">CBD Live Resin</option>
                      <option value="CBD Resin">CBD Resin</option>
                      <option value="CBDv Isolate">CBDv Isolate</option>
                      <option value="CBG Distillate">CBG Distillate</option>
                      <option value="CBG Isolate">CBG Isolate</option>
                      <option value="CBN Isolate">CBN Isolate</option>
                      <option value="Cured Resin">Cured Resin</option>
                      <option value="Diamonds and Sauce">Diamonds and Sauce</option>
                      <option value="Distillate">Distillate</option>
                      <option value="Dry Diamonds">Dry Diamonds</option>
                      <option value="Hash">Hash</option>
                      <option value="Kief">Kief</option>
                      <option value="Liquid Diamonds">Liquid Diamonds</option>
                      <option value="Live Resin">Live Resin</option>
                      <option value="Rosin">Rosin</option>
                      <option value="Shatter">Shatter</option>
                      <option value="THC">THC</option>
                      <option value="THC Distillate">THC Distillate</option>
                      <option value="THC Isolate">THC Isolate</option>
                      <option value="THCa Micro Powder">THCa Micro Powder</option>
                      <option value="THCv Isolate">THCv Isolate</option>
                      <option value="Vape Ready Resin">Vape Ready Resin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Extraction Method *</label>
                    <select
                      data-testid="grower-extraction-method"
                      value={formData.extraction_method}
                      onChange={(e) => setFormData(prev => ({ ...prev, extraction_method: e.target.value }))}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                    >
                      <option value="">Select method...</option>
                      <option value="BHO">BHO</option>
                      <option value="CO2">CO2</option>
                      <option value="Ethanol">Ethanol</option>
                      <option value="Water">Water</option>
                      <option value="Solventless">Solventless</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800 p-4">
                    <input
                      data-testid="grower-thc-non-detect"
                      type="checkbox"
                      checked={formData.thc_non_detect}
                      onChange={(e) => setFormData(prev => ({ ...prev, thc_non_detect: e.target.checked }))}
                      className="w-5 h-5 accent-gold-500 cursor-pointer"
                    />
                    <label className="font-manrope text-sm text-zinc-300 cursor-pointer" onClick={() => setFormData(prev => ({ ...prev, thc_non_detect: !prev.thc_non_detect }))}>
                      THC Non-Detect (ND/THC)
                    </label>
                    <span className="text-zinc-600 text-xs ml-auto">Concentrate tests below detectable THC levels</span>
                  </div>
                  <div>
                    <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Total Extract Purity % <span className="text-zinc-700">(optional)</span></label>
                    <input
                      data-testid="grower-extract-purity"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={formData.extract_purity}
                      onChange={(e) => setFormData(prev => ({ ...prev, extract_purity: e.target.value }))}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                      placeholder="e.g., 92.5"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">THC % *</label>
                <input
                  data-testid="grower-thc-input"
                  type="text"
                  required
                  value={formData.thc_percentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, thc_percentage: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                  placeholder="e.g., 28.5%"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">CBD % (optional)</label>
                <input
                  data-testid="grower-cbd-input"
                  type="text"
                  value={formData.cbd_percentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, cbd_percentage: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                  placeholder="e.g., 0.5%"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Terpene % (optional)</label>
                <input
                  data-testid="grower-terpene-pct-input"
                  type="text"
                  value={formData.terpene_percentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, terpene_percentage: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                  placeholder="e.g., 3.2%"
                />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500">Lineage / Genetics {!formData.no_specific_lineage && '*'}</label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      data-testid="grower-no-specific-lineage"
                      type="checkbox"
                      checked={formData.no_specific_lineage || false}
                      onChange={(e) => setFormData(prev => ({ ...prev, no_specific_lineage: e.target.checked, lineage: e.target.checked ? 'No Specific Strain' : '' }))}
                      className="w-4 h-4 accent-gold-500"
                    />
                    <span className="font-manrope text-xs text-zinc-400">No Specific Strain</span>
                  </label>
                </div>
                <input
                  data-testid="grower-lineage-input"
                  type="text"
                  required={!formData.no_specific_lineage}
                  disabled={formData.no_specific_lineage}
                  value={formData.lineage}
                  onChange={(e) => setFormData(prev => ({ ...prev, lineage: e.target.value }))}
                  className={`w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none ${formData.no_specific_lineage ? 'opacity-50' : ''}`}
                  placeholder="e.g., OG Kush x Durban Poison"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Terpene Profile *</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(typeof formData.terpene_profile === 'string' ? formData.terpene_profile.split(',').filter(t => t.trim()) : (formData.terpene_profile || [])).map((t, i) => (
                    <span key={i} className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs px-2 py-1 flex items-center gap-1">
                      {typeof t === 'string' ? t.trim() : t}
                      <button type="button" data-testid={`grower-terpene-remove-${i}`} onClick={() => {
                        const current = typeof formData.terpene_profile === 'string' ? formData.terpene_profile.split(',').filter(x => x.trim()) : (formData.terpene_profile || []);
                        setFormData(prev => ({...prev, terpene_profile: current.filter((_, idx) => idx !== i).join(', ')}));
                      }}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <select
                  data-testid="grower-terpene-input"
                  value=""
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val) {
                      const current = typeof formData.terpene_profile === 'string' ? formData.terpene_profile.split(',').map(t => t.trim()).filter(Boolean) : (formData.terpene_profile || []);
                      if (!current.includes(val)) {
                        setFormData(prev => ({...prev, terpene_profile: [...current, val].join(', ')}));
                      }
                    }
                  }}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-emerald-500 text-zinc-400 h-12 px-4 transition-colors outline-none text-sm"
                >
                  <option value="">Add terpene...</option>
                  {ALL_TERPENES.filter(t => {
                    const current = typeof formData.terpene_profile === 'string' ? formData.terpene_profile.split(',').map(x => x.trim()).filter(Boolean) : (formData.terpene_profile || []);
                    return !current.includes(t);
                  }).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Potential Terpene Effects * (select up to 8)</label>
                <p className="text-zinc-600 text-[10px] mb-2">Reported terpene-associated experiences may vary between individuals. FireGold does not make medical claims regarding cannabis products.</p>
                <EffectsMultiSelect
                  selected={formData.effects}
                  onChange={(effects) => setFormData(prev => ({ ...prev, effects }))}
                  testId="grower-effects-select"
                />
              </div>
            </div>
          </div>

          {/* Availability & Export */}
          <div>
            <h3 className="font-cinzel font-bold text-lg text-gold-500 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" /> Availability & Export
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Total Lot Size (kg) *</label>
                <input
                  data-testid="grower-lot-size-input"
                  type="number"
                  required
                  min="1"
                  step="0.1"
                  value={formData.lot_size_kg}
                  onChange={(e) => handleLotSizeChange(e.target.value)}
                  className={`w-full bg-zinc-900/50 border ${lotError ? 'border-red-500' : 'border-zinc-800'} focus:border-gold-500 text-white h-12 px-4 outline-none`}
                  placeholder="e.g., 50"
                />
                {lotError && <p className="text-red-500 text-xs mt-1">{lotError}</p>}
              </div>

              {/* Flower Grade Breakdown */}
              <div className="md:col-span-2">
                <label className="block font-manrope text-xs uppercase tracking-widest text-gold-500 mb-3 font-bold">Flower Grade Breakdown (kg per grade)</label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Grade A (Bigs) (kg)</label>
                    <input
                      data-testid="grower-grade-a-input"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.grade_a_kg}
                      onChange={(e) => setFormData(prev => ({ ...prev, grade_a_kg: e.target.value }))}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Grade B (Mids) (kg)</label>
                    <input
                      data-testid="grower-grade-b-input"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.grade_b_kg}
                      onChange={(e) => setFormData(prev => ({ ...prev, grade_b_kg: e.target.value }))}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Grade C (Smalls) (kg)</label>
                    <input
                      data-testid="grower-grade-c-input"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.grade_c_kg}
                      onChange={(e) => setFormData(prev => ({ ...prev, grade_c_kg: e.target.value }))}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Harvest Date */}
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Harvest Date {formData.category !== 'Concentrates' && '*'}</label>
                <input
                  data-testid="grower-harvest-date-input"
                  type="date"
                  required={formData.category !== 'Concentrates'}
                  value={formData.harvest_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, harvest_date: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                />
              </div>

              {/* Export country field removed */}
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Growing Method</label>
                <select
                  data-testid="grower-method-select"
                  value={formData.growing_method}
                  onChange={(e) => setFormData(prev => ({ ...prev, growing_method: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                >
                  <option value="Indoor">Indoor</option>
                  <option value="Outdoor">Outdoor</option>
                  <option value="Greenhouse">Greenhouse</option>
                </select>
              </div>
            </div>
          </div>

          {/* Irradiation */}
          <div>
            <h3 className="font-cinzel font-bold text-lg text-gold-500 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" /> Compliance
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Has the flower been irradiated? *</label>
                <select
                  data-testid="grower-irradiated-select"
                  required
                  value={formData.is_irradiated}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_irradiated: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                >
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Willing to irradiate if required? *</label>
                <select
                  data-testid="grower-willing-irradiate-select"
                  required
                  value={formData.willing_to_irradiate}
                  onChange={(e) => setFormData(prev => ({ ...prev, willing_to_irradiate: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                >
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Is your product GACP compliant? *</label>
                <select
                  data-testid="grower-gacp-select"
                  required
                  value={formData.gacp_compliant}
                  onChange={(e) => setFormData(prev => ({ ...prev, gacp_compliant: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                >
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Is your product GMP compliant? *</label>
                <select
                  data-testid="grower-gmp-select"
                  required
                  value={formData.gmp_compliant}
                  onChange={(e) => setFormData(prev => ({ ...prev, gmp_compliant: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                >
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>
            {/* BC DD Compliance */}
            <div className="mt-4 bg-zinc-900/30 border border-white/5 p-4">
              <label className="flex items-center gap-3 cursor-pointer" data-testid="bc-dd-checkbox">
                <input
                  type="checkbox"
                  checked={formData.bc_dd_compliant}
                  onChange={(e) => setFormData(prev => ({ ...prev, bc_dd_compliant: e.target.checked }))}
                  className="w-5 h-5 accent-gold-500"
                />
                <div>
                  <span className="text-white font-bold text-sm">BC Direct Delivery (DD) Compliant</span>
                  <p className="text-zinc-500 text-xs mt-0.5">Check this if your product meets BC Direct Delivery compliance requirements.</p>
                </div>
              </label>
              {formData.bc_dd_compliant && !ddcFile && (
                <p className="text-gold-500 text-xs mt-2 ml-8">Please upload your Direct Delivery Compliance document in the Documents section below.</p>
              )}
            </div>
          </div>

          {/* Strain Image Upload */}
          <div>
            <h3 className="font-cinzel font-bold text-lg text-gold-500 mb-4 flex items-center gap-2">
              <Image className="w-5 h-5" /> Strain Image
            </h3>
            <div className="relative">
              <input
                data-testid="grower-image-upload"
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={(e) => setStrainImage(e.target.files[0] || null)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className={`w-full border-2 border-dashed ${strainImage ? 'border-cannabis-green bg-cannabis-green/10' : 'border-zinc-700 hover:border-gold-500/50'} p-6 transition-colors text-center`}>
                {strainImage ? (
                  <div className="flex items-center justify-center gap-3 text-cannabis-green">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">{strainImage.name}</span>
                    <span className="text-zinc-500 text-sm">({(strainImage.size / 1024).toFixed(1)} KB)</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-zinc-500">
                    <Upload className="w-8 h-8" />
                    <span>Upload a photo of your flower (JPG, PNG, WebP)</span>
                    <span className="text-xs text-zinc-600">Black background recommended</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Document Uploads */}
          <div>
            <h3 className="font-cinzel font-bold text-lg text-gold-500 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" /> Documents
            </h3>

            {/* License Recognition Banner */}
            {licensesOnFile && (
              <div className="bg-cannabis-green/10 border border-cannabis-green/30 p-4 mb-4" data-testid="licenses-on-file-banner">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-cannabis-green" />
                  <span className="text-cannabis-green font-bold text-sm uppercase tracking-widest">
                    {licensesOnFile.company_name} — Recognized
                  </span>
                </div>
                <p className="text-zinc-400 text-sm mb-3">We have records from {licensesOnFile.previous_submissions} previous submission{licensesOnFile.previous_submissions > 1 ? 's' : ''} for your LP.</p>
                <div className="flex flex-wrap gap-3">
                  {licensesOnFile.licenses_on_file.health_canada_license && (
                    <span className="flex items-center gap-1 bg-cannabis-green/20 text-cannabis-green text-xs px-3 py-1 font-bold">
                      <Check className="w-3 h-3" /> Health Canada License on file
                    </span>
                  )}
                  {licensesOnFile.licenses_on_file.cra_license && (
                    <span className="flex items-center gap-1 bg-cannabis-green/20 text-cannabis-green text-xs px-3 py-1 font-bold">
                      <Check className="w-3 h-3" /> CRA License on file
                    </span>
                  )}
                  {licensesOnFile.licenses_on_file.coa && (
                    <span className="flex items-center gap-1 bg-cannabis-green/20 text-cannabis-green text-xs px-3 py-1 font-bold">
                      <Check className="w-3 h-3" /> COA on file
                    </span>
                  )}
                </div>
                <p className="text-zinc-500 text-xs mt-2">You can still upload new documents below to update your records.</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* COA Upload */}
              <div className="flex flex-col">
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2 min-h-[2rem] flex items-end">
                  Certificate of Analysis (COA)
                  {licensesOnFile?.licenses_on_file?.coa && <span className="text-cannabis-green ml-1">- on file</span>}
                </label>
                <div className="relative flex-1">
                  <input
                    data-testid="grower-coa-upload"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setCoaFile(e.target.files[0] || null)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className={`w-full h-full min-h-[80px] border-2 border-dashed ${coaFile ? 'border-cannabis-green bg-cannabis-green/10' : 'border-zinc-700 hover:border-gold-500/50'} p-4 transition-colors flex items-center justify-center`}>
                    {coaFile ? (
                      <div className="flex items-center justify-center gap-2 text-cannabis-green">
                        <Check className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm truncate">{coaFile.name}</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-zinc-500">
                        <Upload className="w-6 h-6" />
                        <span className="text-xs">Upload PDF</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Additional COAs */}
              {additionalCoas.map((file, idx) => (
                <div key={idx} className="flex flex-col">
                  <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2 min-h-[2rem] flex items-end">
                    Additional COA #{idx + 2}
                  </label>
                  <div className="relative flex-1">
                    <div className={`w-full h-full min-h-[80px] border-2 border-dashed border-cannabis-green bg-cannabis-green/10 p-4 transition-colors flex items-center justify-center`}>
                      <div className="flex items-center justify-center gap-2 text-cannabis-green">
                        <Check className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm truncate">{file.name}</span>
                        <button type="button" onClick={() => setAdditionalCoas(prev => prev.filter((_, i) => i !== idx))} className="ml-2 text-red-400 hover:text-red-300">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add COA Button */}
              <div className="flex flex-col">
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2 min-h-[2rem] flex items-end">
                  &nbsp;
                </label>
                <div className="relative flex-1">
                  <input
                    data-testid="grower-add-extra-coa"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        setAdditionalCoas(prev => [...prev, e.target.files[0]]);
                        e.target.value = '';
                      }
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className="w-full h-full min-h-[80px] border-2 border-dashed border-zinc-700 hover:border-gold-500/50 p-4 transition-colors flex items-center justify-center cursor-pointer">
                    <div className="flex flex-col items-center gap-1 text-zinc-500">
                      <Plus className="w-6 h-6" />
                      <span className="text-xs">Add COA</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Health Canada License Upload */}
              <div className="flex flex-col">
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2 min-h-[2rem] flex items-end">
                  Health Canada License
                  {licensesOnFile?.licenses_on_file?.health_canada_license && <span className="text-cannabis-green ml-1">- on file</span>}
                </label>
                <div className="relative flex-1">
                  <input
                    data-testid="grower-hc-license-upload"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setHcLicenseFile(e.target.files[0] || null)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className={`w-full h-full min-h-[80px] border-2 border-dashed ${hcLicenseFile ? 'border-cannabis-green bg-cannabis-green/10' : 'border-zinc-700 hover:border-gold-500/50'} p-4 transition-colors flex items-center justify-center`}>
                    {hcLicenseFile ? (
                      <div className="flex items-center justify-center gap-2 text-cannabis-green">
                        <Check className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm truncate">{hcLicenseFile.name}</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-zinc-500">
                        <Upload className="w-6 h-6" />
                        <span className="text-xs">Upload PDF</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* CRA License Upload */}
              <div className="flex flex-col">
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2 min-h-[2rem] flex items-end">
                  CRA Excise License
                  {licensesOnFile?.licenses_on_file?.cra_license && <span className="text-cannabis-green ml-1">- on file</span>}
                </label>
                <div className="relative flex-1">
                  <input
                    data-testid="grower-cra-license-upload"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setCraLicenseFile(e.target.files[0] || null)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className={`w-full h-full min-h-[80px] border-2 border-dashed ${craLicenseFile ? 'border-cannabis-green bg-cannabis-green/10' : 'border-zinc-700 hover:border-gold-500/50'} p-4 transition-colors flex items-center justify-center`}>
                    {craLicenseFile ? (
                      <div className="flex items-center justify-center gap-2 text-cannabis-green">
                        <Check className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm truncate">{craLicenseFile.name}</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-zinc-500">
                        <Upload className="w-6 h-6" />
                        <span className="text-xs">Upload PDF</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Direct Delivery Compliance Document Upload */}
              <div className="flex flex-col">
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2 min-h-[2rem] flex items-end">
                  Direct Delivery Compliance
                </label>
                <div className="relative flex-1">
                  <input
                    data-testid="grower-ddc-upload"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setDdcFile(e.target.files[0] || null)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  <div className={`w-full h-full min-h-[80px] border-2 border-dashed ${ddcFile ? 'border-cannabis-green bg-cannabis-green/10' : 'border-zinc-700 hover:border-gold-500/50'} p-4 transition-colors flex items-center justify-center`}>
                    {ddcFile ? (
                      <div className="flex items-center justify-center gap-2 text-cannabis-green">
                        <Check className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm truncate">{ddcFile.name}</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-zinc-500">
                        <Upload className="w-6 h-6" />
                        <span className="text-xs">Upload PDF</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <p className="text-zinc-600 text-xs mt-2">Upload PDFs of your COA, Health Canada License, CRA Excise License, and Direct Delivery Compliance document if available.</p>
          </div>

          {/* Target Price per Gram */}
          <div>
            <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Target Price per Gram (optional)</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
              <input
                data-testid="grower-target-price-input"
                type="number"
                step="0.01"
                min="0"
                value={formData.target_price_per_gram}
                onChange={(e) => setFormData(prev => ({ ...prev, target_price_per_gram: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 pl-8 pr-12 outline-none"
                placeholder="e.g. 2.10"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">/ g</span>
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Additional Notes (optional)</label>
            <textarea
              data-testid="grower-notes-input"
              value={formData.additional_notes}
              onChange={(e) => setFormData(prev => ({ ...prev, additional_notes: e.target.value }))}
              rows={3}
              className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white px-4 py-3 outline-none resize-none"
              placeholder="Any additional information about your flower, COA availability, etc."
            />
          </div>

          <button
            data-testid="grower-submit-btn"
            type="submit"
            disabled={submitting || !!lotError}
            className="w-full bg-cannabis-green hover:bg-green-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-bold uppercase tracking-widest px-8 py-4 transition-all flex items-center justify-center gap-2"
          >
            <Send className="w-5 h-5" />
            {submitting ? 'Submitting...' : 'Submit for Review'}
          </button>

          <p className="text-zinc-600 text-xs text-center">
            Submissions are reviewed within 2-3 business days. Admin will set pricing and authorize your strain for the catalog.
          </p>
        </form>
      </main>

      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff'
          }
        }}
      />
    </div>
  );
};

// Auth Page Component (Register/Login)
const AuthPage = ({ onAuth }) => {
  const [mode, setMode] = useState('register');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralName, setReferralName] = useState('');
  const [showAuthInstallBanner, setShowAuthInstallBanner] = useState(false);
  const [authIsIOS, setAuthIsIOS] = useState(false);
  const [authDeferredPrompt, setAuthDeferredPrompt] = useState(null);

  // PWA Install prompt for auth page — always show on mobile
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isStandalone || localStorage.getItem('pwa_install_dismissed')) return;
    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    const isMobile = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
    setAuthIsIOS(ios);
    // Always show on mobile devices
    if (isMobile) {
      setShowAuthInstallBanner(true);
    }
    // Also listen for the browser prompt on non-iOS
    if (!ios) {
      const handler = (e) => {
        e.preventDefault();
        setAuthDeferredPrompt(e);
        setShowAuthInstallBanner(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);
  const [registerData, setRegisterData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    country: '',
    email: '',
    phone: '',
    license_number: '',
    open_permit: '',
    permit_jurisdiction: '',
    user_type: ''
  });
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [showVerificationSent, setShowVerificationSent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error('Please enter your email'); return; }
    setForgotSending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (err) {
      toast.error('Failed to send reset link');
    } finally {
      setForgotSending(false);
    }
  };
  const [showContactFromAuth, setShowContactFromAuth] = useState(false);
  const [authContactForm, setAuthContactForm] = useState({ name: '', email: '', company: '', message: '' });
  const [authContactSending, setAuthContactSending] = useState(false);

  const handleAuthContactSubmit = async (e) => {
    e.preventDefault();
    if (!authContactForm.name || !authContactForm.email || !authContactForm.message) {
      toast.error('Please fill in name, email, and message');
      return;
    }
    setAuthContactSending(true);
    try {
      await axios.post(`${API}/contact`, authContactForm);
      toast.success('Message sent! We\'ll be in touch soon.');
      setAuthContactForm({ name: '', email: '', company: '', message: '' });
      setShowContactFromAuth(false);
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setAuthContactSending(false);
    }
  };

  // Capture referral code from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      axios.get(`${API}/affiliate/resolve/${ref}`).then(res => {
        if (res.data.valid) {
          setReferralName(res.data.affiliate_name);
        }
      }).catch(() => {});
    }
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/register`, { ...registerData, referral_code: referralCode || undefined });
      if (response.data.requires_verification) {
        setShowVerificationSent(true);
        toast.success('Check your email to verify your account!');
      } else {
        // Backward compat: if server returns token directly (legacy)
        localStorage.setItem('userToken', response.data.token);
        localStorage.setItem('userData', JSON.stringify(response.data.user));
        onAuth(response.data.token, response.data.user);
        toast.success('Registration successful! Welcome to FireGold.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginData.email.trim(),
        password: loginData.password
      });
      localStorage.setItem('userToken', response.data.token);
      localStorage.setItem('userData', JSON.stringify(response.data.user));
      onAuth(response.data.token, response.data.user);
      toast.success('Welcome back!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex flex-col relative">
      {/* PWA Install Banner - Top of page */}
      {showAuthInstallBanner && (
        <div data-testid="auth-pwa-install-banner" className="bg-gold-500/15 border-b border-gold-500/30 py-3 px-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-3 max-w-lg mx-auto">
            <div className="flex items-center gap-3 min-w-0">
              <img src="/firegold_icon_192.png" alt="Firegold" className="w-9 h-9 rounded-lg flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-white font-manrope text-sm font-bold">Install Firegold</p>
                {authIsIOS ? (
                  <p className="text-zinc-300 text-xs">
                    Tap <span className="inline-flex items-center text-gold-500 font-bold">
                      <svg className="w-3.5 h-3.5 inline mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                      Share
                    </span> then <span className="text-gold-500 font-bold">"Add to Home Screen"</span>
                  </p>
                ) : (
                  <p className="text-zinc-400 text-xs">Add to home screen for quick access</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!authIsIOS && authDeferredPrompt && (
                <button
                  onClick={async () => {
                    authDeferredPrompt.prompt();
                    await authDeferredPrompt.userChoice;
                    setAuthDeferredPrompt(null);
                    setShowAuthInstallBanner(false);
                  }}
                  className="bg-gold-500 text-black font-bold text-xs uppercase tracking-widest px-3 py-1.5 hover:bg-gold-400 transition-all"
                >
                  Install
                </button>
              )}
              <button
                onClick={() => { setShowAuthInstallBanner(false); localStorage.setItem('pwa_install_dismissed', 'true'); }}
                className="w-7 h-7 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"
              >
                <X className="w-3.5 h-3.5 text-black" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex items-center justify-center p-6">
      {/* Contact Us floating button - positioned below install banner on mobile */}
      <button
        onClick={() => setShowContactFromAuth(true)}
        data-testid="auth-contact-btn"
        className="fixed top-6 right-6 z-50 flex items-center gap-2 bg-zinc-900/80 border border-zinc-700 hover:border-gold-500 text-zinc-300 hover:text-gold-500 px-4 py-2 transition-all backdrop-blur-sm"
        style={{ top: showAuthInstallBanner ? '60px' : '24px' }}
      >
        <MessageCircle className="w-4 h-4" />
        <span className="text-xs uppercase tracking-widest font-manrope">Contact Us</span>
      </button>

      {/* Contact Us Modal */}
      {showContactFromAuth && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowContactFromAuth(false)} />
          <div data-testid="auth-contact-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-lg p-8">
            <button 
              onClick={() => setShowContactFromAuth(false)} 
              data-testid="auth-contact-modal-close"
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"
            >
              <X className="w-5 h-5 text-black" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle className="w-6 h-6 text-gold-500" />
              <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest">Contact Us</h2>
            </div>
            <p className="text-zinc-400 text-sm mb-6">Leave a message and our team will get back to you shortly.</p>
            <form onSubmit={handleAuthContactSubmit} className="space-y-4">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Name *</label>
                <input
                  data-testid="auth-contact-name"
                  type="text"
                  value={authContactForm.name}
                  onChange={(e) => setAuthContactForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Email *</label>
                <input
                  data-testid="auth-contact-email"
                  type="email"
                  value={authContactForm.email}
                  onChange={(e) => setAuthContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Company</label>
                <input
                  data-testid="auth-contact-company"
                  type="text"
                  value={authContactForm.company}
                  onChange={(e) => setAuthContactForm(prev => ({ ...prev, company: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm"
                  placeholder="Company name (optional)"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Message *</label>
                <textarea
                  data-testid="auth-contact-message"
                  value={authContactForm.message}
                  onChange={(e) => setAuthContactForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white px-4 py-3 outline-none text-sm min-h-[120px] resize-none"
                  placeholder="How can we help you?"
                />
              </div>
              <button
                type="submit"
                data-testid="auth-contact-submit"
                disabled={authContactSending}
                className="w-full bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all"
              >
                {authContactSending ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="bg-charcoal border border-white/10 p-8 max-w-lg w-full">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src={FIREGOLD_LOGO} alt="Firegold Brands" className="h-32 w-auto" />
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-8">
          <button
            onClick={() => { setMode('register'); setError(''); }}
            className={`flex-1 py-3 font-bold uppercase tracking-widest text-sm transition-all ${
              mode === 'register' 
                ? 'bg-gold-500 text-black' 
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            <UserPlus className="w-4 h-4 inline mr-2" />
            Register
          </button>
          <button
            onClick={() => { setMode('login'); setError(''); }}
            className={`flex-1 py-3 font-bold uppercase tracking-widest text-sm transition-all ${
              mode === 'login' 
                ? 'bg-gold-500 text-black' 
                : 'bg-zinc-800 text-zinc-400 hover:text-white'
            }`}
          >
            <User className="w-4 h-4 inline mr-2" />
            Login
          </button>
        </div>

        {mode === 'register' ? (
          showVerificationSent ? (
            <div data-testid="verification-sent-msg" className="text-center py-8 space-y-4">
              <div className="w-16 h-16 mx-auto bg-gold-500/15 border border-gold-500/30 flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-gold-500" />
              </div>
              <h3 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest">Check Your Email</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">We've sent a verification link to <span className="text-gold-500 font-bold">{registerData.email}</span>. Click the link to create your password and activate your account.</p>
              <p className="text-zinc-600 text-xs">The link expires in 24 hours.</p>
              <button
                data-testid="resend-verification-btn"
                type="button"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/auth/resend-verification`, { email: registerData.email });
                    toast.success('Verification email re-sent!');
                  } catch { toast.error('Failed to resend'); }
                }}
                className="text-gold-500 hover:text-gold-400 text-xs uppercase tracking-widest mt-4 transition-colors"
              >
                Resend Verification Email
              </button>
              <div className="pt-4">
                <button
                  type="button"
                  onClick={() => { setShowVerificationSent(false); setMode('login'); }}
                  className="text-zinc-400 hover:text-white text-xs uppercase tracking-widest transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </div>
          ) : (
          <form onSubmit={handleRegister} className="space-y-4">
            {referralName && (
              <div className="bg-gold-500/10 border border-gold-500/30 p-3 text-center mb-2" data-testid="referral-banner">
                <p className="text-gold-500 text-sm font-bold">Referred by {referralName}</p>
              </div>
            )}
            <p className="text-zinc-400 text-sm text-center mb-6">
              Register to access our premium cultivar catalog
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  First Name *
                </label>
                <input
                  data-testid="register-firstname"
                  type="text"
                  required
                  value={registerData.first_name}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, first_name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Last Name *
                </label>
                <input
                  data-testid="register-lastname"
                  type="text"
                  required
                  value={registerData.last_name}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, last_name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <Building2 className="w-4 h-4 inline mr-1" /> LP / Company Name *
              </label>
              <input
                data-testid="register-company"
                type="text"
                required
                value={registerData.company}
                onChange={(e) => setRegisterData(prev => ({ ...prev, company: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                placeholder="Licensed Producer or Company"
              />
            </div>

            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <Globe className="w-4 h-4 inline mr-1" /> Country *
              </label>
              <select
                data-testid="register-country"
                required
                value={registerData.country}
                onChange={(e) => setRegisterData(prev => ({ ...prev, country: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
              >
                <option value="">Select country...</option>
                <option value="Domestic (Canada)">Domestic (Canada)</option>
                <option value="Australia">Australia</option>
                <option value="Czech Republic">Czech Republic</option>
                <option value="Germany">Germany</option>
                <option value="Israel">Israel</option>
                <option value="Portugal">Portugal</option>
                <option value="South Africa">South Africa</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <Mail className="w-4 h-4 inline mr-1" /> Email *
              </label>
              <input
                data-testid="register-email"
                type="email"
                required
                value={registerData.email}
                onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                placeholder="email@company.com"
              />
            </div>

            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <Phone className="w-4 h-4 inline mr-1" /> Phone *
              </label>
              <input
                data-testid="register-phone"
                type="tel"
                required
                value={registerData.phone}
                onChange={(e) => setRegisterData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                placeholder="+1 (555) 000-0000"
              />
            </div>

            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <FileText className="w-4 h-4 inline mr-1" /> Health Canada License # (optional)
              </label>
              <input
                data-testid="register-license"
                type="text"
                value={registerData.license_number || ''}
                onChange={(e) => setRegisterData(prev => ({ ...prev, license_number: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                placeholder="e.g. LIC-123456"
              />
            </div>

            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                I am a... *
              </label>
              <select
                data-testid="register-user-type"
                required
                value={registerData.user_type}
                onChange={(e) => setRegisterData(prev => ({ ...prev, user_type: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
              >
                <option value="">Select your role...</option>
                <option value="Buyer">Buyer</option>
                <option value="Seller">Seller</option>
                <option value="Both">Buyer & Seller</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Do you have an Open Permit?
                </label>
                <select
                  data-testid="register-open-permit"
                  value={registerData.open_permit}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, open_permit: e.target.value, permit_jurisdiction: e.target.value !== 'Yes' ? '' : prev.permit_jurisdiction }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                >
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Permit Jurisdiction
                </label>
                <select
                  data-testid="register-jurisdiction"
                  value={registerData.permit_jurisdiction}
                  onChange={(e) => setRegisterData(prev => ({ ...prev, permit_jurisdiction: e.target.value }))}
                  className={`w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none ${registerData.open_permit !== 'Yes' ? 'opacity-50' : ''}`}
                  disabled={registerData.open_permit !== 'Yes'}
                >
                  <option value="">Select jurisdiction...</option>
                  <option value="Domestic (Canada)">Domestic (Canada)</option>
                  <option value="Australia">Australia</option>
                  <option value="Czech Republic">Czech Republic</option>
                  <option value="Germany">Germany</option>
                  <option value="Israel">Israel</option>
                  <option value="Portugal">Portugal</option>
                  <option value="South Africa">South Africa</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="text-red-500 text-sm text-center">{error}</div>
            )}

            <button
              data-testid="register-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 text-black font-bold uppercase tracking-widest px-8 py-4 transition-all mt-6"
            >
              {loading ? 'Submitting...' : 'Register & Verify Email'}
            </button>
          </form>
          )
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <p className="text-zinc-400 text-sm text-center mb-6">
              Welcome back! Enter your credentials to continue.
            </p>

            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <Mail className="w-4 h-4 inline mr-1" /> Email
              </label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={loginData.email}
                onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                placeholder="email@company.com"
              />
            </div>

            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <Lock className="w-4 h-4 inline mr-1" /> Password
              </label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={loginData.password}
                onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                placeholder="Your password"
              />
            </div>

            {error && (
              <div data-testid="login-error" className={`text-sm text-center p-3 ${error.includes('warren@firegold.ca') ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'text-red-500'}`}>
                {error.includes('warren@firegold.ca') ? (
                  <div>
                    <p className="font-bold mb-1">{error.split('Please')[0]}</p>
                    <p>Please contact <a href="mailto:warren@firegold.ca" className="text-gold-500 underline hover:text-gold-400">warren@firegold.ca</a> to request new access.</p>
                  </div>
                ) : error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 text-black font-bold uppercase tracking-widest px-8 py-4 transition-all mt-6"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              data-testid="forgot-password-link"
              className="w-full text-center text-gold-500 hover:text-gold-400 text-xs uppercase tracking-widest mt-3 transition-colors"
            >
              Forgot Password?
            </button>
          </form>
        )}

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setShowForgotPassword(false); setForgotSent(false); }} />
            <div data-testid="forgot-password-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-md p-8">
              <button onClick={() => { setShowForgotPassword(false); setForgotSent(false); }} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"><X className="w-5 h-5 text-black" /></button>
              <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest mb-4">Reset Password</h2>
              {forgotSent ? (
                <div className="text-center py-4">
                  <CheckCircle2 className="w-12 h-12 text-cannabis-green mx-auto mb-4" />
                  <p className="text-white mb-2">Reset link sent!</p>
                  <p className="text-zinc-400 text-sm">Check your email for a link to reset your password. It expires in 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-zinc-400 text-sm mb-4">Enter your email and we'll send you a link to reset your password.</p>
                  <input
                    data-testid="forgot-email-input"
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                    placeholder="Your email address"
                  />
                  <button
                    type="submit"
                    disabled={forgotSending}
                    data-testid="forgot-submit-btn"
                    className="w-full bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all"
                  >
                    {forgotSending ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        <p className="text-zinc-600 text-xs text-center mt-6">
          By registering, you agree to our terms of service.
        </p>
      </div>
      </div>

      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff'
          }
        }}
      />
    </div>
  );
};

// Submission Status Tracker (Public)
const SubmissionTracker = () => {
  const [searchType, setSearchType] = useState('company'); // 'company' or 'id'
  const [searchValue, setSearchValue] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue.trim()) return;
    setSearching(true);
    setError('');
    setResults(null);

    try {
      const param = searchType === 'company' 
        ? `company_name=${encodeURIComponent(searchValue.trim())}`
        : `submission_id=${encodeURIComponent(searchValue.trim())}`;
      const res = await axios.get(`${API}/grower/track?${param}`);
      setResults(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No submissions found');
    } finally {
      setSearching(false);
    }
  };

  const statusColor = (status) => {
    if (status === 'approved') return 'bg-cannabis-green/20 text-cannabis-green';
    if (status === 'rejected') return 'bg-red-500/20 text-red-500';
    return 'bg-gold-500/20 text-gold-500';
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <header className="bg-black/80 backdrop-blur-md border-b border-white/5 h-20 flex items-center justify-between px-6 md:px-12">
        <a href="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm">Back to Catalog</span>
        </a>
        <img src={FIREGOLD_LOGO} alt="Firegold Brands" className="h-28 w-auto" />
        <div></div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-gold-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Search className="w-8 h-8 text-gold-500" />
          </div>
          <h1 className="font-cinzel font-bold text-3xl text-white mb-4">Track Your Submission</h1>
          <p className="text-zinc-400">Check the status of your strain submission by LP name or reference ID.</p>
        </div>

        <form onSubmit={handleSearch} className="bg-charcoal border border-white/10 p-6 mb-8" data-testid="tracker-form">
          <div className="flex gap-2 mb-4">
            <button type="button" onClick={() => setSearchType('company')}
              className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest transition-all ${searchType === 'company' ? 'bg-gold-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}>
              LP / Company Name
            </button>
            <button type="button" onClick={() => setSearchType('id')}
              className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest transition-all ${searchType === 'id' ? 'bg-gold-500 text-black' : 'bg-zinc-800 text-zinc-400'}`}>
              Reference ID
            </button>
          </div>
          <div className="flex gap-2">
            <input
              data-testid="tracker-search-input"
              type="text"
              required
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex-1 bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
              placeholder={searchType === 'company' ? 'Enter LP or company name...' : 'Enter reference ID...'}
            />
            <button data-testid="tracker-search-btn" type="submit" disabled={searching}
              className="bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 text-black font-bold uppercase tracking-widest px-6 h-12 transition-all">
              {searching ? '...' : 'Track'}
            </button>
          </div>
        </form>

        {error && (
          <div className="bg-charcoal border border-red-500/30 p-6 text-center">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {results && (
          <div className="space-y-6" data-testid="tracker-results">
            {/* Licenses on File */}
            {(results.licenses_on_file.health_canada_license || results.licenses_on_file.cra_license || results.licenses_on_file.coa) && (
              <div className="bg-cannabis-green/10 border border-cannabis-green/30 p-5">
                <h3 className="text-cannabis-green font-bold text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5" /> Licenses on File
                </h3>
                <div className="flex flex-wrap gap-3">
                  {results.licenses_on_file.health_canada_license && (
                    <span className="flex items-center gap-1 bg-cannabis-green/20 text-cannabis-green text-xs px-3 py-1.5 font-bold">
                      <Check className="w-3 h-3" /> Health Canada License
                    </span>
                  )}
                  {results.licenses_on_file.cra_license && (
                    <span className="flex items-center gap-1 bg-cannabis-green/20 text-cannabis-green text-xs px-3 py-1.5 font-bold">
                      <Check className="w-3 h-3" /> CRA Excise License
                    </span>
                  )}
                  {results.licenses_on_file.coa && (
                    <span className="flex items-center gap-1 bg-cannabis-green/20 text-cannabis-green text-xs px-3 py-1.5 font-bold">
                      <Check className="w-3 h-3" /> COA
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Submissions List */}
            <div>
              <h3 className="text-zinc-400 text-sm uppercase tracking-widest mb-3">{results.submissions.length} Submission{results.submissions.length !== 1 ? 's' : ''}</h3>
              <div className="space-y-3">
                {results.submissions.map(sub => (
                  <div key={sub.id} data-testid={`tracker-result-${sub.id}`} className="bg-charcoal border border-white/10 p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-cinzel font-bold text-lg text-white">{sub.strain_name}</h4>
                        <p className="text-zinc-500 text-sm">{sub.company_name}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-bold uppercase tracking-widest ${statusColor(sub.status)}`}>
                        {sub.status}
                      </span>
                    </div>
                    <div className="flex gap-6 text-xs text-zinc-500">
                      <span>Submitted: {new Date(sub.submitted_at).toLocaleDateString()}</span>
                      {sub.price_per_gram && <span className="text-gold-500">Price: ${sub.price_per_gram}/g</span>}
                    </div>
                    <div className="mt-2">
                      <span className="text-zinc-600 font-mono text-xs">Ref: {sub.id}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <a href="/submit-strain" className="block w-full bg-cannabis-green hover:bg-green-600 text-white text-center font-bold uppercase tracking-widest px-8 py-4 transition-all">
              Submit Another Product
            </a>
          </div>
        )}
      </main>
    </div>
  );
};

// Admin Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/login`, { password: password.trim() });
      localStorage.setItem('adminToken', response.data.token);
      onLogin(response.data.token);
      toast.success('Admin access granted');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid admin password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
      <div className="bg-charcoal border border-white/10 p-8 max-w-md w-full">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Lock className="w-8 h-8 text-gold-500" />
          <h1 className="font-cinzel font-bold text-2xl text-white">Admin Access</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
              Admin Password
            </label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
              placeholder="Enter admin password"
              required
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm text-center">{error}</div>
          )}

          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 text-black font-bold uppercase tracking-widest px-8 py-4 transition-all"
          >
            {loading ? 'Verifying...' : 'Access Admin Panel'}
          </button>
        </form>

        <a 
          href="/"
          className="block text-center mt-6 text-zinc-500 hover:text-gold-500 text-sm transition-colors"
        >
          ← Back to Catalog
        </a>
      </div>
    </div>
  );
};

// Admin Page Component
const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [strains, setStrains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [stats, setStats] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsView, setAnalyticsView] = useState('overview'); // overview, users, user-detail, engagement
  const [userActivityReport, setUserActivityReport] = useState([]);
  const [userActivitySort, setUserActivitySort] = useState('login_count');
  const [columnSort, setColumnSort] = useState({ key: 'login_count', dir: 'desc' });

  const handleColumnSort = (key) => {
    setColumnSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Secure file download — fetches via Authorization header, opens as blob URL
  const openSecureFile = async (filePath) => {
    try {
      // Handle GridFS paths (/api/media/xxx) and legacy disk paths (/uploads/xxx)
      let fetchUrl;
      if (filePath.startsWith('/api/media/')) {
        fetchUrl = `${API}/media/${filePath.replace('/api/media/', '')}`;
      } else if (filePath.startsWith('/api/')) {
        fetchUrl = `${API}${filePath.replace('/api', '')}`;
      } else {
        fetchUrl = `${API}/files/${filePath.replace('/uploads/', '')}`;
      }
      const res = await fetch(fetchUrl, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (!res.ok) { toast.error('Failed to open file'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 120000);
    } catch (err) {
      toast.error('Failed to open file');
    }
  };

  const sortedActivityReport = useMemo(() => {
    if (!userActivityReport.length) return [];
    const { key, dir } = columnSort;
    return [...userActivityReport].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      if (key === 'name') { aVal = (a.name || '').toLowerCase(); bVal = (b.name || '').toLowerCase(); }
      if (key === 'company') { aVal = (a.company || '').toLowerCase(); bVal = (b.company || '').toLowerCase(); }
      if (key === 'last_active') { aVal = aVal ? new Date(aVal).getTime() : 0; bVal = bVal ? new Date(bVal).getTime() : 0; }
      if (aVal < bVal) return dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [userActivityReport, columnSort]);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [editingStrain, setEditingStrain] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const openEditStrain = (strain) => {
    setEditingStrain(strain.id);
    setEditForm({
      strain_name: strain.strain_name || '',
      type: strain.type || '',
      thc_percentage: strain.thc_percentage || '',
      cbd_percentage: strain.cbd_percentage || '',
      price_tier: strain.price_tier || '$$',
      terpene_profile: Array.isArray(strain.terpene_profile) ? strain.terpene_profile.join(', ') : (strain.terpene_profile || ''),
      lineage: strain.lineage || '',
      lot_size: strain.lot_size || '',
      effects: Array.isArray(strain.effects) ? strain.effects : (typeof strain.effects === 'string' && strain.effects ? strain.effects.split(',').map(e => e.trim()).filter(Boolean) : []),
      grade_a_kg: strain.grade_a_kg || '',
      grade_b_kg: strain.grade_b_kg || '',
      grade_c_kg: strain.grade_c_kg || '',
      harvest_date: strain.harvest_date || '',
      growing_method: strain.growing_method || 'Indoor',
      category: strain.category || 'Dried Flower',
      bc_dd_compliant: strain.bc_dd_compliant || false,
      is_gacp_certified: strain.is_gacp_certified || false,
      is_gmp_certified: strain.is_gmp_certified || false,
      terpene_percentage: strain.terpene_percentage || '',
      extract_purity: strain.extract_purity || '',
      thc_non_detect: strain.thc_non_detect || false,
      grower_company: strain.grower_company || '',
      grower_name: strain.grower_name || '',
      grower_email: strain.grower_email || '',
      lots: strain.lots || []
    });
  };

  const saveEditStrain = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        effects: Array.isArray(editForm.effects) ? editForm.effects : [],
        terpene_profile: editForm.terpene_profile.split(',').map(t => t.trim()).filter(Boolean),
        grade_a_kg: parseFloat(editForm.grade_a_kg) || null,
        grade_b_kg: parseFloat(editForm.grade_b_kg) || null,
        grade_c_kg: parseFloat(editForm.grade_c_kg) || null,
        harvest_date: editForm.harvest_date || null,
        growing_method: editForm.growing_method || null,
        category: editForm.category || null,
        bc_dd_compliant: editForm.bc_dd_compliant || false,
        is_gacp_certified: editForm.is_gacp_certified || false,
        is_gmp_certified: editForm.is_gmp_certified || false,
        terpene_percentage: parseFloat(editForm.terpene_percentage) || null,
        extract_purity: parseFloat(editForm.extract_purity) || null,
        thc_non_detect: editForm.thc_non_detect || false,
        grower_company: editForm.grower_company || null,
        grower_name: editForm.grower_name || null,
        grower_email: editForm.grower_email || null,
        lots: editForm.lots || []
      };
      await axios.put(`${API}/strains/${editingStrain}`, payload, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Strain updated');
      setEditingStrain(null);
      fetchStrains(adminToken);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setSavingEdit(false);
  };
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('images'); // 'images', 'analytics', 'users', 'flags', 'submissions', 'directory', 'document-vault'
  const [flags, setFlags] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [submissionFilter, setSubmissionFilter] = useState('all');
  const [reviewingSubmission, setReviewingSubmission] = useState(null);
  const [pricePerGram, setPricePerGram] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [processingSubmission, setProcessingSubmission] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [directorySearch, setDirectorySearch] = useState('');
  const [inventory, setInventory] = useState(null);
  const [adminInquiries, setAdminInquiries] = useState([]);
  const [vaultData, setVaultData] = useState({ entries: [], total: 0, summary: {} });
  const [vaultFilter, setVaultFilter] = useState('all');
  const [vaultSearch, setVaultSearch] = useState('');
  const [vaultSort, setVaultSort] = useState({ key: 'company', dir: 'asc' });
  const [vaultUploading, setVaultUploading] = useState(null);
  const [inquiryFilter, setInquiryFilter] = useState('all');
  const [inquirySearch, setInquirySearch] = useState('');
  const [processingInquiry, setProcessingInquiry] = useState(null);
  const [inquiryNotes, setInquiryNotes] = useState('');
  const [expandedInquiry, setExpandedInquiry] = useState(null);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [userProfileLoading, setUserProfileLoading] = useState(false);
  const [editingDirectoryLicense, setEditingDirectoryLicense] = useState(null);
  const [directoryLicenseForm, setDirectoryLicenseForm] = useState({});
  const [affiliates, setAffiliates] = useState([]);
  const [showAddAffiliate, setShowAddAffiliate] = useState(false);
  const [affiliateForm, setAffiliateForm] = useState({ name: '', email: '', phone: '', company: '', referral_code: '' });
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [convertingInquiry, setConvertingInquiry] = useState(null);
  const [conversionData, setConversionData] = useState({ sale_amount: '', total_grams: '', commission_type: '', commission_value: '' });

  // Auto-generate referral code from name
  const autoGenerateCode = (name) => {
    if (!name) return '';
    const parts = name.trim().toUpperCase().split(/\s+/);
    if (parts.length >= 2) return parts[0] + parts[parts.length - 1].charAt(0);
    return parts[0].slice(0, 8);
  };
  const [selectedAffiliateDetail, setSelectedAffiliateDetail] = useState(null);
  const [conversionForm, setConversionForm] = useState({ inquiry_id: '', sale_amount: '' });

  // Messages state
  const [contactMessages, setContactMessages] = useState([]);
  const [replyingMessage, setReplyingMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [messagesFilter, setMessagesFilter] = useState('all');

  // Notification state
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const prevCountsRef = useRef({ messages: 0, inquiries: 0 });

  // Bot Training state
  const [botTraining, setBotTraining] = useState([]);
  const [newTraining, setNewTraining] = useState({ question: '', answer: '', category: 'general' });
  const [editingTraining, setEditingTraining] = useState(null);
  const [adminLists, setAdminLists] = useState([]);
  const [adminListsAnalytics, setAdminListsAnalytics] = useState(null);
  const [loadingAdminLists, setLoadingAdminLists] = useState(false);

  // Admin Deals state
  const [adminDeals, setAdminDeals] = useState([]);
  const [dealFilter, setDealFilter] = useState('all');

  // Knowledge Library state
  const [knowledgeEntries, setKnowledgeEntries] = useState([]);
  const [knowledgeStats, setKnowledgeStats] = useState(null);
  const [knowledgeFilter, setKnowledgeFilter] = useState({ category: '', search: '', evidence_tier: '', stale_only: false, review_status: '', jurisdiction: '' });
  const [editingKnowledge, setEditingKnowledge] = useState(null);
  const [showKnowledgeForm, setShowKnowledgeForm] = useState(false);
  const [knowledgeForm, setKnowledgeForm] = useState({
    title: '', question_variants: [], answer: '', category: 'terpene_science', subcategory: '',
    tags: [], jurisdiction: '', country: '', evidence_tier: 'industry_reference',
    confidence_level: 'moderate', source_name: '', source_url: '', source_date: '',
    stale_after_days: null, compliance_flag: false, visibility: 'public', active: true,
    edit_note: '', review_notes: '', review_owner: '', next_review_due_at: ''
  });
  const [previewQuestion, setPreviewQuestion] = useState('');
  const [previewResults, setPreviewResults] = useState(null);
  const [variantInput, setVariantInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(null);
  const [entryHistory, setEntryHistory] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(null);
  const [reviewForm, setReviewForm] = useState({ review_notes: '', review_owner: '', next_review_due_at: '', review_status: 'current' });

  // Admin Strain Hunter state
  const [adminHunterRequests, setAdminHunterRequests] = useState(null);
  const [adminHunterFilter, setAdminHunterFilter] = useState('');
  const [adminHunterUpdating, setAdminHunterUpdating] = useState(null);

  // Media Health Report state
  const [mediaHealth, setMediaHealth] = useState(null);
  const [mediaHealthLoading, setMediaHealthLoading] = useState(false);

  // COA Intelligence Engine state
  const [coaParsingStrain, setCoaParsingStrain] = useState(null);
  const [coaParsedData, setCoaParsedData] = useState(null);
  const [coaParseLoading, setCoaParseLoading] = useState(false);
  const [coaEditMode, setCoaEditMode] = useState(false);
  const [batchParseRunning, setBatchParseRunning] = useState(false);
  const [batchParseResult, setBatchParseResult] = useState(null);


  const [emailsEnabled, setEmailsEnabled] = useState(false);

  // Request notification permission and set up polling
  useEffect(() => {
    if (!isAuthenticated || !adminToken) return;
    
    // Request permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(p => setNotificationsEnabled(p === 'granted'));
    } else if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }

    // Poll for new messages/inquiries every 30 seconds
    const interval = setInterval(async () => {
      try {
        const [msgResp, inqResp] = await Promise.all([
          axios.get(`${API}/admin/contact-messages`, { headers: { Authorization: `Bearer ${adminToken}` } }),
          axios.get(`${API}/admin/inquiries`, { headers: { Authorization: `Bearer ${adminToken}` } })
        ]);
        
        const newMsgCount = msgResp.data.filter(m => m.status === 'unread').length;
        const newInqCount = inqResp.data.filter(i => i.status === 'pending' || i.status === 'new').length;
        const prev = prevCountsRef.current;
        
        if (newMsgCount > prev.messages && prev.messages > 0) {
          playNotificationSound();
          if (notificationsEnabled) {
            new Notification('Firegold - New Message', {
              body: `You have ${newMsgCount} unread message${newMsgCount > 1 ? 's' : ''}`,
              icon: '/firegold_logo_only.png'
            });
          }
          toast.info(`${newMsgCount - prev.messages} new message(s) received`);
        }
        
        if (newInqCount > prev.inquiries && prev.inquiries > 0) {
          playNotificationSound();
          if (notificationsEnabled) {
            new Notification('Firegold - New Inquiry', {
              body: `You have ${newInqCount} pending inquir${newInqCount > 1 ? 'ies' : 'y'}`,
              icon: '/firegold_logo_only.png'
            });
          }
          toast.info(`${newInqCount - prev.inquiries} new inquiry(ies) received`);
        }
        
        prevCountsRef.current = { messages: newMsgCount, inquiries: newInqCount };
        setContactMessages(msgResp.data);
      } catch (err) {
        // Silently fail on polling
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, adminToken, notificationsEnabled]);

  // Sort state for all admin tabs
  const [usersSort, setUsersSort] = useState({ key: 'created_at', dir: 'desc' });
  const [submissionsSort, setSubmissionsSort] = useState({ key: 'created_at', dir: 'desc' });
  const [inquiriesSort, setInquiriesSort] = useState({ key: 'created_at', dir: 'desc' });
  const [directorySort, setDirectorySort] = useState({ key: 'strain_name', dir: 'asc' });
  const [affiliatesSort, setAffiliatesSort] = useState({ key: 'total_referrals', dir: 'desc' });
  const [messagesSort, setMessagesSort] = useState({ key: 'created_at', dir: 'desc' });

  const makeSortHandler = (setter) => (key) => {
    setter(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const sortData = (data, sortState) => {
    if (!data.length) return [];
    const { key, dir } = sortState;
    return [...data].sort((a, b) => {
      let aVal = a[key]; let bVal = b[key];
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = (bVal || '').toLowerCase(); }
      if (key.includes('created_at') || key.includes('date') || key === 'last_active') { aVal = aVal ? new Date(aVal).getTime() : 0; bVal = bVal ? new Date(bVal).getTime() : 0; }
      if (aVal == null) aVal = ''; if (bVal == null) bVal = '';
      if (aVal < bVal) return dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader = ({ columns, sortState, onSort, testPrefix }) => (
    <thead className="bg-zinc-900">
      <tr>
        {columns.map(col => (
          <th
            key={col.key}
            data-testid={`${testPrefix}-sort-${col.key}`}
            onClick={() => onSort(col.key)}
            className={`text-${col.align || 'left'} text-xs uppercase tracking-widest p-4 cursor-pointer select-none transition-colors hover:text-gold-500 ${
              sortState.key === col.key ? 'text-gold-500' : 'text-zinc-500'
            } ${col.className || ''}`}
          >
            {col.label}
            {sortState.key === col.key && (
              <span className="ml-1">{sortState.dir === 'desc' ? '\u25BC' : '\u25B2'}</span>
            )}
          </th>
        ))}
      </tr>
    </thead>
  );

  const SortButtons = ({ options, sortState, onSort, testPrefix }) => (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-zinc-500 text-xs uppercase tracking-widest">Sort:</span>
      {options.map(opt => (
        <button
          key={opt.key}
          data-testid={`${testPrefix}-sort-${opt.key}`}
          onClick={() => onSort(opt.key)}
          className={`px-3 py-1 text-xs font-bold uppercase tracking-widest transition-all ${
            sortState.key === opt.key ? 'bg-gold-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'
          }`}
        >
          {opt.label}{sortState.key === opt.key ? (sortState.dir === 'desc' ? ' \u25BC' : ' \u25B2') : ''}
        </button>
      ))}
    </div>
  );

  const fetchDirectory = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/strain-directory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDirectory(response.data);
    } catch (error) {
      console.error('Failed to load directory');
    }
  };

  const fetchInventory = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/inventory-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventory(response.data);
    } catch (error) {
      console.error('Failed to load inventory');
    }
  };

  const fetchAdminInquiries = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminInquiries(response.data);
    } catch (error) {
      console.error('Failed to load inquiries');
    }
  };

  const fetchVaultData = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/document-vault`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVaultData(response.data);
    } catch (error) {
      console.error('Failed to load document vault');
    }
  };

  const handleVaultUpload = async (source, sourceId, docType, file) => {
    const uploadKey = `${sourceId}-${docType}`;
    setVaultUploading(uploadKey);
    try {
      const formData = new FormData();
      formData.append('source', source);
      formData.append('source_id', sourceId);
      formData.append('doc_type', docType);
      formData.append('file', file);
      await axios.post(`${API}/admin/document-vault/upload`, formData, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${docType.replace(/_/g, ' ').toUpperCase()} uploaded successfully`);
      fetchVaultData(adminToken);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setVaultUploading(null);
    }
  };

  const handleVaultDownload = (fileUrl, strainName, docType) => {
    const fileId = (fileUrl || '').replace('/api/media/', '').replace(/^\/api\//, '');
    if (!fileId) return;
    window.open(`${API}/admin/document-vault/download/${fileId}?token=${adminToken}`, '_blank');
  };

  const [vaultProcessing, setVaultProcessing] = useState(null);

  const pollJobStatus = async (token, jobId, onComplete, onFail) => {
    const maxAttempts = 60; // 2 minutes max
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await axios.get(`${API}/admin/ai-jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.status === 'completed') { onComplete(res.data.result); return; }
        if (res.data.status === 'failed') { onFail(res.data.error || 'Processing failed'); return; }
      } catch (e) {}
    }
    onFail('Processing timed out after 2 minutes');
  };

  const handleRedactCoa = async (entry) => {
    const key = `redact-${entry.id}`;
    setVaultProcessing(key);
    try {
      const formData = new FormData();
      formData.append('source', entry.source);
      formData.append('source_id', entry.id);
      formData.append('file_url', entry.coa.file_url);
      const resp = await axios.post(`${API}/admin/document-vault/redact-coa`, formData, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
        timeout: 15000
      });
      if (resp.data.job_id) {
        toast.info('Redacting COA... this may take a minute');
        await pollJobStatus(adminToken, resp.data.job_id,
          (result) => {
            if (result.redacted_count > 0) toast.success(`Redacted ${result.redacted_count} grower items`);
            else toast.info('No grower-identifying info found to redact');
            fetchVaultData(adminToken);
            setVaultProcessing(null);
          },
          (err) => { toast.error(`Redaction failed: ${err}`); setVaultProcessing(null); }
        );
      } else {
        if (resp.data.redacted_count > 0) toast.success(`Redacted ${resp.data.redacted_count} grower items`);
        else toast.info('No grower-identifying info found');
        fetchVaultData(adminToken);
        setVaultProcessing(null);
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Redaction failed — check server logs';
      toast.error(typeof msg === 'string' ? msg : 'Redaction failed');
      setVaultProcessing(null);
    }
  };

  const handleEnhanceImage = async (entry) => {
    const key = `enhance-${entry.id}`;
    setVaultProcessing(key);
    try {
      const formData = new FormData();
      formData.append('source', entry.source);
      formData.append('source_id', entry.id);
      formData.append('file_url', entry.image.file_url);
      const resp = await axios.post(`${API}/admin/document-vault/enhance-image`, formData, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
        timeout: 15000
      });
      if (resp.data.job_id) {
        toast.info('Enhancing image... this may take a minute');
        await pollJobStatus(adminToken, resp.data.job_id,
          (result) => {
            toast.success(result.message || 'Image enhanced');
            fetchVaultData(adminToken);
            setVaultProcessing(null);
          },
          (err) => { toast.error(`Enhancement failed: ${err}`); setVaultProcessing(null); }
        );
      } else {
        toast.success(resp.data.message || 'Image enhanced');
        fetchVaultData(adminToken);
        setVaultProcessing(null);
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Enhancement failed — check server logs';
      toast.error(typeof msg === 'string' ? msg : 'Enhancement failed');
      setVaultProcessing(null);
    }
  };


  const updateInquiryStatus = async (inquiryId, status, notes) => {
    setProcessingInquiry(inquiryId);
    try {
      await axios.put(`${API}/admin/inquiries/${inquiryId}/status`, {
        status,
        admin_notes: notes || null
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Inquiry marked as ${status}`);
      fetchAdminInquiries(adminToken);
      setInquiryNotes('');
      setExpandedInquiry(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update inquiry');
    }
    setProcessingInquiry(null);
  };

  const fetchUserProfile = async (userId) => {
    setUserProfileLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/profile`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSelectedUserProfile(response.data);
    } catch (err) {
      toast.error('Failed to load user profile');
    }
    setUserProfileLoading(false);
  };

  const updateDirectoryLicense = async (strainId) => {
    try {
      await axios.put(`${API}/admin/strain-directory/${strainId}/license`, directoryLicenseForm, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('License info updated');
      setEditingDirectoryLicense(null);
      setDirectoryLicenseForm({});
      fetchDirectory(adminToken);
    } catch (err) {
      toast.error('Failed to update license');
    }
  };

  const fetchAffiliates = async () => {
    try {
      const response = await axios.get(`${API}/admin/affiliates`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAffiliates(response.data);
    } catch (err) {
      console.error('Failed to load affiliates');
    }
  };

  const fetchContactMessages = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/contact-messages`, {
        headers: { Authorization: `Bearer ${token || adminToken}` }
      });
      setContactMessages(response.data);
    } catch (err) {
      console.error('Failed to load messages');
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await axios.put(`${API}/admin/contact-messages/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setContactMessages(prev => prev.map(m => m.id === messageId ? {...m, status: 'read'} : m));
      toast.success('Marked as read');
    } catch (err) {
      toast.error('Failed to update message');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message permanently?')) return;
    try {
      await axios.delete(`${API}/admin/contact-messages/${messageId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setContactMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  const handleReplyMessage = async () => {
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      await axios.post(`${API}/admin/contact-messages/${replyingMessage.id}/reply`, 
        { reply_text: replyText },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setContactMessages(prev => prev.map(m => m.id === replyingMessage.id ? {...m, status: 'replied'} : m));
      toast.success('Reply sent to ' + replyingMessage.email);
      setReplyingMessage(null);
      setReplyText('');
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  // Bot Training CRUD
  const fetchBotTraining = async () => {
    try {
      const resp = await axios.get(`${API}/admin/bot-training`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setBotTraining(resp.data);
    } catch (err) {
      console.error('Failed to load training data');
    }
  };

  const addTrainingEntry = async () => {
    if (!newTraining.question.trim() || !newTraining.answer.trim()) return;
    try {
      const resp = await axios.post(`${API}/admin/bot-training`, newTraining, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setBotTraining(prev => [...prev, resp.data]);
      setNewTraining({ question: '', answer: '', category: 'general' });
      toast.success('Training entry added');
    } catch (err) {
      toast.error('Failed to add entry');
    }
  };

  const updateTrainingEntry = async (entry) => {
    try {
      await axios.put(`${API}/admin/bot-training/${entry.id}`, {
        question: entry.question,
        answer: entry.answer,
        category: entry.category
      }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setBotTraining(prev => prev.map(e => e.id === entry.id ? entry : e));
      setEditingTraining(null);
      toast.success('Training entry updated');
    } catch (err) {
      toast.error('Failed to update entry');
    }
  };

  const deleteTrainingEntry = async (entryId) => {
    if (!window.confirm('Delete this training entry?')) return;
    try {
      await axios.delete(`${API}/admin/bot-training/${entryId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setBotTraining(prev => prev.filter(e => e.id !== entryId));
      toast.success('Entry deleted');
    } catch (err) {
      toast.error('Failed to delete entry');
    }
  };

  // Settings
  const [settingsMeta, setSettingsMeta] = useState({});
  const fetchSettings = async (token) => {
    try {
      const resp = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token || adminToken}` }
      });
      const data = resp.data.settings || resp.data;
      setEmailsEnabled(data.emails_enabled || false);
      if (resp.data.meta) setSettingsMeta(resp.data.meta);
    } catch (err) {
      console.error('Failed to load settings');
    }
  };

  // Saved Lists (Admin)
  const fetchAdminLists = async () => {
    setLoadingAdminLists(true);
    try {
      const [listsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/lists`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        axios.get(`${API}/admin/lists/analytics`, { headers: { Authorization: `Bearer ${adminToken}` } })
      ]);
      setAdminLists(listsRes.data);
      setAdminListsAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Failed to load admin lists:', err);
    }
    setLoadingAdminLists(false);
  };

  // Compliance Vault (Admin)
  const [complianceDocs, setComplianceDocs] = useState([]);
  const [complianceFilter, setComplianceFilter] = useState({ company: '', doc_type: '', province: '' });
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [certReport, setCertReport] = useState(null);

  const fetchComplianceDocs = async (filters = complianceFilter) => {
    try {
      const params = new URLSearchParams();
      if (filters.company) params.append('company', filters.company);
      if (filters.doc_type) params.append('doc_type', filters.doc_type);
      if (filters.province) params.append('province', filters.province);
      const res = await axios.get(`${API}/admin/compliance-docs?${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setComplianceDocs(res.data);
    } catch (err) { console.error('Failed to load compliance docs:', err); }
  };

  const fetchAdminDeals = async (status = dealFilter) => {
    try {
      const params = status && status !== 'all' ? `?status=${status}` : '';
      const res = await axios.get(`${API}/admin/deals${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAdminDeals(res.data);
    } catch (err) { console.error('Failed to load deals:', err); }
  };

  const updateDealStatus = async (dealId, newStatus, notes = '') => {
    try {
      await axios.put(`${API}/admin/deals/${dealId}/status`, { status: newStatus, admin_notes: notes }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Deal status updated to ${newStatus}`);
      fetchAdminDeals(dealFilter);
    } catch (err) { toast.error('Failed to update deal status'); }
  };

  // Admin Strain Hunter functions
  const fetchAdminHunterRequests = async (statusFilter = adminHunterFilter) => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/admin/strain-hunter/requests${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setAdminHunterRequests(res.data);
    } catch (err) { console.error('Hunter requests fetch failed:', err); }
  };

  const updateHunterRequestStatus = async (requestId, newStatus, notes = '') => {
    setAdminHunterUpdating(requestId);
    try {
      await axios.put(`${API}/admin/strain-hunter/requests/${requestId}/status`, {
        status: newStatus, admin_notes: notes || undefined
      }, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success(`Request updated to ${newStatus}`);
      fetchAdminHunterRequests(adminHunterFilter);
    } catch (err) { toast.error('Failed to update request'); }
    setAdminHunterUpdating(null);
  };

  const seedStrainReferenceLibrary = async () => {
    try {
      const res = await axios.post(`${API}/admin/strain-hunter/seed-reference`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(res.data.message);
    } catch (err) { toast.error('Failed to seed reference library'); }
  };

  // Media Health Report functions
  const fetchMediaHealth = async () => {
    setMediaHealthLoading(true);
    try {
      const res = await axios.get(`${API}/admin/media-health`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setMediaHealth(res.data);
    } catch (err) { toast.error('Failed to fetch media health report'); }
    setMediaHealthLoading(false);
  };

  // Knowledge Library functions
  const fetchKnowledge = async (filters = knowledgeFilter) => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      if (filters.evidence_tier) params.append('evidence_tier', filters.evidence_tier);
      if (filters.stale_only) params.append('stale_only', 'true');
      if (filters.review_status) params.append('review_status', filters.review_status);
      if (filters.jurisdiction) params.append('jurisdiction', filters.jurisdiction);
      const res = await axios.get(`${API}/admin/knowledge-library?${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setKnowledgeEntries(res.data);
    } catch (err) { console.error('Knowledge fetch failed:', err); }
  };

  const fetchKnowledgeStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/knowledge-library/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setKnowledgeStats(res.data);
    } catch (err) { console.error('Knowledge stats failed:', err); }
  };

  const saveKnowledgeEntry = async () => {
    try {
      if (editingKnowledge) {
        await axios.put(`${API}/admin/knowledge-library/${editingKnowledge}`, knowledgeForm, {
          headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' }
        });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/admin/knowledge-library`, knowledgeForm, {
          headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' }
        });
        toast.success('Entry created');
      }
      setShowKnowledgeForm(false);
      setEditingKnowledge(null);
      resetKnowledgeForm();
      fetchKnowledge();
      fetchKnowledgeStats();
    } catch (err) { toast.error('Failed to save entry'); }
  };

  const deleteKnowledgeEntry = async (id) => {
    if (!window.confirm('Delete this knowledge entry?')) return;
    try {
      await axios.delete(`${API}/admin/knowledge-library/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Entry deleted');
      fetchKnowledge();
      fetchKnowledgeStats();
    } catch (err) { toast.error('Failed to delete entry'); }
  };

  const markEntryReviewed = async (id, reviewData = {}) => {
    try {
      await axios.put(`${API}/admin/knowledge-library/${id}/review`, reviewData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Marked as reviewed');
      setShowReviewModal(null);
      fetchKnowledge();
      fetchKnowledgeStats();
    } catch (err) { toast.error('Failed to mark reviewed'); }
  };

  const openReviewModal = (entry) => {
    setShowReviewModal(entry.id);
    setReviewForm({
      review_notes: entry.review_notes || '',
      review_owner: entry.review_owner || '',
      next_review_due_at: entry.next_review_due_at ? entry.next_review_due_at.split('T')[0] : '',
      review_status: 'current'
    });
  };

  const seedKnowledge = async () => {
    try {
      const res = await axios.post(`${API}/admin/knowledge-library/seed`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(res.data.message);
      fetchKnowledge();
      fetchKnowledgeStats();
    } catch (err) { toast.error('Failed to seed library'); }
  };

  const runPreviewRetrieval = async () => {
    if (!previewQuestion.trim()) return;
    try {
      const res = await axios.post(`${API}/admin/knowledge-library/preview-retrieval`,
        { question: previewQuestion },
        { headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' } }
      );
      setPreviewResults(res.data);
    } catch (err) { toast.error('Preview failed'); }
  };

  const resetKnowledgeForm = () => {
    setKnowledgeForm({
      title: '', question_variants: [], answer: '', category: 'terpene_science', subcategory: '',
      tags: [], jurisdiction: '', country: '', evidence_tier: 'industry_reference',
      confidence_level: 'moderate', source_name: '', source_url: '', source_date: '',
      stale_after_days: null, compliance_flag: false, visibility: 'public', active: true,
      edit_note: '', review_notes: '', review_owner: '', next_review_due_at: ''
    });
    setVariantInput('');
    setTagInput('');
  };

  const startEditKnowledge = (entry) => {
    setEditingKnowledge(entry.id);
    setKnowledgeForm({
      title: entry.title || '', question_variants: entry.question_variants || [], answer: entry.answer || '',
      category: entry.category || 'terpene_science', subcategory: entry.subcategory || '',
      tags: entry.tags || [], jurisdiction: entry.jurisdiction || '', country: entry.country || '',
      evidence_tier: entry.evidence_tier || 'industry_reference', confidence_level: entry.confidence_level || 'moderate',
      source_name: entry.source_name || '', source_url: entry.source_url || '', source_date: entry.source_date || '',
      stale_after_days: entry.stale_after_days || null, compliance_flag: entry.compliance_flag || false,
      visibility: entry.visibility || 'public', active: entry.active !== false,
      review_notes: entry.review_notes || '', review_owner: entry.review_owner || '',
      next_review_due_at: entry.next_review_due_at ? entry.next_review_due_at.split('T')[0] : ''
    });
    setShowKnowledgeForm(true);
  };

  const fetchEntryHistory = async (entryId) => {
    try {
      const res = await axios.get(`${API}/admin/knowledge-library/${entryId}/history`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setEntryHistory(res.data);
      setShowHistoryModal(entryId);
    } catch (err) { toast.error('Failed to load history'); }
  };

  const restoreVersion = async (entryId, version) => {
    if (!window.confirm(`Restore this entry to version ${version}? A new revision will be created.`)) return;
    try {
      await axios.post(`${API}/admin/knowledge-library/${entryId}/restore/${version}`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Restored to version ${version}`);
      fetchEntryHistory(entryId);
      fetchKnowledge();
    } catch (err) { toast.error('Failed to restore version'); }
  };

  const handleUploadComplianceDoc = async (e) => {
    e.preventDefault();
    const form = e.target;
    const fd = new FormData(form);
    try {
      await axios.post(`${API}/admin/compliance-docs`, fd, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded');
      setShowUploadDoc(false);
      form.reset();
      fetchComplianceDocs();
    } catch (err) { toast.error('Upload failed'); }
  };

  const deleteComplianceDoc = async (docId) => {
    if (!window.confirm('Delete this compliance document?')) return;
    try {
      await axios.delete(`${API}/admin/compliance-docs/${docId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Document deleted');
      fetchComplianceDocs();
    } catch (err) { toast.error('Delete failed'); }
  };

  const fetchCertReport = async () => {
    try {
      const res = await axios.get(`${API}/admin/certifications/report`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCertReport(res.data);
    } catch (err) { console.error('Failed to load cert report:', err); }
  };

  const updateCertification = async (entityType, entityId, certData) => {
    try {
      await axios.put(`${API}/admin/certifications/${entityType}/${entityId}`, certData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Certification updated');
      fetchCertReport();
    } catch (err) { toast.error('Update failed'); }
  };

  const toggleEmails = async () => {
    const newValue = !emailsEnabled;
    try {
      await axios.put(`${API}/admin/settings`, 
        { emails_enabled: newValue },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setEmailsEnabled(newValue);
      toast.success(newValue ? 'Emails ENABLED — notifications will be sent' : 'Emails DISABLED — no notifications will be sent');
      fetchSettings();
    } catch (err) {
      toast.error('Failed to update setting');
    }
  };

  const createAffiliate = async () => {
    try {
      await axios.post(`${API}/admin/affiliates`, affiliateForm, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Affiliate ${affiliateForm.referral_code.toUpperCase()} created`);
      setShowAddAffiliate(false);
      setAffiliateForm({ name: '', email: '', phone: '', company: '', referral_code: '' });
      fetchAffiliates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create affiliate');
    }
  };

  const fetchAffiliateDetail = async (affiliateId) => {
    try {
      const response = await axios.get(`${API}/admin/affiliates/${affiliateId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSelectedAffiliateDetail(response.data);
    } catch (err) {
      toast.error('Failed to load affiliate details');
    }
  };

  const saveAffiliateEdit = async () => {
    if (!editingAffiliate) return;
    try {
      await axios.put(`${API}/admin/affiliates/${editingAffiliate.id}`, editingAffiliate, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Affiliate updated');
      setEditingAffiliate(null);
      fetchAffiliates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const recordConversion = async () => {
    if (!convertingInquiry) return;
    try {
      const res = await axios.post(`${API}/admin/inquiries/${convertingInquiry.id}/convert`, {
        sale_amount: parseFloat(conversionData.sale_amount),
        total_grams: parseFloat(conversionData.total_grams) || 0,
        commission_type: conversionData.commission_type || null,
        commission_value: conversionData.commission_value ? parseFloat(conversionData.commission_value) : null
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(res.data.message);
      setConvertingInquiry(null);
      setConversionData({ sale_amount: '', total_grams: '', commission_type: '', commission_value: '' });
      fetchAffiliates();
      fetchAdminInquiries(adminToken);
      if (selectedAffiliateDetail) fetchAffiliateDetail(selectedAffiliateDetail.affiliate.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record conversion');
    }
  };

  const markCommissionPaid = async (eventId) => {
    try {
      await axios.put(`${API}/admin/referrals/${eventId}/paid`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Commission marked as paid');
      if (selectedAffiliateDetail) fetchAffiliateDetail(selectedAffiliateDetail.affiliate.id);
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleCoaUpload = async (strainId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('coa', file);
    try {
      await axios.put(`${API}/strains/${strainId}/coa`, formData, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('COA uploaded — now live for users');
      fetchStrains(adminToken);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload COA');
    }
  };

  const handleRemoveCoa = async (strainId) => {
    try {
      await axios.delete(`${API}/strains/${strainId}/coa`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('COA removed');
      fetchStrains(adminToken);
    } catch (err) {
      toast.error('Failed to remove COA');
    }
  };

  // COA Intelligence Engine functions
  const handleParseCoa = async (strain) => {
    setCoaParsingStrain(strain);
    setCoaParseLoading(true);
    setCoaParsedData(null);
    setCoaEditMode(false);
    try {
      const resp = await axios.post(`${API}/admin/strains/${strain.id}/parse-coa`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCoaParsedData(resp.data);
      toast.success(`COA parsed: ${resp.data.parse_status} (${resp.data.parse_confidence?.score}% confidence)`);
      fetchStrains(adminToken);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse COA');
    }
    setCoaParseLoading(false);
  };

  const handleViewParsedCoa = async (strain) => {
    setCoaParsingStrain(strain);
    setCoaParseLoading(true);
    setCoaParsedData(null);
    setCoaEditMode(false);
    try {
      const resp = await axios.get(`${API}/admin/strains/${strain.id}/parsed-coa`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCoaParsedData(resp.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setCoaParsedData(null);
      } else {
        toast.error('Failed to load parsed COA data');
      }
    }
    setCoaParseLoading(false);
  };

  const handleSaveCoaCorrections = async (corrections) => {
    if (!coaParsingStrain) return;
    try {
      const resp = await axios.put(`${API}/admin/strains/${coaParsingStrain.id}/parsed-coa`, corrections, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setCoaParsedData(resp.data);
      setCoaEditMode(false);
      toast.success('COA corrections saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save corrections');
    }
  };

  const handleBatchParseCoas = async () => {
    setBatchParseRunning(true);
    setBatchParseResult(null);
    try {
      const resp = await axios.post(`${API}/admin/coa/batch-parse`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` },
        timeout: 300000,
      });
      setBatchParseResult(resp.data);
      toast.success(`Batch parse complete: ${resp.data.newly_parsed} parsed, ${resp.data.failed} failed`);
      fetchStrains(adminToken);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Batch parse failed');
      setBatchParseResult({ error: err.response?.data?.detail || 'Request failed or timed out' });
    }
    setBatchParseRunning(false);
  };
  const [showAddStrain, setShowAddStrain] = useState(false);
  const [newStrain, setNewStrain] = useState({
    strain_name: '',
    type: 'Balanced Hybrid',
    thc_percentage: '',
    price_tier: '$$',
    terpene_profile: '',
    lineage: '',
    no_specific_lineage: false,
    lot_size: '',
    effects: [],
    bc_dd_compliant: false,
    thc_non_detect: false,
    terpene_percentage: '',
    extract_purity: '',
    category: 'Dried Flower',
    extract_type: ''
  });
  const [newStrainImage, setNewStrainImage] = useState(null);
  const [addingStrain, setAddingStrain] = useState(false);

  useEffect(() => {
    // Check for existing token
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyToken(token);
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminToken(token);
      setIsAuthenticated(true);
      fetchStrains(token);
      fetchStats(token);
    } catch (err) {
      localStorage.removeItem('adminToken');
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLogin = (token) => {
    setAdminToken(token);
    setIsAuthenticated(true);
    
    // IDENTITY BOUNDARY: Clear any grower/LP session data from localStorage
    // to prevent admin actions from being contaminated by a previous submitter identity
    localStorage.removeItem('firegold_submitter_info');
    
    fetchStrains(token);
    fetchStats(token);
    fetchAnalytics(token);
    fetchUsers(token);
    fetchFlags(token);
    fetchSubmissions(token);
    fetchInventory(token);
    fetchAdminInquiries(token);
    fetchContactMessages(token);
    fetchSettings(token);
    
    // Set baseline counts for notifications
    Promise.all([
      axios.get(`${API}/admin/contact-messages`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/admin/inquiries`, { headers: { Authorization: `Bearer ${token}` } })
    ]).then(([msgResp, inqResp]) => {
      prevCountsRef.current = {
        messages: msgResp.data.filter(m => m.status === 'unread').length,
        inquiries: inqResp.data.filter(i => i.status === 'pending' || i.status === 'new').length
      };
    }).catch(() => {});
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
    } catch (err) {
      // Ignore errors
    }
    localStorage.removeItem('adminToken');
    setAdminToken(null);
    setIsAuthenticated(false);
    toast.success('Logged out');
  };

  const fetchStrains = async (token) => {
    try {
      // Admin fetches strains without user token (public endpoint still works for admin)
      const response = await axios.get(`${API}/strains/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrains(response.data);
    } catch (error) {
      toast.error('Failed to load strains');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (token) => {
    try {
      const response = await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const fetchAnalytics = async (token) => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics');
    }
  };

  const fetchUserActivityReport = async (sortBy) => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API}/analytics/users-report?sort_by=${sortBy}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUserActivityReport(response.data);
    } catch (error) {
      console.error('Failed to load user activity');
    }
    setLoadingReport(false);
  };

  const fetchUserDetail = async (userId) => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API}/analytics/user/${userId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSelectedUserDetail(response.data);
      setAnalyticsView('user-detail');
    } catch (error) {
      toast.error('Failed to load user details');
    }
    setLoadingReport(false);
  };

  const fetchEngagement = async () => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API}/analytics/engagement?days=30`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setEngagementData(response.data);
    } catch (error) {
      console.error('Failed to load engagement data');
    }
    setLoadingReport(false);
  };

  const fetchGlobalInsights = async () => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API}/analytics/global`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setGlobalData(response.data);
    } catch (error) {
      console.error('Failed to load global insights');
    }
    setLoadingReport(false);
  };

  const exportUserActivityCSV = async () => {
    try {
      const response = await axios.get(`${API}/analytics/users-export/csv`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'firegold_user_activity.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('User activity report exported');
    } catch { toast.error('Export failed'); }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const [usersLoading, setUsersLoading] = useState(false);
  const fetchUsers = async (token) => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${token || adminToken}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
    setUsersLoading(false);
  };

  const fetchFlags = async (token) => {
    try {
      const response = await axios.get(`${API}/analytics/flags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlags(response.data);
    } catch (error) {
      console.error('Failed to load flags');
    }
  };

  const fetchSubmissions = async (token) => {
    try {
      const response = await axios.get(`${API}/grower/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to load submissions');
    }
  };

  const handleSubmissionAction = async (submissionId, status) => {
    setProcessingSubmission(true);
    try {
      const body = {
        status,
        admin_notes: adminNotes || null,
        admin_price_per_gram: pricePerGram || null
      };
      await axios.put(`${API}/grower/submissions/${submissionId}/status`, body, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`Submission ${status}${status === 'approved' ? ' — strain added to catalog' : ''}`);
      setReviewingSubmission(null);
      setPricePerGram('');
      setAdminNotes('');
      fetchSubmissions(adminToken);
      if (status === 'approved') {
        fetchStrains(adminToken);
        fetchStats(adminToken);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : `Failed to ${status} submission`;
      toast.error(msg);
    } finally {
      setProcessingSubmission(false);
    }
  };

  const filteredSubmissions = submissions.filter(s => 
    submissionFilter === 'all' ? true : s.status === submissionFilter
  );
  const pendingCount = submissions.filter(s => s.status === 'pending').length;

  const exportCSV = async (type) => {
    const endpoint = type === 'analytics' ? '/analytics/export/csv?days=30' : '/users/export/csv';
    try {
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', type === 'analytics' ? 'firegold_analytics.csv' : 'firegold_users.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type === 'analytics' ? 'Analytics' : 'Users'} exported to CSV`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleAddStrain = async (e) => {
    e.preventDefault();
    if (!newStrainImage) {
      toast.error('Please upload a strain image');
      return;
    }
    
    setAddingStrain(true);
    
    try {
      // First create the strain
      const strainData = {
        ...newStrain,
        terpene_profile: newStrain.terpene_profile.split(',').map(t => t.trim()).filter(t => t)
      };
      
      const createResponse = await axios.post(`${API}/strains/admin/create`, strainData, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      const strainId = createResponse.data.id;
      
      // Then upload the image
      const formData = new FormData();
      formData.append('image', newStrainImage);
      
      await axios.put(`${API}/strains/${strainId}/image`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      toast.success(`${newStrain.strain_name} added successfully!`);
      
      // Reset form
      setNewStrain({
        strain_name: '',
        type: 'Balanced Hybrid',
        thc_percentage: '',
        price_tier: '$$',
        terpene_profile: '',
        lineage: '',
        no_specific_lineage: false,
        lot_size: '',
        effects: [],
        bc_dd_compliant: false,
        thc_non_detect: false,
        terpene_percentage: '',
        extract_purity: '',
        category: 'Dried Flower',
        extract_type: ''
      });
      setNewStrainImage(null);
      setShowAddStrain(false);
      setActiveTab('images');
      fetchStrains(adminToken);
      fetchStats(adminToken);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ') : 'Failed to add strain';
      toast.error(msg);
    } finally {
      setAddingStrain(false);
    }
  };

  const confirmDeleteStrain = async (strainId, strainName) => {
    if (!window.confirm(`Are you sure you want to remove "${strainName}" from the catalog?\n\nThis action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/strains/${strainId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success(`${strainName} removed from catalog`);
      fetchStrains(adminToken);
      fetchStats(adminToken);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to remove strain');
      }
    }
  };

  const handleImageUpload = async (strainId, file) => {
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image');
      return;
    }

    setUploadingId(strainId);
    const formData = new FormData();
    formData.append('image', file);

    try {
      await axios.put(`${API}/strains/${strainId}/image`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${adminToken}`
        }
      });
      toast.success('Image updated successfully');
      fetchStrains(adminToken);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to update image');
      }
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteStrain = async (strainId, strainName) => {
    if (!window.confirm(`Are you sure you want to delete "${strainName}"?`)) return;
    
    try {
      await axios.delete(`${API}/strains/${strainId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      toast.success('Strain deleted');
      fetchStrains(adminToken);
      fetchStats(adminToken);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to delete strain');
      }
    }
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold-500">Verifying access...</div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="bg-black/80 backdrop-blur-md border-b border-white/5 h-20 flex items-center justify-between px-6 md:px-12">
        <div className="flex items-center gap-4">
          <a href="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back to Catalog</span>
          </a>
        </div>
        <div className="flex items-center gap-3">
          <Lock className="w-5 h-5 text-cannabis-green" />
          <Settings className="w-6 h-6 text-gold-500" />
          <span className="font-cinzel font-bold text-xl text-white">Admin Panel</span>
        </div>
        <div className="flex items-center gap-6">
          <button 
            onClick={toggleEmails}
            data-testid="email-toggle-btn"
            className={`flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold transition-all border ${
              emailsEnabled 
                ? 'bg-cannabis-green/20 border-cannabis-green/40 text-cannabis-green' 
                : 'bg-red-500/10 border-red-500/30 text-red-400'
            }`}
            title={`Emails: ${emailsEnabled ? 'ON' : 'OFF'}${settingsMeta.emails_enabled?.last_updated_at ? ` · Last changed: ${new Date(settingsMeta.emails_enabled.last_updated_at).toLocaleString()}` : ''}`}
          >
            <Mail className="w-4 h-4" />
            Emails: {emailsEnabled ? 'ON' : 'OFF'}
          </button>
          <button 
            onClick={handleLogout}
          data-testid="admin-logout-btn"
          className="flex items-center gap-2 text-zinc-400 hover:text-red-400 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-sm">Logout</span>
        </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="bg-charcoal border border-white/5 p-6">
              <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Total Strains</div>
              <div className="text-4xl font-bold text-white font-mono">{stats.total_strains}</div>
            </div>
            <div className="bg-charcoal border border-white/5 p-6">
              <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Inquiries</div>
              <div className="text-4xl font-bold text-gold-500 font-mono">{stats.total_inquiries}</div>
            </div>
            <div className="bg-charcoal border border-white/5 p-6">
              <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Sample Requests</div>
              <div className="text-4xl font-bold text-cannabis-green font-mono">{stats.total_sample_requests}</div>
            </div>
            <div className="bg-charcoal border border-white/5 p-6">
              <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Registered Users</div>
              <div className="text-4xl font-bold text-purple-400 font-mono">{users.length}</div>
            </div>
          </div>
        )}

        {/* Total Inventory Summary */}
        {inventory && inventory.total_inventory_kg > 0 && (
          <div data-testid="inventory-summary" className="mb-12 bg-charcoal border border-gold-500/20 p-4 sm:p-6">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <Package className="w-5 h-5 text-gold-500" />
              <h2 className="font-cinzel font-bold text-base sm:text-lg text-white">Total Inventory</h2>
              <span className="sm:ml-auto bg-gold-500/10 text-gold-500 text-xs font-bold px-3 py-1 font-mono">
                {inventory.total_inventory_kg.toFixed(1)} kg total
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="bg-cannabis-green/10 border border-cannabis-green/30 p-4 text-center">
                <div className="text-cannabis-green font-bold text-2xl sm:text-3xl font-mono">{inventory.total_grade_a.toFixed(1)}</div>
                <div className="text-cannabis-green/70 text-xs uppercase tracking-widest mt-1">Grade A (Bigs) (kg)</div>
              </div>
              <div className="bg-gold-500/10 border border-gold-500/30 p-4 text-center">
                <div className="text-gold-500 font-bold text-2xl sm:text-3xl font-mono">{inventory.total_grade_b.toFixed(1)}</div>
                <div className="text-gold-500/70 text-xs uppercase tracking-widest mt-1">Grade B (Mids) (kg)</div>
              </div>
              <div className="bg-zinc-700/20 border border-zinc-600/30 p-4 text-center">
                <div className="text-zinc-400 font-bold text-2xl sm:text-3xl font-mono">{inventory.total_grade_c.toFixed(1)}</div>
                <div className="text-zinc-500 text-xs uppercase tracking-widest mt-1">Grade C (Smalls) (kg)</div>
              </div>
            </div>
            <p className="text-zinc-600 text-xs mt-3 text-center sm:text-left">{inventory.strains_with_grades} of {inventory.total_strains} strains have grade data</p>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-1.5 mb-8 border-b border-white/5 pb-4">
          <button
            onClick={() => setActiveTab('images')}
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'images' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Image className="w-3.5 h-3.5 inline mr-1.5" />Images
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'analytics' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Activity className="w-3.5 h-3.5 inline mr-1.5" />Analytics
          </button>
          <button
            onClick={() => { setActiveTab('users'); fetchUsers(); }}
            data-testid="admin-tab-users"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'users' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <User className="w-3.5 h-3.5 inline mr-1.5" />Users
          </button>
          <button
            onClick={() => setActiveTab('flags')}
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all relative ${
              activeTab === 'flags' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5" />Flags
            {flags.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">
                {flags.length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('submissions'); fetchSubmissions(adminToken); }}
            data-testid="admin-tab-submissions"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all relative ${
              activeTab === 'submissions' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <ClipboardList className="w-3.5 h-3.5 inline mr-1.5" />Submissions
            {pendingCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-cannabis-green text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">
                {pendingCount}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('directory'); fetchDirectory(adminToken); }}
            data-testid="admin-tab-directory"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'directory' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <FileText className="w-3.5 h-3.5 inline mr-1.5" />Directory
          </button>
          <button
            onClick={() => { setActiveTab('inquiries'); fetchAdminInquiries(adminToken); }}
            data-testid="admin-tab-inquiries"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all relative ${
              activeTab === 'inquiries' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Mail className="w-3.5 h-3.5 inline mr-1.5" />Inquiries
            {adminInquiries.filter(i => i.status === 'pending').length > 0 && (
              <span className="absolute -top-1 -right-1 bg-gold-500 text-black text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                {adminInquiries.filter(i => i.status === 'pending').length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('affiliates'); fetchAffiliates(); }}
            data-testid="admin-tab-affiliates"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'affiliates' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Users className="w-3.5 h-3.5 inline mr-1.5" />Affiliates
          </button>
          <button
            onClick={() => { setActiveTab('messages'); fetchContactMessages(); }}
            data-testid="admin-tab-messages"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all relative ${
              activeTab === 'messages' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <MessageCircle className="w-3.5 h-3.5 inline mr-1.5" />Messages
            {contactMessages.filter(m => m.status === 'unread').length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                {contactMessages.filter(m => m.status === 'unread').length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('bot-training'); fetchBotTraining(); }}
            data-testid="admin-tab-bot-training"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'bot-training' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Bot className="w-3.5 h-3.5 inline mr-1.5" />Bot Training
          </button>
          <button
            onClick={() => { setActiveTab('saved-lists'); fetchAdminLists(); }}
            data-testid="admin-tab-saved-lists"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'saved-lists' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Bookmark className="w-3.5 h-3.5 inline mr-1.5" />User Lists
          </button>
          <button
            onClick={() => { setActiveTab('compliance'); fetchComplianceDocs(); }}
            data-testid="admin-tab-compliance"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'compliance' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5" />License Vault
          </button>
          <button
            onClick={() => { setActiveTab('certifications'); fetchCertReport(); }}
            data-testid="admin-tab-certifications"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'certifications' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Award className="w-3.5 h-3.5 inline mr-1.5" />GACP / GMP
          </button>
          <button
            onClick={() => { setActiveTab('deals'); fetchAdminDeals(); }}
            data-testid="admin-tab-deals"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'deals' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Layers className="w-3.5 h-3.5 inline mr-1.5" />Deal Pipeline
          </button>
          <button
            onClick={() => { setActiveTab('knowledge'); fetchKnowledge(); fetchKnowledgeStats(); }}
            data-testid="admin-tab-knowledge"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'knowledge' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <BookmarkPlus className="w-3.5 h-3.5 inline mr-1.5" />Knowledge Library
          </button>
          <button
            onClick={() => { setActiveTab('crm'); }}
            data-testid="admin-tab-crm"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'crm' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Users className="w-3.5 h-3.5 inline mr-1.5" />CRM
          </button>
          <button
            onClick={() => { setActiveTab('email-blast'); }}
            data-testid="admin-tab-email-blast"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'email-blast' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Mail className="w-3.5 h-3.5 inline mr-1.5" />Email Blast
          </button>
          <button
            onClick={() => { setActiveTab('strain-hunter'); fetchAdminHunterRequests(); }}
            data-testid="admin-tab-strain-hunter"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'strain-hunter' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Crosshair className="w-3.5 h-3.5 inline mr-1.5" />Strain Hunter
          </button>
          <button
            onClick={() => { setActiveTab('media-health'); fetchMediaHealth(); }}
            data-testid="admin-tab-media-health"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all relative ${
              activeTab === 'media-health' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <HeartPulse className="w-3.5 h-3.5 inline mr-1.5" />Media Health
            {mediaHealth && mediaHealth.summary && mediaHealth.summary.total_issues > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">
                {mediaHealth.summary.total_issues}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('document-vault'); fetchVaultData(adminToken); }}
            data-testid="admin-tab-document-vault"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'document-vault' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <FileText className="w-3.5 h-3.5 inline mr-1.5" />Document Vault
          </button>
          <button
            onClick={() => setActiveTab('supply-forms')}
            data-testid="admin-tab-supply-forms"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'supply-forms' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <ClipboardList className="w-3.5 h-3.5 inline mr-1.5" />Supply Forms
          </button>
          <button
            onClick={() => setActiveTab('grower-pipeline')}
            data-testid="admin-tab-grower-pipeline"
            className={`px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all ${
              activeTab === 'grower-pipeline' ? 'bg-gold-500 text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Users className="w-3.5 h-3.5 inline mr-1.5" />Manage Growers
          </button>
        </div>
        {/* Affiliates Tab */}
        {activeTab === 'affiliates' && (
          <>
            {/* Affiliate Detail Modal */}
            {selectedAffiliateDetail && (
              <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelectedAffiliateDetail(null)}>
                <div className="bg-charcoal border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="sticky top-0 bg-charcoal border-b border-white/5 p-6 flex justify-between items-start z-10">
                    <div>
                      <h2 className="font-cinzel font-bold text-2xl text-white">{selectedAffiliateDetail.affiliate.name}</h2>
                      <p className="text-gold-500 font-mono text-sm">Code: {selectedAffiliateDetail.affiliate.referral_code} &middot; {selectedAffiliateDetail.affiliate.commission_rate}% commission</p>
                    </div>
                    <button onClick={() => setSelectedAffiliateDetail(null)} className="w-10 h-10 bg-gold-500 text-black hover:bg-gold-400 flex items-center justify-center transition-all">
                      <X className="w-5 h-5 text-black" />
                    </button>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: 'Referrals', value: selectedAffiliateDetail.stats.total_referrals },
                        { label: 'Conversions', value: selectedAffiliateDetail.stats.total_conversions },
                        { label: 'Revenue', value: `$${selectedAffiliateDetail.stats.total_revenue.toLocaleString()}` },
                        { label: 'Commission', value: `$${selectedAffiliateDetail.stats.total_commission.toLocaleString()}` },
                      ].map(s => (
                        <div key={s.label} className="bg-zinc-900 border border-white/5 p-4 text-center">
                          <p className="text-2xl font-bold text-white">{s.value}</p>
                          <p className="text-zinc-500 text-[10px] uppercase tracking-widest">{s.label}</p>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gold-500/10 border border-gold-500/30 p-4 text-center">
                        <p className="text-xl font-bold text-gold-500">${selectedAffiliateDetail.stats.pending_commission.toLocaleString()}</p>
                        <p className="text-gold-500/60 text-xs uppercase">Pending Payout</p>
                      </div>
                      <div className="bg-cannabis-green/10 border border-cannabis-green/30 p-4 text-center">
                        <p className="text-xl font-bold text-cannabis-green">${selectedAffiliateDetail.stats.paid_commission.toLocaleString()}</p>
                        <p className="text-cannabis-green/60 text-xs uppercase">Paid Out</p>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-manrope font-bold text-xs uppercase tracking-widest text-gold-500 mb-3">Referral Events</h3>
                      {selectedAffiliateDetail.events.length === 0 ? (
                        <p className="text-zinc-600 text-sm">No events yet</p>
                      ) : (
                        <div className="space-y-2">
                          {selectedAffiliateDetail.events.map((evt, idx) => (
                            <div key={idx} className="bg-zinc-900/50 border border-white/5 p-4 flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${evt.event_type === 'conversion' ? 'bg-cannabis-green/20 text-cannabis-green' : 'bg-gold-500/20 text-gold-500'}`}>{evt.event_type}</span>
                                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${evt.status === 'paid' ? 'bg-cannabis-green/20 text-cannabis-green' : 'bg-zinc-700/30 text-zinc-400'}`}>{evt.status}</span>
                                </div>
                                <p className="text-zinc-300 text-sm">{evt.referred_user ? `${evt.referred_user.first_name} ${evt.referred_user.last_name} (${evt.referred_user.company})` : evt.referred_email || 'Unknown'}</p>
                                {evt.sale_amount > 0 && <p className="text-zinc-500 text-xs">Sale: ${evt.sale_amount.toLocaleString()} &middot; Commission: ${evt.commission_amount?.toLocaleString()}</p>}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-zinc-600 text-xs">{evt.created_at ? new Date(evt.created_at).toLocaleDateString() : ''}</span>
                                {evt.event_type === 'conversion' && evt.status === 'pending' && (
                                  <button onClick={() => markCommissionPaid(evt.id)} className="bg-cannabis-green/20 hover:bg-cannabis-green/30 text-cannabis-green text-xs font-bold uppercase px-3 py-1.5 transition-colors">Mark Paid</button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-500">
                      <span>Shareable link:</span>
                      <code className="bg-zinc-900 border border-white/10 px-3 py-1 text-gold-500 font-mono text-xs">{window.location.origin}?ref={selectedAffiliateDetail.affiliate.referral_code}</code>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Affiliate Program</h2>
                <p className="text-zinc-500 text-sm">{affiliates.length} affiliates</p>
              </div>
              <button data-testid="add-affiliate-btn" onClick={() => setShowAddAffiliate(true)} className="bg-gold-500 hover:bg-gold-400 text-black px-5 py-2 font-bold uppercase tracking-widest text-xs transition-colors flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Affiliate
              </button>
            </div>
            {showAddAffiliate && (
              <div className="bg-charcoal border border-gold-500/30 p-6 mb-8">
                <h3 className="font-manrope font-bold text-sm uppercase tracking-widest text-gold-500 mb-4">New Affiliate</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  <input data-testid="affiliate-name" type="text" value={affiliateForm.name} onChange={e => { const name = e.target.value; setAffiliateForm(p => ({...p, name, referral_code: p.referral_code || autoGenerateCode(name)})); }} className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="Full Name *" />
                  <input data-testid="affiliate-email" type="email" value={affiliateForm.email} onChange={e => setAffiliateForm(p => ({...p, email: e.target.value}))} className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="Email *" />
                  <input data-testid="affiliate-phone" type="text" value={affiliateForm.phone} onChange={e => setAffiliateForm(p => ({...p, phone: e.target.value}))} className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="Phone" />
                  <input data-testid="affiliate-company" type="text" value={affiliateForm.company} onChange={e => setAffiliateForm(p => ({...p, company: e.target.value}))} className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="Company" />
                  <div className="relative">
                    <input data-testid="affiliate-code" type="text" value={affiliateForm.referral_code} onChange={e => setAffiliateForm(p => ({...p, referral_code: e.target.value.toUpperCase()}))} className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-12 px-4 outline-none font-mono uppercase w-full" placeholder="Code (auto-generated)" />
                    {affiliateForm.name && !affiliateForm.referral_code && (
                      <button onClick={() => setAffiliateForm(p => ({...p, referral_code: autoGenerateCode(p.name)}))} className="absolute right-2 top-1/2 -translate-y-1/2 text-gold-500 text-xs hover:underline">Generate</button>
                    )}
                  </div>
                </div>
                <p className="text-zinc-500 text-xs mt-1">Commission will be set per-deal when converting inquiries to sales.</p>
                <div className="flex gap-3 mt-3">
                  <button data-testid="save-affiliate-btn" onClick={createAffiliate} className="bg-gold-500 hover:bg-gold-400 text-black px-6 py-2 font-bold uppercase tracking-widest text-xs transition-colors">Create</button>
                  <button onClick={() => setShowAddAffiliate(false)} className="bg-zinc-700 text-white px-6 py-2 text-xs uppercase tracking-widest">Cancel</button>
                </div>
              </div>
            )}
            {affiliates.length === 0 ? (
              <div className="bg-charcoal border border-white/5 p-12 text-center">
                <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400">No affiliates yet</p>
                <p className="text-zinc-600 text-sm mt-1">Click "Add Affiliate" to start the referral program</p>
              </div>
            ) : (
              <>
              <SortButtons
                testPrefix="affiliates"
                sortState={affiliatesSort}
                onSort={makeSortHandler(setAffiliatesSort)}
                options={[
                  { key: 'name', label: 'Name' },
                  { key: 'total_referrals', label: 'Referrals' },
                  { key: 'total_conversions', label: 'Sales' },
                  { key: 'total_commission', label: 'Commission' },
                  { key: 'created_at', label: 'Date Added' },
                ]}
              />
              <div className="space-y-4 mt-4">
                {sortData(affiliates, affiliatesSort).map(aff => (
                  <div key={aff.id} className="bg-charcoal border border-white/5 hover:border-gold-500/30 transition-colors p-5">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <button onClick={() => fetchAffiliateDetail(aff.id)} className="text-gold-500 hover:text-gold-400 hover:underline font-bold text-base transition-colors">{aff.name}</button>
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${aff.is_active ? 'bg-cannabis-green/20 text-cannabis-green' : 'bg-red-500/20 text-red-500'}`}>{aff.is_active ? 'Active' : 'Inactive'}</span>
                        </div>
                        <div className="flex items-center gap-4 text-zinc-400 text-sm flex-wrap">
                          <span className="font-mono text-gold-500/70">?ref={aff.referral_code}</span>
                          <span>{aff.email}</span>
                          {aff.phone && <span>{aff.phone}</span>}
                          {aff.company && <span className="text-zinc-300">{aff.company}</span>}
                          <span className="bg-zinc-800 px-2 py-0.5 text-xs font-mono">
                            {(aff.commission_type || 'per_gram') === 'per_gram' ? `$${aff.commission_value ?? 0.03}/gram` : (aff.commission_type || 'per_gram') === 'percentage' ? `${aff.commission_value ?? 5}%` : `$${aff.commission_value ?? 500} flat`}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-center"><p className="text-white font-bold text-lg">{aff.total_referrals}</p><p className="text-zinc-600 text-[10px] uppercase">Referrals</p></div>
                        <div className="text-center"><p className="text-cannabis-green font-bold text-lg">{aff.total_conversions}</p><p className="text-zinc-600 text-[10px] uppercase">Sales</p></div>
                        <div className="text-center"><p className="text-gold-500 font-bold text-lg">${aff.total_commission.toLocaleString()}</p><p className="text-zinc-600 text-[10px] uppercase">Commission</p></div>
                        <button onClick={async () => { await axios.put(`${API}/admin/affiliates/${aff.id}/toggle`, {}, { headers: { Authorization: `Bearer ${adminToken}` } }); fetchAffiliates(); }}
                          className={`text-xs font-bold uppercase px-3 py-1.5 transition-colors ${aff.is_active ? 'bg-yellow-500/20 text-yellow-500' : 'bg-cannabis-green/20 text-cannabis-green'}`}>
                          {aff.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button onClick={() => setEditingAffiliate({...aff})} data-testid={`edit-affiliate-${aff.id}`}
                          className="text-xs font-bold uppercase px-3 py-1.5 bg-gold-500/20 text-gold-500 transition-colors hover:bg-gold-500/30">
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
          </>
        )}

        {activeTab === 'images' && (
          <>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Strain Management</h2>
                <p className="text-zinc-500 text-sm">Add new strains, update images, or remove discontinued products</p>
              </div>
              <button
                onClick={() => setShowAddStrain(true)}
                className="bg-cannabis-green hover:bg-green-600 text-white px-6 py-3 font-bold uppercase tracking-widest text-sm flex items-center gap-2 transition-colors"
              >
                <span className="text-xl">+</span> Add New Strain
              </button>
            </div>

            {/* COA Intelligence Batch Parse Controls */}
            <div className="mb-6 border border-white/10 bg-charcoal p-4" data-testid="batch-parse-panel">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gold-500" />
                  <div>
                    <span className="text-white text-sm font-bold">COA Intelligence</span>
                    <span className="text-zinc-500 text-xs ml-2">
                      {strains.filter(s => s.coa_parse_status === 'complete').length} complete
                      {strains.filter(s => s.coa_parse_status === 'partial').length > 0 && `, ${strains.filter(s => s.coa_parse_status === 'partial').length} partial`}
                      {strains.filter(s => s.coa_parse_status === 'failed').length > 0 && `, ${strains.filter(s => s.coa_parse_status === 'failed').length} failed`}
                      {' / '}
                      {strains.filter(s => s.coa_url).length} with COA
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleBatchParseCoas}
                  disabled={batchParseRunning}
                  data-testid="batch-parse-all-btn"
                  className={`px-5 py-2 font-bold uppercase tracking-widest text-sm transition-all flex items-center gap-2 ${
                    batchParseRunning
                      ? 'bg-zinc-700 text-zinc-400 cursor-wait'
                      : 'bg-gold-500 hover:bg-gold-400 text-black'
                  }`}
                >
                  {batchParseRunning ? (
                    <><RefreshCw className="w-4 h-4 animate-spin" /> Parsing...</>
                  ) : (
                    'Batch Parse All'
                  )}
                </button>
              </div>

              {/* Batch Parse Results */}
              {batchParseResult && !batchParseResult.error && (
                <div className="mt-4 pt-4 border-t border-white/5 space-y-3" data-testid="batch-parse-results">
                  <div className="flex gap-4 text-xs">
                    <span className="bg-zinc-900 px-3 py-1.5 border border-white/5">
                      <span className="text-zinc-500">Total COAs:</span> <span className="text-white font-bold">{batchParseResult.total_with_coa}</span>
                    </span>
                    <span className="bg-zinc-900 px-3 py-1.5 border border-white/5">
                      <span className="text-zinc-500">Skipped:</span> <span className="text-zinc-400 font-bold">{batchParseResult.already_parsed}</span>
                    </span>
                    <span className="bg-zinc-900 px-3 py-1.5 border border-emerald-500/20">
                      <span className="text-zinc-500">Parsed:</span> <span className="text-emerald-400 font-bold">{batchParseResult.newly_parsed}</span>
                    </span>
                    <span className="bg-zinc-900 px-3 py-1.5 border border-red-500/20">
                      <span className="text-zinc-500">Failed:</span> <span className="text-red-400 font-bold">{batchParseResult.failed}</span>
                    </span>
                  </div>

                  {/* Parsed strains list */}
                  {batchParseResult.parsed_strains?.length > 0 && (
                    <div>
                      <p className="text-emerald-400 text-[10px] uppercase tracking-widest font-bold mb-1">Parsed Successfully</p>
                      <div className="flex flex-wrap gap-1.5">
                        {batchParseResult.parsed_strains.map((s, i) => (
                          <span key={i} className="text-[10px] font-mono bg-emerald-500/10 text-emerald-400 px-2 py-0.5 border border-emerald-500/20">
                            {s.name} ({s.confidence}%)
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Failed strains list */}
                  {batchParseResult.failed_strains?.length > 0 && (
                    <div>
                      <p className="text-red-400 text-[10px] uppercase tracking-widest font-bold mb-1">Failed</p>
                      <div className="flex flex-wrap gap-1.5">
                        {batchParseResult.failed_strains.map((s, i) => (
                          <span key={i} className="text-[10px] font-mono bg-red-500/10 text-red-400 px-2 py-0.5 border border-red-500/20" title={s.reason}>
                            {s.name}: {s.reason?.substring(0, 50)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error state */}
              {batchParseResult?.error && (
                <div className="mt-3 p-2.5 bg-red-500/10 border border-red-500/20">
                  <p className="text-red-400 text-xs">{batchParseResult.error}</p>
                </div>
              )}
            </div>

            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="aspect-square bg-charcoal animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {strains.map((strain) => (
                  <div 
                    key={strain.id} 
                    className="bg-charcoal border border-white/5 hover:border-gold-500/50 transition-all group relative"
                  >
                    <div className="aspect-square relative overflow-hidden">
                      <img 
                        src={strain.image_url}
                        alt={strain.strain_name}
                        className="w-full h-full object-cover"
                      />
                      
                      <label className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex flex-col items-center justify-center gap-2">
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp"
                          className="hidden"
                          onChange={(e) => handleImageUpload(strain.id, e.target.files[0])}
                          disabled={uploadingId === strain.id}
                        />
                        {uploadingId === strain.id ? (
                          <div className="text-gold-500">Uploading...</div>
                        ) : (
                          <>
                            <Image className="w-8 h-8 text-gold-500" />
                            <span className="text-white text-xs">Change Image</span>
                          </>
                        )}
                      </label>
                    </div>
                    
                    <div className="p-3">
                      <div className="font-semibold text-white text-sm truncate">{strain.strain_name}</div>
                      <div className="text-zinc-500 text-xs truncate">{strain.type}</div>
                      {strain.grower_company && (
                        <div className="text-gold-500/70 text-[10px] truncate mt-0.5">Grower: {strain.grower_company}</div>
                      )}
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-gold-500 font-mono text-xs">THC {getStrainThc(strain)}</span>
                        <span className="text-zinc-600 text-xs">{strain.lots && strain.lots.length > 1 ? `${strain.lots.length} lots` : ''}</span>
                      </div>
                      {/* COA Management */}
                      <div className="mt-2 pt-2 border-t border-white/5">
                        {strain.coa_url ? (
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-1 text-cannabis-green text-xs font-bold">
                                <FileText className="w-3 h-3" /> COA Live
                              </span>
                              <button
                                onClick={() => handleRemoveCoa(strain.id)}
                                data-testid={`remove-coa-${strain.id}`}
                                className="text-red-400 hover:text-red-300 text-xs"
                              >
                                Remove
                              </button>
                            </div>
                            {/* COA Parse Controls */}
                            <div className="flex items-center justify-between">
                              {strain.coa_parse_status ? (
                                <button
                                  onClick={() => handleViewParsedCoa(strain)}
                                  data-testid={`view-parsed-coa-${strain.id}`}
                                  className={`text-xs px-1.5 py-0.5 rounded font-mono ${
                                    strain.coa_parse_status === 'complete' ? 'bg-emerald-500/20 text-emerald-400' :
                                    strain.coa_parse_status === 'partial' ? 'bg-amber-500/20 text-amber-400' :
                                    'bg-red-500/20 text-red-400'
                                  }`}
                                >
                                  {strain.coa_parse_status === 'complete' ? 'Parsed' :
                                   strain.coa_parse_status === 'partial' ? 'Partial' : 'Failed'}
                                </button>
                              ) : (
                                <span className="text-zinc-600 text-[10px] font-mono">Not parsed</span>
                              )}
                              <button
                                onClick={() => handleParseCoa(strain)}
                                data-testid={`parse-coa-${strain.id}`}
                                className="text-xs text-gold-500 hover:text-gold-400 font-mono transition-colors"
                              >
                                {strain.coa_parse_status ? 'Re-parse' : 'Parse COA'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <label className="flex items-center justify-center gap-1 cursor-pointer text-zinc-500 hover:text-gold-500 text-xs transition-colors">
                            <input
                              type="file"
                              accept=".pdf"
                              className="hidden"
                              data-testid={`upload-coa-${strain.id}`}
                              onChange={(e) => handleCoaUpload(strain.id, e.target.files[0])}
                            />
                            <Upload className="w-3 h-3" />
                            Upload COA
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Action buttons - visible on hover */}
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={() => openEditStrain(strain)}
                        data-testid={`edit-strain-${strain.id}`}
                        className="w-8 h-8 bg-gold-500/90 hover:bg-gold-500 rounded flex items-center justify-center"
                        title="Edit strain details"
                      >
                        <Edit2 className="w-4 h-4 text-black" />
                      </button>
                      <button
                        onClick={() => confirmDeleteStrain(strain.id, strain.strain_name)}
                        className="w-8 h-8 bg-red-500/90 hover:bg-red-600 rounded flex items-center justify-center"
                        title="Remove strain"
                      >
                        <Trash2 className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Edit Strain Modal */}
        {editingStrain && (
          <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-charcoal border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <h2 className="font-cinzel font-bold text-2xl text-white">Edit Strain</h2>
                  <button onClick={() => setEditingStrain(null)} className="w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all">
                    <X className="w-6 h-6 text-black" />
                  </button>
                </div>

                <div className="space-y-4" data-testid="edit-strain-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Strain Name *</label>
                      <input data-testid="edit-strain-name" type="text" value={editForm.strain_name}
                        onChange={e => setEditForm(p => ({...p, strain_name: e.target.value}))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Type *</label>
                      <select data-testid="edit-strain-type" value={editForm.type}
                        onChange={e => setEditForm(p => ({...p, type: e.target.value}))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none">
                        <option value="Balanced Hybrid">Balanced Hybrid</option>
                        <option value="Hybrid, Indica Leaning">Hybrid, Indica Leaning</option>
                        <option value="Hybrid, Sativa Leaning">Hybrid, Sativa Leaning</option>
                        <option value="Indica Dominant">Indica Dominant</option>
                        <option value="Sativa Dominant">Sativa Dominant</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">THC % *</label>
                      <input data-testid="edit-strain-thc" type="text" value={editForm.thc_percentage}
                        onChange={e => setEditForm(p => ({...p, thc_percentage: e.target.value}))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">CBD %</label>
                      <input data-testid="edit-strain-cbd" type="text" value={editForm.cbd_percentage}
                        onChange={e => setEditForm(p => ({...p, cbd_percentage: e.target.value}))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Terpene %</label>
                      <input data-testid="edit-strain-terpene-pct" type="number" step="0.1" min="0" value={editForm.terpene_percentage}
                        onChange={e => setEditForm(p => ({...p, terpene_percentage: e.target.value}))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="e.g., 3.5" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Extract Purity %</label>
                      <input data-testid="edit-strain-extract-purity" type="number" step="0.1" min="0" max="100" value={editForm.extract_purity}
                        onChange={e => setEditForm(p => ({...p, extract_purity: e.target.value}))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="e.g., 92.5" />
                    </div>
                  </div>
                  <LotsEditor testIdPrefix="edit-strain" lots={editForm.lots} onChange={lots => setEditForm(p => ({...p, lots}))} />
                  <div className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800 p-3">
                    <input data-testid="edit-strain-thc-nd" type="checkbox" checked={editForm.thc_non_detect}
                      onChange={e => setEditForm(p => ({...p, thc_non_detect: e.target.checked}))}
                      className="w-4 h-4 accent-gold-500" />
                    <label className="font-manrope text-sm text-zinc-300">THC Non-Detect (ND/THC)</label>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Price Tier *</label>
                      <select data-testid="edit-strain-tier" value={editForm.price_tier}
                        onChange={e => setEditForm(p => ({...p, price_tier: e.target.value}))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none">
                        <option value="$">$</option>
                        <option value="$$">$$</option>
                        <option value="$$$">$$$</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Category</label>
                      <select data-testid="edit-strain-category" value={editForm.category}
                        onChange={e => setEditForm(p => ({...p, category: e.target.value}))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none">
                        <option value="Dried Flower">Dried Flower</option>
                        <option value="Fresh Frozen">Fresh Frozen</option>
                        <option value="Trim">Trim</option>
                        <option value="Concentrates">Concentrates</option>
                      </select>
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-3 cursor-pointer" data-testid="edit-strain-bc-dd">
                        <input type="checkbox" checked={editForm.bc_dd_compliant}
                          onChange={e => setEditForm(p => ({...p, bc_dd_compliant: e.target.checked}))}
                          className="w-5 h-5 accent-gold-500" />
                        <span className="text-white font-bold text-xs uppercase tracking-widest">BC DD Compliant</span>
                      </label>
                    </div>
                    <div className="flex items-end pb-2 gap-4">
                      <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-strain-gacp">
                        <input type="checkbox" checked={editForm.is_gacp_certified || false}
                          onChange={e => setEditForm(p => ({...p, is_gacp_certified: e.target.checked}))}
                          className="w-5 h-5 accent-cannabis-green" />
                        <span className="text-cannabis-green font-bold text-xs uppercase tracking-widest">GACP</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-strain-gmp">
                        <input type="checkbox" checked={editForm.is_gmp_certified || false}
                          onChange={e => setEditForm(p => ({...p, is_gmp_certified: e.target.checked}))}
                          className="w-5 h-5 accent-blue-500" />
                        <span className="text-blue-400 font-bold text-xs uppercase tracking-widest">GMP</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Terpene Profile *</label>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(typeof editForm.terpene_profile === 'string' ? editForm.terpene_profile.split(',').filter(t => t.trim()) : (editForm.terpene_profile || [])).map((t, i) => (
                        <span key={i} className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs px-2 py-1 flex items-center gap-1">
                          {typeof t === 'string' ? t.trim() : t}
                          <button type="button" data-testid={`edit-terpene-remove-${i}`} onClick={() => {
                            const current = typeof editForm.terpene_profile === 'string' ? editForm.terpene_profile.split(',').filter(x => x.trim()) : (editForm.terpene_profile || []);
                            setEditForm(p => ({...p, terpene_profile: current.filter((_, idx) => idx !== i).join(', ')}));
                          }}><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                    <select
                      data-testid="edit-strain-terpenes"
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) {
                          const current = typeof editForm.terpene_profile === 'string' ? editForm.terpene_profile.split(',').map(t => t.trim()).filter(Boolean) : (editForm.terpene_profile || []);
                          if (!current.includes(val)) {
                            setEditForm(p => ({...p, terpene_profile: [...current, val].join(', ')}));
                          }
                        }
                      }}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-emerald-500 text-zinc-400 h-12 px-4 transition-colors outline-none text-sm"
                    >
                      <option value="">Add terpene...</option>
                      {ALL_TERPENES.filter(t => {
                        const current = typeof editForm.terpene_profile === 'string' ? editForm.terpene_profile.split(',').map(x => x.trim()).filter(Boolean) : (editForm.terpene_profile || []);
                        return !current.includes(t);
                      }).map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Lineage / Genetics *</label>
                    <input data-testid="edit-strain-lineage" type="text" value={editForm.lineage}
                      onChange={e => setEditForm(p => ({...p, lineage: e.target.value}))}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Lot Size *</label>
                      <input data-testid="edit-strain-lot" type="text" value={editForm.lot_size}
                        onChange={e => setEditForm(p => ({...p, lot_size: e.target.value}))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                        placeholder="e.g., 50 kg" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Potential Terpene Effects</label>
                      <EffectsMultiSelect
                        selected={Array.isArray(editForm.effects) ? editForm.effects : (editForm.effects ? editForm.effects.split(',').map(e => e.trim()).filter(Boolean) : [])}
                        onChange={(effects) => setEditForm(p => ({...p, effects}))}
                        testId="edit-strain-effects"
                      />
                    </div>
                  </div>

                  {/* Grade Breakdown */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gold-500 mb-2 font-bold">Grade Breakdown (kg)</label>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Grade A (Bigs)</label>
                        <input data-testid="edit-strain-grade-a" type="number" min="0" step="0.1" value={editForm.grade_a_kg}
                          onChange={e => setEditForm(p => ({...p, grade_a_kg: e.target.value}))}
                          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="0" />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Grade B (Mids)</label>
                        <input data-testid="edit-strain-grade-b" type="number" min="0" step="0.1" value={editForm.grade_b_kg}
                          onChange={e => setEditForm(p => ({...p, grade_b_kg: e.target.value}))}
                          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="0" />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Grade C (Smalls)</label>
                        <input data-testid="edit-strain-grade-c" type="number" min="0" step="0.1" value={editForm.grade_c_kg}
                          onChange={e => setEditForm(p => ({...p, grade_c_kg: e.target.value}))}
                          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="0" />
                      </div>
                    </div>
                  </div>

                  {/* Harvest Date */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Harvest Date</label>
                    <input data-testid="edit-strain-harvest-date" type="date" value={editForm.harvest_date}
                      onChange={e => setEditForm(p => ({...p, harvest_date: e.target.value}))}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" />
                  </div>

                  {/* Growing Method */}
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Growing Method</label>
                    <select data-testid="edit-strain-growing-method" value={editForm.growing_method}
                      onChange={e => setEditForm(p => ({...p, growing_method: e.target.value}))}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none">
                      <option value="Indoor">Indoor</option>
                      <option value="Greenhouse">Greenhouse</option>
                      <option value="Outdoor">Outdoor</option>
                    </select>
                  </div>

                  {/* Grower Info (Admin Only) */}
                  <div className="col-span-2 border-t border-white/10 pt-4 mt-2">
                    <label className="block text-xs uppercase tracking-widest text-gold-500 mb-3 font-bold">Grower Information</label>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Company</label>
                        <input data-testid="edit-strain-grower-company" type="text" value={editForm.grower_company}
                          onChange={e => setEditForm(p => ({...p, grower_company: e.target.value}))}
                          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-10 px-3 text-sm outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Contact Name</label>
                        <input data-testid="edit-strain-grower-name" type="text" value={editForm.grower_name}
                          onChange={e => setEditForm(p => ({...p, grower_name: e.target.value}))}
                          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-10 px-3 text-sm outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Email</label>
                        <input data-testid="edit-strain-grower-email" type="email" value={editForm.grower_email}
                          onChange={e => setEditForm(p => ({...p, grower_email: e.target.value}))}
                          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-10 px-3 text-sm outline-none" />
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      data-testid="save-edit-strain-btn"
                      onClick={saveEditStrain}
                      disabled={savingEdit || !editForm.strain_name}
                      className="flex-1 bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 text-black font-bold uppercase tracking-widest px-8 py-4 transition-all"
                    >
                      {savingEdit ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={() => setEditingStrain(null)}
                      className="bg-zinc-800 hover:bg-zinc-700 text-zinc-400 font-bold uppercase tracking-widest px-8 py-4 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* COA Intelligence Modal */}
        {coaParsingStrain && (
          <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="coa-parse-modal">
            <div className="bg-charcoal border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="font-cinzel font-bold text-xl text-white">COA Intelligence</h2>
                    <p className="text-zinc-400 text-sm mt-1">{coaParsingStrain.strain_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {coaParsedData && !coaParseLoading && (
                      <>
                        <button
                          onClick={() => handleParseCoa(coaParsingStrain)}
                          data-testid="reparse-coa-btn"
                          className="text-xs bg-zinc-800 hover:bg-zinc-700 text-gold-500 px-3 py-1.5 font-mono transition-colors"
                        >
                          Re-parse
                        </button>
                        <button
                          onClick={() => setCoaEditMode(!coaEditMode)}
                          data-testid="edit-parsed-coa-btn"
                          className={`text-xs px-3 py-1.5 font-mono transition-colors ${coaEditMode ? 'bg-gold-500 text-black' : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400'}`}
                        >
                          {coaEditMode ? 'Cancel Edit' : 'Edit Values'}
                        </button>
                      </>
                    )}
                    <button onClick={() => { setCoaParsingStrain(null); setCoaParsedData(null); setCoaEditMode(false); }} className="w-8 h-8 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all">
                      <X className="w-5 h-5 text-black" />
                    </button>
                  </div>
                </div>

                {/* Loading */}
                {coaParseLoading && (
                  <div className="flex items-center justify-center py-16" data-testid="coa-parse-loading">
                    <div className="text-center">
                      <RefreshCw className="w-8 h-8 text-gold-500 animate-spin mx-auto mb-3" />
                      <p className="text-zinc-400 text-sm">Parsing COA...</p>
                    </div>
                  </div>
                )}

                {/* No data yet */}
                {!coaParseLoading && !coaParsedData && (
                  <div className="text-center py-16">
                    <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400 text-sm mb-4">No parsed data available for this COA</p>
                    <button
                      onClick={() => handleParseCoa(coaParsingStrain)}
                      data-testid="initial-parse-coa-btn"
                      className="bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest px-6 py-3 text-sm transition-all"
                    >
                      Parse COA Now
                    </button>
                  </div>
                )}

                {/* Parsed Results */}
                {!coaParseLoading && coaParsedData && (
                  <CoaParsedDisplay
                    data={coaParsedData}
                    editMode={coaEditMode}
                    onSaveCorrections={handleSaveCoaCorrections}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Strain Modal */}
        {showAddStrain && (
          <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-charcoal border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Add New Strain</h2>
                    <p className="text-zinc-400 text-sm">Fill in all details for the new cultivar</p>
                  </div>
                  <button 
                    onClick={() => setShowAddStrain(false)}
                    className="w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"
                  >
                    <X className="w-5 h-5 text-black" />
                  </button>
                </div>

                <form onSubmit={handleAddStrain} className="space-y-6">
                  {/* Strain Name */}
                  <div>
                    <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                      Strain Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={newStrain.strain_name}
                      onChange={(e) => setNewStrain(prev => ({ ...prev, strain_name: e.target.value }))}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                      placeholder="e.g., Blue Dream"
                    />
                  </div>

                  {/* Type and THC */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                        Type *
                      </label>
                      <select
                        value={newStrain.type}
                        onChange={(e) => setNewStrain(prev => ({ ...prev, type: e.target.value }))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 transition-colors outline-none"
                      >
                        <option value="No Specific Strain">No Specific Strain</option>
                        <option value="Balanced Hybrid">Balanced Hybrid</option>
                        <option value="Hybrid, Indica Leaning">Hybrid, Indica Leaning</option>
                        <option value="Hybrid, Sativa Leaning">Hybrid, Sativa Leaning</option>
                        <option value="Indica Dominant">Indica Dominant</option>
                        <option value="Sativa Dominant">Sativa Dominant</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                        THC % *
                      </label>
                      <input
                        type="text"
                        required
                        value={newStrain.thc_percentage}
                        onChange={(e) => setNewStrain(prev => ({ ...prev, thc_percentage: e.target.value }))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                        placeholder="e.g., 28.5%"
                      />
                    </div>
                  </div>
                  <LotsEditor testIdPrefix="new-strain" lots={newStrain.lots} onChange={lots => setNewStrain(prev => ({ ...prev, lots }))} />

                  {/* Price Tier and Lot Size */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                        Price Tier *
                      </label>
                      <select
                        value={newStrain.price_tier}
                        onChange={(e) => setNewStrain(prev => ({ ...prev, price_tier: e.target.value }))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 transition-colors outline-none"
                      >
                        <option value="$">$ - Value</option>
                        <option value="$$">$$ - Standard</option>
                        <option value="$$$">$$$ - Premium</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                        Lot Size / Availability *
                      </label>
                      <input
                        type="text"
                        required
                        value={newStrain.lot_size}
                        onChange={(e) => setNewStrain(prev => ({ ...prev, lot_size: e.target.value }))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                        placeholder="e.g., 35 kg or In Harvest"
                      />
                    </div>
                  </div>

                  {/* Category & Concentrates Options */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Category</label>
                      <select
                        data-testid="admin-category-select"
                        value={newStrain.category}
                        onChange={(e) => setNewStrain(prev => ({ ...prev, category: e.target.value, extract_type: '', thc_non_detect: false }))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 transition-colors outline-none"
                      >
                        {['Dried Flower', 'Fresh Frozen', 'Trim', 'Concentrates'].map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    {newStrain.category === 'Concentrates' && (
                      <div>
                        <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Extract Type</label>
                        <select
                          data-testid="admin-extract-type"
                          value={newStrain.extract_type || ''}
                          onChange={(e) => setNewStrain(prev => ({ ...prev, extract_type: e.target.value }))}
                          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 transition-colors outline-none"
                        >
                          <option value="">Select extract type...</option>
                          <option value="Bubble Hash">Bubble Hash</option>
                          <option value="CBC Isolate">CBC Isolate</option>
                          <option value="CBD Distillate">CBD Distillate</option>
                          <option value="CBD Isolate">CBD Isolate</option>
                          <option value="CBD Live Resin">CBD Live Resin</option>
                          <option value="CBD Resin">CBD Resin</option>
                          <option value="CBDv Isolate">CBDv Isolate</option>
                          <option value="CBG Distillate">CBG Distillate</option>
                          <option value="CBG Isolate">CBG Isolate</option>
                          <option value="CBN Isolate">CBN Isolate</option>
                          <option value="Cured Resin">Cured Resin</option>
                          <option value="Diamonds and Sauce">Diamonds and Sauce</option>
                          <option value="Distillate">Distillate</option>
                          <option value="Dry Diamonds">Dry Diamonds</option>
                          <option value="Hash">Hash</option>
                          <option value="Kief">Kief</option>
                          <option value="Liquid Diamonds">Liquid Diamonds</option>
                          <option value="Live Resin">Live Resin</option>
                          <option value="Rosin">Rosin</option>
                          <option value="Shatter">Shatter</option>
                          <option value="THC">THC</option>
                          <option value="THC Distillate">THC Distillate</option>
                          <option value="THC Isolate">THC Isolate</option>
                          <option value="THCa Micro Powder">THCa Micro Powder</option>
                          <option value="THCv Isolate">THCv Isolate</option>
                          <option value="Vape Ready Resin">Vape Ready Resin</option>
                        </select>
                      </div>
                    )}
                  </div>
                  {newStrain.category === 'Concentrates' && (
                    <div className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800 p-4">
                      <input
                        data-testid="admin-thc-non-detect"
                        type="checkbox"
                        checked={newStrain.thc_non_detect}
                        onChange={(e) => setNewStrain(prev => ({ ...prev, thc_non_detect: e.target.checked }))}
                        className="w-5 h-5 accent-gold-500 cursor-pointer"
                      />
                      <label className="font-manrope text-sm text-zinc-300 cursor-pointer" onClick={() => setNewStrain(prev => ({ ...prev, thc_non_detect: !prev.thc_non_detect }))}>
                        THC Non-Detect (ND/THC)
                      </label>
                      <span className="text-zinc-600 text-xs ml-auto">Concentrate tests below detectable THC levels</span>
                    </div>
                  )}
                  {newStrain.category === 'Concentrates' && (
                    <div>
                      <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Total Extract Purity % <span className="text-zinc-700">(optional)</span></label>
                      <input
                        data-testid="admin-extract-purity"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={newStrain.extract_purity}
                        onChange={(e) => setNewStrain(prev => ({ ...prev, extract_purity: e.target.value }))}
                        className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none"
                        placeholder="e.g., 92.5"
                      />
                    </div>
                  )}

                  {/* Lineage */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="font-manrope text-xs uppercase tracking-widest text-zinc-500">
                        Lineage {!newStrain.no_specific_lineage && '*'}
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          data-testid="admin-no-specific-lineage"
                          type="checkbox"
                          checked={newStrain.no_specific_lineage || false}
                          onChange={(e) => setNewStrain(prev => ({ ...prev, no_specific_lineage: e.target.checked, lineage: e.target.checked ? 'No Specific Strain' : '' }))}
                          className="w-4 h-4 accent-gold-500"
                        />
                        <span className="font-manrope text-xs text-zinc-400">No Specific Strain</span>
                      </label>
                    </div>
                    <input
                      type="text"
                      required={!newStrain.no_specific_lineage}
                      disabled={newStrain.no_specific_lineage}
                      value={newStrain.lineage}
                      onChange={(e) => setNewStrain(prev => ({ ...prev, lineage: e.target.value }))}
                      className={`w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 px-4 transition-colors outline-none ${newStrain.no_specific_lineage ? 'opacity-50' : ''}`}
                      placeholder="e.g., Blueberry x Haze"
                    />
                  </div>

                  {/* Terpenes */}
                  <div>
                    <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                      Terpene Profile *
                    </label>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {(Array.isArray(newStrain.terpene_profile) ? newStrain.terpene_profile : (newStrain.terpene_profile || '').split(',').filter(t => t.trim())).map((t, i) => (
                        <span key={i} className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs px-2 py-1 flex items-center gap-1">
                          {typeof t === 'string' ? t.trim() : t}
                          <button type="button" onClick={() => {
                            const current = Array.isArray(newStrain.terpene_profile) ? newStrain.terpene_profile : (newStrain.terpene_profile || '').split(',').filter(x => x.trim());
                            setNewStrain(prev => ({...prev, terpene_profile: current.filter((_, idx) => idx !== i).join(', ')}));
                          }}><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                    <select
                      data-testid="add-strain-terpenes"
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) {
                          const current = (newStrain.terpene_profile || '').split(',').map(t => t.trim()).filter(Boolean);
                          if (!current.includes(val)) {
                            setNewStrain(prev => ({...prev, terpene_profile: [...current, val].join(', ')}));
                          }
                        }
                      }}
                      className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-zinc-400 h-12 px-4 transition-colors outline-none text-sm"
                    >
                      <option value="">Add terpene...</option>
                      {ALL_TERPENES.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  {/* Effects */}
                  <div>
                    <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                      Potential Terpene Effects *
                    </label>
                    <EffectsMultiSelect
                      selected={Array.isArray(newStrain.effects) ? newStrain.effects : []}
                      onChange={(effects) => setNewStrain(prev => ({ ...prev, effects }))}
                      testId="add-strain-effects"
                    />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                      Strain Image * (Black background recommended)
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        onChange={(e) => setNewStrainImage(e.target.files[0])}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      />
                      <div className={`w-full border-2 border-dashed ${newStrainImage ? 'border-cannabis-green bg-cannabis-green/10' : 'border-zinc-700 hover:border-gold-500/50'} p-6 transition-colors text-center`}>
                        {newStrainImage ? (
                          <div className="flex items-center justify-center gap-3 text-cannabis-green">
                            <Check className="w-5 h-5" />
                            <span className="font-medium">{newStrainImage.name}</span>
                            <span className="text-zinc-500 text-sm">({(newStrainImage.size / 1024).toFixed(1)} KB)</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2 text-zinc-500">
                            <Upload className="w-8 h-8" />
                            <span>Click to upload image (JPG, PNG, WebP)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddStrain(false)}
                      className="flex-1 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 font-bold uppercase tracking-widest px-8 py-4 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={addingStrain}
                      className="flex-1 bg-cannabis-green hover:bg-green-600 disabled:bg-zinc-700 text-white font-bold uppercase tracking-widest px-8 py-4 transition-all"
                    >
                      {addingStrain ? 'Adding...' : 'Add Strain'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <>
            {/* Analytics Sub-Navigation */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex gap-2">
                {[
                  { key: 'overview', label: 'Overview' },
                  { key: 'users', label: 'User Activity' },
                  { key: 'engagement', label: 'Engagement' },
                  { key: 'global', label: 'Global Insights' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    data-testid={`analytics-sub-${tab.key}`}
                    onClick={() => {
                      setAnalyticsView(tab.key);
                      if (tab.key === 'users' && userActivityReport.length === 0) fetchUserActivityReport('login_count');
                      if (tab.key === 'engagement' && !engagementData) fetchEngagement();
                      if (tab.key === 'global' && !globalData) fetchGlobalInsights();
                    }}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                      analyticsView === tab.key ? 'bg-gold-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
                {analyticsView === 'user-detail' && (
                  <button
                    onClick={() => setAnalyticsView('users')}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-gold-500 text-black"
                  >
                    <ArrowLeft className="w-3 h-3 inline mr-1" /> User Detail
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => exportCSV('analytics')} className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 text-sm flex items-center gap-2 transition-colors">
                  <FileText className="w-4 h-4" /> Export Events CSV
                </button>
                <button onClick={exportUserActivityCSV} className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 text-sm flex items-center gap-2 transition-colors">
                  <FileText className="w-4 h-4" /> Export Users CSV
                </button>
              </div>
            </div>

            {/* OVERVIEW */}
            {analyticsView === 'overview' && analytics && (
              <>
                <div className="mb-6">
                  <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Analytics Overview</h2>
                  <p className="text-zinc-500 text-sm">Last 30 days · {analytics.summary.total_events} events from {analytics.summary.unique_users} users</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Top Viewed Strains */}
                  <div className="bg-charcoal border border-white/5 p-6">
                    <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Top Viewed Strains</h3>
                    <div className="space-y-3">
                      {analytics.top_strains.map((s, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className="text-gold-500 font-mono text-xs w-5">{i+1}.</span>
                            <span className="text-white text-sm">{s.strain_name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-1.5 bg-zinc-800 overflow-hidden">
                              <div className="h-full bg-gold-500" style={{width: `${Math.min(100, (s.views / (analytics.top_strains[0]?.views || 1)) * 100)}%`}} />
                            </div>
                            <span className="text-zinc-500 font-mono text-xs w-12 text-right">{s.views}</span>
                          </div>
                        </div>
                      ))}
                      {analytics.top_strains.length === 0 && <p className="text-zinc-600 text-sm">No strain views yet</p>}
                    </div>
                  </div>

                  {/* THC Interest */}
                  <div className="bg-charcoal border border-white/5 p-6">
                    <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">THC Potency Interest</h3>
                    <div className="space-y-3">
                      {analytics.thc_interest.map((t, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-white text-sm">{t.thc}</span>
                          <span className="text-zinc-500 font-mono text-sm">{t.count}</span>
                        </div>
                      ))}
                      {analytics.thc_interest.length === 0 && <p className="text-zinc-600 text-sm">No THC data yet</p>}
                    </div>
                  </div>

                  {/* Strain Type Interest */}
                  <div className="bg-charcoal border border-white/5 p-6">
                    <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Strain Type Preference</h3>
                    <div className="space-y-3">
                      {analytics.strain_type_interest.slice(0, 5).map((t, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-white text-sm truncate max-w-[180px]">{t.type}</span>
                          <span className="text-zinc-500 font-mono text-sm">{t.count}</span>
                        </div>
                      ))}
                      {analytics.strain_type_interest.length === 0 && <p className="text-zinc-600 text-sm">No type data yet</p>}
                    </div>
                  </div>

                  {/* Price Tier Interest */}
                  <div className="bg-charcoal border border-white/5 p-6">
                    <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Price Tier Interest</h3>
                    <div className="space-y-3">
                      {analytics.price_tier_interest.map((p, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-white text-sm font-mono">{p.tier}</span>
                          <span className="text-zinc-500 font-mono text-sm">{p.count}</span>
                        </div>
                      ))}
                      {analytics.price_tier_interest.length === 0 && <p className="text-zinc-600 text-sm">No price data yet</p>}
                    </div>
                  </div>

                  {/* Top Searches */}
                  <div className="bg-charcoal border border-white/5 p-6">
                    <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Top Search Queries</h3>
                    <div className="space-y-3">
                      {analytics.top_searches.map((s, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-white text-sm">"{s.query}"</span>
                          <span className="text-zinc-500 font-mono text-sm">{s.count}x</span>
                        </div>
                      ))}
                      {analytics.top_searches.length === 0 && <p className="text-zinc-600 text-sm">No searches yet</p>}
                    </div>
                  </div>

                  {/* Company Engagement */}
                  <div className="bg-charcoal border border-white/5 p-6">
                    <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Most Active Companies</h3>
                    <div className="space-y-3">
                      {analytics.company_engagement.map((c, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-white text-sm truncate max-w-[140px]">{c.company}</span>
                          <span className="text-zinc-500 font-mono text-xs">{c.events} events · {c.unique_strains} strains</span>
                        </div>
                      ))}
                      {analytics.company_engagement.length === 0 && <p className="text-zinc-600 text-sm">No company data yet</p>}
                    </div>
                  </div>

                  {/* Event Breakdown */}
                  <div className="bg-charcoal border border-white/5 p-6 md:col-span-2 lg:col-span-3">
                    <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Event Breakdown</h3>
                    <div className="flex flex-wrap gap-4">
                      {analytics.event_breakdown.map((e, i) => (
                        <div key={i} className="bg-zinc-900 px-4 py-2 border border-white/5">
                          <span className="text-white text-sm">{e._id}</span>
                          <span className="text-gold-500 font-mono ml-2">{e.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* USER ACTIVITY REPORT */}
            {analyticsView === 'users' && (
              <>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="font-cinzel font-bold text-2xl text-white mb-2">User Activity Report</h2>
                    <p className="text-zinc-500 text-sm">{userActivityReport.length} users · Click any user for detailed drill-down</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500 text-xs uppercase tracking-widest">Sort by:</span>
                    {['login_count', 'total_events', 'last_active', 'strains_viewed'].map(s => (
                      <button
                        key={s}
                        data-testid={`sort-${s}`}
                        onClick={() => { setUserActivitySort(s); fetchUserActivityReport(s); }}
                        className={`px-3 py-1 text-xs font-bold uppercase tracking-widest transition-all ${
                          userActivitySort === s ? 'bg-gold-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                        }`}
                      >
                        {s.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {loadingReport ? (
                  <div className="bg-charcoal border border-white/5 p-12 text-center"><p className="text-zinc-500">Loading report...</p></div>
                ) : (
                  <div className="bg-charcoal border border-white/5 overflow-x-auto">
                    <table className="w-full" data-testid="user-activity-table">
                      <thead className="bg-zinc-900">
                        <tr>
                          {[
                            { key: 'name', label: 'User', align: 'left' },
                            { key: 'company', label: 'Company', align: 'left' },
                            { key: 'login_count', label: 'Logins', align: 'center' },
                            { key: 'total_events', label: 'Events', align: 'center' },
                            { key: 'strains_viewed', label: 'Strains', align: 'center' },
                            { key: 'searches', label: 'Searches', align: 'center' },
                            { key: 'coa_views', label: 'COA Views', align: 'center' },
                            { key: 'sample_requests', label: 'Samples', align: 'center' },
                            { key: 'total_duration_seconds', label: 'Time Spent', align: 'center' },
                            { key: 'last_active', label: 'Last Active', align: 'left' },
                            { key: 'actions', label: '', align: 'center' },
                          ].map(col => (
                            <th
                              key={col.key}
                              data-testid={`sort-col-${col.key}`}
                              onClick={() => handleColumnSort(col.key)}
                              className={`text-${col.align} text-xs uppercase tracking-widest p-4 cursor-pointer select-none transition-colors hover:text-gold-500 ${
                                columnSort.key === col.key ? 'text-gold-500' : 'text-zinc-500'
                              }`}
                            >
                              {col.label}
                              {columnSort.key === col.key && (
                                <span className="ml-1">{columnSort.dir === 'desc' ? '\u25BC' : '\u25B2'}</span>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedActivityReport.map((u, i) => (
                          <tr
                            key={u.user_id}
                            data-testid={`user-row-${u.user_id}`}
                            onClick={() => fetchUserDetail(u.user_id)}
                            className={`cursor-pointer hover:bg-zinc-800/50 transition-colors ${i % 2 === 0 ? '' : 'bg-zinc-900/30'}`}
                          >
                            <td className="p-4">
                              <div className="text-white text-sm font-medium">{u.name || 'Unknown'}</div>
                              <div className="text-zinc-600 text-xs">{u.email}</div>
                            </td>
                            <td className="p-4 text-zinc-400 text-sm">{u.company}</td>
                            <td className="p-4 text-center">
                              <span className={`font-mono text-sm ${u.login_count > 5 ? 'text-cannabis-green font-bold' : u.login_count > 1 ? 'text-gold-500' : 'text-zinc-500'}`}>
                                {u.login_count}
                              </span>
                            </td>
                            <td className="p-4 text-center text-zinc-300 font-mono text-sm">{u.total_events}</td>
                            <td className="p-4 text-center text-zinc-300 font-mono text-sm">{u.strains_viewed}</td>
                            <td className="p-4 text-center text-zinc-300 font-mono text-sm">{u.searches}</td>
                            <td className="p-4 text-center text-zinc-300 font-mono text-sm">{u.coa_views}</td>
                            <td className="p-4 text-center">
                              <span className={`font-mono text-sm ${u.sample_requests > 0 ? 'text-cannabis-green font-bold' : 'text-zinc-600'}`}>
                                {u.sample_requests}
                              </span>
                            </td>
                            <td className="p-4 text-center text-zinc-400 font-mono text-xs">{formatDuration(u.total_duration_seconds)}</td>
                            <td className="p-4 text-zinc-500 text-xs">{u.last_active ? new Date(u.last_active).toLocaleString() : 'Never'}</td>
                            <td className="p-4 text-center">
                              <button
                                data-testid={`delete-user-${u.user_id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Delete user "${u.name || u.email}"? This cannot be undone.`)) {
                                    axios.delete(`${API}/admin/users/${u.user_id}`, { headers: { Authorization: `Bearer ${adminToken}` } })
                                      .then(() => {
                                        toast.success('User deleted');
                                        setUserActivityReport(prev => prev.filter(r => r.user_id !== u.user_id));
                                      })
                                      .catch(() => toast.error('Failed to delete user'));
                                  }
                                }}
                                className="w-8 h-8 flex items-center justify-center bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white transition-all"
                                title="Delete user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {/* USER DETAIL DRILL-DOWN */}
            {analyticsView === 'user-detail' && selectedUserDetail && (
              <>
                <div className="mb-6">
                  <button onClick={() => setAnalyticsView('users')} className="text-zinc-400 hover:text-white text-sm flex items-center gap-1 mb-4">
                    <ArrowLeft className="w-4 h-4" /> Back to User Activity
                  </button>
                  <h2 className="font-cinzel font-bold text-2xl text-white mb-2">
                    {selectedUserDetail.user_info?.first_name} {selectedUserDetail.user_info?.last_name}
                  </h2>
                  <p className="text-zinc-500 text-sm">
                    {selectedUserDetail.user_info?.company} · {selectedUserDetail.user_info?.email}
                  </p>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
                  {[
                    { label: 'Total Sessions', value: selectedUserDetail.total_sessions, color: 'text-gold-500' },
                    { label: 'Total Events', value: selectedUserDetail.total_events, color: 'text-white' },
                    { label: 'Strains Viewed', value: selectedUserDetail.strains_viewed?.length || 0, color: 'text-cannabis-green' },
                    { label: 'Searches', value: selectedUserDetail.search_queries?.length || 0, color: 'text-blue-400' },
                    { label: 'Avg Session', value: formatDuration(selectedUserDetail.avg_session_seconds), color: 'text-gold-500' },
                    { label: 'Total Time', value: formatDuration(selectedUserDetail.total_duration_seconds), color: 'text-white' }
                  ].map((card, i) => (
                    <div key={i} className="bg-charcoal border border-white/5 p-4 text-center">
                      <div className={`font-mono font-bold text-xl ${card.color}`}>{card.value}</div>
                      <div className="text-zinc-600 text-xs uppercase tracking-widest mt-1">{card.label}</div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  {/* Top Strains for this User */}
                  <div className="bg-charcoal border border-white/5 p-6">
                    <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Most Viewed Strains</h3>
                    <div className="space-y-3">
                      {selectedUserDetail.top_strains?.map((s, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-white text-sm">{s.strain_name}</span>
                          <span className="text-zinc-500 font-mono text-sm">{s.views}x</span>
                        </div>
                      ))}
                      {(!selectedUserDetail.top_strains || selectedUserDetail.top_strains.length === 0) && (
                        <p className="text-zinc-600 text-sm">No strain views</p>
                      )}
                    </div>
                  </div>

                  {/* Event Breakdown */}
                  <div className="bg-charcoal border border-white/5 p-6">
                    <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Activity Breakdown</h3>
                    <div className="space-y-3">
                      {Object.entries(selectedUserDetail.event_breakdown || {}).sort((a,b) => b[1]-a[1]).map(([type, count], i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-white text-sm">{type.replace(/_/g, ' ')}</span>
                          <span className="text-zinc-500 font-mono text-sm">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Activity Timeline */}
                <div className="bg-charcoal border border-white/5 p-6">
                  <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">
                    Recent Activity Timeline
                    <span className="text-zinc-600 font-normal ml-2">({selectedUserDetail.recent_activity?.length || 0} events)</span>
                  </h3>
                  <div className="max-h-[400px] overflow-y-auto space-y-2">
                    {selectedUserDetail.recent_activity?.map((event, i) => (
                      <div key={i} className="flex items-start gap-4 py-2 border-b border-white/5">
                        <span className="text-zinc-600 font-mono text-xs w-36 shrink-0">
                          {new Date(event.timestamp).toLocaleString()}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-widest shrink-0 ${
                          event.event_type === 'strain_view' ? 'bg-cannabis-green/20 text-cannabis-green' :
                          event.event_type === 'search' ? 'bg-blue-500/20 text-blue-400' :
                          event.event_type === 'filter_apply' ? 'bg-purple-500/20 text-purple-400' :
                          event.event_type === 'coa_view' ? 'bg-gold-500/20 text-gold-500' :
                          'bg-zinc-800 text-zinc-400'
                        }`}>
                          {event.event_type.replace(/_/g, ' ')}
                        </span>
                        <span className="text-zinc-400 text-sm">
                          {event.strain_name && <span className="text-white">{event.strain_name}</span>}
                          {event.search_query && <span>Searched: "<span className="text-white">{event.search_query}</span>"</span>}
                          {event.page && !event.strain_name && !event.search_query && <span>{event.page}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ENGAGEMENT METRICS */}
            {analyticsView === 'engagement' && (
              <>
                <div className="mb-6">
                  <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Engagement Metrics</h2>
                  <p className="text-zinc-500 text-sm">Session data, peak usage, conversion funnel, and navigation flows</p>
                </div>

                {!engagementData ? (
                  <div className="bg-charcoal border border-white/5 p-12 text-center"><p className="text-zinc-500">Loading...</p></div>
                ) : (
                  <>
                    {/* Summary Row */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                      <div className="bg-charcoal border border-white/5 p-5 text-center">
                        <div className="text-gold-500 font-mono font-bold text-2xl">{engagementData.sessions.total}</div>
                        <div className="text-zinc-600 text-xs uppercase tracking-widest mt-1">Total Sessions</div>
                      </div>
                      <div className="bg-charcoal border border-white/5 p-5 text-center">
                        <div className="text-white font-mono font-bold text-2xl">{formatDuration(engagementData.sessions.avg_duration_seconds)}</div>
                        <div className="text-zinc-600 text-xs uppercase tracking-widest mt-1">Avg Session</div>
                      </div>
                      <div className="bg-charcoal border border-white/5 p-5 text-center">
                        <div className="text-white font-mono font-bold text-2xl">{formatDuration(engagementData.sessions.max_duration_seconds)}</div>
                        <div className="text-zinc-600 text-xs uppercase tracking-widest mt-1">Longest Session</div>
                      </div>
                      <div className="bg-charcoal border border-white/5 p-5 text-center">
                        <div className="text-cannabis-green font-mono font-bold text-2xl">{engagementData.conversion.conversion_rate}%</div>
                        <div className="text-zinc-600 text-xs uppercase tracking-widest mt-1">Sample Request Rate</div>
                      </div>
                      <div className="bg-charcoal border border-white/5 p-5 text-center">
                        <div className="text-gold-500 font-mono font-bold text-2xl">{engagementData.user_base.active_this_period}</div>
                        <div className="text-zinc-600 text-xs uppercase tracking-widest mt-1">Active Users (30d)</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Peak Hours */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Peak Usage Hours (UTC)</h3>
                        <div className="space-y-3">
                          {engagementData.peak_hours.map((h, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="text-zinc-400 font-mono text-sm w-12">{h.hour}:00</span>
                              <div className="flex-1 h-4 bg-zinc-800 overflow-hidden">
                                <div
                                  className={`h-full ${i === 0 ? 'bg-gold-500' : 'bg-gold-500/50'}`}
                                  style={{width: `${Math.min(100, (h.events / (engagementData.peak_hours[0]?.events || 1)) * 100)}%`}}
                                />
                              </div>
                              <span className="text-zinc-500 font-mono text-xs w-12 text-right">{h.events}</span>
                            </div>
                          ))}
                          {engagementData.peak_hours.length === 0 && <p className="text-zinc-600 text-sm">No data yet</p>}
                        </div>
                      </div>

                      {/* Conversion Funnel */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Conversion Funnel</h3>
                        <div className="space-y-4">
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-zinc-400">Strain Viewers</span>
                              <span className="text-white font-mono">{engagementData.conversion.total_viewers}</span>
                            </div>
                            <div className="w-full h-3 bg-zinc-800"><div className="h-full bg-gold-500" style={{width: '100%'}} /></div>
                          </div>
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-zinc-400">Sample Requesters</span>
                              <span className="text-cannabis-green font-mono">{engagementData.conversion.sample_requesters}</span>
                            </div>
                            <div className="w-full h-3 bg-zinc-800">
                              <div className="h-full bg-cannabis-green" style={{width: `${engagementData.conversion.conversion_rate}%`}} />
                            </div>
                          </div>
                          <p className="text-zinc-500 text-xs text-center">
                            {engagementData.conversion.conversion_rate}% of viewers requested a sample
                          </p>
                        </div>
                      </div>

                      {/* Navigation Flows */}
                      <div className="bg-charcoal border border-white/5 p-6 lg:col-span-2">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Top Navigation Flows</h3>
                        <p className="text-zinc-600 text-xs mb-4">Most common user journeys (first 3 actions per session)</p>
                        <div className="space-y-3">
                          {engagementData.top_navigation_flows.map((f, i) => (
                            <div key={i} className="flex justify-between items-center">
                              <div className="flex items-center gap-1 text-sm flex-wrap">
                                {f.flow.split(' → ').map((step, j) => (
                                  <span key={j} className="flex items-center gap-1">
                                    {j > 0 && <span className="text-zinc-600 mx-1">→</span>}
                                    <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-widest ${
                                      step === 'strain_view' ? 'bg-cannabis-green/20 text-cannabis-green' :
                                      step === 'search' ? 'bg-blue-500/20 text-blue-400' :
                                      step === 'page_view' ? 'bg-zinc-800 text-zinc-400' :
                                      'bg-gold-500/20 text-gold-500'
                                    }`}>
                                      {step.replace(/_/g, ' ')}
                                    </span>
                                  </span>
                                ))}
                              </div>
                              <span className="text-zinc-500 font-mono text-sm shrink-0 ml-4">{f.count} users</span>
                            </div>
                          ))}
                          {engagementData.top_navigation_flows.length === 0 && <p className="text-zinc-600 text-sm">No flow data yet</p>}
                        </div>
                      </div>

                      {/* Daily Activity */}
                      <div className="bg-charcoal border border-white/5 p-6 lg:col-span-2">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Daily Activity (Last 30 Days)</h3>
                        <div className="flex items-end gap-1 h-24">
                          {engagementData.daily_activity.map((d, i) => {
                            const maxCount = Math.max(...engagementData.daily_activity.map(x => x.count), 1);
                            const height = Math.max(4, (d.count / maxCount) * 96);
                            return (
                              <div key={i} className="flex-1 group relative" title={`${d._id}: ${d.count} events`}>
                                <div className="w-full bg-gold-500/60 hover:bg-gold-500 transition-colors" style={{height: `${height}px`}} />
                              </div>
                            );
                          })}
                          {engagementData.daily_activity.length === 0 && (
                            <p className="text-zinc-600 text-sm w-full text-center">No daily data yet</p>
                          )}
                        </div>
                        {engagementData.daily_activity.length > 0 && (
                          <div className="flex justify-between text-zinc-600 text-xs mt-2">
                            <span>{engagementData.daily_activity[0]?._id}</span>
                            <span>{engagementData.daily_activity[engagementData.daily_activity.length - 1]?._id}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {/* GLOBAL INSIGHTS */}
            {analyticsView === 'global' && (
              <>
                <div className="mb-6">
                  <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Global Insights</h2>
                  <p className="text-zinc-500 text-sm">Geographic breakdown, demand signals, supply analysis, and market intelligence</p>
                </div>

                {!globalData ? (
                  <div className="bg-charcoal border border-white/5 p-12 text-center"><p className="text-zinc-500">Loading...</p></div>
                ) : (
                  <>
                    {/* Platform Totals */}
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-8">
                      {[
                        { label: 'Users', value: globalData.totals.users, color: 'text-gold-500' },
                        { label: 'Strains', value: globalData.totals.strains, color: 'text-cannabis-green' },
                        { label: 'With COA', value: globalData.totals.strains_with_coa, color: 'text-blue-400' },
                        { label: 'Submissions', value: globalData.totals.submissions, color: 'text-white' },
                        { label: 'Sample Req', value: globalData.totals.sample_requests, color: 'text-gold-500' },
                        { label: 'Events', value: globalData.totals.total_events, color: 'text-zinc-300' }
                      ].map((t, i) => (
                        <div key={i} className="bg-charcoal border border-white/5 p-4 text-center">
                          <div className={`font-mono font-bold text-xl ${t.color}`}>{t.value}</div>
                          <div className="text-zinc-600 text-xs uppercase tracking-widest mt-1">{t.label}</div>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      {/* Users by Country */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Users by Country</h3>
                        <div className="space-y-3">
                          {globalData.geographic.users_by_country.map((c, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="text-white text-sm flex-1">{c.country}</span>
                              <div className="w-24 h-2 bg-zinc-800 overflow-hidden">
                                <div className="h-full bg-gold-500" style={{width: `${(c.count / (globalData.geographic.users_by_country[0]?.count || 1)) * 100}%`}} />
                              </div>
                              <span className="text-zinc-500 font-mono text-xs w-8 text-right">{c.count}</span>
                            </div>
                          ))}
                          {globalData.geographic.users_by_country.length === 0 && <p className="text-zinc-600 text-sm">No data</p>}
                        </div>
                      </div>

                      {/* Export Countries (from Grower Submissions) */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Export Destinations (Grower Submissions)</h3>
                        <div className="space-y-3">
                          {globalData.geographic.exports_by_country.map((c, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="text-white text-sm flex-1">{c.country}</span>
                              <div className="w-24 h-2 bg-zinc-800 overflow-hidden">
                                <div className="h-full bg-cannabis-green" style={{width: `${(c.count / (globalData.geographic.exports_by_country[0]?.count || 1)) * 100}%`}} />
                              </div>
                              <span className="text-zinc-500 font-mono text-xs w-8 text-right">{c.count}</span>
                            </div>
                          ))}
                          {globalData.geographic.exports_by_country.length === 0 && <p className="text-zinc-600 text-sm">No export data yet</p>}
                        </div>
                      </div>

                      {/* Top Company Activity by Country */}
                      <div className="bg-charcoal border border-white/5 p-6 lg:col-span-2">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Company Activity by Region</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-white/5">
                                <th className="text-left text-zinc-500 text-xs uppercase tracking-widest py-2 pr-4">Country</th>
                                <th className="text-left text-zinc-500 text-xs uppercase tracking-widest py-2 pr-4">Company</th>
                                <th className="text-center text-zinc-500 text-xs uppercase tracking-widest py-2 pr-4">Events</th>
                                <th className="text-center text-zinc-500 text-xs uppercase tracking-widest py-2">Strains Viewed</th>
                              </tr>
                            </thead>
                            <tbody>
                              {globalData.geographic.company_activity_by_country.slice(0, 15).map((r, i) => (
                                <tr key={i} className="border-b border-white/5">
                                  <td className="py-2 pr-4 text-zinc-400 text-sm">{r.country || 'N/A'}</td>
                                  <td className="py-2 pr-4 text-white text-sm">{r.company || 'N/A'}</td>
                                  <td className="py-2 pr-4 text-center text-gold-500 font-mono text-sm">{r.events}</td>
                                  <td className="py-2 text-center text-cannabis-green font-mono text-sm">{r.strains_viewed}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Demand Signals */}
                    <h3 className="font-cinzel font-bold text-lg text-gold-500 mb-4 mt-8">Demand Signals</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      {/* Most Requested Samples */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Most Requested Samples</h3>
                        <div className="space-y-3">
                          {globalData.demand.top_sample_requests.map((s, i) => (
                            <div key={i} className="flex justify-between items-center">
                              <span className="text-white text-sm">{s.strain_name}</span>
                              <span className="text-zinc-500 text-xs">{s.requests} req · {s.unique_companies} companies</span>
                            </div>
                          ))}
                          {globalData.demand.top_sample_requests.length === 0 && <p className="text-zinc-600 text-sm">No sample requests yet</p>}
                        </div>
                      </div>

                      {/* Terpene Interest */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Terpene Interest</h3>
                        <div className="flex flex-wrap gap-2">
                          {globalData.demand.terpene_interest.map((t, i) => (
                            <div key={i} className="bg-zinc-900 border border-white/5 px-3 py-1.5">
                              <span className="text-white text-sm">{t.terpene}</span>
                              <span className="text-gold-500 font-mono ml-2 text-xs">{t.views}</span>
                            </div>
                          ))}
                          {globalData.demand.terpene_interest.length === 0 && <p className="text-zinc-600 text-sm">No data</p>}
                        </div>
                      </div>

                      {/* Type Demand */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Strain Type Demand</h3>
                        <div className="space-y-3">
                          {globalData.demand.type_demand.map((t, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="text-white text-sm flex-1 truncate">{t.type}</span>
                              <div className="w-20 h-2 bg-zinc-800 overflow-hidden">
                                <div className="h-full bg-cannabis-green" style={{width: `${(t.views / (globalData.demand.type_demand[0]?.views || 1)) * 100}%`}} />
                              </div>
                              <span className="text-zinc-500 text-xs">{t.views} views · {t.unique_users} users</span>
                            </div>
                          ))}
                          {globalData.demand.type_demand.length === 0 && <p className="text-zinc-600 text-sm">No data</p>}
                        </div>
                      </div>

                      {/* Price Tier Demand */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Price Tier Demand</h3>
                        <div className="space-y-3">
                          {globalData.demand.tier_demand.map((t, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="text-gold-500 font-mono text-lg w-12">{t.tier}</span>
                              <div className="flex-1 h-6 bg-zinc-800 overflow-hidden">
                                <div className="h-full bg-gold-500/60" style={{width: `${(t.views / (globalData.demand.tier_demand[0]?.views || 1)) * 100}%`}} />
                              </div>
                              <span className="text-zinc-500 text-xs w-28 text-right">{t.views} views · {t.unique_users} users</span>
                            </div>
                          ))}
                          {globalData.demand.tier_demand.length === 0 && <p className="text-zinc-600 text-sm">No data</p>}
                        </div>
                      </div>
                    </div>

                    {/* Supply-Side */}
                    <h3 className="font-cinzel font-bold text-lg text-gold-500 mb-4 mt-8">Supply Side (Grower Submissions)</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                      {/* Submission Status */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Submission Pipeline</h3>
                        <div className="space-y-3">
                          {Object.entries(globalData.supply.submission_status).map(([status, count], i) => (
                            <div key={i} className="flex justify-between items-center">
                              <span className={`px-3 py-1 text-xs font-bold uppercase tracking-widest ${
                                status === 'pending' ? 'bg-gold-500/20 text-gold-500' :
                                status === 'approved' ? 'bg-cannabis-green/20 text-cannabis-green' :
                                'bg-red-500/20 text-red-500'
                              }`}>{status}</span>
                              <span className="text-white font-mono">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Lot Size Stats */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Lot Size Analysis</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between"><span className="text-zinc-400 text-sm">Average Lot</span><span className="text-white font-mono">{Math.round(globalData.supply.lot_stats.avg_lot || 0)} kg</span></div>
                          <div className="flex justify-between"><span className="text-zinc-400 text-sm">Smallest Lot</span><span className="text-white font-mono">{globalData.supply.lot_stats.min_lot || 0} kg</span></div>
                          <div className="flex justify-between"><span className="text-zinc-400 text-sm">Largest Lot</span><span className="text-white font-mono">{globalData.supply.lot_stats.max_lot || 0} kg</span></div>
                          <div className="flex justify-between border-t border-white/5 pt-2"><span className="text-zinc-400 text-sm">Total Available</span><span className="text-gold-500 font-mono font-bold">{Math.round(globalData.supply.lot_stats.total_kg || 0)} kg</span></div>
                        </div>
                      </div>

                      {/* Irradiation Stats */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Irradiation Status</h3>
                        <div className="space-y-3">
                          {Object.entries(globalData.supply.irradiation).map(([status, count], i) => (
                            <div key={i} className="flex justify-between items-center">
                              <span className={`text-sm ${status === 'Yes' ? 'text-cannabis-green' : status === 'No' ? 'text-red-400' : 'text-zinc-500'}`}>{status}</span>
                              <span className="text-white font-mono">{count}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Grower Types & Growth & Grade Distribution */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Grade Distribution */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Grade Distribution</h3>
                        {globalData.supply.grade_distribution ? (
                          <div className="space-y-4">
                            <div>
                              <p className="text-zinc-600 text-xs uppercase tracking-widest mb-2">Submissions</p>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-cannabis-green text-sm">Grade A (Bigs)</span>
                                  <span className="text-white font-mono">{globalData.supply.grade_distribution.submissions?.total_grade_a || 0} kg</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gold-500 text-sm">Grade B (Mids)</span>
                                  <span className="text-white font-mono">{globalData.supply.grade_distribution.submissions?.total_grade_b || 0} kg</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-zinc-400 text-sm">Grade C (Smalls)</span>
                                  <span className="text-white font-mono">{globalData.supply.grade_distribution.submissions?.total_grade_c || 0} kg</span>
                                </div>
                              </div>
                            </div>
                            <div className="border-t border-white/5 pt-3">
                              <p className="text-zinc-600 text-xs uppercase tracking-widest mb-2">Catalog (Approved)</p>
                              <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-cannabis-green text-sm">Grade A (Bigs)</span>
                                  <span className="text-white font-mono">{globalData.supply.grade_distribution.catalog?.total_grade_a || 0} kg</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gold-500 text-sm">Grade B (Mids)</span>
                                  <span className="text-white font-mono">{globalData.supply.grade_distribution.catalog?.total_grade_b || 0} kg</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-zinc-400 text-sm">Grade C (Smalls)</span>
                                  <span className="text-white font-mono">{globalData.supply.grade_distribution.catalog?.total_grade_c || 0} kg</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : <p className="text-zinc-600 text-sm">No grade data</p>}
                      </div>

                      {/* Grower Submission Types */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Submitted Strain Types</h3>
                        <div className="space-y-3">
                          {globalData.supply.grower_types.map((g, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <span className="text-white text-sm flex-1 truncate">{g.type}</span>
                              <div className="w-20 h-2 bg-zinc-800 overflow-hidden">
                                <div className="h-full bg-cannabis-green/60" style={{width: `${(g.count / (globalData.supply.grower_types[0]?.count || 1)) * 100}%`}} />
                              </div>
                              <span className="text-zinc-500 font-mono text-xs">{g.count}</span>
                            </div>
                          ))}
                          {globalData.supply.grower_types.length === 0 && <p className="text-zinc-600 text-sm">No submissions</p>}
                        </div>
                      </div>

                      {/* Registration Growth */}
                      <div className="bg-charcoal border border-white/5 p-6">
                        <h3 className="text-gold-500 font-bold uppercase tracking-widest text-sm mb-4">Registration Growth</h3>
                        <div className="flex items-end gap-1 h-24">
                          {globalData.growth.registration_timeline.map((d, i) => {
                            const maxCount = Math.max(...globalData.growth.registration_timeline.map(x => x.count), 1);
                            const height = Math.max(4, (d.count / maxCount) * 96);
                            return (
                              <div key={i} className="flex-1 group relative" title={`${d._id}: ${d.count} registrations`}>
                                <div className="w-full bg-cannabis-green/60 hover:bg-cannabis-green transition-colors" style={{height: `${height}px`}} />
                              </div>
                            );
                          })}
                          {globalData.growth.registration_timeline.length === 0 && (
                            <p className="text-zinc-600 text-sm w-full text-center">No data</p>
                          )}
                        </div>
                        {globalData.growth.registration_timeline.length > 0 && (
                          <div className="flex justify-between text-zinc-600 text-xs mt-2">
                            <span>{globalData.growth.registration_timeline[0]?._id}</span>
                            <span>{globalData.growth.registration_timeline[globalData.growth.registration_timeline.length - 1]?._id}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            {/* User Detail Modal */}
            {selectedUserProfile && (
              <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelectedUserProfile(null)}>
                <div className="bg-charcoal border border-white/10 max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="sticky top-0 bg-charcoal border-b border-white/5 p-6 flex justify-between items-start z-10">
                    <div>
                      <h2 className="font-cinzel font-bold text-2xl text-white">{selectedUserProfile.user.first_name} {selectedUserProfile.user.last_name}</h2>
                      <p className="text-zinc-400 text-sm">{selectedUserProfile.user.company} · {selectedUserProfile.user.email}</p>
                    </div>
                    <button onClick={() => setSelectedUserProfile(null)} className="w-10 h-10 bg-gold-500 text-black hover:bg-gold-400 flex items-center justify-center transition-all">
                      <X className="w-5 h-5 text-black" />
                    </button>
                  </div>
                  <div className="p-6 space-y-6">
                    {/* User Info Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-zinc-900/50 border border-white/5 p-4">
                        <p className="text-zinc-600 text-[10px] uppercase tracking-widest mb-1">Phone</p>
                        <p className="text-zinc-200 text-sm">{selectedUserProfile.user.phone || 'N/A'}</p>
                      </div>
                      <div className="bg-zinc-900/50 border border-white/5 p-4">
                        <p className="text-zinc-600 text-[10px] uppercase tracking-widest mb-1">Country</p>
                        <p className="text-zinc-200 text-sm">{selectedUserProfile.user.country || 'N/A'}</p>
                      </div>
                      <div className="bg-zinc-900/50 border border-white/5 p-4">
                        <p className="text-zinc-600 text-[10px] uppercase tracking-widest mb-1">License #</p>
                        <p className="text-zinc-200 text-sm font-mono">{selectedUserProfile.user.license_number || 'N/A'}</p>
                      </div>
                      <div className="bg-zinc-900/50 border border-white/5 p-4">
                        <p className="text-zinc-600 text-[10px] uppercase tracking-widest mb-1">Registered</p>
                        <p className="text-zinc-200 text-sm">{selectedUserProfile.user.created_at ? new Date(selectedUserProfile.user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</p>
                      </div>
                    </div>

                    {/* Activity Summary */}
                    <div>
                      <h3 className="font-manrope font-bold text-xs uppercase tracking-widest text-gold-500 mb-3">Activity Summary</h3>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {[
                          { label: 'Logins', value: selectedUserProfile.activity_summary.login_count },
                          { label: 'Page Views', value: selectedUserProfile.activity_summary.total_page_views },
                          { label: 'Strains Viewed', value: selectedUserProfile.activity_summary.total_strain_views },
                          { label: 'Inquiries', value: selectedUserProfile.activity_summary.total_inquiries },
                          { label: 'Sample Requests', value: selectedUserProfile.activity_summary.total_sample_requests },
                        ].map((stat) => (
                          <div key={stat.label} className="bg-zinc-900 border border-white/5 p-3 text-center">
                            <p className="text-2xl font-bold text-white">{stat.value}</p>
                            <p className="text-zinc-500 text-[10px] uppercase tracking-widest">{stat.label}</p>
                          </div>
                        ))}
                      </div>
                      {selectedUserProfile.activity_summary.last_login && (
                        <p className="text-zinc-500 text-xs mt-2">Last login: {new Date(selectedUserProfile.activity_summary.last_login).toLocaleString()}</p>
                      )}
                    </div>

                    {/* Top Strains Viewed */}
                    {selectedUserProfile.top_strains_viewed.length > 0 && (
                      <div>
                        <h3 className="font-manrope font-bold text-xs uppercase tracking-widest text-gold-500 mb-3">Top Strains Viewed</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {selectedUserProfile.top_strains_viewed.map((s, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-zinc-900/50 border border-white/5 px-4 py-2">
                              <span className="text-zinc-200 text-sm">{s.name}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-gold-500 font-mono text-sm font-bold">{s.count}x</span>
                                {s.last_viewed && <span className="text-zinc-600 text-xs">{new Date(s.last_viewed).toLocaleDateString()}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Login History */}
                    {selectedUserProfile.activity_summary.login_history.length > 0 && (
                      <div>
                        <h3 className="font-manrope font-bold text-xs uppercase tracking-widest text-gold-500 mb-3">Login History</h3>
                        <div className="flex flex-wrap gap-2">
                          {selectedUserProfile.activity_summary.login_history.map((ts, idx) => (
                            <span key={idx} className="bg-zinc-900 border border-white/5 text-zinc-400 text-xs px-3 py-1.5 font-mono">
                              {new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Inquiries */}
                    {selectedUserProfile.inquiries.length > 0 && (
                      <div>
                        <h3 className="font-manrope font-bold text-xs uppercase tracking-widest text-gold-500 mb-3">Inquiries ({selectedUserProfile.inquiries.length})</h3>
                        <div className="space-y-2">
                          {selectedUserProfile.inquiries.map((inq, idx) => (
                            <div key={idx} className="bg-zinc-900/50 border border-white/5 p-4">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex flex-wrap gap-1">
                                  {(inq.strain_names || []).map((name, i) => (
                                    <span key={i} className="bg-gold-500/10 border border-gold-500/20 text-gold-500 text-[10px] font-bold px-2 py-0.5">{name}</span>
                                  ))}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${inq.status === 'pending' ? 'bg-gold-500/20 text-gold-500' : inq.status === 'responded' ? 'bg-cannabis-green/20 text-cannabis-green' : 'bg-zinc-700/30 text-zinc-500'}`}>{inq.status}</span>
                                  <span className="text-zinc-600 text-xs">{inq.created_at ? new Date(inq.created_at).toLocaleDateString() : ''}</span>
                                </div>
                              </div>
                              <p className="text-zinc-400 text-sm">{inq.message}</p>
                              {inq.strain_volumes && Object.keys(inq.strain_volumes).length > 0 && (
                                <div className="mt-2 flex gap-2">
                                  {(inq.strain_names || []).map((name, i) => {
                                    const vol = inq.strain_volumes[(inq.strain_ids || [])[i]];
                                    return vol ? <span key={i} className="text-zinc-500 text-xs">{name}: {vol}kg</span> : null;
                                  })}
                                </div>
                              )}
                              {inq.market_of_interest && <p className="text-gold-500/70 text-xs mt-1">Market: {inq.market_of_interest}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sample Requests */}
                    {selectedUserProfile.sample_requests.length > 0 && (
                      <div>
                        <h3 className="font-manrope font-bold text-xs uppercase tracking-widest text-gold-500 mb-3">Sample Requests ({selectedUserProfile.sample_requests.length})</h3>
                        <div className="space-y-2">
                          {selectedUserProfile.sample_requests.map((sr, idx) => (
                            <div key={idx} className="bg-zinc-900/50 border border-white/5 p-4 flex justify-between items-center">
                              <div>
                                <p className="text-zinc-200 text-sm">{sr.strain_name || 'Unknown strain'}</p>
                                <p className="text-zinc-500 text-xs">{sr.message}</p>
                              </div>
                              <span className="text-zinc-600 text-xs">{sr.created_at ? new Date(sr.created_at).toLocaleDateString() : ''}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Filter Usage */}
                    {Object.keys(selectedUserProfile.filter_usage).length > 0 && (
                      <div>
                        <h3 className="font-manrope font-bold text-xs uppercase tracking-widest text-gold-500 mb-3">Filter Usage</h3>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(selectedUserProfile.filter_usage).sort((a, b) => b[1] - a[1]).map(([filter, count]) => (
                            <span key={filter} className="bg-zinc-900 border border-white/5 text-zinc-400 text-xs px-3 py-1.5">
                              {filter}: <span className="text-gold-500 font-bold">{count}x</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Registered Users</h2>
                <p className="text-zinc-500 text-sm">{usersLoading ? 'Loading...' : `${users.length} total users`} · Click any name for detailed profile</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchUsers()}
                  disabled={usersLoading}
                  data-testid="users-refresh-btn"
                  className="bg-gold-500 hover:bg-gold-400 text-black px-4 py-2 text-sm flex items-center gap-2 transition-colors disabled:opacity-50 font-bold"
                >
                  <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                <button
                  onClick={() => exportCSV('users')}
                  className="bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 text-sm flex items-center gap-2 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Marketing CSV
                </button>
              </div>
            </div>

            <div className="bg-charcoal border border-white/5 overflow-x-auto">
              <table className="w-full min-w-[1200px]">
                <SortableHeader
                  testPrefix="users"
                  sortState={usersSort}
                  onSort={makeSortHandler(setUsersSort)}
                  columns={[
                    { key: 'first_name', label: 'Name' },
                    { key: 'company', label: 'Company' },
                    { key: 'country', label: 'Country' },
                    { key: 'email', label: 'Email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'license_number', label: 'License #' },
                    { key: 'referred_by', label: 'Referred By' },
                    { key: 'status', label: 'Status' },
                    { key: 'created_at', label: 'Registered' },
                    { key: '_actions', label: 'Actions', align: 'right' },
                  ]}
                />
                <tbody>
                  {sortData(users, usersSort).map((user, i) => (
                    <tr key={user.id || i} className={`border-t border-white/5 hover:bg-zinc-900/50 ${user.status === 'suspended' ? 'opacity-60' : ''} ${user.status === 'removed' ? 'opacity-30' : ''}`}>
                      <td className="p-4">
                        <button
                          data-testid={`user-profile-${user.id}`}
                          onClick={() => fetchUserProfile(user.id)}
                          className="text-gold-500 hover:text-gold-400 hover:underline font-bold text-left transition-colors"
                        >
                          {user.first_name} {user.last_name}
                        </button>
                      </td>
                      <td className="p-4 text-zinc-400">{user.company}</td>
                      <td className="p-4 text-zinc-400">{user.country}</td>
                      <td className="p-4 text-zinc-400">{user.email}</td>
                      <td className="p-4 text-zinc-400 text-sm">{user.phone || '—'}</td>
                      <td className="p-4 text-zinc-400 font-mono text-xs">{user.license_number || '—'}</td>
                      <td className="p-4 text-sm">
                        {(user.referred_by || user.referred_by_code) ? (
                          <span className="text-gold-500 font-mono text-xs">{(() => {
                            const code = user.referred_by || user.referred_by_code;
                            const aff = affiliates.find(a => a.referral_code === code);
                            return aff ? aff.name : code;
                          })()}</span>
                        ) : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="p-4">
                        {user.status === 'suspended' ? (
                          <span data-testid={`user-status-${user.id}`} className="bg-yellow-500/20 text-yellow-500 text-xs font-bold uppercase tracking-widest px-2 py-1">Paused</span>
                        ) : user.status === 'removed' ? (
                          <span data-testid={`user-status-${user.id}`} className="bg-red-500/20 text-red-500 text-xs font-bold uppercase tracking-widest px-2 py-1">Removed</span>
                        ) : (
                          <span data-testid={`user-status-${user.id}`} className="bg-cannabis-green/20 text-cannabis-green text-xs font-bold uppercase tracking-widest px-2 py-1">Active</span>
                        )}
                      </td>
                      <td className="p-4 text-zinc-500 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 text-right">
                        {user.status !== 'removed' && (
                          <div className="flex gap-2 justify-end">
                            {user.status === 'suspended' ? (
                              <button
                                data-testid={`user-activate-${user.id}`}
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/admin/users/${user.id}/status`, { status: 'active' }, { headers: { Authorization: `Bearer ${adminToken}` } });
                                    toast.success('User access restored');
                                    fetchUsers(adminToken);
                                  } catch { toast.error('Failed to restore access'); }
                                }}
                                className="bg-cannabis-green/20 hover:bg-cannabis-green/30 text-cannabis-green text-xs font-bold uppercase tracking-widest px-3 py-1.5 transition-colors"
                              >
                                Restore
                              </button>
                            ) : (
                              <button
                                data-testid={`user-pause-${user.id}`}
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/admin/users/${user.id}/status`, { status: 'suspended' }, { headers: { Authorization: `Bearer ${adminToken}` } });
                                    toast.success('User access paused');
                                    fetchUsers(adminToken);
                                  } catch { toast.error('Failed to pause access'); }
                                }}
                                className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-500 text-xs font-bold uppercase tracking-widest px-3 py-1.5 transition-colors"
                              >
                                Pause Access
                              </button>
                            )}
                            <button
                              data-testid={`user-remove-${user.id}`}
                              onClick={async () => {
                                if (!window.confirm(`Remove ${user.first_name} ${user.last_name} (${user.email})? This will revoke their access permanently.`)) return;
                                try {
                                  await axios.delete(`${API}/admin/users/${user.id}`, { headers: { Authorization: `Bearer ${adminToken}` } });
                                  toast.success('User removed');
                                  fetchUsers(adminToken);
                                } catch { toast.error('Failed to remove user'); }
                              }}
                              className="bg-red-500/20 hover:bg-red-500/30 text-red-500 text-xs font-bold uppercase tracking-widest px-3 py-1.5 transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="10" className="p-8 text-center text-zinc-600">No users registered yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Flags Tab */}
        {activeTab === 'flags' && (
          <>
            <div className="mb-8">
              <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Registration Flags</h2>
              <p className="text-zinc-500 text-sm">Duplicate company names, phone numbers, and email domains are flagged</p>
            </div>

            {flags.length === 0 ? (
              <div className="bg-charcoal border border-white/5 p-12 text-center">
                <ShieldCheck className="w-12 h-12 text-cannabis-green mx-auto mb-4" />
                <p className="text-zinc-400">No flags detected. All registrations look clean.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {flags.map((flag, i) => (
                  <div key={flag.id || i} className="bg-charcoal border border-yellow-500/30 p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className={`inline-block px-3 py-1 text-xs font-bold uppercase tracking-widest mb-2 ${
                          flag.flag_type === 'duplicate_company' ? 'bg-yellow-500/20 text-yellow-500' :
                          flag.flag_type === 'duplicate_phone' ? 'bg-red-500/20 text-red-500' :
                          'bg-blue-500/20 text-blue-500'
                        }`}>
                          {flag.flag_type.replace(/_/g, ' ')}
                        </span>
                        <p className="text-white">{flag.flag_reason}</p>
                        <p className="text-zinc-500 text-sm mt-2">
                          User ID: {flag.user_id}
                        </p>
                        {flag.matched_user_ids && flag.matched_user_ids.length > 0 && (
                          <p className="text-zinc-500 text-sm">
                            Matches with: {flag.matched_user_ids.join(', ')}
                          </p>
                        )}
                      </div>
                      <span className="text-zinc-600 text-xs">
                        {new Date(flag.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Grower Submissions Tab */}
        {activeTab === 'submissions' && (
          <>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Grower Submissions</h2>
                <p className="text-zinc-500 text-sm">{submissions.length} total · {pendingCount} pending authorization</p>
              </div>
              <div className="flex gap-2">
                {['all', 'pending', 'approved', 'rejected'].map(f => (
                  <button
                    key={f}
                    data-testid={`submission-filter-${f}`}
                    onClick={() => setSubmissionFilter(f)}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${
                      submissionFilter === f ? 'bg-gold-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:text-white'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <SortButtons
              testPrefix="submissions"
              sortState={submissionsSort}
              onSort={makeSortHandler(setSubmissionsSort)}
              options={[
                { key: 'created_at', label: 'Date' },
                { key: 'strain_name', label: 'Strain' },
                { key: 'company_name', label: 'Company' },
                { key: 'status', label: 'Status' },
                { key: 'lot_size_kg', label: 'Lot Size' },
              ]}
            />

            {filteredSubmissions.length === 0 ? (
              <div className="bg-charcoal border border-white/5 p-12 text-center">
                <ClipboardList className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400">No {submissionFilter === 'all' ? '' : submissionFilter} submissions yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sortData(filteredSubmissions, submissionsSort).map((sub) => (
                  <div key={sub.id} data-testid={`submission-card-${sub.id}`} className={`bg-charcoal border p-6 ${
                    sub.status === 'pending' ? 'border-gold-500/30' : sub.status === 'approved' ? 'border-cannabis-green/30' : 'border-red-500/30'
                  }`}>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-cinzel font-bold text-xl text-white">{sub.strain_name}</h3>
                          <span className={`px-3 py-1 text-xs font-bold uppercase tracking-widest ${
                            sub.status === 'pending' ? 'bg-gold-500/20 text-gold-500' :
                            sub.status === 'approved' ? 'bg-cannabis-green/20 text-cannabis-green' :
                            'bg-red-500/20 text-red-500'
                          }`}>
                            {sub.status}
                          </span>
                        </div>
                        <p className="text-zinc-400 text-sm">{sub.strain_type} · THC {sub.thc_percentage}{sub.cbd_percentage ? ` · CBD ${sub.cbd_percentage}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-600 text-xs">{new Date(sub.created_at).toLocaleDateString()}</span>
                        <button
                          data-testid={`delete-submission-${sub.id}`}
                          onClick={() => {
                            if (window.confirm('Are you sure you want to permanently delete this submission?')) {
                              axios.delete(`${API}/admin/submissions/${sub.id}`, { headers: { Authorization: `Bearer ${adminToken}` } })
                                .then(() => { toast.success('Submission deleted'); setSubmissions(prev => prev.filter(s => s.id !== sub.id)); })
                                .catch(() => toast.error('Failed to delete submission'));
                            }
                          }}
                          className="w-7 h-7 flex items-center justify-center bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white transition-all"
                          title="Delete submission"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Strain Details Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Lineage</p>
                        <p className="text-zinc-300 text-sm">{sub.lineage}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Terpenes</p>
                        <p className="text-zinc-300 text-sm">{sub.terpene_profile}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Lot Size</p>
                        <p className="text-zinc-300 text-sm">{sub.lot_size_kg} kg</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Method</p>
                        <p className="text-zinc-300 text-sm">{sub.growing_method || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Export Country</p>
                        <p className="text-zinc-300 text-sm">{sub.export_country || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Open Permit</p>
                        <p className={`text-sm font-medium ${sub.open_permit === 'Yes' ? 'text-cannabis-green' : sub.open_permit === 'No' ? 'text-red-400' : 'text-zinc-500'}`}>{sub.open_permit || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Irradiated</p>
                        <p className={`text-sm font-medium ${sub.is_irradiated === 'Yes' ? 'text-cannabis-green' : sub.is_irradiated === 'No' ? 'text-red-400' : 'text-zinc-500'}`}>{sub.is_irradiated || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Willing to Irradiate</p>
                        <p className={`text-sm font-medium ${sub.willing_to_irradiate === 'Yes' ? 'text-cannabis-green' : sub.willing_to_irradiate === 'No' ? 'text-red-400' : 'text-zinc-500'}`}>{sub.willing_to_irradiate || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">GACP Compliant</p>
                        <p className={`text-sm font-medium ${sub.gacp_compliant === 'Yes' ? 'text-cannabis-green' : sub.gacp_compliant === 'No' ? 'text-red-400' : 'text-zinc-500'}`}>{sub.gacp_compliant || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">License #</p>
                        <p className="text-zinc-300 text-sm font-mono">{sub.license_number || 'N/A'}</p>
                      </div>
                    </div>

                    {/* Grade Breakdown */}
                    {(sub.grade_a_kg || sub.grade_b_kg || sub.grade_c_kg) && (
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="bg-cannabis-green/10 border border-cannabis-green/30 p-3 text-center">
                          <p className="text-cannabis-green font-bold font-mono text-lg">{sub.grade_a_kg || 0}</p>
                          <p className="text-cannabis-green/70 text-[10px] uppercase tracking-widest">Grade A (Bigs) (kg)</p>
                        </div>
                        <div className="bg-gold-500/10 border border-gold-500/30 p-3 text-center">
                          <p className="text-gold-500 font-bold font-mono text-lg">{sub.grade_b_kg || 0}</p>
                          <p className="text-gold-500/70 text-[10px] uppercase tracking-widest">Grade B (Mids) (kg)</p>
                        </div>
                        <div className="bg-zinc-700/30 border border-zinc-600/30 p-3 text-center">
                          <p className="text-zinc-400 font-bold font-mono text-lg">{sub.grade_c_kg || 0}</p>
                          <p className="text-zinc-500 text-[10px] uppercase tracking-widest">Grade C (Smalls) (kg)</p>
                        </div>
                      </div>
                    )}

                    {/* Submitted Image */}
                    {sub.image_path && (
                      <div className="mb-4">
                        <p className="text-zinc-600 text-xs uppercase tracking-widest mb-2">Submitted Image</p>
                        <img src={sub.image_path.startsWith('/api/media/') ? `${API}/media/${sub.image_path.replace('/api/media/', '')}` : sub.image_path} alt={sub.strain_name} className="w-32 h-32 object-cover border border-white/10" />
                      </div>
                    )}

                    <div className="mb-4">
                      <p className="text-zinc-600 text-xs uppercase tracking-widest">Potential Terpene Effects</p>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {(typeof sub.effects === 'string' ? sub.effects.split(',').map(e => e.trim()).filter(Boolean) : (Array.isArray(sub.effects) ? sub.effects : [])).map((effect, i) => (
                          <span key={i} className="bg-gold-500/10 border border-gold-500/30 text-gold-500 text-xs px-2 py-0.5">{effect}</span>
                        ))}
                      </div>
                    </div>

                    {/* Grower Info */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 bg-zinc-900/50 p-4 border border-white/5">
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Grower</p>
                        <p className="text-zinc-300 text-sm">{sub.grower_name}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Company</p>
                        <p className="text-zinc-300 text-sm">{sub.company_name}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Email</p>
                        <p className="text-zinc-300 text-sm">{sub.email}</p>
                      </div>
                      <div>
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Phone</p>
                        <p className="text-zinc-300 text-sm">{sub.phone}</p>
                      </div>
                    </div>

                    {sub.additional_notes && (
                      <div className="mb-4">
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Notes from Grower</p>
                        <p className="text-zinc-400 text-sm italic">{sub.additional_notes}</p>
                      </div>
                    )}

                    {/* Uploaded Documents & COA Redaction */}
                    {(sub.coa_path || sub.health_canada_license_path || sub.cra_license_path) && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-3 mb-3">
                          {sub.coa_path && (
                            <a href={`${API}/files/${sub.coa_path.replace('/uploads/', '')}`} target="_blank" rel="noopener noreferrer" data-testid={`doc-coa-${sub.id}`}
                              className="flex items-center gap-2 bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-300 hover:border-gold-500/50 transition-colors"
                              onClick={(e) => { e.preventDefault(); openSecureFile(sub.coa_path); }}>
                              <FileText className="w-4 h-4 text-gold-500" />
                              {sub.coa_redacted ? 'COA (Redacted)' : 'COA (Original)'}
                            </a>
                          )}
                          {sub.health_canada_license_path && (
                            <a href="#" data-testid={`doc-hc-${sub.id}`}
                              className="flex items-center gap-2 bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-300 hover:border-gold-500/50 transition-colors"
                              onClick={(e) => { e.preventDefault(); openSecureFile(sub.health_canada_license_path); }}>
                              <FileText className="w-4 h-4 text-cannabis-green" />
                              HC License
                            </a>
                          )}
                          {sub.cra_license_path && (
                            <a href="#" data-testid={`doc-cra-${sub.id}`}
                              className="flex items-center gap-2 bg-zinc-900 border border-white/10 px-3 py-2 text-xs text-zinc-300 hover:border-gold-500/50 transition-colors"
                              onClick={(e) => { e.preventDefault(); openSecureFile(sub.cra_license_path); }}>
                              <FileText className="w-4 h-4 text-blue-400" />
                              CRA License
                            </a>
                          )}
                        </div>

                        {/* COA Redaction Upload */}
                        {sub.coa_path && sub.status === 'pending' && (
                          <div data-testid={`coa-redaction-${sub.id}`} className={`p-3 border ${sub.coa_redacted ? 'border-cannabis-green/30 bg-cannabis-green/5' : 'border-gold-500/30 bg-gold-500/5'}`}>
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2">
                                {sub.coa_redacted ? (
                                  <>
                                    <CheckCircle className="w-4 h-4 text-cannabis-green" />
                                    <span className="text-cannabis-green text-xs font-bold uppercase tracking-widest">Redacted COA uploaded</span>
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw className="w-4 h-4 text-gold-500" />
                                    <span className="text-gold-500 text-xs font-bold uppercase tracking-widest">Upload redacted COA before approving</span>
                                  </>
                                )}
                              </div>
                              <label
                                data-testid={`upload-redacted-coa-${sub.id}`}
                                className="cursor-pointer flex items-center gap-2 bg-zinc-900 border border-white/10 hover:border-gold-500/50 px-3 py-2 text-xs text-zinc-300 transition-colors"
                              >
                                <Upload className="w-3.5 h-3.5" />
                                {sub.coa_redacted ? 'Replace' : 'Upload Redacted'}
                                <input type="file" accept=".pdf" className="hidden" onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  const formData = new FormData();
                                  formData.append('coa', file);
                                  try {
                                    await axios.put(`${API}/admin/submissions/${sub.id}/redacted-coa`, formData, {
                                      headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' }
                                    });
                                    toast.success('Redacted COA uploaded');
                                    fetchSubmissions(adminToken);
                                  } catch { toast.error('Failed to upload redacted COA'); }
                                  e.target.value = '';
                                }} />
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {sub.target_price_per_gram && (
                      <div className="mb-4 bg-zinc-800/50 border border-zinc-700 p-3 inline-block mr-3">
                        <p className="text-zinc-500 text-xs uppercase tracking-widest">Grower Target Price</p>
                        <p className="text-white font-mono font-bold">${Number(sub.target_price_per_gram).toFixed(2)} / g</p>
                      </div>
                    )}

                    {sub.admin_price_per_gram && (
                      <div className="mb-4 bg-gold-500/10 border border-gold-500/30 p-3 inline-block">
                        <p className="text-gold-500 text-xs uppercase tracking-widest">Admin Price/Gram</p>
                        <p className="text-gold-500 font-mono font-bold">${sub.admin_price_per_gram}/g</p>
                      </div>
                    )}

                    {sub.admin_notes && (
                      <div className="mb-4">
                        <p className="text-zinc-600 text-xs uppercase tracking-widest">Admin Notes</p>
                        <p className="text-zinc-400 text-sm">{sub.admin_notes}</p>
                      </div>
                    )}

                    {/* Action Buttons for Pending */}
                    {sub.status === 'pending' && (
                      <>
                        {reviewingSubmission === sub.id ? (
                          <div className="border-t border-white/5 pt-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block font-manrope text-xs uppercase tracking-widest text-gold-500 mb-2">
                                  <DollarSign className="w-4 h-4 inline mr-1" /> Price per Gram (Admin Evaluation) *
                                </label>
                                <input
                                  data-testid={`price-per-gram-${sub.id}`}
                                  type="text"
                                  value={pricePerGram}
                                  onChange={(e) => setPricePerGram(e.target.value)}
                                  className="w-full bg-zinc-900/50 border border-gold-500/50 focus:border-gold-500 text-white h-12 px-4 outline-none"
                                  placeholder="e.g., 6.50"
                                />
                                <p className="text-zinc-600 text-xs mt-1">Sets price tier: &lt;$5 = $, $5-$8 = $$, $8+ = $$$</p>
                              </div>
                              <div>
                                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Admin Notes (optional)</label>
                                <input
                                  data-testid={`admin-notes-${sub.id}`}
                                  type="text"
                                  value={adminNotes}
                                  onChange={(e) => setAdminNotes(e.target.value)}
                                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                                  placeholder="Internal notes..."
                                />
                              </div>
                            </div>
                            <div className="flex gap-3">
                              <button
                                data-testid={`approve-submission-${sub.id}`}
                                onClick={() => handleSubmissionAction(sub.id, 'approved')}
                                disabled={processingSubmission}
                                className="flex-1 bg-cannabis-green hover:bg-green-600 disabled:bg-zinc-700 text-white font-bold uppercase tracking-widest px-6 py-3 transition-all flex items-center justify-center gap-2"
                              >
                                <CheckCircle className="w-4 h-4" />
                                {processingSubmission ? 'Processing...' : 'Approve & Add to Catalog'}
                              </button>
                              <button
                                data-testid={`reject-submission-${sub.id}`}
                                onClick={() => handleSubmissionAction(sub.id, 'rejected')}
                                disabled={processingSubmission}
                                className="bg-red-500/20 hover:bg-red-500/40 text-red-400 font-bold uppercase tracking-widest px-6 py-3 transition-all flex items-center justify-center gap-2"
                              >
                                <XCircle className="w-4 h-4" />
                                Reject
                              </button>
                              <button
                                onClick={() => { setReviewingSubmission(null); setPricePerGram(''); setAdminNotes(''); }}
                                className="bg-zinc-800 hover:bg-zinc-700 text-zinc-400 font-bold uppercase tracking-widest px-6 py-3 transition-all"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            data-testid={`review-submission-${sub.id}`}
                            onClick={() => setReviewingSubmission(sub.id)}
                            className="border-2 border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black font-bold uppercase tracking-widest px-6 py-3 transition-all flex items-center gap-2"
                          >
                            <ClipboardList className="w-4 h-4" />
                            Review & Authorize
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Directory Tab */}
        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Inquiry Hub</h2>
                <p className="text-zinc-500 text-sm">
                  {adminInquiries.length} total · {adminInquiries.filter(i => i.status === 'pending').length} pending response
                </p>
              </div>
              <div className="flex gap-3 items-center w-full md:w-auto">
                <input
                  data-testid="inquiry-hub-search"
                  type="text"
                  placeholder="Search by name, company, strain..."
                  value={inquirySearch}
                  onChange={(e) => setInquirySearch(e.target.value)}
                  className="bg-zinc-900 border border-white/10 focus:border-gold-500 text-white px-4 py-2 text-sm outline-none flex-1 md:w-64"
                />
                <select
                  data-testid="inquiry-hub-filter"
                  value={inquiryFilter}
                  onChange={(e) => setInquiryFilter(e.target.value)}
                  className="bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm outline-none"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="responded">Responded</option>
                  <option value="converted">Converted</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
            <SortButtons
              testPrefix="inquiries"
              sortState={inquiriesSort}
              onSort={makeSortHandler(setInquiriesSort)}
              options={[
                { key: 'created_at', label: 'Date' },
                { key: 'contact_name', label: 'Name' },
                { key: 'company_name', label: 'Company' },
                { key: 'status', label: 'Status' },
              ]}
            />

            {adminInquiries.length === 0 ? (
              <div className="bg-charcoal border border-white/5 p-12 text-center">
                <Mail className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400">No inquiries yet</p>
                <p className="text-zinc-600 text-sm mt-1">Inquiries from users will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sortData(adminInquiries
                  .filter(inq => {
                    if (inquiryFilter !== 'all' && inq.status !== inquiryFilter) return false;
                    if (inquirySearch) {
                      const q = inquirySearch.toLowerCase();
                      return (
                        (inq.contact_name || '').toLowerCase().includes(q) ||
                        (inq.company_name || '').toLowerCase().includes(q) ||
                        (inq.email || '').toLowerCase().includes(q) ||
                        (inq.strain_names || []).some(s => s.toLowerCase().includes(q)) ||
                        (inq.message || '').toLowerCase().includes(q)
                      );
                    }
                    return true;
                  }), inquiriesSort)
                  .map((inq) => (
                  <div 
                    key={inq.id} 
                    data-testid={`inquiry-card-${inq.id}`}
                    className={`bg-charcoal border ${inq.status === 'pending' ? 'border-gold-500/30' : 'border-white/5'} hover:border-white/15 transition-colors`}
                  >
                    {/* Inquiry Header */}
                    <div 
                      className="flex flex-col md:flex-row items-start md:items-center gap-4 p-5 cursor-pointer"
                      onClick={() => setExpandedInquiry(expandedInquiry === inq.id ? null : inq.id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-bold text-white text-base truncate">{inq.contact_name}</h3>
                          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${
                            inq.status === 'pending' ? 'bg-gold-500/20 text-gold-500' :
                            inq.status === 'responded' ? 'bg-cannabis-green/20 text-cannabis-green' :
                            inq.status === 'converted' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-zinc-700/30 text-zinc-500'
                          }`}>
                            {inq.status}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-zinc-400 text-sm">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5" />{inq.company_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" />{inq.email}
                          </span>
                          {inq.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" />{inq.phone}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-600 text-xs">
                            {inq.created_at ? new Date(inq.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                          <button
                            data-testid={`delete-inquiry-${inq.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Are you sure you want to permanently delete this inquiry?')) {
                                axios.delete(`${API}/admin/inquiries/${inq.id}`, { headers: { Authorization: `Bearer ${adminToken}` } })
                                  .then(() => { toast.success('Inquiry deleted'); setAdminInquiries(prev => prev.filter(i => i.id !== inq.id)); })
                                  .catch(() => toast.error('Failed to delete inquiry'));
                              }
                            }}
                            className="w-7 h-7 flex items-center justify-center bg-red-600/20 hover:bg-red-600 text-red-400 hover:text-white transition-all"
                            title="Delete inquiry"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1 justify-end">
                          {(inq.strain_names || []).map((name, idx) => {
                            const strainId = (inq.strain_ids || [])[idx];
                            const volume = strainId && inq.strain_volumes ? inq.strain_volumes[strainId] : null;
                            return (
                              <span key={idx} className="bg-gold-500/10 border border-gold-500/20 text-gold-500 text-[10px] font-bold px-2 py-0.5">
                                {name}{volume ? ` · ${volume}kg` : ''}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Detail */}
                    {expandedInquiry === inq.id && (
                      <div className="border-t border-white/5 p-5 bg-zinc-900/30">
                        {/* Message */}
                        <div className="mb-5">
                          <p className="text-zinc-600 text-xs uppercase tracking-widest mb-2 font-bold">Message</p>
                          <p className="text-zinc-300 text-sm leading-relaxed bg-zinc-900/50 border border-white/5 p-4">{inq.message}</p>
                        </div>

                        {/* Market of Interest */}
                        {inq.market_of_interest && (
                          <div className="mb-5">
                            <p className="text-zinc-600 text-xs uppercase tracking-widest mb-2 font-bold">Market of Interest</p>
                            <p className="text-gold-500 text-sm font-bold bg-gold-500/5 border border-gold-500/20 px-4 py-2">{inq.market_of_interest}</p>
                          </div>
                        )}

                        {/* User Account Details */}
                        {inq.user_id && (
                          <div className="mb-5">
                            <p className="text-zinc-600 text-xs uppercase tracking-widest mb-2 font-bold">Registered Account</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div>
                                <p className="text-zinc-600 text-[10px] uppercase">Name</p>
                                <p className="text-zinc-200">{inq.user_first_name || ''} {inq.user_last_name || ''}</p>
                              </div>
                              <div>
                                <p className="text-zinc-600 text-[10px] uppercase">Company</p>
                                <p className="text-zinc-200">{inq.user_company || inq.company_name}</p>
                              </div>
                              <div>
                                <p className="text-zinc-600 text-[10px] uppercase">Email</p>
                                <a href={`mailto:${inq.user_email || inq.email}`} className="text-gold-500 hover:underline">{inq.user_email || inq.email}</a>
                              </div>
                              <div>
                                <p className="text-zinc-600 text-[10px] uppercase">Phone</p>
                                <a href={`tel:${inq.user_phone || inq.phone}`} className="text-gold-500 hover:underline">{inq.user_phone || inq.phone || 'N/A'}</a>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Strains Inquired About */}
                        <div className="mb-5">
                          <p className="text-zinc-600 text-xs uppercase tracking-widest mb-2 font-bold">Strains Requested ({(inq.strain_names || []).length})</p>
                          <div className="space-y-2">
                            {(inq.strain_names || []).map((name, idx) => {
                              const strainId = (inq.strain_ids || [])[idx];
                              const volume = strainId && inq.strain_volumes ? inq.strain_volumes[strainId] : null;
                              return (
                                <div key={idx} className="flex items-center justify-between bg-gold-500/5 border border-gold-500/20 px-4 py-2">
                                  <span className="text-gold-500 font-bold text-sm">{name}</span>
                                  {volume ? (
                                    <span className="text-white font-mono text-sm bg-gold-500/20 px-3 py-0.5">{volume} kg</span>
                                  ) : (
                                    <span className="text-zinc-600 text-xs italic">No volume specified</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Admin Notes */}
                        {inq.admin_notes && (
                          <div className="mb-5">
                            <p className="text-zinc-600 text-xs uppercase tracking-widest mb-2 font-bold">Admin Notes</p>
                            <p className="text-zinc-400 text-sm bg-zinc-900/50 border border-white/5 p-3">{inq.admin_notes}</p>
                          </div>
                        )}

                        {/* Action Area */}
                        <div className="border-t border-white/5 pt-4 mt-4">
                          <div className="flex flex-col md:flex-row gap-3">
                            <textarea
                              data-testid={`inquiry-notes-${inq.id}`}
                              value={expandedInquiry === inq.id ? inquiryNotes : ''}
                              onChange={(e) => setInquiryNotes(e.target.value)}
                              rows={2}
                              placeholder="Add a note (optional)..."
                              className="flex-1 bg-zinc-900 border border-white/10 focus:border-gold-500 text-white text-sm px-4 py-2 outline-none resize-none"
                            />
                            <div className="flex gap-2">
                              {inq.status !== 'responded' && (
                                <button
                                  data-testid={`inquiry-respond-${inq.id}`}
                                  onClick={() => updateInquiryStatus(inq.id, 'responded', inquiryNotes)}
                                  disabled={processingInquiry === inq.id}
                                  className="bg-cannabis-green hover:bg-green-600 text-white px-5 py-2 font-bold uppercase tracking-widest text-xs flex items-center gap-2 transition-colors whitespace-nowrap disabled:opacity-50"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Mark Responded
                                </button>
                              )}
                              {inq.status !== 'archived' && (
                                <button
                                  data-testid={`inquiry-archive-${inq.id}`}
                                  onClick={() => updateInquiryStatus(inq.id, 'archived', inquiryNotes)}
                                  disabled={processingInquiry === inq.id}
                                  className="bg-zinc-700 hover:bg-zinc-600 text-white px-5 py-2 font-bold uppercase tracking-widest text-xs flex items-center gap-2 transition-colors whitespace-nowrap disabled:opacity-50"
                                >
                                  Archive
                                </button>
                              )}
                              {inq.status !== 'pending' && (
                                <button
                                  data-testid={`inquiry-reopen-${inq.id}`}
                                  onClick={() => updateInquiryStatus(inq.id, 'pending', inquiryNotes)}
                                  disabled={processingInquiry === inq.id}
                                  className="border border-gold-500/50 text-gold-500 hover:bg-gold-500/10 px-5 py-2 font-bold uppercase tracking-widest text-xs flex items-center gap-2 transition-colors whitespace-nowrap disabled:opacity-50"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                  Reopen
                                </button>
                              )}
                              {inq.status !== 'converted' && (
                                <button
                                  data-testid={`inquiry-convert-${inq.id}`}
                                  onClick={() => { setConvertingInquiry(inq); setConversionData({ sale_amount: '', total_grams: '', commission_type: '', commission_value: '' }); }}
                                  className="bg-gold-500 hover:bg-gold-400 text-black px-5 py-2 font-bold uppercase tracking-widest text-xs flex items-center gap-2 transition-colors whitespace-nowrap"
                                >
                                  Convert to Sale
                                </button>
                              )}
                              {inq.status === 'converted' && (
                                <span className="bg-gold-500/20 text-gold-500 px-4 py-2 text-xs font-bold uppercase tracking-widest">
                                  Converted — ${inq.sale_amount?.toLocaleString() || '0'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'directory' && (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Strain Directory</h2>
                <p className="text-zinc-500 text-sm">{directory.length} strains · Quick access to all documents & grower contacts</p>
              </div>
              <div className="flex gap-3 items-center w-full md:w-auto">
                <input
                  data-testid="directory-search"
                  type="text"
                  placeholder="Search strains..."
                  value={directorySearch}
                  onChange={(e) => setDirectorySearch(e.target.value)}
                  className="bg-zinc-900 border border-white/10 focus:border-gold-500 text-white px-4 py-2 text-sm outline-none flex-1 md:w-64"
                />
                <button
                  data-testid="directory-csv-export"
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/admin/strain-directory/csv`, {
                        headers: { Authorization: `Bearer ${adminToken}` },
                        responseType: 'blob'
                      });
                      const url = URL.createObjectURL(response.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `firegold_strain_directory_${new Date().toISOString().slice(0,10)}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('CSV downloaded');
                    } catch { toast.error('Failed to export CSV'); }
                  }}
                  className="bg-gold-500 hover:bg-gold-600 text-black px-4 py-2 font-bold uppercase tracking-widest text-xs flex items-center gap-2 transition-colors whitespace-nowrap"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
            </div>
            <SortButtons
              testPrefix="directory"
              sortState={directorySort}
              onSort={makeSortHandler(setDirectorySort)}
              options={[
                { key: 'strain_name', label: 'Name' },
                { key: 'type', label: 'Type' },
                { key: 'thc_percentage', label: 'THC' },
                { key: 'lot_size', label: 'Lot Size' },
              ]}
            />

            {directory.length === 0 ? (
              <div className="bg-charcoal border border-white/5 p-12 text-center">
                <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400">Loading directory...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sortData(directory
                  .filter(s => !directorySearch || s.strain_name.toLowerCase().includes(directorySearch.toLowerCase())), directorySort)
                  .map((entry) => (
                  <div key={entry.id} data-testid={`directory-entry-${entry.id}`} className="bg-charcoal border border-white/5 hover:border-white/10 transition-colors">
                    {/* Strain Header */}
                    <div className="flex items-center gap-4 p-4 border-b border-white/5">
                      {entry.image_url && (
                        <img src={entry.image_url} alt={entry.strain_name} className="w-12 h-12 object-cover border border-white/10 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-cinzel font-bold text-lg text-white truncate">{entry.strain_name}</h3>
                        <p className="text-zinc-500 text-xs">{entry.type} · THC {entry.thc_percentage} · {entry.lot_size}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-0">
                      {/* Documents Column */}
                      <div className="p-4 border-b md:border-b-0 md:border-r border-white/5">
                        <p className="text-zinc-600 text-xs uppercase tracking-widest mb-3 font-bold">Documents</p>
                        <div className="flex flex-wrap gap-2">
                          {entry.coa_url ? (
                            <button
                              data-testid={`dir-coa-${entry.id}`}
                              onClick={() => {
                                const token = localStorage.getItem('userToken') || adminToken;
                                axios.get(`${API}/strains/${entry.id}/coa`, {
                                  headers: { Authorization: `Bearer ${token}` },
                                  responseType: 'blob'
                                }).then(res => {
                                  const url = URL.createObjectURL(res.data);
                                  window.open(url, '_blank');
                                }).catch(() => toast.error('Unable to load COA'));
                              }}
                              className="flex items-center gap-2 bg-cannabis-green/15 border border-cannabis-green/40 hover:bg-cannabis-green/30 px-3 py-2 text-xs text-cannabis-green transition-colors"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              COA
                            </button>
                          ) : (
                            <span className="flex items-center gap-2 bg-zinc-900 border border-white/5 px-3 py-2 text-xs text-zinc-600">
                              <FileText className="w-3.5 h-3.5" />
                              No COA
                            </span>
                          )}
                          {entry.health_canada_license_path ? (
                            <button
                              data-testid={`dir-hc-${entry.id}`}
                              onClick={() => openSecureFile(entry.health_canada_license_path)}
                              className="flex items-center gap-2 bg-blue-500/15 border border-blue-500/40 hover:bg-blue-500/30 px-3 py-2 text-xs text-blue-400 transition-colors"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              HC License
                            </button>
                          ) : (
                            <span className="flex items-center gap-2 bg-zinc-900 border border-white/5 px-3 py-2 text-xs text-zinc-600">
                              <FileText className="w-3.5 h-3.5" />
                              No HC License
                            </span>
                          )}
                          {entry.cra_license_path ? (
                            <button
                              data-testid={`dir-cra-${entry.id}`}
                              onClick={() => openSecureFile(entry.cra_license_path)}
                              className="flex items-center gap-2 bg-purple-500/15 border border-purple-500/40 hover:bg-purple-500/30 px-3 py-2 text-xs text-purple-400 transition-colors"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              CRA License
                            </button>
                          ) : (
                            <span className="flex items-center gap-2 bg-zinc-900 border border-white/5 px-3 py-2 text-xs text-zinc-600">
                              <FileText className="w-3.5 h-3.5" />
                              No CRA License
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Grower/LP Contact Column */}
                      <div className="p-4">
                        <p className="text-zinc-600 text-xs uppercase tracking-widest mb-3 font-bold">Grower / LP Contact</p>
                        {entry.grower_name || entry.company_name ? (
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                            <div>
                              <p className="text-zinc-600 text-[10px] uppercase tracking-widest">Name</p>
                              <p className="text-zinc-200 text-sm">{entry.grower_name || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-zinc-600 text-[10px] uppercase tracking-widest">Company</p>
                              <p className="text-zinc-200 text-sm">{entry.company_name || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-zinc-600 text-[10px] uppercase tracking-widest">Email</p>
                              <a href={`mailto:${entry.grower_email}`} className="text-gold-500 text-sm hover:underline">{entry.grower_email || 'N/A'}</a>
                            </div>
                            <div>
                              <p className="text-zinc-600 text-[10px] uppercase tracking-widest">Phone</p>
                              <a href={`tel:${entry.grower_phone}`} className="text-gold-500 text-sm hover:underline">{entry.grower_phone || 'N/A'}</a>
                            </div>
                            <div className="col-span-2">
                              <p className="text-zinc-600 text-[10px] uppercase tracking-widest">License #</p>
                              {entry.license_number ? (
                                <p className="text-zinc-200 text-sm font-mono">{entry.license_number}</p>
                              ) : editingDirectoryLicense === entry.id ? (
                                <div className="flex gap-2 mt-1">
                                  <input
                                    data-testid={`dir-license-input-${entry.id}`}
                                    type="text"
                                    value={directoryLicenseForm.license_number || ''}
                                    onChange={(e) => setDirectoryLicenseForm(prev => ({ ...prev, license_number: e.target.value }))}
                                    className="flex-1 bg-zinc-900 border border-gold-500/50 text-white text-sm h-8 px-3 outline-none"
                                    placeholder="Enter license #"
                                    autoFocus
                                  />
                                  <button
                                    data-testid={`dir-license-save-${entry.id}`}
                                    onClick={() => updateDirectoryLicense(entry.id)}
                                    className="bg-gold-500 text-black px-3 h-8 text-xs font-bold uppercase"
                                  >Save</button>
                                  <button
                                    onClick={() => { setEditingDirectoryLicense(null); setDirectoryLicenseForm({}); }}
                                    className="bg-zinc-700 text-white px-3 h-8 text-xs"
                                  >Cancel</button>
                                </div>
                              ) : (
                                <button
                                  data-testid={`dir-add-license-${entry.id}`}
                                  onClick={() => {
                                    setEditingDirectoryLicense(entry.id);
                                    setDirectoryLicenseForm({ license_number: '', grower_company: entry.company_name || '', grower_name: entry.grower_name || '', grower_email: entry.grower_email || '' });
                                  }}
                                  className="text-gold-500 text-xs hover:underline cursor-pointer mt-1 flex items-center gap-1"
                                >
                                  <Plus className="w-3 h-3" /> Add License #
                                </button>
                              )}
                            </div>
                            {entry.gacp_compliant && (
                              <div>
                                <p className="text-zinc-600 text-[10px] uppercase tracking-widest">GACP</p>
                                <p className={`text-sm font-medium ${entry.gacp_compliant === 'Yes' ? 'text-cannabis-green' : 'text-red-400'}`}>{entry.gacp_compliant}</p>
                              </div>
                            )}
                          </div>
                        ) : editingDirectoryLicense === entry.id ? (
                          <div className="space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <input type="text" value={directoryLicenseForm.grower_company || ''} onChange={(e) => setDirectoryLicenseForm(prev => ({ ...prev, grower_company: e.target.value }))}
                                className="bg-zinc-900 border border-zinc-700 text-white text-sm h-8 px-3 outline-none" placeholder="Company" />
                              <input type="text" value={directoryLicenseForm.grower_name || ''} onChange={(e) => setDirectoryLicenseForm(prev => ({ ...prev, grower_name: e.target.value }))}
                                className="bg-zinc-900 border border-zinc-700 text-white text-sm h-8 px-3 outline-none" placeholder="Contact name" />
                              <input type="email" value={directoryLicenseForm.grower_email || ''} onChange={(e) => setDirectoryLicenseForm(prev => ({ ...prev, grower_email: e.target.value }))}
                                className="bg-zinc-900 border border-zinc-700 text-white text-sm h-8 px-3 outline-none" placeholder="Email" />
                              <input type="text" value={directoryLicenseForm.license_number || ''} onChange={(e) => setDirectoryLicenseForm(prev => ({ ...prev, license_number: e.target.value }))}
                                className="bg-zinc-900 border border-zinc-700 text-white text-sm h-8 px-3 outline-none" placeholder="License #" />
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => updateDirectoryLicense(entry.id)} className="bg-gold-500 text-black px-3 py-1.5 text-xs font-bold uppercase">Save</button>
                              <button onClick={() => { setEditingDirectoryLicense(null); setDirectoryLicenseForm({}); }} className="bg-zinc-700 text-white px-3 py-1.5 text-xs">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <p className="text-zinc-600 text-sm italic">No grower info linked</p>
                            <button
                              data-testid={`dir-add-grower-${entry.id}`}
                              onClick={() => {
                                setEditingDirectoryLicense(entry.id);
                                setDirectoryLicenseForm({});
                              }}
                              className="text-gold-500 text-xs hover:underline flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3" /> Add Info
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {activeTab === 'messages' && (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Messages</h2>
                <p className="text-zinc-500 text-sm">{contactMessages.length} messages · {contactMessages.filter(m => m.status === 'unread').length} unread</p>
              </div>
              <div className="flex gap-2">
                {['all', 'unread', 'read', 'replied'].map(f => (
                  <button key={f} onClick={() => setMessagesFilter(f)}
                    data-testid={`messages-filter-${f}`}
                    className={`px-4 py-2 text-xs uppercase tracking-widest font-bold transition-all ${messagesFilter === f ? 'bg-gold-500 text-black' : 'bg-zinc-900 text-zinc-400 hover:text-white border border-white/5'}`}>
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Reply Modal */}
            {replyingMessage && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setReplyingMessage(null); setReplyText(''); }} />
                <div data-testid="reply-message-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-lg p-8">
                  <button onClick={() => { setReplyingMessage(null); setReplyText(''); }} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"><X className="w-5 h-5 text-black" /></button>
                  <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest mb-2">Reply</h2>
                  <p className="text-zinc-500 text-sm mb-4">To: {replyingMessage.name} ({replyingMessage.email})</p>
                  <div className="bg-zinc-900 border border-white/5 p-4 mb-4">
                    <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Original Message</p>
                    <p className="text-zinc-300 text-sm">{replyingMessage.message}</p>
                  </div>
                  <textarea
                    data-testid="reply-text-input"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    rows={5}
                    className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white px-4 py-3 outline-none text-sm resize-none mb-4"
                  />
                  <button
                    data-testid="send-reply-btn"
                    onClick={handleReplyMessage}
                    disabled={sendingReply || !replyText.trim()}
                    className="w-full bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 text-black font-bold uppercase tracking-widest px-6 py-3 transition-all"
                  >
                    {sendingReply ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </div>
            )}

            {sortData(
              contactMessages.filter(m => messagesFilter === 'all' || m.status === messagesFilter),
              messagesSort
            ).length === 0 ? (
              <div className="bg-zinc-900/30 border border-white/5 p-12 text-center">
                <MessageCircle className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No messages found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Sort headers */}
                <div className="grid grid-cols-12 gap-4 px-6 py-3 text-xs uppercase tracking-widest text-zinc-600 border-b border-white/5">
                  <div className="col-span-2 cursor-pointer hover:text-gold-500" onClick={() => makeSortHandler(setMessagesSort)('name')}>
                    Name {messagesSort.key === 'name' && (messagesSort.dir === 'asc' ? '↑' : '↓')}
                  </div>
                  <div className="col-span-2 cursor-pointer hover:text-gold-500" onClick={() => makeSortHandler(setMessagesSort)('email')}>
                    Email {messagesSort.key === 'email' && (messagesSort.dir === 'asc' ? '↑' : '↓')}
                  </div>
                  <div className="col-span-1 cursor-pointer hover:text-gold-500" onClick={() => makeSortHandler(setMessagesSort)('company')}>
                    Company {messagesSort.key === 'company' && (messagesSort.dir === 'asc' ? '↑' : '↓')}
                  </div>
                  <div className="col-span-3">Message</div>
                  <div className="col-span-1 cursor-pointer hover:text-gold-500" onClick={() => makeSortHandler(setMessagesSort)('status')}>
                    Status {messagesSort.key === 'status' && (messagesSort.dir === 'asc' ? '↑' : '↓')}
                  </div>
                  <div className="col-span-1 cursor-pointer hover:text-gold-500" onClick={() => makeSortHandler(setMessagesSort)('created_at')}>
                    Date {messagesSort.key === 'created_at' && (messagesSort.dir === 'asc' ? '↑' : '↓')}
                  </div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                {sortData(
                  contactMessages.filter(m => messagesFilter === 'all' || m.status === messagesFilter),
                  messagesSort
                ).map(msg => (
                  <div key={msg.id} data-testid={`message-row-${msg.id}`}
                    className={`grid grid-cols-12 gap-4 px-6 py-4 border border-white/5 items-center transition-all hover:border-gold-500/30 ${msg.status === 'unread' ? 'bg-zinc-900/60 border-l-2 border-l-gold-500' : 'bg-zinc-900/20'}`}>
                    <div className="col-span-2">
                      <p className={`text-sm font-bold ${msg.status === 'unread' ? 'text-white' : 'text-zinc-400'}`}>{msg.name}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-zinc-400 text-sm truncate">{msg.email}</p>
                    </div>
                    <div className="col-span-1">
                      <p className="text-zinc-500 text-sm truncate">{msg.company || '—'}</p>
                    </div>
                    <div className="col-span-3">
                      <p className="text-zinc-300 text-sm line-clamp-2">{msg.message}</p>
                    </div>
                    <div className="col-span-1">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${
                        msg.status === 'unread' ? 'bg-gold-500/20 text-gold-500' : 
                        msg.status === 'replied' ? 'bg-cannabis-green/20 text-cannabis-green' : 
                        'bg-zinc-700/50 text-zinc-500'
                      }`}>
                        {msg.status}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <p className="text-zinc-600 text-xs">{msg.created_at ? new Date(msg.created_at).toLocaleDateString() : '—'}</p>
                    </div>
                    <div className="col-span-2 flex gap-2 justify-end">
                      {msg.status === 'unread' && (
                        <button data-testid={`mark-read-${msg.id}`} onClick={() => handleMarkRead(msg.id)}
                          className="text-zinc-400 hover:text-gold-500 transition-colors" title="Mark as read">
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button data-testid={`reply-msg-${msg.id}`} onClick={() => { setReplyingMessage(msg); setReplyText(''); }}
                        className="text-zinc-400 hover:text-cannabis-green transition-colors" title="Reply">
                        <Send className="w-4 h-4" />
                      </button>
                      <button data-testid={`delete-msg-${msg.id}`} onClick={() => handleDeleteMessage(msg.id)}
                        className="text-zinc-400 hover:text-red-500 transition-colors" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {activeTab === 'bot-training' && (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Bot Training</h2>
                <p className="text-zinc-500 text-sm">{botTraining.length} custom Q&A entries — The chatbot uses these to answer visitor questions.</p>
              </div>
            </div>

            {/* Add New Entry Form */}
            <div className="bg-charcoal border border-white/10 p-6 mb-8" data-testid="add-training-form">
              <h3 className="font-cinzel font-bold text-sm text-gold-500 uppercase tracking-widest mb-4">Add Training Entry</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <select
                  data-testid="training-category-select"
                  value={newTraining.category}
                  onChange={(e) => setNewTraining(prev => ({...prev, category: e.target.value}))}
                  className="bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none"
                >
                  <option value="general">General</option>
                  <option value="pricing">Pricing</option>
                  <option value="products">Products</option>
                  <option value="compliance">Compliance</option>
                  <option value="shipping">Shipping & Logistics</option>
                  <option value="process">Process & How It Works</option>
                  <option value="custom">Custom Response</option>
                </select>
              </div>
              <div className="space-y-3 mb-4">
                <input
                  data-testid="training-question-input"
                  type="text"
                  placeholder="Question or topic (e.g., 'What are your payment terms?')"
                  value={newTraining.question}
                  onChange={(e) => setNewTraining(prev => ({...prev, question: e.target.value}))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none text-sm"
                />
                <textarea
                  data-testid="training-answer-input"
                  placeholder="How the bot should respond..."
                  value={newTraining.answer}
                  onChange={(e) => setNewTraining(prev => ({...prev, answer: e.target.value}))}
                  rows={3}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white px-4 py-3 outline-none text-sm resize-none"
                />
              </div>
              <button
                data-testid="add-training-btn"
                onClick={addTrainingEntry}
                disabled={!newTraining.question.trim() || !newTraining.answer.trim()}
                className="bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 text-black font-bold uppercase tracking-widest px-6 py-3 text-sm transition-all"
              >
                <Plus className="w-4 h-4 inline mr-2" />Add Entry
              </button>
            </div>

            {/* Training Entries List */}
            {botTraining.length === 0 ? (
              <div className="bg-zinc-900/30 border border-white/5 p-12 text-center">
                <Bot className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 mb-2">No custom training data yet</p>
                <p className="text-zinc-600 text-sm">The chatbot uses built-in Firegold knowledge. Add custom Q&A pairs above to teach it your specific responses.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {botTraining.map(entry => (
                  <div key={entry.id} data-testid={`training-entry-${entry.id}`}
                    className="bg-charcoal border border-white/5 p-5 hover:border-gold-500/30 transition-all">
                    {editingTraining === entry.id ? (
                      <div className="space-y-3">
                        <select
                          value={entry.category || 'general'}
                          onChange={(e) => setBotTraining(prev => prev.map(t => t.id === entry.id ? {...t, category: e.target.value} : t))}
                          className="bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-10 px-3 outline-none text-sm"
                        >
                          <option value="general">General</option>
                          <option value="pricing">Pricing</option>
                          <option value="products">Products</option>
                          <option value="compliance">Compliance</option>
                          <option value="shipping">Shipping & Logistics</option>
                          <option value="process">Process & How It Works</option>
                          <option value="custom">Custom Response</option>
                        </select>
                        <input
                          type="text"
                          value={entry.question}
                          onChange={(e) => setBotTraining(prev => prev.map(t => t.id === entry.id ? {...t, question: e.target.value} : t))}
                          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-10 px-3 outline-none text-sm"
                        />
                        <textarea
                          value={entry.answer}
                          onChange={(e) => setBotTraining(prev => prev.map(t => t.id === entry.id ? {...t, answer: e.target.value} : t))}
                          rows={3}
                          className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white px-3 py-2 outline-none text-sm resize-none"
                        />
                        <div className="flex gap-2">
                          <button onClick={() => updateTrainingEntry(entry)}
                            className="bg-cannabis-green/20 text-cannabis-green hover:bg-cannabis-green/30 px-4 py-2 text-xs uppercase tracking-widest font-bold transition-all">
                            <Save className="w-3 h-3 inline mr-1" />Save
                          </button>
                          <button onClick={() => setEditingTraining(null)}
                            className="bg-zinc-800 text-zinc-400 hover:text-white px-4 py-2 text-xs uppercase tracking-widest font-bold transition-all">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-gold-500/10 text-gold-500 border border-gold-500/20">
                              {entry.category || 'general'}
                            </span>
                          </div>
                          <p className="text-white font-bold text-sm mb-1">Q: {entry.question}</p>
                          <p className="text-zinc-400 text-sm">A: {entry.answer}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button data-testid={`edit-training-${entry.id}`} onClick={() => setEditingTraining(entry.id)}
                            className="text-zinc-500 hover:text-gold-500 transition-colors">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button data-testid={`delete-training-${entry.id}`} onClick={() => deleteTrainingEntry(entry.id)}
                            className="text-zinc-500 hover:text-red-500 transition-colors">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Saved Lists Tab */}
        {activeTab === 'saved-lists' && (
          <>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white mb-2">User Saved Lists</h2>
                <p className="text-zinc-500 text-sm">View all lists saved by users and analytics on common trends.</p>
              </div>
            </div>

            {loadingAdminLists ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="w-8 h-8 text-gold-500 animate-spin" />
              </div>
            ) : (
              <>
                {/* Analytics Summary */}
                {adminListsAnalytics && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-charcoal border border-white/10 p-6">
                      <p className="text-zinc-500 text-xs uppercase tracking-widest mb-1">Total Lists</p>
                      <p className="text-white font-mono text-3xl font-bold">{adminListsAnalytics.total_lists}</p>
                    </div>
                    <div className="bg-charcoal border border-white/10 p-6">
                      <p className="text-zinc-500 text-xs uppercase tracking-widest mb-1">Total Saves</p>
                      <p className="text-white font-mono text-3xl font-bold">{adminListsAnalytics.total_saves}</p>
                    </div>
                    <div className="bg-charcoal border border-white/10 p-6">
                      <p className="text-zinc-500 text-xs uppercase tracking-widest mb-1">Unique Users</p>
                      <p className="text-white font-mono text-3xl font-bold">{adminListsAnalytics.unique_users}</p>
                    </div>
                  </div>
                )}

                {/* Top Strains & Effects */}
                {adminListsAnalytics && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* Top Saved Strains */}
                    <div className="bg-charcoal border border-white/10 p-6">
                      <h3 className="font-cinzel font-bold text-sm text-gold-500 uppercase tracking-widest mb-4">
                        <BarChart3 className="w-4 h-4 inline mr-2" />Most Saved Strains
                      </h3>
                      {adminListsAnalytics.top_strains?.length > 0 ? (
                        <div className="space-y-2">
                          {adminListsAnalytics.top_strains.map((s, i) => (
                            <div key={s.strain_id} className="flex justify-between items-center py-1.5 border-b border-zinc-800 last:border-0">
                              <div className="flex items-center gap-2">
                                <span className="text-gold-500 font-mono text-xs w-5">{i + 1}.</span>
                                <span className="text-white text-sm">{s.strain_name}</span>
                                <span className="text-zinc-600 text-xs">({s.category})</span>
                              </div>
                              <span className="text-gold-500 font-mono text-sm font-bold">{s.save_count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-zinc-600 text-sm">No data yet</p>
                      )}
                    </div>

                    {/* Top Effects */}
                    <div className="bg-charcoal border border-white/10 p-6">
                      <h3 className="font-cinzel font-bold text-sm text-gold-500 uppercase tracking-widest mb-4">
                        <Activity className="w-4 h-4 inline mr-2" />Most Common Potential Terpene Effects
                      </h3>
                      {adminListsAnalytics.top_effects?.length > 0 ? (
                        <div className="space-y-2">
                          {adminListsAnalytics.top_effects.map((e, i) => (
                            <div key={e.effect} className="flex justify-between items-center py-1.5 border-b border-zinc-800 last:border-0">
                              <div className="flex items-center gap-2">
                                <span className="text-gold-500 font-mono text-xs w-5">{i + 1}.</span>
                                <span className="text-white text-sm">{e.effect}</span>
                              </div>
                              <span className="text-gold-500 font-mono text-sm font-bold">{e.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-zinc-600 text-sm">No data yet</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Category Breakdown */}
                {adminListsAnalytics?.category_breakdown?.length > 0 && (
                  <div className="bg-charcoal border border-white/10 p-6 mb-8">
                    <h3 className="font-cinzel font-bold text-sm text-gold-500 uppercase tracking-widest mb-4">Category Breakdown</h3>
                    <div className="flex flex-wrap gap-3">
                      {adminListsAnalytics.category_breakdown.map(c => (
                        <div key={c.category} className="bg-zinc-900/50 border border-zinc-700 px-4 py-2">
                          <p className="text-white text-sm font-bold">{c.category || 'Unknown'}</p>
                          <p className="text-gold-500 font-mono text-xs">{c.count} saves</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Lists */}
                <div className="bg-charcoal border border-white/10 p-6">
                  <h3 className="font-cinzel font-bold text-sm text-gold-500 uppercase tracking-widest mb-4">All User Lists ({adminLists.length})</h3>
                  {adminLists.length === 0 ? (
                    <p className="text-zinc-600 text-sm text-center py-8">No lists saved yet by users.</p>
                  ) : (
                    <div className="space-y-3">
                      {adminLists.map(list => (
                        <div key={list.id} data-testid={`admin-list-${list.id}`} className="bg-zinc-900/50 border border-zinc-800 p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="text-white font-manrope font-bold text-sm">{list.list_name}</h4>
                              <p className="text-zinc-500 text-xs mt-0.5">
                                {list.user_email} · {list.user_company} · {list.strain_count} strains · {new Date(list.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <button
                              data-testid={`admin-download-list-${list.id}`}
                              onClick={async () => {
                                try {
                                  const resp = await axios.get(`${API}/admin/lists/${list.id}/csv`, {
                                    headers: { Authorization: `Bearer ${adminToken}` },
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([resp.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `firegold_${list.list_name.replace(/\s+/g, '_')}.csv`;
                                  link.click();
                                  window.URL.revokeObjectURL(url);
                                  toast.success('CSV downloaded');
                                } catch { toast.error('Failed to download CSV'); }
                              }}
                              className="flex items-center gap-1 text-gold-500 hover:text-gold-400 text-xs border border-gold-500/30 px-2 py-1 hover:border-gold-500 transition-all"
                              title="Download CSV"
                            >
                              <Download className="w-3 h-3" /> CSV
                            </button>
                          </div>
                          {list.strain_names && list.strain_names.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {list.strain_names.slice(0, 8).map((name, i) => (
                                <span key={i} className="bg-zinc-800 text-zinc-400 text-[10px] px-1.5 py-0.5">{name}</span>
                              ))}
                              {list.strain_names.length > 8 && (
                                <span className="text-zinc-600 text-[10px] px-1.5 py-0.5">+{list.strain_names.length - 8} more</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* Compliance Document Vault Tab */}
        {activeTab === 'compliance' && (
          <>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white mb-2">License Vault</h2>
                <p className="text-zinc-500 text-sm">Central repository for all compliance documents</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  data-testid="sync-licenses-btn"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/admin/compliance-docs/sync-from-submissions`, {}, {
                        headers: { Authorization: `Bearer ${adminToken}` }
                      });
                      toast.success(res.data.message);
                      fetchComplianceDocs();
                    } catch (err) { toast.error('Failed to sync licenses'); }
                  }}
                  className="border border-gold-500/40 hover:bg-gold-500/10 text-gold-500 font-bold uppercase tracking-widest text-xs px-5 py-3 transition-all flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" /> Sync from Submissions
                </button>
                <button
                  data-testid="upload-compliance-doc-btn"
                  onClick={() => setShowUploadDoc(!showUploadDoc)}
                  className="bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest text-xs px-6 py-3 transition-all flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Upload Document
                </button>
              </div>
            </div>

            {showUploadDoc && (
              <form onSubmit={handleUploadComplianceDoc} className="bg-zinc-900/50 border border-zinc-800 p-6 mb-8 space-y-4" data-testid="compliance-upload-form">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Company Name *</label>
                    <input name="company_name" required className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-10 px-3 outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Grower Name</label>
                    <input name="grower_name" className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-10 px-3 outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Document Type *</label>
                    <select name="document_type" required className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-10 px-3 outline-none text-sm">
                      <option value="">Select type...</option>
                      <option value="health_canada_license">Health Canada License</option>
                      <option value="cra_license">CRA License</option>
                      <option value="bcdd_letter">BCDD Letter</option>
                      <option value="gacp_cert">GACP Certificate</option>
                      <option value="gmp_cert">GMP Certificate</option>
                      <option value="coa">Certificate of Analysis</option>
                      <option value="insurance">Insurance</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Document Number</label>
                    <input name="document_number" className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-10 px-3 outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Issue Date</label>
                    <input name="issue_date" type="date" className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-10 px-3 outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Expiry Date</label>
                    <input name="expiry_date" type="date" className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-10 px-3 outline-none text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Province</label>
                    <input name="province" className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-10 px-3 outline-none text-sm" placeholder="e.g., BC, ON" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">File *</label>
                    <input name="file" type="file" required className="w-full bg-zinc-900 border border-zinc-700 text-zinc-400 h-10 px-3 text-sm file:bg-gold-500/20 file:text-gold-500 file:border-0 file:mr-3 file:text-xs" />
                  </div>
                  <div className="flex items-end gap-4">
                    <label className="flex items-center gap-2 text-zinc-400 text-xs cursor-pointer">
                      <input name="is_gacp_certified" type="checkbox" value="true" className="accent-gold-500" /> GACP
                    </label>
                    <label className="flex items-center gap-2 text-zinc-400 text-xs cursor-pointer">
                      <input name="is_gmp_certified" type="checkbox" value="true" className="accent-gold-500" /> GMP
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Notes</label>
                  <textarea name="notes" rows={2} className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white px-3 py-2 outline-none text-sm" />
                </div>
                <div className="flex gap-3">
                  <button type="submit" className="bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase text-xs px-6 py-2 transition-all">Upload</button>
                  <button type="button" onClick={() => setShowUploadDoc(false)} className="text-zinc-500 hover:text-white text-xs uppercase">Cancel</button>
                </div>
              </form>
            )}

            {/* Filters */}
            <div className="flex gap-3 mb-6">
              <input
                data-testid="compliance-filter-company"
                placeholder="Filter by company..."
                value={complianceFilter.company}
                onChange={(e) => { const f = { ...complianceFilter, company: e.target.value }; setComplianceFilter(f); fetchComplianceDocs(f); }}
                className="bg-zinc-900 border border-zinc-800 text-white h-10 px-3 outline-none text-sm w-48 focus:border-gold-500"
              />
              <select
                data-testid="compliance-filter-type"
                value={complianceFilter.doc_type}
                onChange={(e) => { const f = { ...complianceFilter, doc_type: e.target.value }; setComplianceFilter(f); fetchComplianceDocs(f); }}
                className="bg-zinc-900 border border-zinc-800 text-white h-10 px-3 outline-none text-sm focus:border-gold-500"
              >
                <option value="">All Types</option>
                <option value="health_canada_license">Health Canada</option>
                <option value="cra_license">CRA</option>
                <option value="bcdd_letter">BCDD</option>
                <option value="gacp_cert">GACP</option>
                <option value="gmp_cert">GMP</option>
                <option value="coa">COA</option>
                <option value="insurance">Insurance</option>
                <option value="other">Other</option>
              </select>
              <input
                placeholder="Province..."
                value={complianceFilter.province}
                onChange={(e) => { const f = { ...complianceFilter, province: e.target.value }; setComplianceFilter(f); fetchComplianceDocs(f); }}
                className="bg-zinc-900 border border-zinc-800 text-white h-10 px-3 outline-none text-sm w-24 focus:border-gold-500"
              />
            </div>

            {/* Documents Table */}
            <div className="space-y-2">
              {complianceDocs.length === 0 && (
                <p className="text-zinc-600 text-sm py-8 text-center">No compliance documents found. Upload your first document above.</p>
              )}
              {complianceDocs.map(doc => {
                const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();
                const typeLabels = {
                  health_canada_license: 'Health Canada', cra_license: 'CRA', bcdd_letter: 'BCDD',
                  gacp_cert: 'GACP Cert', gmp_cert: 'GMP Cert', coa: 'COA', insurance: 'Insurance', other: 'Other'
                };
                return (
                  <div key={doc.id} data-testid={`compliance-doc-${doc.id}`} className={`bg-zinc-900/50 border p-4 flex items-center justify-between ${isExpired ? 'border-red-500/30' : 'border-zinc-800'}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-gold-500 font-bold text-sm">{doc.company_name}</span>
                        <span className="text-xs bg-zinc-800 text-zinc-300 px-2 py-0.5">{typeLabels[doc.document_type] || doc.document_type}</span>
                        {isExpired && <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5">EXPIRED</span>}
                        {doc.is_gacp_certified && <span className="text-xs bg-cannabis-green/20 text-cannabis-green px-2 py-0.5">GACP</span>}
                        {doc.is_gmp_certified && <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5">GMP</span>}
                      </div>
                      <div className="text-zinc-500 text-xs mt-1 flex items-center gap-4 flex-wrap">
                        {doc.grower_name && <span>{doc.grower_name}</span>}
                        {doc.document_number && <span>#{doc.document_number}</span>}
                        {doc.province && <span>{doc.province}</span>}
                        {doc.issue_date && <span>Issued: {doc.issue_date}</span>}
                        {doc.expiry_date && <span>Expires: {doc.expiry_date}</span>}
                      </div>
                      {doc.notes && <p className="text-zinc-600 text-xs mt-1">{doc.notes}</p>}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={async () => {
                          try {
                            const resp = await axios.get(`${API}/files/${doc.file_path}`, {
                              headers: { Authorization: `Bearer ${adminToken}` },
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(new Blob([resp.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = doc.original_filename || 'document';
                            link.click();
                            window.URL.revokeObjectURL(url);
                          } catch { toast.error('Download failed'); }
                        }}
                        className="text-gold-500 hover:text-gold-400 p-1"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteComplianceDoc(doc.id)}
                        className="text-zinc-600 hover:text-red-400 p-1"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* GACP / GMP Certifications Tab */}
        {activeTab === 'certifications' && (
          <>
            <div className="mb-8">
              <h2 className="font-cinzel font-bold text-2xl text-white mb-2">GACP / GMP Certifications</h2>
              <p className="text-zinc-500 text-sm">Track certified growers and products</p>
            </div>

            {certReport ? (
              <div className="space-y-8">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-cannabis-green/10 border border-cannabis-green/30 p-4 text-center">
                    <div className="text-cannabis-green font-bold text-2xl">{certReport.totals.gacp_strains}</div>
                    <div className="text-zinc-500 text-xs uppercase tracking-widest mt-1">GACP Strains</div>
                  </div>
                  <div className="bg-cannabis-green/10 border border-cannabis-green/30 p-4 text-center">
                    <div className="text-cannabis-green font-bold text-2xl">{certReport.totals.gacp_growers}</div>
                    <div className="text-zinc-500 text-xs uppercase tracking-widest mt-1">GACP Growers</div>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 p-4 text-center">
                    <div className="text-blue-400 font-bold text-2xl">{certReport.totals.gmp_strains}</div>
                    <div className="text-zinc-500 text-xs uppercase tracking-widest mt-1">GMP Strains</div>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 p-4 text-center">
                    <div className="text-blue-400 font-bold text-2xl">{certReport.totals.gmp_growers}</div>
                    <div className="text-zinc-500 text-xs uppercase tracking-widest mt-1">GMP Growers</div>
                  </div>
                </div>

                {/* GACP Section */}
                <div>
                  <h3 className="font-cinzel font-bold text-lg text-cannabis-green mb-4">
                    <Award className="w-5 h-5 inline mr-2" />GACP Certified
                  </h3>
                  {certReport.gacp.strains.length === 0 && certReport.gacp.growers.length === 0 ? (
                    <p className="text-zinc-600 text-sm">No GACP certifications yet. Mark strains or submissions as GACP certified via the strain editor or License Vault.</p>
                  ) : (
                    <div className="space-y-2">
                      {certReport.gacp.strains.map(s => (
                        <div key={s.id} className="bg-zinc-900/50 border border-zinc-800 p-3 flex justify-between items-center">
                          <div>
                            <span className="text-white font-bold text-sm">{s.strain_name}</span>
                            <span className="text-zinc-500 text-xs ml-3">{s.type}</span>
                            <span className="text-xs bg-cannabis-green/20 text-cannabis-green px-2 py-0.5 ml-2">Strain</span>
                          </div>
                          {s.certification_notes && <span className="text-zinc-600 text-xs">{s.certification_notes}</span>}
                        </div>
                      ))}
                      {certReport.gacp.growers.map(g => (
                        <div key={g.id} className="bg-zinc-900/50 border border-zinc-800 p-3 flex justify-between items-center">
                          <div>
                            <span className="text-white font-bold text-sm">{g.company_name}</span>
                            <span className="text-zinc-500 text-xs ml-3">{g.strain_name}</span>
                            <span className="text-xs bg-gold-500/20 text-gold-500 px-2 py-0.5 ml-2">Grower</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* GMP Section */}
                <div>
                  <h3 className="font-cinzel font-bold text-lg text-blue-400 mb-4">
                    <Award className="w-5 h-5 inline mr-2" />GMP Certified
                  </h3>
                  {certReport.gmp.strains.length === 0 && certReport.gmp.growers.length === 0 ? (
                    <p className="text-zinc-600 text-sm">No GMP certifications yet. Mark strains or submissions as GMP certified via the strain editor or License Vault.</p>
                  ) : (
                    <div className="space-y-2">
                      {certReport.gmp.strains.map(s => (
                        <div key={s.id} className="bg-zinc-900/50 border border-zinc-800 p-3 flex justify-between items-center">
                          <div>
                            <span className="text-white font-bold text-sm">{s.strain_name}</span>
                            <span className="text-zinc-500 text-xs ml-3">{s.type}</span>
                            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 ml-2">Strain</span>
                          </div>
                        </div>
                      ))}
                      {certReport.gmp.growers.map(g => (
                        <div key={g.id} className="bg-zinc-900/50 border border-zinc-800 p-3 flex justify-between items-center">
                          <div>
                            <span className="text-white font-bold text-sm">{g.company_name}</span>
                            <span className="text-zinc-500 text-xs ml-3">{g.strain_name}</span>
                            <span className="text-xs bg-gold-500/20 text-gold-500 px-2 py-0.5 ml-2">Grower</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-zinc-500 text-sm">Loading certification data...</p>
            )}
          </>
        )}

        {/* Deals Pipeline Tab */}
        {activeTab === 'deals' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white uppercase tracking-widest">Deal Pipeline</h2>
                <p className="text-zinc-500 text-sm">Manage buyer deal requests and track deal flow</p>
              </div>
              <div className="flex gap-2">
                {['all', 'submitted', 'reviewing', 'negotiating', 'accepted', 'completed', 'rejected'].map(s => (
                  <button
                    key={s}
                    onClick={() => { setDealFilter(s); fetchAdminDeals(s); }}
                    data-testid={`deal-filter-${s}`}
                    className={`px-3 py-1 text-[10px] uppercase tracking-widest font-bold transition-all ${
                      dealFilter === s ? 'bg-gold-500 text-black' : 'text-zinc-500 border border-zinc-800 hover:text-white'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {adminDeals.length === 0 ? (
              <div className="text-center py-16">
                <Layers className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm">No deals found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {adminDeals.map(deal => (
                  <div key={deal.id} data-testid={`admin-deal-${deal.id}`} className="bg-zinc-900/50 border border-zinc-800 p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-white font-bold text-lg">{deal.user_company || 'Unknown'}</h3>
                        <p className="text-zinc-400 text-sm">{deal.user_name} &middot; {deal.user_email}</p>
                        <p className="text-zinc-500 text-xs mt-1">{new Date(deal.created_at).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs uppercase tracking-widest font-bold px-3 py-1 ${
                          deal.status === 'submitted' ? 'bg-blue-500/20 text-blue-400' :
                          deal.status === 'reviewing' ? 'bg-gold-500/20 text-gold-500' :
                          deal.status === 'negotiating' ? 'bg-purple-500/20 text-purple-400' :
                          deal.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' :
                          deal.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>{deal.status}</span>
                        <p className="text-gold-500 font-mono font-bold text-xl mt-2">{deal.total_kg} kg</p>
                      </div>
                    </div>

                    {/* Deal items */}
                    <div className="bg-black/30 border border-zinc-800/50 p-3 mb-4">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-zinc-500 text-[10px] uppercase tracking-widest">
                            <th className="text-left py-1">Strain</th>
                            <th className="text-right py-1">Volume</th>
                            <th className="text-right py-1">Target Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deal.items.map((item, idx) => (
                            <tr key={idx} className="border-t border-zinc-800/50">
                              <td className="text-white py-2">{item.strain_name}</td>
                              <td className="text-zinc-300 font-mono text-right">{item.desired_kg} kg</td>
                              <td className="text-gold-500 font-mono text-right">{item.target_price_per_gram ? `$${item.target_price_per_gram}/g` : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {deal.message && <p className="text-zinc-400 text-sm mb-3"><span className="text-zinc-500 text-xs uppercase tracking-widest">Message:</span> {deal.message}</p>}
                    {deal.market_of_interest && <p className="text-zinc-400 text-sm mb-3"><span className="text-zinc-500 text-xs uppercase tracking-widest">Market:</span> {deal.market_of_interest}</p>}

                    {/* Status actions */}
                    <div className="flex gap-2 mt-3">
                      {deal.status === 'submitted' && (
                        <>
                          <button onClick={() => updateDealStatus(deal.id, 'reviewing')} className="px-4 py-1.5 bg-gold-500/20 text-gold-500 text-xs uppercase tracking-widest font-bold hover:bg-gold-500/30 transition-all">Start Review</button>
                          <button onClick={() => updateDealStatus(deal.id, 'rejected')} className="px-4 py-1.5 bg-red-500/10 text-red-400 text-xs uppercase tracking-widest font-bold hover:bg-red-500/20 transition-all">Reject</button>
                        </>
                      )}
                      {deal.status === 'reviewing' && (
                        <>
                          <button onClick={() => updateDealStatus(deal.id, 'negotiating')} className="px-4 py-1.5 bg-purple-500/20 text-purple-400 text-xs uppercase tracking-widest font-bold hover:bg-purple-500/30 transition-all">Move to Negotiation</button>
                          <button onClick={() => updateDealStatus(deal.id, 'rejected')} className="px-4 py-1.5 bg-red-500/10 text-red-400 text-xs uppercase tracking-widest font-bold hover:bg-red-500/20 transition-all">Reject</button>
                        </>
                      )}
                      {deal.status === 'negotiating' && (
                        <>
                          <button onClick={() => updateDealStatus(deal.id, 'accepted')} className="px-4 py-1.5 bg-emerald-500/20 text-emerald-400 text-xs uppercase tracking-widest font-bold hover:bg-emerald-500/30 transition-all">Accept Deal</button>
                          <button onClick={() => updateDealStatus(deal.id, 'rejected')} className="px-4 py-1.5 bg-red-500/10 text-red-400 text-xs uppercase tracking-widest font-bold hover:bg-red-500/20 transition-all">Reject</button>
                        </>
                      )}
                      {deal.status === 'accepted' && (
                        <button onClick={() => updateDealStatus(deal.id, 'completed')} className="px-4 py-1.5 bg-green-500/20 text-green-400 text-xs uppercase tracking-widest font-bold hover:bg-green-500/30 transition-all">Mark Complete</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Knowledge Library Tab */}
        {activeTab === 'knowledge' && (
          <>
            {/* Stats Bar */}
            {knowledgeStats && (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
                <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Total Entries</p>
                  <p className="text-white font-mono text-xl font-bold">{knowledgeStats.total}</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4 cursor-pointer hover:border-emerald-500/30 transition-all" onClick={() => { const f = {...knowledgeFilter, review_status: 'current', stale_only: false}; setKnowledgeFilter(f); fetchKnowledge(f); }}>
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Current</p>
                  <p className="text-emerald-400 font-mono text-xl font-bold">{knowledgeStats.current_count || 0}</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4 cursor-pointer hover:border-yellow-500/30 transition-all" onClick={() => { const f = {...knowledgeFilter, review_status: 'needs_review', stale_only: false}; setKnowledgeFilter(f); fetchKnowledge(f); }}>
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Needs Review</p>
                  <p className={`font-mono text-xl font-bold ${knowledgeStats.needs_review_count > 0 ? 'text-yellow-400' : 'text-zinc-600'}`}>{knowledgeStats.needs_review_count || 0}</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4 cursor-pointer hover:border-red-500/30 transition-all" onClick={() => { const f = {...knowledgeFilter, review_status: 'stale', stale_only: false}; setKnowledgeFilter(f); fetchKnowledge(f); }}>
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Stale</p>
                  <p className={`font-mono text-xl font-bold ${knowledgeStats.stale_count > 0 ? 'text-red-400' : 'text-zinc-600'}`}>{knowledgeStats.stale_count}</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4 cursor-pointer hover:border-zinc-500/30 transition-all" onClick={() => { const f = {...knowledgeFilter, review_status: 'archived', stale_only: false}; setKnowledgeFilter(f); fetchKnowledge(f); }}>
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Archived</p>
                  <p className="text-zinc-500 font-mono text-xl font-bold">{knowledgeStats.archived_count || 0}</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope mb-2">By Category</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(knowledgeStats.by_category || {}).map(([cat, count]) => (
                      <span key={cat} className="text-[10px] bg-zinc-800 text-zinc-400 px-2 py-0.5 cursor-pointer hover:text-white transition-all" onClick={() => { const f = {...knowledgeFilter, category: cat}; setKnowledgeFilter(f); fetchKnowledge(f); }}>{cat.replace(/_/g,' ')}: {count}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <button onClick={() => { resetKnowledgeForm(); setEditingKnowledge(null); setShowKnowledgeForm(true); }} data-testid="knowledge-add-btn"
                className="bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest px-5 py-2 text-xs transition-all">
                <Plus className="w-3.5 h-3.5 inline mr-1.5" />New Entry
              </button>
              <button onClick={seedKnowledge} data-testid="knowledge-seed-btn"
                className="border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 font-bold uppercase tracking-widest px-4 py-2 text-xs transition-all">
                Seed Phase A
              </button>
              <button onClick={async () => {
                try {
                  const res = await axios.post(`${API}/admin/knowledge-library/seed-phase-b`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
                  toast.success(res.data.message);
                  fetchKnowledge(); fetchKnowledgeStats();
                } catch (err) { toast.error('Phase B seed failed'); }
              }} data-testid="knowledge-seed-b-btn"
                className="border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 font-bold uppercase tracking-widest px-4 py-2 text-xs transition-all">
                Seed Phase B
              </button>
              <button onClick={async () => {
                try {
                  const res = await axios.post(`${API}/admin/knowledge-library/upgrade-schema`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
                  toast.success(`Upgraded ${res.data.updated} entries`);
                  fetchKnowledge();
                } catch (err) { toast.error('Schema upgrade failed'); }
              }} data-testid="knowledge-upgrade-btn"
                className="border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 font-bold uppercase tracking-widest px-4 py-2 text-xs transition-all">
                Upgrade Schema
              </button>
              <input
                type="text" placeholder="Search entries..."
                value={knowledgeFilter.search}
                onChange={(e) => { const f = {...knowledgeFilter, search: e.target.value}; setKnowledgeFilter(f); fetchKnowledge(f); }}
                data-testid="knowledge-search"
                className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm flex-1 min-w-[200px]"
              />
              <select value={knowledgeFilter.category} onChange={(e) => { const f = {...knowledgeFilter, category: e.target.value}; setKnowledgeFilter(f); fetchKnowledge(f); }}
                data-testid="knowledge-filter-category" className="bg-zinc-900 border border-zinc-700 text-white h-9 px-3 text-xs">
                <option value="">All Categories</option>
                {['terpene_science','medical_cannabis_evidence','international_regulatory_compliance','import_export_requirements','strain_lineage_and_chemovar','buyer_due_diligence','certifications_and_quality','country_market_profiles'].map(c => (
                  <option key={c} value={c}>{c.replace(/_/g,' ')}</option>
                ))}
              </select>
              <select value={knowledgeFilter.evidence_tier} onChange={(e) => { const f = {...knowledgeFilter, evidence_tier: e.target.value}; setKnowledgeFilter(f); fetchKnowledge(f); }}
                data-testid="knowledge-filter-tier" className="bg-zinc-900 border border-zinc-700 text-white h-9 px-3 text-xs">
                <option value="">All Evidence Tiers</option>
                {['regulator','clinical_guideline','systematic_review','review_article','observational','industry_reference','strain_database','internal_firegold'].map(t => (
                  <option key={t} value={t}>{t.replace(/_/g,' ')}</option>
                ))}
              </select>
              <select value={knowledgeFilter.review_status} onChange={(e) => { const f = {...knowledgeFilter, review_status: e.target.value, stale_only: false}; setKnowledgeFilter(f); fetchKnowledge(f); }}
                data-testid="knowledge-filter-review-status" className="bg-zinc-900 border border-zinc-700 text-white h-9 px-3 text-xs">
                <option value="">All Review Status</option>
                <option value="current">Current</option>
                <option value="needs_review">Needs Review</option>
                <option value="stale">Stale</option>
                <option value="archived">Archived</option>
              </select>
              <input type="text" placeholder="Jurisdiction..."
                value={knowledgeFilter.jurisdiction}
                onChange={(e) => { const f = {...knowledgeFilter, jurisdiction: e.target.value}; setKnowledgeFilter(f); fetchKnowledge(f); }}
                data-testid="knowledge-filter-jurisdiction"
                className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-xs w-28"
              />
              <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={knowledgeFilter.stale_only}
                  onChange={(e) => { const f = {...knowledgeFilter, stale_only: e.target.checked, review_status: ''}; setKnowledgeFilter(f); fetchKnowledge(f); }}
                  className="accent-red-500" />
                Stale Only
              </label>
              <button onClick={() => { const f = { category: '', search: '', evidence_tier: '', stale_only: false, review_status: '', jurisdiction: '' }; setKnowledgeFilter(f); fetchKnowledge(f); }}
                data-testid="knowledge-filter-clear"
                className="text-zinc-500 hover:text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 border border-zinc-800 hover:border-zinc-600 transition-all">
                Clear
              </button>
            </div>

            {/* Retrieval Preview Tool */}
            <div className="bg-zinc-900/30 border border-zinc-800 p-4 mb-6" data-testid="knowledge-preview-tool">
              <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope mb-2">Chatbot Retrieval Preview</p>
              <div className="flex gap-2">
                <input type="text" placeholder="Enter a sample chatbot question..."
                  value={previewQuestion} onChange={(e) => setPreviewQuestion(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && runPreviewRetrieval()}
                  data-testid="knowledge-preview-input"
                  className="flex-1 bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" />
                <button onClick={runPreviewRetrieval} data-testid="knowledge-preview-btn"
                  className="bg-gold-500/20 text-gold-500 font-bold uppercase tracking-widest px-4 py-1.5 text-xs hover:bg-gold-500/30 transition-all">
                  Preview
                </button>
              </div>
              {previewResults && (
                <div className="mt-3 space-y-2">
                  <p className="text-zinc-400 text-xs">{previewResults.matched_entries} entries matched for: <span className="text-white">"{previewResults.question}"</span></p>
                  {previewResults.results.map((r, idx) => (
                    <div key={idx} className="bg-black/30 border border-zinc-800/50 p-3 flex items-start gap-3">
                      <span className="text-gold-500 font-mono text-xs font-bold shrink-0">#{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-bold">{r.title}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5">{r.evidence_tier}</span>
                          <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5">{r.confidence_level}</span>
                          <span className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5">{r.category}</span>
                          {r.country && <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5">{r.country}</span>}
                        </div>
                        <p className="text-zinc-500 text-xs mt-1 truncate">{r.answer_preview}...</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Knowledge Entries List */}
            {knowledgeEntries.length === 0 ? (
              <div className="text-center py-16">
                <BookmarkPlus className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm mb-2">No knowledge entries found</p>
                <p className="text-zinc-600 text-xs">Click "Seed Library" to load initial curated entries, or create entries manually.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {knowledgeEntries.map(entry => (
                  <div key={entry.id} data-testid={`knowledge-entry-${entry.id}`}
                    className={`bg-zinc-900/30 border p-4 hover:border-gold-500/30 transition-all ${entry.is_stale ? 'border-red-500/40' : 'border-zinc-800'}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-white font-bold text-sm">{entry.title}</h3>
                          {entry.is_stale && <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 font-bold">STALE</span>}
                          {entry.review_status === 'needs_review' && <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 font-bold">NEEDS REVIEW</span>}
                          {entry.review_status === 'archived' && <span className="text-[10px] bg-zinc-700 text-zinc-500 px-1.5 py-0.5 font-bold">ARCHIVED</span>}
                          {!entry.active && <span className="text-[10px] bg-zinc-700 text-zinc-400 px-1.5 py-0.5">INACTIVE</span>}
                          {entry.compliance_flag && <span className="text-[10px] bg-gold-500/20 text-gold-500 px-1.5 py-0.5">COMPLIANCE</span>}
                          {entry.admin_reviewed === false && <span className="text-[10px] bg-orange-500/20 text-orange-400 px-1.5 py-0.5">UNREVIEWED</span>}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          <span className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5">{entry.category?.replace(/_/g,' ')}</span>
                          <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5">{entry.evidence_tier?.replace(/_/g,' ')}</span>
                          <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5">{entry.confidence_level}</span>
                          {entry.country && <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5">{entry.country}</span>}
                          {entry.jurisdiction_type && <span className="text-[10px] bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5">{entry.jurisdiction_type}</span>}
                          {entry.visibility === 'admin' && <span className="text-[10px] bg-gold-500/20 text-gold-500 px-1.5 py-0.5">ADMIN ONLY</span>}
                        </div>
                        <p className="text-zinc-500 text-xs mt-1.5 line-clamp-2">{entry.answer}</p>
                        {entry.source_name && <p className="text-zinc-600 text-[10px] mt-1">Source: {entry.source_name} {entry.source_date ? `(${entry.source_date})` : ''}</p>}
                        {entry.stale_reason && <p className="text-red-400/70 text-[10px] mt-0.5">{entry.stale_reason}</p>}
                        {(entry.review_owner || entry.next_review_due_at || entry.review_notes) && (
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                            {entry.review_owner && <span className="text-[10px] text-cyan-400/70">Owner: {entry.review_owner}</span>}
                            {entry.next_review_due_at && <span className="text-[10px] text-zinc-500">Next review: {new Date(entry.next_review_due_at).toLocaleDateString()}</span>}
                            {entry.review_notes && <span className="text-[10px] text-zinc-500 italic">Note: {entry.review_notes}</span>}
                          </div>
                        )}
                        {entry.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {entry.tags.map(tag => <span key={tag} className="text-[10px] text-zinc-600 bg-zinc-900 px-1.5 py-0.5">#{tag}</span>)}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        {(entry.is_stale || entry.review_status === 'needs_review' || entry.review_status === 'stale') && (
                          <button onClick={() => openReviewModal(entry)} title="Review Entry" data-testid={`knowledge-review-${entry.id}`}
                            className="text-zinc-500 hover:text-emerald-400 transition-colors p-1">
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => fetchEntryHistory(entry.id)} data-testid={`knowledge-history-${entry.id}`} title="View History"
                          className="text-zinc-500 hover:text-blue-400 transition-colors p-1">
                          <Clock className="w-4 h-4" />
                        </button>
                        <button onClick={() => startEditKnowledge(entry)} data-testid={`knowledge-edit-${entry.id}`} title="Edit"
                          className="text-zinc-500 hover:text-gold-500 transition-colors p-1">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => deleteKnowledgeEntry(entry.id)} data-testid={`knowledge-delete-${entry.id}`} title="Delete"
                          className="text-zinc-500 hover:text-red-400 transition-colors p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}


        {/* ── Supply Forms Tab ── */}
        {activeTab === 'supply-forms' && (
          <SupplyFormsPanel adminToken={adminToken} API={API} />
        )}

        {/* ── Grower Pipeline Tab ── */}
        {activeTab === 'grower-pipeline' && (
          <GrowerPipelinePanel adminToken={adminToken} API={API} />
        )}


        {/* ── Admin CRM Tab ── */}
        {activeTab === 'crm' && (
          <CRMPanel adminToken={adminToken} API={API} />
        )}

        {/* ── Admin Email Blast Tab ── */}
        {activeTab === 'email-blast' && (
          <EmailBlastPanel adminToken={adminToken} API={API} />
        )}

        {/* ── Admin Strain Hunter Tab ── */}
        {activeTab === 'strain-hunter' && (
          <>
            {/* Stats */}
            {adminHunterRequests && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Total Requests</p>
                  <p className="text-white font-mono text-xl font-bold">{adminHunterRequests.total_requests}</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Open</p>
                  <p className="text-blue-400 font-mono text-xl font-bold">{adminHunterRequests.status_counts?.open || 0}</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Sourcing</p>
                  <p className="text-amber-400 font-mono text-xl font-bold">{adminHunterRequests.status_counts?.sourcing || 0}</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Fulfilled</p>
                  <p className="text-emerald-400 font-mono text-xl font-bold">{adminHunterRequests.status_counts?.fulfilled || 0}</p>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Total Volume</p>
                  <p className="text-gold-500 font-mono text-xl font-bold">{adminHunterRequests.total_volume_kg} <span className="text-zinc-500 text-xs">kg</span></p>
                </div>
              </div>
            )}

            {/* Top Sought Strains */}
            {adminHunterRequests?.top_sought_strains?.length > 0 && (
              <div className="bg-zinc-900/50 border border-zinc-800 p-4 mb-6">
                <h3 className="text-white font-manrope text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-red-400" />
                  Top Sought Strains (Unmet Demand)
                </h3>
                <div className="flex flex-wrap gap-2">
                  {adminHunterRequests.top_sought_strains.map(s => (
                    <span key={s.name} className="bg-amber-500/10 border border-amber-500/20 text-amber-400 px-3 py-1.5 text-xs font-bold">
                      {s.name} <span className="text-zinc-500 ml-1">x{s.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Demand Analytics Row */}
            {adminHunterRequests && (adminHunterRequests.demand_by_effect?.length > 0 || adminHunterRequests.demand_by_market?.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {adminHunterRequests.demand_by_effect?.length > 0 && (
                  <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope mb-2">Top Demanded Effects</p>
                    <div className="space-y-1">
                      {adminHunterRequests.demand_by_effect.slice(0, 5).map(d => (
                        <div key={d.name} className="flex items-center justify-between text-xs">
                          <span className="text-zinc-300">{d.name}</span>
                          <span className="text-amber-400 font-mono font-bold">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {adminHunterRequests.demand_by_terpene?.length > 0 && (
                  <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope mb-2">Top Demanded Terpenes</p>
                    <div className="space-y-1">
                      {adminHunterRequests.demand_by_terpene.slice(0, 5).map(d => (
                        <div key={d.name} className="flex items-center justify-between text-xs">
                          <span className="text-zinc-300">{d.name}</span>
                          <span className="text-emerald-400 font-mono font-bold">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {adminHunterRequests.demand_by_market?.length > 0 && (
                  <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope mb-2">Top Markets</p>
                    <div className="space-y-1">
                      {adminHunterRequests.demand_by_market.slice(0, 5).map(d => (
                        <div key={d.name} className="flex items-center justify-between text-xs">
                          <span className="text-zinc-300">{d.name}</span>
                          <span className="text-blue-400 font-mono font-bold">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Knowledge Gaps */}
            {adminHunterRequests?.knowledge_gaps?.length > 0 && (
              <div data-testid="admin-knowledge-gaps" className="bg-red-500/5 border border-red-500/20 p-4 mb-6">
                <h3 className="text-white font-manrope text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  Knowledge & Catalog Gaps
                </h3>
                <p className="text-zinc-400 text-xs mb-3">Strains being sought that are missing from catalog or knowledge library.</p>
                <div className="space-y-2">
                  {adminHunterRequests.knowledge_gaps.map(g => (
                    <div key={g.strain_name} className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 px-3 py-2">
                      <div className="flex items-center gap-3">
                        <span className="text-white font-bold text-sm">{g.strain_name}</span>
                        <span className="text-amber-400 font-mono text-xs">x{g.request_count}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${g.in_catalog ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                          {g.in_catalog ? 'In Catalog' : 'Not in Catalog'}
                        </span>
                        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${g.in_reference_library ? 'bg-blue-500/15 text-blue-400' : 'bg-zinc-700 text-zinc-500'}`}>
                          {g.in_reference_library ? 'In Ref Lib' : 'No Ref'}
                        </span>
                        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${g.in_knowledge_library ? 'bg-cyan-500/15 text-cyan-400' : 'bg-zinc-700 text-zinc-500'}`}>
                          {g.in_knowledge_library ? 'Has Knowledge' : 'No Knowledge'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <select
                data-testid="admin-hunter-filter"
                value={adminHunterFilter}
                onChange={e => { setAdminHunterFilter(e.target.value); fetchAdminHunterRequests(e.target.value); }}
                className="bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm outline-none"
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="sourcing">Sourcing</option>
                <option value="fulfilled">Fulfilled</option>
                <option value="closed">Closed</option>
              </select>
              <button
                data-testid="admin-seed-reference-btn"
                onClick={seedStrainReferenceLibrary}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs uppercase tracking-widest px-4 py-2.5 font-bold transition-all"
              >
                Seed Reference Library
              </button>
              <button
                data-testid="admin-notify-growers-btn"
                onClick={async () => {
                  if (!window.confirm('Send demand alert emails to all growers whose strains match top sought demand? This will email growers with matching inventory.')) return;
                  try {
                    const res = await axios.post(`${API}/admin/strain-hunter/notify-growers`, {}, {
                      headers: { Authorization: `Bearer ${adminToken}` }
                    });
                    toast.success(res.data.message);
                  } catch (err) { toast.error('Failed to send demand notifications'); }
                }}
                className="bg-amber-600 hover:bg-amber-500 text-black text-xs uppercase tracking-widest px-4 py-2.5 font-bold transition-all flex items-center gap-2"
              >
                <Bell className="w-3.5 h-3.5" /> Notify Growers of Demand
              </button>
            </div>

            {/* Request list */}
            {adminHunterRequests?.requests?.length === 0 && (
              <div className="text-center py-16">
                <Crosshair className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No strain hunter requests yet.</p>
              </div>
            )}
            <div className="space-y-3">
              {(adminHunterRequests?.requests || []).map(req => (
                <div key={req.id} data-testid={`admin-hunter-${req.id}`} className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-bold">{req.strain_name}</span>
                        {req.strain_type && <span className="text-zinc-500 text-xs">({req.strain_type})</span>}
                      </div>
                      <p className="text-zinc-500 text-xs mt-0.5">{req.user_email} &middot; {req.user_company}</p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${
                      req.status === 'open' ? 'bg-blue-500/20 text-blue-400' :
                      req.status === 'sourcing' ? 'bg-amber-500/20 text-amber-400' :
                      req.status === 'fulfilled' ? 'bg-emerald-500/20 text-emerald-400' :
                      'bg-zinc-700/50 text-zinc-400'
                    }`}>{req.status}</span>
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400 mb-3">
                    {req.desired_kg && <span>Volume: <span className="text-white font-mono">{req.desired_kg}kg</span></span>}
                    {req.min_thc && <span>Min THC: <span className="text-white font-mono">{req.min_thc}%</span></span>}
                    {req.target_market && <span>Market: <span className="text-white">{req.target_market}</span></span>}
                    <span>Created: {new Date(req.created_at).toLocaleDateString()}</span>
                  </div>

                  {req.desired_effects?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {req.desired_effects.map(e => (
                        <span key={e} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-amber-500/10 text-amber-400/70 border border-amber-500/15">{e}</span>
                      ))}
                    </div>
                  )}

                  {req.notes && <p className="text-zinc-400 text-xs mb-3 italic">"{req.notes}"</p>}

                  <div className="flex items-center gap-2 pt-2 border-t border-zinc-800/50">
                    {['open', 'sourcing', 'fulfilled', 'closed'].map(st => (
                      <button
                        key={st}
                        disabled={adminHunterUpdating === req.id || req.status === st}
                        onClick={() => updateHunterRequestStatus(req.id, st)}
                        className={`text-[10px] uppercase tracking-widest px-3 py-1 font-bold transition-all border ${
                          req.status === st
                            ? 'border-gold-500/50 bg-gold-500/10 text-gold-500 cursor-default'
                            : 'border-zinc-700 text-zinc-500 hover:text-white hover:border-zinc-500'
                        }`}
                      >
                        {st}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Media Health Report Tab */}
        {activeTab === 'media-health' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white uppercase tracking-widest" data-testid="media-health-title">Media Health Report</h2>
                <p className="text-zinc-500 text-xs mt-1 font-manrope">Diagnostic scan of all strain media assets</p>
              </div>
              <button
                onClick={fetchMediaHealth}
                disabled={mediaHealthLoading}
                data-testid="media-health-rescan-btn"
                className="bg-gold-500 hover:bg-gold-400 text-black px-5 py-2 font-bold uppercase text-xs tracking-widest transition-all disabled:opacity-50 flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${mediaHealthLoading ? 'animate-spin' : ''}`} />
                {mediaHealthLoading ? 'Scanning...' : 'Rescan Now'}
              </button>
            </div>

            {mediaHealthLoading && !mediaHealth && (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 text-gold-500 animate-spin" />
              </div>
            )}

            {mediaHealth && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="media-health-summary">
                  <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Total Strains</p>
                    <p className="text-white font-mono text-xl font-bold">{mediaHealth.summary.total_strains}</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Healthy</p>
                    <p className="text-green-400 font-mono text-xl font-bold">{mediaHealth.summary.healthy}</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Issues Found</p>
                    <p className={`font-mono text-xl font-bold ${mediaHealth.summary.total_issues > 0 ? 'text-red-400' : 'text-green-400'}`}>{mediaHealth.summary.total_issues}</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Last Scanned</p>
                    <p className="text-zinc-300 font-mono text-sm">{mediaHealth.summary.scanned_at ? new Date(mediaHealth.summary.scanned_at).toLocaleString() : 'N/A'}</p>
                  </div>
                </div>

                {/* Breakdown Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="media-health-breakdown">
                  <div className="bg-zinc-900/30 border border-zinc-800/50 p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-500/10 flex items-center justify-center"><Image className="w-4 h-4 text-amber-400" /></div>
                    <div><p className="text-zinc-600 text-[9px] uppercase tracking-widest">Missing Image</p><p className="text-amber-400 font-mono font-bold">{mediaHealth.summary.missing_image}</p></div>
                  </div>
                  <div className="bg-zinc-900/30 border border-zinc-800/50 p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-500/10 flex items-center justify-center"><FileText className="w-4 h-4 text-amber-400" /></div>
                    <div><p className="text-zinc-600 text-[9px] uppercase tracking-widest">Missing COA</p><p className="text-amber-400 font-mono font-bold">{mediaHealth.summary.missing_coa}</p></div>
                  </div>
                  <div className="bg-zinc-900/30 border border-zinc-800/50 p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-500/10 flex items-center justify-center"><XCircle className="w-4 h-4 text-red-400" /></div>
                    <div><p className="text-zinc-600 text-[9px] uppercase tracking-widest">Broken URL</p><p className="text-red-400 font-mono font-bold">{mediaHealth.summary.broken_image + mediaHealth.summary.broken_coa}</p></div>
                  </div>
                  <div className="bg-zinc-900/30 border border-zinc-800/50 p-3 flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-500/10 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-red-400" /></div>
                    <div><p className="text-zinc-600 text-[9px] uppercase tracking-widest">Invalid Type</p><p className="text-red-400 font-mono font-bold">{mediaHealth.summary.invalid_image_type + mediaHealth.summary.invalid_coa_type}</p></div>
                  </div>
                </div>

                {/* Issues Table */}
                {mediaHealth.issues.length === 0 ? (
                  <div className="bg-green-900/10 border border-green-800/30 p-8 text-center" data-testid="media-health-all-clear">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-green-400 font-bold uppercase tracking-widest text-sm">All Media Healthy</p>
                    <p className="text-zinc-500 text-xs mt-1">Every strain has valid, accessible image and COA files.</p>
                  </div>
                ) : (
                  <div className="border border-zinc-800 overflow-hidden" data-testid="media-health-issues-table">
                    <div className="bg-zinc-900/80 border-b border-zinc-800 px-4 py-3 flex items-center justify-between">
                      <p className="text-zinc-400 text-xs uppercase tracking-widest font-manrope">{mediaHealth.issues.length} strain{mediaHealth.issues.length !== 1 ? 's' : ''} with issues</p>
                    </div>
                    <div className="divide-y divide-zinc-800/50 max-h-[60vh] overflow-y-auto">
                      {mediaHealth.issues.map((item, idx) => (
                        <div key={idx} className="px-4 py-3 hover:bg-zinc-900/30 transition-colors" data-testid={`media-health-issue-${idx}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-bold text-sm">{item.strain_name}</span>
                            <span className="text-zinc-600 font-mono text-[10px]">{item.strain_id}</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {item.issues.map((issue, j) => (
                              <span
                                key={j}
                                data-testid={`media-health-flag-${issue.type}`}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold ${
                                  issue.severity === 'error'
                                    ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                                    : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                }`}
                              >
                                {issue.type === 'missing_image' && <Image className="w-3 h-3" />}
                                {issue.type === 'missing_coa' && <FileText className="w-3 h-3" />}
                                {issue.type === 'broken_image' && <XCircle className="w-3 h-3" />}
                                {issue.type === 'broken_coa' && <XCircle className="w-3 h-3" />}
                                {issue.type === 'invalid_image_type' && <AlertTriangle className="w-3 h-3" />}
                                {issue.type === 'invalid_coa_type' && <AlertTriangle className="w-3 h-3" />}
                                {issue.type.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                          <div className="mt-1.5 space-y-0.5">
                            {item.issues.map((issue, j) => (
                              <p key={j} className="text-zinc-600 text-[11px] font-mono">{issue.detail}</p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── Document Vault ── */}
        {activeTab === 'document-vault' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-cinzel font-bold text-2xl text-white uppercase tracking-widest" data-testid="vault-title">Document Vault</h2>
                <p className="text-zinc-500 text-xs mt-1 font-manrope">Download, replace, and manage all documents &amp; images</p>
              </div>
              <button onClick={() => fetchVaultData(adminToken)} data-testid="vault-refresh" className="bg-gold-500 hover:bg-gold-400 text-black px-5 py-2 font-bold uppercase text-xs tracking-widest transition-all flex items-center gap-2">
                <RefreshCw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6" data-testid="vault-summary">
              <div className="bg-charcoal border border-white/5 p-3">
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Total</p>
                <p className="text-white font-mono text-lg font-bold">{vaultData.total}</p>
              </div>
              {[
                { key: 'has_image', label: 'Images', count: vaultData.summary?.with_image, color: 'text-blue-400' },
                { key: 'has_coa', label: 'COAs', count: vaultData.summary?.with_coa, color: 'text-cannabis-green' },
                { key: 'has_cra', label: 'CRA', count: vaultData.summary?.with_cra_license, color: 'text-white' },
                { key: 'has_hc', label: 'HC License', count: vaultData.summary?.with_hc_license, color: 'text-white' },
                { key: 'has_bcdd', label: 'BCDD', count: vaultData.summary?.with_bcdd_letter, color: 'text-white' },
              ].map(s => (
                <div key={s.key} className={`bg-charcoal border p-3 cursor-pointer transition-colors ${vaultFilter === s.key ? 'border-gold-500/50' : 'border-white/5 hover:border-gold-500/30'}`} onClick={() => setVaultFilter(vaultFilter === s.key ? 'all' : s.key)}>
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">{s.label}</p>
                  <p className={`font-mono text-lg font-bold ${vaultFilter === s.key ? 'text-gold-500' : s.color}`}>{s.count || 0}</p>
                </div>
              ))}
            </div>

            {/* Search & Filter */}
            <div className="flex gap-4 mb-6">
              <input data-testid="vault-search" type="text" value={vaultSearch} onChange={(e) => setVaultSearch(e.target.value)} className="flex-1 bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-10 px-4 text-sm outline-none" placeholder="Search by strain name, company, or grower..." />
              <select data-testid="vault-filter" value={vaultFilter} onChange={(e) => setVaultFilter(e.target.value)} className="bg-zinc-900/50 border border-zinc-800 text-white h-10 px-4 text-sm outline-none">
                <option value="all">All Entries</option>
                <option value="strain">Strains Only</option>
                <option value="submission">Submissions Only</option>
                <option value="missing_docs">Missing Documents</option>
                <option value="has_image">Has Image</option>
                <option value="has_coa">Has COA</option>
                <option value="has_cra">Has CRA License</option>
                <option value="has_hc">Has HC License</option>
                <option value="has_bcdd">Has BCDD Letter</option>
              </select>
            </div>

            {/* Vault Table */}
            {(() => {
              const filtered = (vaultData.entries || [])
                .filter(e => {
                  if (vaultFilter === 'strain') return e.source === 'strain';
                  if (vaultFilter === 'submission') return e.source === 'submission';
                  if (vaultFilter === 'missing_docs') return !e.coa || !e.cra_license || !e.hc_license || !e.bcdd_letter;
                  if (vaultFilter === 'has_image') return !!e.image;
                  if (vaultFilter === 'has_coa') return !!e.coa;
                  if (vaultFilter === 'has_cra') return !!e.cra_license;
                  if (vaultFilter === 'has_hc') return !!e.hc_license;
                  if (vaultFilter === 'has_bcdd') return !!e.bcdd_letter;
                  return true;
                })
                .filter(e => {
                  if (!vaultSearch) return true;
                  const q = vaultSearch.toLowerCase();
                  return (e.strain_name || '').toLowerCase().includes(q) || (e.company || '').toLowerCase().includes(q) || (e.grower || '').toLowerCase().includes(q);
                });

              const sorted = [...filtered].sort((a, b) => {
                const key = vaultSort.key;
                const docKeys = ['image', 'coa', 'cra_license', 'hc_license', 'bcdd_letter'];
                let aVal, bVal;
                if (docKeys.includes(key)) { aVal = a[key] ? 1 : 0; bVal = b[key] ? 1 : 0; }
                else { aVal = (a[key] || '').toString().toLowerCase(); bVal = (b[key] || '').toString().toLowerCase(); }
                if (aVal < bVal) return vaultSort.dir === 'asc' ? -1 : 1;
                if (aVal > bVal) return vaultSort.dir === 'asc' ? 1 : -1;
                return 0;
              });

              const toggleSort = (key) => setVaultSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

              const SortHeader = ({ colKey, label, className = '' }) => (
                <th className={`text-left text-zinc-500 text-[10px] uppercase tracking-widest font-manrope py-2 px-2 cursor-pointer hover:text-gold-500 transition-colors select-none whitespace-nowrap ${className}`} onClick={() => toggleSort(colKey)} data-testid={`vault-sort-${colKey}`}>
                  {label} {vaultSort.key === colKey && <span className="ml-0.5 text-gold-500">{vaultSort.dir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                </th>
              );

              const DocCell = ({ entry, docType }) => {
                const doc = entry[docType];
                const uploadKey = `${entry.id}-${docType}`;
                const isUploading = vaultUploading === uploadKey;
                const acceptAttr = docType === 'image' ? '.jpg,.jpeg,.png,.webp' : '.pdf';
                const isProcessing = vaultProcessing === `redact-${entry.id}` || vaultProcessing === `enhance-${entry.id}`;
                if (doc) {
                  const fileUrl = doc.file_url || '';
                  return (
                    <td className="py-2 px-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <a href={fileUrl.startsWith('/api/') ? `${API}${fileUrl.replace('/api', '')}` : fileUrl.startsWith('/strains/') ? fileUrl : `${API}${fileUrl}`} target="_blank" rel="noopener noreferrer" className="text-cannabis-green hover:text-cannabis-green/80 text-[10px] font-bold uppercase" data-testid={`vault-view-${entry.id}-${docType}`} title="View">
                          <Eye className="w-3.5 h-3.5" />
                        </a>
                        <button onClick={() => handleVaultDownload(fileUrl, entry.strain_name, docType)} className="text-blue-400 hover:text-blue-300 text-[10px]" title="Download" data-testid={`vault-dl-${entry.id}-${docType}`}>
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <label className="text-gold-500 hover:text-gold-400 text-[10px] cursor-pointer" title="Replace" data-testid={`vault-replace-${entry.id}-${docType}`}>
                          <RefreshCw className="w-3 h-3" />
                          <input type="file" accept={acceptAttr} className="hidden" onChange={(ev) => { if (ev.target.files[0]) handleVaultUpload(entry.source, entry.id, docType, ev.target.files[0]); ev.target.value = ''; }} />
                        </label>
                        {docType === 'coa' && (
                          <button onClick={() => handleRedactCoa(entry)} disabled={isProcessing} className={`text-[10px] font-bold uppercase px-1.5 py-0.5 border transition-all ${isProcessing ? 'border-gold-500/50 text-gold-500 animate-pulse' : 'border-red-400/30 text-red-400 hover:border-red-400 hover:text-red-300'}`} title="AI Redact grower info" data-testid={`vault-redact-${entry.id}`}>
                            {isProcessing ? 'Redacting...' : 'Redact'}
                          </button>
                        )}
                        {docType === 'image' && (
                          <button onClick={() => handleEnhanceImage(entry)} disabled={isProcessing} className={`text-[10px] font-bold uppercase px-1.5 py-0.5 border transition-all ${isProcessing ? 'border-gold-500/50 text-gold-500 animate-pulse' : 'border-blue-400/30 text-blue-400 hover:border-blue-400 hover:text-blue-300'}`} title="AI Enhance: black bg, sharpen, trichome detail" data-testid={`vault-enhance-${entry.id}`}>
                            {isProcessing ? 'Enhancing...' : 'Enhance'}
                          </button>
                        )}
                      </div>
                    </td>
                  );
                }
                return (
                  <td className="py-2 px-2 text-center">
                    <label className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase cursor-pointer transition-colors ${isUploading ? 'text-gold-500 animate-pulse' : 'text-red-400/70 hover:text-gold-500'}`} data-testid={`vault-upload-${entry.id}-${docType}`}>
                      {isUploading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      {isUploading ? '...' : 'Add'}
                      <input type="file" accept={acceptAttr} className="hidden" onChange={(ev) => { if (ev.target.files[0]) handleVaultUpload(entry.source, entry.id, docType, ev.target.files[0]); ev.target.value = ''; }} disabled={isUploading} />
                    </label>
                  </td>
                );
              };

              return sorted.length === 0 ? (
                <div className="text-center py-16 text-zinc-600 font-manrope" data-testid="vault-empty">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No entries found</p>
                </div>
              ) : (
                <div className="overflow-x-auto" data-testid="vault-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <SortHeader colKey="company" label="Company" />
                        <SortHeader colKey="strain_name" label="Strain / Product" />
                        <SortHeader colKey="source" label="Source" className="hidden md:table-cell" />
                        <SortHeader colKey="status" label="Status" className="hidden lg:table-cell" />
                        <th className="text-center text-zinc-500 text-[10px] uppercase tracking-widest font-manrope py-2 px-2 cursor-pointer hover:text-gold-500 whitespace-nowrap" onClick={() => toggleSort('image')} data-testid="vault-sort-image">
                          Photo {vaultSort.key === 'image' && <span className="ml-0.5 text-gold-500">{vaultSort.dir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                        </th>
                        <th className="text-center text-zinc-500 text-[10px] uppercase tracking-widest font-manrope py-2 px-2 cursor-pointer hover:text-gold-500 whitespace-nowrap" onClick={() => toggleSort('coa')} data-testid="vault-sort-coa">
                          COA {vaultSort.key === 'coa' && <span className="ml-0.5 text-gold-500">{vaultSort.dir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                        </th>
                        <th className="text-center text-zinc-500 text-[10px] uppercase tracking-widest font-manrope py-2 px-2 cursor-pointer hover:text-gold-500 whitespace-nowrap" onClick={() => toggleSort('cra_license')} data-testid="vault-sort-cra">
                          CRA {vaultSort.key === 'cra_license' && <span className="ml-0.5 text-gold-500">{vaultSort.dir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                        </th>
                        <th className="text-center text-zinc-500 text-[10px] uppercase tracking-widest font-manrope py-2 px-2 cursor-pointer hover:text-gold-500 whitespace-nowrap" onClick={() => toggleSort('hc_license')} data-testid="vault-sort-hc">
                          HC License {vaultSort.key === 'hc_license' && <span className="ml-0.5 text-gold-500">{vaultSort.dir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                        </th>
                        <th className="text-center text-zinc-500 text-[10px] uppercase tracking-widest font-manrope py-2 px-2 cursor-pointer hover:text-gold-500 whitespace-nowrap" onClick={() => toggleSort('bcdd_letter')} data-testid="vault-sort-bcdd">
                          BCDD {vaultSort.key === 'bcdd_letter' && <span className="ml-0.5 text-gold-500">{vaultSort.dir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((entry) => (
                        <tr key={`${entry.source}-${entry.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/50 transition-colors" data-testid={`vault-row-${entry.id}`}>
                          <td className="py-2.5 px-2 text-white font-bold text-xs max-w-[140px] truncate">{entry.company || '-'}</td>
                          <td className="py-2.5 px-2 text-zinc-300 text-xs max-w-[160px] truncate">{entry.strain_name || '-'}</td>
                          <td className="py-2.5 px-2 hidden md:table-cell">
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${entry.source === 'strain' ? 'bg-cannabis-green/20 text-cannabis-green' : 'bg-gold-500/20 text-gold-500'}`}>{entry.source}</span>
                          </td>
                          <td className="py-2.5 px-2 hidden lg:table-cell">
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${entry.status === 'approved' ? 'bg-cannabis-green/20 text-cannabis-green' : entry.status === 'pending' ? 'bg-gold-500/20 text-gold-500' : 'bg-zinc-700/30 text-zinc-400'}`}>{entry.status || '-'}</span>
                          </td>
                          <DocCell entry={entry} docType="image" />
                          <DocCell entry={entry} docType="coa" />
                          <DocCell entry={entry} docType="cra_license" />
                          <DocCell entry={entry} docType="hc_license" />
                          <DocCell entry={entry} docType="bcdd_letter" />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-zinc-600 text-xs mt-4 font-manrope text-center">{sorted.length} of {vaultData.total} entries shown</p>
                </div>
              );
            })()}
          </>
        )}

      </main>
      {editingAffiliate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditingAffiliate(null)} />
          <div data-testid="edit-affiliate-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-lg p-8">
            <button onClick={() => setEditingAffiliate(null)} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"><X className="w-5 h-5 text-black" /></button>
            <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest mb-6">Edit Affiliate</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Name</label>
                  <input type="text" value={editingAffiliate.name} onChange={e => setEditingAffiliate(p => ({...p, name: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Email</label>
                  <input type="email" value={editingAffiliate.email} onChange={e => setEditingAffiliate(p => ({...p, email: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Phone</label>
                  <input type="text" value={editingAffiliate.phone || ''} onChange={e => setEditingAffiliate(p => ({...p, phone: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Company</label>
                  <input type="text" value={editingAffiliate.company || ''} onChange={e => setEditingAffiliate(p => ({...p, company: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Referral Code</label>
                <input type="text" value={editingAffiliate.referral_code} onChange={e => setEditingAffiliate(p => ({...p, referral_code: e.target.value.toUpperCase()}))} className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm font-mono uppercase" />
              </div>
              <p className="text-zinc-500 text-xs">Commission is set per-deal when converting inquiries to sales.</p>
              <button onClick={saveAffiliateEdit} data-testid="save-affiliate-btn" className="w-full bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Knowledge Entry Form Modal */}
      {showKnowledgeForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setShowKnowledgeForm(false); setEditingKnowledge(null); }} />
          <div data-testid="knowledge-form-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
            <button onClick={() => { setShowKnowledgeForm(false); setEditingKnowledge(null); }} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all z-10"><X className="w-5 h-5 text-black" /></button>
            <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest mb-6">{editingKnowledge ? 'Edit' : 'New'} Knowledge Entry</h2>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Title *</label>
                <input type="text" value={knowledgeForm.title} onChange={(e) => setKnowledgeForm(p => ({...p, title: e.target.value}))}
                  data-testid="knowledge-form-title" className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-10 px-3 outline-none text-sm" placeholder="e.g. Myrcene: Profile and Reported Associations" />
              </div>

              {/* Category + Evidence + Confidence */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Category *</label>
                  <select value={knowledgeForm.category} onChange={(e) => setKnowledgeForm(p => ({...p, category: e.target.value}))}
                    data-testid="knowledge-form-category" className="w-full bg-zinc-900 border border-zinc-700 text-white h-10 px-3 text-sm">
                    {['terpene_science','medical_cannabis_evidence','international_regulatory_compliance','import_export_requirements','strain_lineage_and_chemovar','buyer_due_diligence','certifications_and_quality','country_market_profiles'].map(c => (
                      <option key={c} value={c}>{c.replace(/_/g,' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Evidence Tier *</label>
                  <select value={knowledgeForm.evidence_tier} onChange={(e) => setKnowledgeForm(p => ({...p, evidence_tier: e.target.value}))}
                    data-testid="knowledge-form-tier" className="w-full bg-zinc-900 border border-zinc-700 text-white h-10 px-3 text-sm">
                    {['regulator','clinical_guideline','systematic_review','review_article','observational','industry_reference','strain_database','internal_firegold'].map(t => (
                      <option key={t} value={t}>{t.replace(/_/g,' ')}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Confidence *</label>
                  <select value={knowledgeForm.confidence_level} onChange={(e) => setKnowledgeForm(p => ({...p, confidence_level: e.target.value}))}
                    data-testid="knowledge-form-confidence" className="w-full bg-zinc-900 border border-zinc-700 text-white h-10 px-3 text-sm">
                    {['high','moderate','low','emerging'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Answer */}
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Answer / Content *</label>
                <textarea value={knowledgeForm.answer} onChange={(e) => setKnowledgeForm(p => ({...p, answer: e.target.value}))}
                  data-testid="knowledge-form-answer" rows={5}
                  className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white px-3 py-2 outline-none text-sm resize-none" placeholder="Detailed answer content..." />
              </div>

              {/* Question Variants */}
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Question Variants</label>
                <div className="flex gap-2 mb-2">
                  <input type="text" value={variantInput} onChange={(e) => setVariantInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && variantInput.trim()) { setKnowledgeForm(p => ({...p, question_variants: [...p.question_variants, variantInput.trim()]})); setVariantInput(''); }}}
                    className="flex-1 bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" placeholder="Type a question variant and press Enter" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {knowledgeForm.question_variants.map((v, i) => (
                    <span key={i} className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 flex items-center gap-1.5">
                      {v} <button onClick={() => setKnowledgeForm(p => ({...p, question_variants: p.question_variants.filter((_, j) => j !== i)}))} className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Tags</label>
                <div className="flex gap-2 mb-2">
                  <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && tagInput.trim()) { setKnowledgeForm(p => ({...p, tags: [...p.tags, tagInput.trim()]})); setTagInput(''); }}}
                    className="flex-1 bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" placeholder="Add tag and press Enter" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {knowledgeForm.tags.map((t, i) => (
                    <span key={i} className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 flex items-center gap-1.5">
                      #{t} <button onClick={() => setKnowledgeForm(p => ({...p, tags: p.tags.filter((_, j) => j !== i)}))} className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Country / Jurisdiction */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Country</label>
                  <input type="text" value={knowledgeForm.country} onChange={(e) => setKnowledgeForm(p => ({...p, country: e.target.value}))}
                    className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" placeholder="e.g. Germany" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Jurisdiction</label>
                  <input type="text" value={knowledgeForm.jurisdiction} onChange={(e) => setKnowledgeForm(p => ({...p, jurisdiction: e.target.value}))}
                    className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" placeholder="e.g. Federal / EU" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Jurisdiction Type</label>
                  <select value={knowledgeForm.jurisdiction_type || ''} onChange={(e) => setKnowledgeForm(p => ({...p, jurisdiction_type: e.target.value || null}))}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white h-9 px-3 text-sm">
                    <option value="">None</option>
                    <option value="country-level">Country-level</option>
                    <option value="federal">Federal</option>
                    <option value="provincial/state">Provincial / State</option>
                    <option value="regulator-specific">Regulator-specific</option>
                    <option value="international">International</option>
                  </select>
                </div>
              </div>

              {/* Source metadata */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Source Name</label>
                  <input type="text" value={knowledgeForm.source_name} onChange={(e) => setKnowledgeForm(p => ({...p, source_name: e.target.value}))}
                    className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" placeholder="e.g. NASEM 2017" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Source URL</label>
                  <input type="url" value={knowledgeForm.source_url} onChange={(e) => setKnowledgeForm(p => ({...p, source_url: e.target.value}))}
                    className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Source Date</label>
                  <input type="date" value={knowledgeForm.source_date} onChange={(e) => setKnowledgeForm(p => ({...p, source_date: e.target.value}))}
                    className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" />
                </div>
              </div>

              {/* Staleness / Flags */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Stale After (days)</label>
                  <input type="number" value={knowledgeForm.stale_after_days || ''} onChange={(e) => setKnowledgeForm(p => ({...p, stale_after_days: e.target.value ? parseInt(e.target.value) : null}))}
                    className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm font-mono" placeholder="Auto from category" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Visibility</label>
                  <select value={knowledgeForm.visibility} onChange={(e) => setKnowledgeForm(p => ({...p, visibility: e.target.value}))}
                    className="w-full bg-zinc-900 border border-zinc-700 text-white h-9 px-3 text-sm">
                    <option value="public">Public (Chatbot)</option>
                    <option value="admin">Admin Only</option>
                  </select>
                </div>
                <div className="flex items-end gap-4 pb-1">
                  <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                    <input type="checkbox" checked={knowledgeForm.compliance_flag}
                      onChange={(e) => setKnowledgeForm(p => ({...p, compliance_flag: e.target.checked}))} className="accent-gold-500" />
                    Compliance Flag
                  </label>
                  <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                    <input type="checkbox" checked={knowledgeForm.active}
                      onChange={(e) => setKnowledgeForm(p => ({...p, active: e.target.checked}))} className="accent-emerald-500" />
                    Active
                  </label>
                </div>
              </div>

              {/* Review Workflow */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Review Owner</label>
                  <input type="text" value={knowledgeForm.review_owner} onChange={(e) => setKnowledgeForm(p => ({...p, review_owner: e.target.value}))}
                    data-testid="knowledge-form-review-owner" className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" placeholder="e.g. Compliance Team" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Next Review Due</label>
                  <input type="date" value={knowledgeForm.next_review_due_at} onChange={(e) => setKnowledgeForm(p => ({...p, next_review_due_at: e.target.value}))}
                    data-testid="knowledge-form-next-review" className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Review Notes</label>
                  <input type="text" value={knowledgeForm.review_notes} onChange={(e) => setKnowledgeForm(p => ({...p, review_notes: e.target.value}))}
                    data-testid="knowledge-form-review-notes" className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" placeholder="Internal review notes" />
                </div>
              </div>

              {/* Edit Note (shown only when editing) */}
              {editingKnowledge && (
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Edit Note / Reason for Change</label>
                  <input type="text" value={knowledgeForm.edit_note || ''} onChange={(e) => setKnowledgeForm(p => ({...p, edit_note: e.target.value}))}
                    data-testid="knowledge-form-edit-note" className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm" placeholder="Why is this being changed?" />
                </div>
              )}

              <button onClick={saveKnowledgeEntry} data-testid="knowledge-form-save"
                disabled={!knowledgeForm.title || !knowledgeForm.answer}
                className="w-full bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all">
                {editingKnowledge ? 'Update Entry' : 'Create Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Knowledge Entry History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowHistoryModal(null)} />
          <div data-testid="knowledge-history-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-2xl max-h-[85vh] overflow-y-auto p-8">
            <button onClick={() => setShowHistoryModal(null)} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all z-10">
              <X className="w-5 h-5 text-black" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <Clock className="w-6 h-6 text-gold-500" />
              <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest">Revision History</h2>
            </div>

            {entryHistory.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm">No revision history for this entry</p>
              </div>
            ) : (
              <div className="space-y-3">
                {entryHistory.map((rev) => (
                  <div key={rev.id} data-testid={`history-v${rev.version}`} className="bg-zinc-900/30 border border-zinc-800 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-gold-500 font-mono text-sm font-bold">v{rev.version}</span>
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${
                            rev.action === 'created' ? 'bg-emerald-500/20 text-emerald-400' :
                            rev.action === 'edited' ? 'bg-blue-500/20 text-blue-400' :
                            rev.action === 'restored' ? 'bg-purple-500/20 text-purple-400' :
                            rev.action === 'reviewed' ? 'bg-gold-500/20 text-gold-500' :
                            rev.action === 'deleted' ? 'bg-red-500/20 text-red-400' :
                            'bg-zinc-800 text-zinc-400'
                          }`}>{rev.action}</span>
                          <span className="text-zinc-600 text-xs">{rev.edited_by}</span>
                          <span className="text-zinc-600 text-xs">{new Date(rev.edited_at).toLocaleString()}</span>
                        </div>

                        {rev.edit_note && (
                          <p className="text-zinc-400 text-xs italic mb-2">"{rev.edit_note}"</p>
                        )}

                        {/* Fields changed diff */}
                        {rev.fields_changed && rev.fields_changed.length > 0 && rev.action !== 'created' && (
                          <div className="space-y-1.5 mt-2">
                            {rev.fields_changed.filter(f => f !== 'last_reviewed_at' && f !== 'created_at' && f !== 'id').map(field => {
                              const prev = rev.previous_values?.[field];
                              const next = rev.new_values?.[field];
                              const prevStr = typeof prev === 'object' ? JSON.stringify(prev) : String(prev ?? '');
                              const nextStr = typeof next === 'object' ? JSON.stringify(next) : String(next ?? '');
                              return (
                                <div key={field} className="bg-black/30 border border-zinc-800/50 p-2">
                                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-mono">{field}</p>
                                  <div className="grid grid-cols-2 gap-2 mt-1">
                                    <div>
                                      <span className="text-red-400/60 text-[10px] font-mono">BEFORE</span>
                                      <p className="text-zinc-400 text-xs break-words line-clamp-3">{prevStr || '(empty)'}</p>
                                    </div>
                                    <div>
                                      <span className="text-emerald-400/60 text-[10px] font-mono">AFTER</span>
                                      <p className="text-white text-xs break-words line-clamp-3">{nextStr || '(empty)'}</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {rev.action === 'created' && (
                          <p className="text-zinc-500 text-xs mt-1">Entry created with {rev.fields_changed?.length || 0} fields</p>
                        )}
                      </div>

                      {/* Restore button (not for latest version or deleted actions) */}
                      {rev.version < entryHistory[0]?.version && rev.action !== 'deleted' && (
                        <button
                          onClick={() => restoreVersion(showHistoryModal, rev.version)}
                          data-testid={`restore-v${rev.version}`}
                          className="shrink-0 text-xs text-zinc-500 hover:text-purple-400 border border-zinc-700 hover:border-purple-400/50 px-3 py-1.5 uppercase tracking-widest font-bold transition-all"
                        >
                          Restore
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Workflow Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowReviewModal(null)} />
          <div data-testid="knowledge-review-modal" className="relative bg-zinc-950 border border-emerald-500/30 w-full max-w-lg p-8">
            <button onClick={() => setShowReviewModal(null)} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-emerald-500 hover:bg-emerald-400 transition-all z-10">
              <X className="w-5 h-5 text-black" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              <h2 className="font-cinzel font-bold text-lg text-white uppercase tracking-widest">Review Entry</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Review Status</label>
                <select value={reviewForm.review_status} onChange={(e) => setReviewForm(p => ({...p, review_status: e.target.value}))}
                  data-testid="review-form-status" className="w-full bg-zinc-900 border border-zinc-700 text-white h-10 px-3 text-sm">
                  <option value="current">Current (Confirmed Up-to-Date)</option>
                  <option value="needs_review">Needs Review (Flag for Later)</option>
                  <option value="archived">Archive (Remove from Active)</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Review Owner</label>
                <input type="text" value={reviewForm.review_owner} onChange={(e) => setReviewForm(p => ({...p, review_owner: e.target.value}))}
                  data-testid="review-form-owner" className="w-full bg-zinc-900 border border-zinc-700 focus:border-emerald-500 text-white h-10 px-3 outline-none text-sm" placeholder="Who is responsible for this review?" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Next Review Due</label>
                <input type="date" value={reviewForm.next_review_due_at} onChange={(e) => setReviewForm(p => ({...p, next_review_due_at: e.target.value}))}
                  data-testid="review-form-next-due" className="w-full bg-zinc-900 border border-zinc-700 focus:border-emerald-500 text-white h-10 px-3 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-manrope mb-1">Review Notes</label>
                <textarea value={reviewForm.review_notes} onChange={(e) => setReviewForm(p => ({...p, review_notes: e.target.value}))}
                  data-testid="review-form-notes" rows={3}
                  className="w-full bg-zinc-900 border border-zinc-700 focus:border-emerald-500 text-white px-3 py-2 outline-none text-sm resize-none" placeholder="Verified against latest regulatory update..." />
              </div>
              <button
                data-testid="review-form-submit"
                onClick={() => markEntryReviewed(showReviewModal, {
                  review_notes: reviewForm.review_notes || undefined,
                  review_owner: reviewForm.review_owner || undefined,
                  next_review_due_at: reviewForm.next_review_due_at ? new Date(reviewForm.next_review_due_at).toISOString() : undefined,
                  review_status: reviewForm.review_status
                })}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all"
              >
                Confirm Review
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert Inquiry Modal */}
      {convertingInquiry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setConvertingInquiry(null)} />
          <div data-testid="convert-inquiry-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-lg p-8">
            <button onClick={() => setConvertingInquiry(null)} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"><X className="w-5 h-5 text-black" /></button>
            <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest mb-2">Convert to Sale</h2>
            <p className="text-zinc-400 text-sm mb-6">Inquiry from <span className="text-white">{convertingInquiry.contact_name}</span> — {convertingInquiry.company_name}</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Sale Amount ($) *</label>
                  <input data-testid="convert-sale-amount" type="number" step="0.01" value={conversionData.sale_amount} onChange={e => setConversionData(p => ({...p, sale_amount: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm font-mono" placeholder="Total sale value" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Total Grams</label>
                  <input data-testid="convert-total-grams" type="number" step="0.01" value={conversionData.total_grams} onChange={e => setConversionData(p => ({...p, total_grams: e.target.value}))} className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm font-mono" placeholder="Grams sold" />
                </div>
              </div>
              <div className="border border-gold-500/30 bg-gold-500/5 p-4">
                <label className="block text-xs uppercase tracking-widest text-gold-500 mb-3 font-bold">Affiliate Commission for this Deal *</label>
                <div className="grid grid-cols-2 gap-4">
                  <select data-testid="convert-commission-type" value={conversionData.commission_type} onChange={e => setConversionData(p => ({...p, commission_type: e.target.value}))} className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm">
                    <option value="">Select type...</option>
                    <option value="per_gram">Per Gram ($)</option>
                    <option value="percentage">% of Deal</option>
                    <option value="flat_fee">Flat Fee ($)</option>
                  </select>
                  <input data-testid="convert-commission-value" type="number" step="0.01" value={conversionData.commission_value} onChange={e => setConversionData(p => ({...p, commission_value: e.target.value}))} className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm font-mono" placeholder={conversionData.commission_type === 'per_gram' ? '$/gram' : conversionData.commission_type === 'percentage' ? '%' : conversionData.commission_type === 'flat_fee' ? 'Flat $' : 'Enter value'} />
                </div>
                {conversionData.commission_type && conversionData.commission_value && conversionData.sale_amount && (
                  <p className="text-gold-500 text-xs mt-2 font-mono">
                    Payout: ${conversionData.commission_type === 'per_gram' 
                      ? (parseFloat(conversionData.commission_value) * (parseFloat(conversionData.total_grams) || 0)).toFixed(2) 
                      : conversionData.commission_type === 'percentage' 
                        ? (parseFloat(conversionData.sale_amount) * parseFloat(conversionData.commission_value) / 100).toFixed(2) 
                        : parseFloat(conversionData.commission_value).toFixed(2)}
                  </p>
                )}
              </div>
              <button onClick={recordConversion} disabled={!conversionData.sale_amount || !conversionData.commission_type || !conversionData.commission_value} data-testid="confirm-conversion-btn" className="w-full bg-cannabis-green hover:bg-green-600 disabled:opacity-50 text-white font-bold uppercase tracking-widest py-3 text-sm transition-all">Confirm Sale Conversion</button>
            </div>
          </div>
        </div>
      )}

      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff'
          }
        }}
      />
    </div>
  );
};

// Main Catalog Page
const CatalogPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userToken, setUserToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  const [strains, setStrains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: '', priceTier: '', minThc: null, search: '', grade: '', growingMethod: '', category: '', bcDd: false, selectedEffects: [], selectedTerpenes: [], coaMinThc: '', coaMaxThc: '', coaMinTerpenes: '', coaDominantTerpene: '', coaAvailability: '' });
  const [selectedStrains, setSelectedStrains] = useState([]);
  const [viewingStrain, setViewingStrain] = useState(null);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showSampleModal, setShowSampleModal] = useState(false);
  const [sampleStrain, setSampleStrain] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', message: '' });
  const [contactSending, setContactSending] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [myReferralCode, setMyReferralCode] = useState('');
  const [referralCopied, setReferralCopied] = useState(false);
  const [loadingReferral, setLoadingReferral] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showSaveListModal, setShowSaveListModal] = useState(false);
  const [saveListName, setSaveListName] = useState('');
  const [savingList, setSavingList] = useState(false);
  const [showMyLists, setShowMyLists] = useState(false);
  const [myLists, setMyLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [ageVerified, setAgeVerified] = useState(() => sessionStorage.getItem('ageVerified') === 'true');
  const [catalogViewMode, setCatalogViewMode] = useState(() => sessionStorage.getItem('fg_catalog_view') || 'image');
  const [catalogSort, setCatalogSort] = useState({ field: null, direction: null }); // {field: 'strain_name', direction: 'asc'|'desc'}
  const [detailViewMode, setDetailViewMode] = useState(() => sessionStorage.getItem('fg_detail_view') || 'image');
  const [marketSnapshot, setMarketSnapshot] = useState(null);
  const [demandSignals, setDemandSignals] = useState(null);
  const [inquiryCart, setInquiryCart] = useState([]);
  const [showCartDrawer, setShowCartDrawer] = useState(false);
  const [cartSubmitting, setCartSubmitting] = useState(false);
  const [cartMessage, setCartMessage] = useState('');
  const [cartMarket, setCartMarket] = useState('');
  const [myDeals, setMyDeals] = useState([]);
  const [showDealsModal, setShowDealsModal] = useState(false);

  // Strain Hunter state
  const [showStrainHunter, setShowStrainHunter] = useState(false);

  // COA Chemistry filter state
  const [coaEnrichment, setCoaEnrichment] = useState({});
  const [coaFilterOptions, setCoaFilterOptions] = useState(null);
  const [hunterQuery, setHunterQuery] = useState('');
  const [hunterType, setHunterType] = useState('');
  const [hunterEffects, setHunterEffects] = useState([]);
  const [hunterTerpenes, setHunterTerpenes] = useState([]);
  const [hunterResults, setHunterResults] = useState(null);
  const [hunterSearching, setHunterSearching] = useState(false);
  const [hunterTab, setHunterTab] = useState('search'); // search, request, history
  const [hunterRequestForm, setHunterRequestForm] = useState({ min_thc: '', desired_kg: '', quantity_unit: 'kg', supply_type: 'spot', repeat_period: '', target_market: '', use_case: '', desired_characteristics: '', notes: '', broadcast_opt_in: false });
  const [hunterMyRequests, setHunterMyRequests] = useState([]);
  const [hunterSubmitting, setHunterSubmitting] = useState(false);
  const [hunterBroadcastResult, setHunterBroadcastResult] = useState(null);
  const [inlineBroadcastSent, setInlineBroadcastSent] = useState(false);
  const [inlineBroadcastSending, setInlineBroadcastSending] = useState(false);
  const [inlineBroadcastResult, setInlineBroadcastResult] = useState(null);

  // Notification Center state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [strainAlertSubs, setStrainAlertSubs] = useState([]);
  const prevUnreadRef = useRef(0);

  const fetchNotifications = async (token) => {
    try {
      const res = await axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token || userToken}` } });
      setNotifications(res.data.notifications || []);
      const newUnread = res.data.unread_count || 0;
      // Play sound if unread count increased
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.setValueAtTime(880, ctx.currentTime);
          osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
          gain.gain.setValueAtTime(0.3, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.3);
        } catch(e) {}
      }
      prevUnreadRef.current = newUnread;
      setUnreadCount(newUnread);
      // Update PWA badge
      if ('setAppBadge' in navigator) { newUnread > 0 ? navigator.setAppBadge(newUnread) : navigator.clearAppBadge(); }
    } catch (e) {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { headers: { Authorization: `Bearer ${userToken}` } });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      prevUnreadRef.current = 0;
      if ('clearAppBadge' in navigator) navigator.clearAppBadge();
    } catch (e) {}
  };

  const markNotifRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/mark-read`, { notification_ids: [id] }, { headers: { Authorization: `Bearer ${userToken}` } });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const fetchStrainAlerts = async (token) => {
    try {
      const res = await axios.get(`${API}/strain-alerts/subscriptions`, { headers: { Authorization: `Bearer ${token || userToken}` } });
      setStrainAlertSubs(res.data.subscriptions || []);
    } catch (e) {}
  };

  const toggleStrainAlert = async (strainName) => {
    const isSubbed = strainAlertSubs.some(s => s.strain_name_lower === strainName.toLowerCase());
    try {
      if (isSubbed) {
        await axios.delete(`${API}/strain-alerts/unsubscribe?strain_name=${encodeURIComponent(strainName)}`, { headers: { Authorization: `Bearer ${userToken}` } });
        setStrainAlertSubs(prev => prev.filter(s => s.strain_name_lower !== strainName.toLowerCase()));
        toast.success(`Unsubscribed from ${strainName} alerts`);
      } else {
        await axios.post(`${API}/strain-alerts/subscribe`, { strain_name: strainName }, { headers: { Authorization: `Bearer ${userToken}` } });
        setStrainAlertSubs(prev => [...prev, { strain_name: strainName, strain_name_lower: strainName.toLowerCase() }]);
        toast.success(`Subscribed to ${strainName} alerts — we'll notify you when it's available!`);
      }
    } catch (e) { toast.error('Failed to update alert subscription'); }
  };


  useEffect(() => {
    // Detect iOS
    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
    const isMobile = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
    setIsIOS(ios);

    // Check if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isStandalone || localStorage.getItem('pwa_install_dismissed')) return;

    // Always show on mobile devices
    if (isMobile) {
      setShowInstallBanner(true);
    }

    if (!ios) {
      // Android/Desktop — also listen for the browser's install prompt
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowInstallBanner(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // Can't auto-install on iOS, just dismiss and let them follow instructions
      setShowInstallBanner(false);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      toast.success('Firegold app installed!');
    }
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa_install_dismissed', 'true');
  };

  const handleShareWithBuyer = async () => {
    setShowShareModal(true);
    if (myReferralCode) return; // Already fetched
    setLoadingReferral(true);
    try {
      const response = await axios.get(`${API}/auth/my-referral`, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      setMyReferralCode(response.data.referral_code);
    } catch (err) {
      toast.error('Failed to generate referral link');
    } finally {
      setLoadingReferral(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/?ref=${myReferralCode}`;
    navigator.clipboard.writeText(link);
    setReferralCopied(true);
    setTimeout(() => setReferralCopied(false), 2000);
    toast.success('Link copied to clipboard!');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in name, email, and message');
      return;
    }
    setContactSending(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success('Message sent! We\'ll be in touch soon.');
      setContactForm({ name: '', email: '', company: '', message: '' });
      setShowContactModal(false);
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setContactSending(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('userToken');
    const user = localStorage.getItem('userData');
    if (token && user) {
      verifyAuth(token, JSON.parse(user));
    } else {
      setCheckingAuth(false);
    }
  }, []);

  const verifyAuth = async (token, user) => {
    try {
      await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserToken(token);
      setUserData(user);
      setIsAuthenticated(true);
      fetchStrains(token);
      fetchMarketSnapshot();
      fetchDemandSignals(token);
      fetchCart(token);
      fetchCoaData(token);
      fetchNotifications(token);
      fetchStrainAlerts(token);
      // Track page view
      trackEvent('page_view', { page: 'catalog' });
    } catch (err) {
      localStorage.removeItem('userToken');
      localStorage.removeItem('userData');
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleAuth = (token, user) => {
    setUserToken(token);
    setUserData(user);
    setIsAuthenticated(true);
    fetchStrains(token);
    fetchMarketSnapshot();
    fetchDemandSignals(token);
    fetchCart(token);
    fetchCoaData(token);
    fetchNotifications(token);
    fetchStrainAlerts(token);
    // Replace history so back button doesn't go to login
    window.history.replaceState({ page: 'catalog' }, '', '/');
    trackEvent('page_view', { page: 'catalog', event_data: { first_visit: true } });
  };

  const handleLogout = () => {
    trackEvent('logout', { page: 'catalog' });
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    sessionStorage.removeItem('ageVerified');
    setUserToken(null);
    setUserData(null);
    setIsAuthenticated(false);
    setAgeVerified(false);
    setStrains([]);
    setNotifications([]);
    setUnreadCount(0);
    prevUnreadRef.current = 0;
    if ('clearAppBadge' in navigator) navigator.clearAppBadge();
    window.history.replaceState({ page: 'login' }, '', '/');
  };

  // Poll notifications every 30s when authenticated
  useEffect(() => {
    if (!isAuthenticated || !userToken) return;
    const interval = setInterval(() => fetchNotifications(userToken), 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, userToken]);

  // Handle browser back button for internal views
  useEffect(() => {
    const handlePopState = (e) => {
      if (viewingStrain) {
        e.preventDefault();
        setViewingStrain(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [viewingStrain]);

  const fetchStrains = async (token) => {
    try {
      const response = await axios.get(`${API}/strains`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrains(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        toast.error('Failed to load catalog');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch market snapshot (public)
  const fetchMarketSnapshot = async () => {
    try {
      const res = await axios.get(`${API}/market-snapshot`);
      setMarketSnapshot(res.data);
    } catch (err) { console.debug('Market snapshot failed:', err); }
  };

  // Fetch COA chemistry enrichment & filter options
  const fetchCoaData = async (token) => {
    try {
      const [enrichRes, optionsRes] = await Promise.all([
        axios.get(`${API}/strains/coa-enrichment`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/strains/coa-filter-options`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setCoaEnrichment(enrichRes.data || {});
      setCoaFilterOptions(optionsRes.data || null);
    } catch (err) { console.debug('COA data fetch failed:', err); }
  };

  // Fetch demand signals
  const fetchDemandSignals = async (token) => {
    try {
      const res = await axios.get(`${API}/demand-signals`, { headers: { Authorization: `Bearer ${token}` } });
      setDemandSignals(res.data);
    } catch (err) { console.debug('Demand signals failed:', err); }
  };

  // Fetch inquiry cart
  const fetchCart = async (token) => {
    try {
      const res = await axios.get(`${API}/cart`, { headers: { Authorization: `Bearer ${token}` } });
      setInquiryCart(res.data.items || []);
    } catch (err) { console.debug('Cart fetch failed:', err); }
  };

  // Add to cart
  const addToCart = async (strainId, desiredKg = 0) => {
    try {
      const res = await axios.post(`${API}/cart/add`, { strain_id: strainId, desired_kg: desiredKg }, { headers: { Authorization: `Bearer ${userToken}` } });
      setInquiryCart(res.data.items || []);
      const strain = strains.find(s => s.id === strainId);
      toast.success(`${strain?.strain_name || 'Strain'} added to inquiry cart`);
    } catch (err) { toast.error('Failed to add to cart'); }
  };

  // Remove from cart
  const removeFromCart = async (strainId) => {
    try {
      const res = await axios.delete(`${API}/cart/remove/${strainId}`, { headers: { Authorization: `Bearer ${userToken}` } });
      setInquiryCart(res.data.items || []);
    } catch (err) { toast.error('Failed to remove from cart'); }
  };

  // Update cart item quantity
  const updateCartItem = async (strainId, desiredKg, targetPrice) => {
    try {
      const res = await axios.post(`${API}/cart/add`, {
        strain_id: strainId, desired_kg: desiredKg, target_price_per_gram: targetPrice || null
      }, { headers: { Authorization: `Bearer ${userToken}` } });
      setInquiryCart(res.data.items || []);
    } catch (err) { console.debug('Cart update failed:', err); }
  };

  // Submit cart as deal
  const submitCart = async () => {
    if (inquiryCart.length === 0) { toast.error('Cart is empty'); return; }
    setCartSubmitting(true);
    try {
      await axios.post(`${API}/cart/submit`, {
        items: inquiryCart.map(i => ({ strain_id: i.strain_id, desired_kg: i.desired_kg || 0, target_price_per_gram: i.target_price_per_gram || null, notes: i.notes })),
        message: cartMessage, market_of_interest: cartMarket
      }, { headers: { Authorization: `Bearer ${userToken}` } });
      toast.success('Deal request submitted! Our team will be in touch.');
      setInquiryCart([]);
      setShowCartDrawer(false);
      setCartMessage('');
      setCartMarket('');
    } catch (err) { toast.error('Failed to submit deal request'); }
    setCartSubmitting(false);
  };

  // Fetch user's deals
  const fetchDeals = async () => {
    try {
      const res = await axios.get(`${API}/deals`, { headers: { Authorization: `Bearer ${userToken}` } });
      setMyDeals(res.data);
    } catch (err) { console.debug('Deals fetch failed:', err); }
  };

  // ── Strain Hunter functions ──
  const hunterSearch = async () => {
    if (!hunterQuery.trim()) { toast.error('Enter a strain name to search'); return; }
    setHunterSearching(true); setHunterResults(null); setInlineBroadcastSent(false); setInlineBroadcastResult(null);
    try {
      const res = await axios.post(`${API}/strain-hunter/search`, {
        strain_name: hunterQuery,
        strain_type: hunterType || undefined,
        desired_effects: hunterEffects,
        desired_terpenes: hunterTerpenes,
        use_case: hunterRequestForm.use_case || undefined
      }, { headers: { Authorization: `Bearer ${userToken}` } });
      setHunterResults(res.data);
    } catch (err) { toast.error('Search failed'); }
    setHunterSearching(false);
  };

  const hunterInlineBroadcast = async () => {
    if (!hunterQuery.trim()) return;
    setInlineBroadcastSending(true);
    try {
      const res = await axios.post(`${API}/strain-hunter/request`, {
        strain_name: hunterQuery,
        strain_type: hunterType || null,
        desired_effects: hunterEffects,
        desired_terpenes: hunterTerpenes,
        use_case: hunterRequestForm.use_case || null,
        broadcast_opt_in: true
      }, { headers: { Authorization: `Bearer ${userToken}` } });
      setInlineBroadcastResult(res.data);
      setInlineBroadcastSent(true);
      toast.success('Anonymous request sent!');
    } catch (err) { toast.error('Failed to send request'); }
    setInlineBroadcastSending(false);
  };

  const hunterSubmitRequest = async () => {
    if (!hunterQuery.trim()) { toast.error('Enter a strain name'); return; }
    setHunterSubmitting(true); setHunterBroadcastResult(null);
    try {
      const res = await axios.post(`${API}/strain-hunter/request`, {
        strain_name: hunterQuery,
        strain_type: hunterType || null,
        desired_effects: hunterEffects,
        desired_terpenes: hunterTerpenes,
        min_thc: hunterRequestForm.min_thc ? parseFloat(hunterRequestForm.min_thc) : null,
        desired_kg: hunterRequestForm.desired_kg ? parseFloat(hunterRequestForm.desired_kg) : null,
        quantity_unit: hunterRequestForm.quantity_unit || 'kg',
        supply_type: hunterRequestForm.supply_type || 'spot',
        repeat_period: hunterRequestForm.repeat_period || null,
        target_market: hunterRequestForm.target_market || null,
        use_case: hunterRequestForm.use_case || null,
        desired_characteristics: hunterRequestForm.desired_characteristics || null,
        notes: hunterRequestForm.notes || null,
        broadcast_opt_in: hunterRequestForm.broadcast_opt_in || false
      }, { headers: { Authorization: `Bearer ${userToken}` } });
      setHunterBroadcastResult(res.data);
      toast.success('Seeking request submitted! Check your email for strain matches.');
      setHunterRequestForm({ min_thc: '', desired_kg: '', quantity_unit: 'kg', supply_type: 'spot', repeat_period: '', target_market: '', use_case: '', desired_characteristics: '', notes: '', broadcast_opt_in: false });
      hunterFetchMyRequests();
    } catch (err) { toast.error('Failed to submit request'); }
    setHunterSubmitting(false);
  };

  const hunterFetchMyRequests = async () => {
    try {
      const res = await axios.get(`${API}/strain-hunter/my-requests`, { headers: { Authorization: `Bearer ${userToken}` } });
      setHunterMyRequests(res.data);
    } catch (err) { console.debug('Hunter requests fetch failed:', err); }
  };

  const openStrainHunter = () => {
    setShowStrainHunter(true);
    setHunterTab('search');
    setHunterResults(null);
    setHunterQuery('');
    setHunterType('');
    setHunterEffects([]);
    setHunterTerpenes([]);
    setInlineBroadcastSent(false);
    setInlineBroadcastResult(null);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    if (newFilters.type || newFilters.priceTier || newFilters.minThc || newFilters.grade) {
      trackEvent('filter_apply', {
        filters_applied: {
          type: newFilters.type || null,
          price_tier: newFilters.priceTier || null,
          min_thc: newFilters.minThc || null,
          grade: newFilters.grade || null
        }
      });
    }
  };

  // Track search
  const handleSearch = (searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
    if (searchTerm && searchTerm.length > 2) {
      trackEvent('search', { search_query: searchTerm });
    }
  };

  // Track strain view
  const handleViewStrain = (strain) => {
    setViewingStrain(strain);
    window.history.pushState({ view: 'strain' }, '', '/');
    trackEvent('strain_view', {
      strain_id: strain.id,
      strain_name: strain.strain_name,
      thc_percentage: strain.thc_percentage,
      strain_type: strain.type,
      price_tier: strain.price_tier
    });
  };

  // Track strain card click
  const handleStrainClick = (strain) => {
    trackEvent('strain_click', {
      strain_id: strain.id,
      strain_name: strain.strain_name,
      strain_type: strain.type,
      price_tier: strain.price_tier
    });
    handleViewStrain(strain);
  };

  const filteredStrains = useMemo(() => {
    return strains.filter(strain => {
      if (filters.type && !strain.type.toLowerCase().includes(filters.type.toLowerCase())) return false;
      if (filters.priceTier && strain.price_tier !== filters.priceTier) return false;
      if (filters.search && !strain.strain_name.toLowerCase().includes(filters.search.toLowerCase())) return false;
      if (filters.minThc) {
        const thcVal = parseFloat(strain.thc_percentage.replace('%', '').replace('+', ''));
        if (thcVal < filters.minThc) return false;
      }
      if (filters.grade) {
        const gradeKey = `grade_${filters.grade.toLowerCase()}_kg`;
        if (!strain[gradeKey] || strain[gradeKey] <= 0) return false;
      }
      if (filters.growingMethod && strain.growing_method !== filters.growingMethod) return false;
      if (filters.category && strain.category !== filters.category) return false;
      if (filters.bcDd && !strain.bc_dd_compliant) return false;
      if (filters.selectedEffects && filters.selectedEffects.length > 0) {
        const strainEffects = Array.isArray(strain.effects)
          ? strain.effects
          : (typeof strain.effects === 'string' ? strain.effects.split(',').map(e => e.trim()) : []);
        const hasAll = filters.selectedEffects.every(e => strainEffects.includes(e));
        if (!hasAll) return false;
      }
      if (filters.selectedTerpenes && filters.selectedTerpenes.length > 0) {
        const strainTerps = Array.isArray(strain.terpene_profile)
          ? strain.terpene_profile.map(t => t.toLowerCase())
          : [];
        const hasAllTerps = filters.selectedTerpenes.every(t => strainTerps.some(st => st.includes(t.toLowerCase())));
        if (!hasAllTerps) return false;
      }

      // ── COA Chemistry Filters ──
      const coa = coaEnrichment[strain.id];
      const hasCoa = !!(strain.coa_url || strain.gridfs_coa_id);
      const hasParsedCoa = !!coa;

      // COA Availability filter
      if (filters.coaAvailability === 'has_parsed_coa' && !hasParsedCoa) return false;
      if (filters.coaAvailability === 'has_coa' && !hasCoa) return false;
      if (filters.coaAvailability === 'no_coa' && hasCoa) return false;

      // COA-based filters only apply to strains WITH parsed COA — strains without pass through
      if (hasParsedCoa) {
        if (filters.coaMinThc && coa.total_thc != null && coa.total_thc < parseFloat(filters.coaMinThc)) return false;
        if (filters.coaMaxThc && coa.total_thc != null && coa.total_thc > parseFloat(filters.coaMaxThc)) return false;
        if (filters.coaMinTerpenes && coa.total_terpenes != null && coa.total_terpenes < parseFloat(filters.coaMinTerpenes)) return false;
        if (filters.coaDominantTerpene && coa.dominant_terpene !== filters.coaDominantTerpene) return false;
      } else {
        // Strains without parsed COA: only exclude if a COA-chemistry filter is active AND coaAvailability is 'has_parsed_coa'
        if (filters.coaAvailability === 'has_parsed_coa') return false; // already handled above
        // For other COA chemistry filters — strains without data still pass through (graceful fallback)
      }

      return true;
    });
  }, [strains, filters, coaEnrichment]);

  const sortedStrains = useMemo(() => {
    if (!catalogSort.field || !catalogSort.direction) return filteredStrains;
    const { field, direction } = catalogSort;
    const sorted = [...filteredStrains].sort((a, b) => {
      let valA, valB;
      switch (field) {
        case 'strain_name':
          valA = (a.strain_name || '').toLowerCase();
          valB = (b.strain_name || '').toLowerCase();
          return direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'category':
          valA = (a.category || '').toLowerCase();
          valB = (b.category || '').toLowerCase();
          return direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'thc':
          valA = parseFloat((a.thc_percentage || '0').replace('%', '').replace('+', '')) || 0;
          valB = parseFloat((b.thc_percentage || '0').replace('%', '').replace('+', '')) || 0;
          return direction === 'asc' ? valA - valB : valB - valA;
        case 'lot_size':
          valA = parseFloat((a.lot_size || '0').replace(/[^0-9.]/g, '')) || 0;
          valB = parseFloat((b.lot_size || '0').replace(/[^0-9.]/g, '')) || 0;
          return direction === 'asc' ? valA - valB : valB - valA;
        case 'growing_method':
          valA = (a.growing_method || '').toLowerCase();
          valB = (b.growing_method || '').toLowerCase();
          return direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'target_price':
          valA = parseFloat(a.target_price_per_gram || 0);
          valB = parseFloat(b.target_price_per_gram || 0);
          return direction === 'asc' ? valA - valB : valB - valA;
        case 'province':
          valA = (a.province || '').toLowerCase();
          valB = (b.province || '').toLowerCase();
          return direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        case 'harvest_date':
          valA = a.harvest_date || '';
          valB = b.harvest_date || '';
          return direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        default:
          return 0;
      }
    });
    return sorted;
  }, [filteredStrains, catalogSort]);

  // Group strains by genetic type: Indica, Sativa, Hybrid
  const groupStrainsByCategory = (strainList) => {
    const groups = { 'Indica': [], 'Sativa': [], 'Hybrid': [] };
    strainList.forEach(s => {
      const t = (s.type || '').toLowerCase();
      if (t === 'indica' || t === 'indica dominant') {
        groups['Indica'].push(s);
      } else if (t === 'sativa' || t === 'sativa dominant') {
        groups['Sativa'].push(s);
      } else {
        groups['Hybrid'].push(s);
      }
    });
    const order = ['Indica', 'Sativa', 'Hybrid'];
    return order
      .filter(key => groups[key].length > 0)
      .map(key => ({ heading: key, isConcentrate: false, strains: groups[key] }));
  };


  const toggleCatalogSort = (field) => {
    setCatalogSort(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };

  const SortHeader = ({ field, label, className = '', testId }) => {
    const active = catalogSort.field === field;
    return (
      <th
        data-testid={testId || `sort-header-${field}`}
        onClick={() => toggleCatalogSort(field)}
        className={`py-3 px-2 text-[10px] uppercase tracking-widest font-manrope cursor-pointer select-none group transition-colors hover:text-gold-500 ${active ? 'text-gold-500 font-bold' : 'text-zinc-500'} ${className}`}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {active ? (
            catalogSort.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
          ) : (
            <ArrowUpDown className="w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity" />
          )}
        </span>
      </th>
    );
  };

  const toggleStrainSelection = (strainId) => {
    const strain = strains.find(s => s.id === strainId);
    if (strain) {
      trackEvent('strain_select', {
        strain_id: strain.id,
        strain_name: strain.strain_name,
        strain_type: strain.type,
        price_tier: strain.price_tier
      });
    }
    setSelectedStrains(prev => 
      prev.includes(strainId) 
        ? prev.filter(id => id !== strainId)
        : [...prev, strainId]
    );
  };

  const handleRequestSample = (strain) => {
    trackEvent('sample_request_start', {
      strain_id: strain.id,
      strain_name: strain.strain_name,
      strain_type: strain.type
    });
    setViewingStrain(null);
    setSampleStrain(strain);
    setShowSampleModal(true);
  };

  const handleOpenInquiry = () => {
    trackEvent('inquiry_start', {
      strains_count: selectedStrains.length,
      strain_ids: selectedStrains
    });
    setShowInquiryModal(true);
  };

  // Saved Lists functions
  const fetchMyLists = async () => {
    setLoadingLists(true);
    try {
      const res = await axios.get(`${API}/user/lists`, { headers: { Authorization: `Bearer ${userToken}` } });
      setMyLists(res.data);
    } catch (err) {
      console.error('Failed to fetch lists:', err);
    }
    setLoadingLists(false);
  };

  const handleSaveList = async () => {
    if (!saveListName.trim()) { toast.error('Please enter a list name'); return; }
    const strainIds = filteredStrains.map(s => s.id);
    if (strainIds.length === 0) { toast.error('No strains to save'); return; }
    setSavingList(true);
    try {
      await axios.post(`${API}/user/lists`, {
        list_name: saveListName.trim(),
        strain_ids: strainIds,
        filters_applied: filters
      }, { headers: { Authorization: `Bearer ${userToken}` } });
      toast.success(`List "${saveListName}" saved with ${strainIds.length} strains!`);
      setShowSaveListModal(false);
      setSaveListName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save list');
    }
    setSavingList(false);
  };

  const handleDeleteList = async (listId, listName) => {
    if (!window.confirm(`Delete "${listName}"?`)) return;
    try {
      await axios.delete(`${API}/user/lists/${listId}`, { headers: { Authorization: `Bearer ${userToken}` } });
      setMyLists(prev => prev.filter(l => l.id !== listId));
      toast.success('List deleted');
    } catch (err) {
      toast.error('Failed to delete list');
    }
  };

  const handleDownloadCsv = async (listId, listName) => {
    try {
      const res = await axios.get(`${API}/user/lists/${listId}/csv`, {
        headers: { Authorization: `Bearer ${userToken}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `firegold_${listName.replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error('Failed to download CSV');
    }
  };

  // ── Hooks that must run before conditional returns ──
  useEffect(() => {
    if (!showNotifications) return;
    const handleClick = (e) => {
      if (!e.target.closest('[data-testid="notification-bell"]') && !e.target.closest('[data-testid="notification-panel"]')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showNotifications]);

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-gold-500">Loading...</div>
      </div>
    );
  }

  // Age verification gate — shown once per session before any content
  // Show auth page if not authenticated (BEFORE age gate — users need to login first)
  if (!isAuthenticated) {
    return <AuthPage onAuth={handleAuth} />;
  }

  if (!ageVerified) {
    return (
      <div data-testid="age-gate" className="min-h-screen bg-obsidian flex items-center justify-center p-6">
        <div className="bg-zinc-950 border border-zinc-800 w-full max-w-sm p-8 text-center">
          <img src={FIREGOLD_LOGO} alt="Firegold" className="h-20 mx-auto mb-6" />
          <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest mb-3">Age Verification</h2>
          <p className="text-zinc-400 text-sm mb-8">You must be 19 years old to enter</p>
          <div className="space-y-3">
            <button
              data-testid="age-gate-confirm"
              onClick={() => { sessionStorage.setItem('ageVerified', 'true'); setAgeVerified(true); }}
              className="w-full bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all"
            >
              I am 19+
            </button>
            <button
              data-testid="age-gate-deny"
              onClick={() => window.location.href = 'https://google.com'}
              className="w-full border border-zinc-700 hover:border-red-500 text-zinc-400 hover:text-red-400 font-bold uppercase tracking-widest py-3 text-sm transition-all"
            >
              I am not 19
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="catalog-page" className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="fixed top-0 w-full z-40 bg-black/80 backdrop-blur-md border-b border-white/5 h-20 flex items-center justify-between px-6 md:px-12">
        <div className="flex items-center gap-3">
          <img src={FIREGOLD_LOGO} alt="Firegold Brands" className="h-14 md:h-[4.5rem] w-auto object-contain" />
        </div>
        
        {/* Search */}
        <div className="hidden md:flex items-center flex-1 max-w-md mx-8">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="search-input"
              type="text"
              placeholder="Search strains..."
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-10 pl-10 pr-4 transition-colors outline-none text-sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowContactModal(true)}
            data-testid="contact-us-btn"
            className="hidden md:flex items-center gap-2 text-zinc-400 hover:text-gold-500 transition-colors"
            title="Contact Us"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs uppercase tracking-widest font-manrope">Contact</span>
          </button>
          <a
            href="/submit-strain"
            data-testid="submit-strain-cta"
            className="hidden md:flex items-center justify-center gap-2 border-2 border-cannabis-green text-cannabis-green hover:bg-cannabis-green hover:text-white font-bold uppercase tracking-widest px-5 py-2.5 text-xs transition-all w-52 h-11"
          >
            <Leaf className="w-4 h-4" />
            Submit Your Product
          </a>
          <button
            onClick={openStrainHunter}
            data-testid="strain-hunter-btn"
            className="hidden md:flex items-center justify-center gap-2 border-2 border-amber-500/70 text-amber-400 hover:bg-amber-500 hover:text-black font-bold uppercase tracking-widest px-5 py-2.5 text-xs transition-all w-52 h-11"
          >
            <Crosshair className="w-4 h-4" />
            Strain Hunter
          </button>
          <a
            href="https://www.instagram.com/my_firegold/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-link-desktop"
            className="hidden md:flex items-center justify-center w-10 h-10 border border-zinc-700 hover:border-pink-500 text-zinc-300 hover:text-pink-400 transition-all"
          >
            <InstagramColorIcon className="w-6 h-6" />
          </a>
          <button
            onClick={handleShareWithBuyer}
            data-testid="share-buyer-btn"
            className="hidden md:flex items-center justify-center gap-2 border-2 border-gold-500 text-gold-500 hover:bg-gold-500 hover:text-black font-bold uppercase tracking-widest px-5 py-2.5 text-xs transition-all w-52 h-11"
          >
            <Share2 className="w-4 h-4" />
            Share with Buyer
          </button>
          {selectedStrains.length > 0 && (
            <button
              data-testid="open-inquiry-btn"
              onClick={handleOpenInquiry}
              className="bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest px-6 py-2 text-sm transition-all"
            >
              Inquire ({selectedStrains.length})
            </button>
          )}
          {/* Notification Bell */}
          <div className="relative">
            <button
              data-testid="notification-bell"
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative flex items-center gap-1.5 text-zinc-400 hover:text-gold-500 transition-colors border border-zinc-700 hover:border-gold-500/50 px-3 py-2"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold min-w-[20px] h-5 flex items-center justify-center rounded-full animate-pulse">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>
            {showNotifications && (
              <div data-testid="notification-panel" className="absolute right-0 top-12 w-80 md:w-96 bg-zinc-950 border border-zinc-800 shadow-2xl z-50 max-h-[70vh] flex flex-col">
                <div className="flex items-center justify-between p-3 border-b border-zinc-800">
                  <span className="text-white text-sm font-bold uppercase tracking-widest">Notifications</span>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className="text-gold-500 text-[10px] uppercase tracking-widest hover:text-gold-400 font-bold">Mark All Read</button>
                    )}
                    <button onClick={() => setShowNotifications(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-zinc-600 text-sm">No notifications yet</div>
                  ) : notifications.map(n => (
                    <div
                      key={n.id}
                      data-testid={`notification-item-${n.id}`}
                      onClick={() => { if (!n.read) markNotifRead(n.id); if (n.link) { setShowNotifications(false); } }}
                      className={`p-3 border-b border-zinc-800/50 cursor-pointer hover:bg-zinc-900/50 transition-colors ${!n.read ? 'bg-gold-500/5 border-l-2 border-l-gold-500' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${!n.read ? 'bg-gold-500' : 'bg-zinc-700'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-bold ${!n.read ? 'text-white' : 'text-zinc-400'}`}>{n.title}</p>
                          <p className="text-zinc-500 text-[11px] mt-0.5 line-clamp-2">{n.message}</p>
                          <p className="text-zinc-700 text-[10px] mt-1">{new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Inquiry Cart */}
          <button
            data-testid="inquiry-cart-btn"
            onClick={() => setShowCartDrawer(true)}
            className="relative flex items-center gap-1.5 text-zinc-400 hover:text-gold-500 transition-colors border border-zinc-700 hover:border-gold-500/50 px-3 py-2"
          >
            <ShoppingCart className="w-4 h-4" />
            <span className="text-xs uppercase tracking-widest font-manrope font-bold hidden md:inline">Cart</span>
            {inquiryCart.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-gold-500 text-black text-[10px] font-bold w-5 h-5 flex items-center justify-center">{inquiryCart.length}</span>
            )}
          </button>
          {userData && (
            <div className="flex items-center gap-4">
              <span className="text-zinc-400 text-sm hidden md:block">
                {userData.first_name} · {userData.company}
              </span>
              <button 
                onClick={handleLogout}
                data-testid="user-logout-btn"
                className="text-zinc-500 hover:text-red-400 transition-colors"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          )}
          <button 
            data-testid="mobile-menu-btn"
            className="md:hidden flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 hover:border-gold-500 transition-all"
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
          >
            <Menu className="w-5 h-5 text-gold-500" />
            <span className="text-gold-500 text-xs font-manrope font-bold uppercase tracking-widest">Filter</span>
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-12 relative flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1647268465509-f0229a865b6f)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-transparent to-obsidian" />
        <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-5xl mx-auto">
          <img 
            src={FIREGOLD_MARKETPLACE_LOGO} 
            alt="Firegold Marketplace" 
            data-testid="hero-marketplace-logo"
            className="h-48 sm:h-64 md:h-80 lg:h-96 w-auto mb-0" 
            style={{ mixBlendMode: 'lighten' }} 
          />
          <h1 className="font-cinzel font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-gold-grad tracking-tight mb-2 -mt-4">
            Canadian Craft Cannabis Market
          </h1>
          <p className="font-manrope text-zinc-400 text-lg md:text-xl max-w-2xl">
            Premium wholesale cannabis flower for discerning B2B partners
          </p>
          <p className="font-manrope text-gold-500/60 text-base md:text-lg mt-4">
            {new Date().toLocaleString('en-US', { month: 'long' })} {new Date().getFullYear()} Catalog
          </p>
        </div>
      </section>

      {/* Market Snapshot Bar */}
      {marketSnapshot && (
        <section data-testid="market-snapshot" className="relative z-10 border-y border-gold-500/20 bg-black/60 backdrop-blur-sm">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-gold-500" />
              <h2 className="font-manrope text-xs uppercase tracking-[0.2em] text-gold-500 font-bold">FireGold Market Snapshot</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
              <div data-testid="snapshot-listings">
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Active Listings</p>
                <p className="text-white font-mono text-lg font-bold">{marketSnapshot.active_listings}</p>
              </div>
              <div data-testid="snapshot-flower-inventory">
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Flower Inventory</p>
                <p className="text-white font-mono text-lg font-bold">{(marketSnapshot.flower_inventory_kg || 0).toLocaleString()} <span className="text-zinc-500 text-xs">kg</span></p>
              </div>
              <div data-testid="snapshot-trim-inventory">
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Trim Inventory</p>
                <p className="text-white font-mono text-lg font-bold">{(marketSnapshot.trim_inventory_kg || 0).toLocaleString()} <span className="text-zinc-500 text-xs">kg</span></p>
              </div>
              <div data-testid="snapshot-concentrates-inventory">
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Concentrates Inventory</p>
                <p className="text-white font-mono text-lg font-bold">{(marketSnapshot.concentrates_inventory_kg || 0).toLocaleString()} <span className="text-zinc-500 text-xs">kg</span></p>
              </div>
              <div data-testid="snapshot-thc">
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Avg Flower THC</p>
                <p className="text-gold-500 font-mono text-lg font-bold">{marketSnapshot.avg_flower_thc}%</p>
              </div>
              <div data-testid="snapshot-top-searched">
                <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Top Strain Searched</p>
                {marketSnapshot.top_searched_strains && marketSnapshot.top_searched_strains.length > 0 ? (
                  <div className="flex flex-col gap-0.5">
                    {marketSnapshot.top_searched_strains.map((s, i) => (
                      <p key={i} className="text-gold-500 font-mono text-sm font-bold flex items-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5" />{s.name}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-600 font-mono text-sm">No data yet</p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Demand Intelligence Bar */}
      {demandSignals && demandSignals.total_inquiries > 0 && (
        <section data-testid="demand-signals" className="relative z-10 border-b border-zinc-800/50 bg-zinc-950/80">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-3">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
              <div className="flex items-center gap-2">
                <Target className="w-3.5 h-3.5 text-red-400" />
                <span className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope">Active Buyer Demand</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-zinc-400 text-xs">Volume:</span>
                <span className="text-white font-mono text-xs font-bold">{demandSignals.total_volume_kg} kg</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-zinc-400 text-xs">Inquiries (30d):</span>
                <span className="text-gold-500 font-mono text-xs font-bold">{demandSignals.recent_30d_inquiries}</span>
              </div>
              {demandSignals.top_strains.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-zinc-400 text-xs">Most Requested:</span>
                  <span className="text-white text-xs font-bold">{demandSignals.top_strains[0].name}</span>
                </div>
              )}
              {Object.keys(demandSignals.demand_by_market).length > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-zinc-400 text-xs">Top Market:</span>
                  <span className="text-white text-xs font-bold">{Object.keys(demandSignals.demand_by_market)[0]}</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div data-testid="pwa-install-banner" className="bg-gold-500/15 border-y border-gold-500/30 py-3 px-4 md:px-6">
          <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <img src="/firegold_icon_192.png" alt="Firegold" className="w-10 h-10 rounded-lg flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-white font-manrope text-sm font-bold">Install Firegold App</p>
                {isIOS ? (
                  <p className="text-zinc-300 text-xs">
                    Tap <span className="inline-flex items-center text-gold-500 font-bold">
                      <svg className="w-3.5 h-3.5 inline mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                      Share
                    </span> then <span className="text-gold-500 font-bold">"Add to Home Screen"</span>
                  </p>
                ) : (
                  <p className="text-zinc-400 text-xs hidden md:block">Quick access from your home screen — no app store needed</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isIOS && (
                <button
                  data-testid="pwa-install-btn"
                  onClick={handleInstallClick}
                  className="bg-gold-500 text-black font-bold text-xs uppercase tracking-widest px-4 py-2 hover:bg-gold-400 transition-all"
                >
                  Install
                </button>
              )}
              <button
                data-testid="pwa-dismiss-btn"
                onClick={dismissInstallBanner}
                className="w-8 h-8 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"
              >
                <X className="w-4 h-4 text-black" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 md:px-12 pb-24">
        {/* Mobile Search */}
        <div className="md:hidden mb-6">
          <div className="relative w-full mb-3">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="mobile-search-input"
              type="text"
              placeholder="Search strains..."
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white placeholder-zinc-600 h-12 pl-10 pr-4 transition-colors outline-none"
            />
          </div>
          <a
            href="/submit-strain"
            data-testid="submit-strain-cta-mobile"
            className="flex items-center justify-center gap-2 border-2 border-cannabis-green text-cannabis-green font-bold uppercase tracking-widest px-5 py-3 text-xs transition-all w-full"
          >
            <Leaf className="w-4 h-4" />
            Submit Your Product
          </a>
          <button
            onClick={openStrainHunter}
            data-testid="strain-hunter-btn-mobile"
            className="flex items-center justify-center gap-2 border-2 border-amber-500/70 text-amber-400 font-bold uppercase tracking-widest px-5 py-3 text-xs transition-all w-full"
          >
            <Crosshair className="w-4 h-4" />
            Strain Hunter
          </button>
          <button
            onClick={handleShareWithBuyer}
            data-testid="share-buyer-btn-mobile"
            className="flex items-center justify-center gap-2 border-2 border-gold-500 text-gold-500 font-bold uppercase tracking-widest px-5 py-3 text-xs transition-all w-full"
          >
            <Share2 className="w-4 h-4" />
            Share with Buyer
          </button>
        </div>

        <div className="flex gap-12">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <div className="sticky top-24 overflow-y-auto pr-2 scrollbar-thin pb-8" style={{ maxHeight: 'calc(100vh - 7rem)' }}>
              <div className="flex items-center gap-2 mb-8">
                <Filter className="w-4 h-4 text-gold-500" />
                <h2 className="font-manrope font-semibold text-sm uppercase tracking-widest text-zinc-400">Filters</h2>
              </div>

              {/* Popular Effects Quick Filters */}
              {marketSnapshot?.popular_effects?.length > 0 && (
                <div className="mb-6" data-testid="popular-effects-quickfilters">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope mb-2">Popular Effects</p>
                  <div className="flex flex-wrap gap-1.5">
                    {marketSnapshot.popular_effects.map(effect => {
                      const isActive = (filters.selectedEffects || []).includes(effect);
                      return (
                        <button
                          key={effect}
                          data-testid={`quick-effect-${effect.replace(/\s+/g, '-').toLowerCase()}`}
                          onClick={() => {
                            const current = filters.selectedEffects || [];
                            const updated = isActive ? current.filter(e => e !== effect) : [...current, effect];
                            handleFilterChange({ ...filters, selectedEffects: updated });
                          }}
                          className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-manrope font-bold border transition-all ${
                            isActive ? 'bg-gold-500/20 border-gold-500 text-gold-500' : 'border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300'
                          }`}
                        >
                          {effect}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <FilterSidebar filters={filters} setFilters={handleFilterChange} strains={strains} coaFilterOptions={coaFilterOptions} />
            </div>
          </aside>

          {/* Mobile Filters */}
          {mobileFiltersOpen && (
            <div className="md:hidden fixed inset-0 z-50 bg-black/95 p-6 overflow-y-auto">
              <div className="flex justify-between items-center mb-8">
                <h2 className="font-manrope font-semibold text-lg text-white">Filters</h2>
                <button onClick={() => setMobileFiltersOpen(false)} className="w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all">
                  <X className="w-6 h-6 text-black" />
                </button>
              </div>
              <FilterSidebar filters={filters} setFilters={setFilters} strains={strains} coaFilterOptions={coaFilterOptions} />
              <button 
                onClick={() => setMobileFiltersOpen(false)}
                className="w-full mt-8 bg-gold-500 text-black font-bold uppercase tracking-widest px-6 py-4"
              >
                Apply Filters
              </button>
              <button 
                onClick={() => { setMobileFiltersOpen(false); setShowContactModal(true); }}
                data-testid="mobile-contact-btn"
                className="w-full mt-4 border border-gold-500 text-gold-500 font-bold uppercase tracking-widest px-6 py-4 flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" /> Contact Us
              </button>
              <a
                href="https://www.instagram.com/my_firegold/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-link-mobile"
                className="w-full mt-4 border border-pink-500/50 text-pink-400 font-bold uppercase tracking-widest px-6 py-4 flex items-center justify-center gap-2 hover:bg-pink-500/10 transition-all"
              >
                <InstagramColorIcon className="w-5 h-5" /> Follow Us on Instagram
              </a>
            </div>
          )}

          {/* Product Grid */}
          <div className="flex-1">
            <div className="flex justify-between items-center mb-4">
              <p className="text-zinc-500 text-sm">
                Showing <span className="text-white font-mono">{filteredStrains.length}</span> of {strains.length} strains
              </p>
              <div className="flex items-center gap-3">
                {/* View Toggle */}
                <div className="flex border border-zinc-800 overflow-hidden" data-testid="catalog-view-toggle">
                  <button
                    data-testid="catalog-view-image"
                    onClick={() => { setCatalogViewMode('image'); sessionStorage.setItem('fg_catalog_view', 'image'); }}
                    className={`px-3 py-1.5 text-xs uppercase tracking-widest font-manrope font-bold transition-all ${
                      catalogViewMode === 'image' ? 'bg-gold-500 text-black' : 'text-zinc-500 hover:text-white'
                    }`}
                  >
                    <ImageIcon className="w-3.5 h-3.5 inline mr-1" />Image
                  </button>
                  <button
                    data-testid="catalog-view-data"
                    onClick={() => { setCatalogViewMode('data'); sessionStorage.setItem('fg_catalog_view', 'data'); }}
                    className={`px-3 py-1.5 text-xs uppercase tracking-widest font-manrope font-bold transition-all ${
                      catalogViewMode === 'data' ? 'bg-gold-500 text-black' : 'text-zinc-500 hover:text-white'
                    }`}
                  >
                    <BarChart3 className="w-3.5 h-3.5 inline mr-1" />Data
                  </button>
                </div>
                <button
                  data-testid="save-list-btn"
                  onClick={() => setShowSaveListModal(true)}
                  className="flex items-center gap-1.5 text-gold-500 hover:text-gold-400 text-xs uppercase tracking-widest font-manrope font-bold transition-colors border border-gold-500/30 px-3 py-1.5 hover:border-gold-500"
                >
                  <BookmarkPlus className="w-3.5 h-3.5" />
                  Save List
                </button>
                <button
                  data-testid="my-lists-btn"
                  onClick={() => { setShowMyLists(true); fetchMyLists(); }}
                  className="flex items-center gap-1.5 text-zinc-400 hover:text-gold-500 text-xs uppercase tracking-widest font-manrope font-bold transition-colors border border-zinc-700 px-3 py-1.5 hover:border-gold-500/50"
                >
                  <Bookmark className="w-3.5 h-3.5" />
                  My Lists
                </button>
                {selectedStrains.length > 0 && (
                  <button
                    data-testid="clear-selection-btn"
                    onClick={() => setSelectedStrains([])}
                    className="text-zinc-500 hover:text-gold-500 text-sm transition-colors"
                  >
                    Clear selection
                  </button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-[4/5] bg-charcoal animate-pulse" />
                ))}
              </div>
            ) : filteredStrains.length === 0 ? (
              <div className="text-center py-16">
                <Flame className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No strains match your filters</p>
                <button 
                  onClick={() => setFilters({ type: '', priceTier: '', minThc: null, search: '', grade: '', growingMethod: '', category: '', bcDd: false, selectedEffects: [], selectedTerpenes: [] })}
                  className="mt-4 text-gold-500 hover:text-gold-400 text-sm"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <>
              {/* Active COA Chemistry Filter Tags */}
              {(filters.coaMinThc || filters.coaMaxThc || filters.coaMinTerpenes || filters.coaDominantTerpene || filters.coaAvailability) && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="active-coa-filters">
                  {filters.coaMinThc && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400 text-[11px] tracking-wide">
                      THC ≥ {filters.coaMinThc}%
                      <button onClick={() => setFilters(f => ({...f, coaMinThc: ''}))} className="hover:text-white ml-0.5">×</button>
                    </span>
                  )}
                  {filters.coaMaxThc && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400 text-[11px] tracking-wide">
                      THC ≤ {filters.coaMaxThc}%
                      <button onClick={() => setFilters(f => ({...f, coaMaxThc: ''}))} className="hover:text-white ml-0.5">×</button>
                    </span>
                  )}
                  {filters.coaMinTerpenes && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400 text-[11px] tracking-wide">
                      Terpenes ≥ {filters.coaMinTerpenes}%
                      <button onClick={() => setFilters(f => ({...f, coaMinTerpenes: ''}))} className="hover:text-white ml-0.5">×</button>
                    </span>
                  )}
                  {filters.coaDominantTerpene && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400 text-[11px] tracking-wide">
                      Dominant: {filters.coaDominantTerpene}
                      <button onClick={() => setFilters(f => ({...f, coaDominantTerpene: ''}))} className="hover:text-white ml-0.5">×</button>
                    </span>
                  )}
                  {filters.coaAvailability && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400 text-[11px] tracking-wide">
                      {filters.coaAvailability === 'has_parsed_coa' ? 'Parsed COA' : filters.coaAvailability === 'has_coa' ? 'Has COA' : 'No COA'}
                      <button onClick={() => setFilters(f => ({...f, coaAvailability: ''}))} className="hover:text-white ml-0.5">×</button>
                    </span>
                  )}
                </div>
              )}
              {catalogViewMode === 'image' ? (
              <div>
                {groupStrainsByCategory(filteredStrains).map((section) => (
                  <div key={section.heading} className="mb-10">
                    <div className="flex items-center gap-3 mb-5 border-b border-zinc-800 pb-3" data-testid={`section-heading-${section.heading.replace(/\s+/g, '-').toLowerCase()}`}>
                      <h3 className="font-manrope text-sm uppercase tracking-[0.15em] text-gold-500 font-bold">{section.heading}</h3>
                      <span className="text-zinc-600 text-xs font-mono">{section.strains.length}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {section.strains.map((strain) => (
                        <StrainCard 
                          key={strain.id}
                          strain={strain}
                          onSelect={toggleStrainSelection}
                          isSelected={selectedStrains.includes(strain.id)}
                          onViewDetails={handleStrainClick}
                          onAddToCart={addToCart}
                          isInCart={inquiryCart.some(i => i.strain_id === strain.id)}
                          isAlerted={strainAlertSubs.some(s => s.strain_name_lower === strain.strain_name.toLowerCase())}
                          onToggleAlert={toggleStrainAlert}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              ) : (
              /* DATA VIEW - Table layout */
              <div className="overflow-x-auto">
                {/* Mobile sort control */}
                <div className="flex items-center gap-2 mb-3 md:hidden">
                  <ArrowUpDown className="w-3.5 h-3.5 text-zinc-500" />
                  <select
                    data-testid="mobile-sort-select"
                    value={catalogSort.field ? `${catalogSort.field}_${catalogSort.direction}` : ''}
                    onChange={(e) => {
                      if (!e.target.value) { setCatalogSort({ field: null, direction: null }); return; }
                      const [f, d] = e.target.value.split('_');
                      setCatalogSort({ field: f, direction: d });
                    }}
                    className="bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs h-8 px-2 outline-none flex-1"
                  >
                    <option value="">Sort by...</option>
                    <option value="strain_name_asc">Strain A-Z</option>
                    <option value="strain_name_desc">Strain Z-A</option>
                    <option value="thc_asc">THC Low-High</option>
                    <option value="thc_desc">THC High-Low</option>
                    <option value="target_price_asc">Price Low-High</option>
                    <option value="target_price_desc">Price High-Low</option>
                    <option value="category_asc">Category A-Z</option>
                    <option value="lot_size_desc">Lot Size Large-Small</option>
                    <option value="growing_method_asc">Method A-Z</option>
                  </select>
                  {catalogSort.field && (
                    <button onClick={() => setCatalogSort({ field: null, direction: null })} className="text-zinc-600 hover:text-zinc-400 text-xs">Clear</button>
                  )}
                </div>
                <table className="w-full text-left border-collapse" data-testid="catalog-data-table">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="py-3 px-2 text-zinc-500 text-[10px] uppercase tracking-widest font-manrope"></th>
                      <SortHeader field="strain_name" label="Strain" />
                      <SortHeader field="category" label="Category" className="hidden md:table-cell" />
                      <SortHeader field="thc" label="THC" />
                      <th className="py-3 px-2 text-zinc-500 text-[10px] uppercase tracking-widest font-manrope hidden lg:table-cell">Top Terpenes</th>
                      <th className="py-3 px-2 text-zinc-500 text-[10px] uppercase tracking-widest font-manrope hidden lg:table-cell">Effects</th>
                      <SortHeader field="lot_size" label="Lot" className="hidden md:table-cell" />
                      <SortHeader field="growing_method" label="Method" className="hidden lg:table-cell" />
                      <SortHeader field="target_price" label="Price" className="hidden lg:table-cell" />
                      <th className="py-3 px-2 text-zinc-500 text-[10px] uppercase tracking-widest font-manrope"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupStrainsByCategory(sortedStrains).map((section) => [
                        <tr key={`section-${section.heading}`} data-testid={`data-section-${section.heading.replace(/\s+/g, '-').toLowerCase()}`}>
                          <td colSpan="10" className="pt-6 pb-2 px-2 border-b border-zinc-800">
                            <span className="font-manrope text-sm uppercase tracking-[0.15em] text-gold-500 font-bold">{section.heading}</span>
                            <span className="text-zinc-600 text-xs font-mono ml-3">{section.strains.length}</span>
                          </td>
                        </tr>,
                        ...section.strains.map((strain) => {
                      const terpenes = Array.isArray(strain.terpene_profile) ? strain.terpene_profile.slice(0, 3).join(', ') : '';
                      const effects = Array.isArray(strain.effects) ? strain.effects.slice(0, 3).join(', ') : '';
                      const imgUrl = strain.images && strain.images.length > 0
                        ? `${API}/files/${strain.images[0]}` : null;
                      return (
                        <tr
                          key={strain.id}
                          data-testid={`data-row-${strain.id}`}
                          className="border-b border-zinc-900 hover:bg-zinc-900/50 transition-colors cursor-pointer"
                          onClick={() => handleStrainClick(strain)}
                        >
                          <td className="py-2 px-2">
                            {imgUrl ? (
                              <img src={strain.image_url || imgUrl} alt={strain.strain_name}
                                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1721058454918-5582b68de13c'; }}
                                className="w-10 h-10 object-cover border border-zinc-800" />
                            ) : (
                              <div className="w-10 h-10 bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                                <Flame className="w-4 h-4 text-zinc-700" />
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            <div className="text-white font-bold text-sm">{strain.strain_name}</div>
                            <div className="text-zinc-500 text-xs">{strain.type}</div>
                          </td>
                          <td className="py-2 px-2 text-zinc-400 text-xs hidden md:table-cell">{categoryLabel(strain.category)}</td>
                          <td className="py-2 px-2 text-gold-500 font-mono text-sm font-bold">{getStrainThc(strain)}</td>
                          <td className="py-2 px-2 text-zinc-400 text-xs hidden lg:table-cell max-w-[120px] truncate">{terpenes}</td>
                          <td className="py-2 px-2 text-zinc-400 text-xs hidden lg:table-cell max-w-[140px] truncate">{effects}</td>
                          <td className="py-2 px-2 text-zinc-400 text-xs hidden md:table-cell">{strain.lot_size}</td>
                          <td className="py-2 px-2 text-zinc-400 text-xs hidden lg:table-cell">{strain.growing_method}</td>
                          <td className="py-2 px-2 hidden lg:table-cell">
                            {strain.target_price_per_gram ? (
                              <span className="text-gold-500 font-mono text-xs">${Number(strain.target_price_per_gram).toFixed(2)}/g</span>
                            ) : (
                              <span className="text-zinc-700 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => { e.stopPropagation(); addToCart(strain.id); }}
                                data-testid={`data-cart-${strain.id}`}
                                className={`p-1 border transition-all ${inquiryCart.some(i => i.strain_id === strain.id) ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-700 hover:border-emerald-400'}`}
                                title="Add to inquiry cart"
                              >
                                <ShoppingCart className={`w-3 h-3 ${inquiryCart.some(i => i.strain_id === strain.id) ? 'text-black' : 'text-zinc-500'}`} />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleStrainClick(strain); }}
                                className="text-gold-500 hover:text-gold-400 text-xs font-bold uppercase"
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                    ]).flat()}
                  </tbody>
                </table>
              </div>
              )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6 md:px-12 relative z-10 bg-obsidian">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <img src={FIREGOLD_LOGO} alt="Firegold Brands" className="h-28 w-auto" />
          <div className="text-center md:text-right">
            <p className="text-zinc-500 text-sm">The Firegold Standard</p>
            <p className="text-zinc-600 text-xs mt-1">© 2026 Firegold Brands. Premium B2B Cannabis Wholesale.</p>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <StrainDetailModal 
        strain={viewingStrain}
        onClose={() => setViewingStrain(null)}
        onAddToInquiry={toggleStrainSelection}
        isInInquiry={viewingStrain ? selectedStrains.includes(viewingStrain.id) : false}
        onRequestSample={handleRequestSample}
      />

      <SampleRequestModal
        isOpen={showSampleModal}
        onClose={() => {
          setShowSampleModal(false);
          setSampleStrain(null);
        }}
        strain={sampleStrain}
        userData={userData}
        userToken={userToken}
      />

      <InquiryModal 
        isOpen={showInquiryModal}
        onClose={() => setShowInquiryModal(false)}
        selectedStrains={selectedStrains}
        strains={strains}
        userData={userData}
        userToken={userToken}
      />

      {/* Contact Us Modal */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowContactModal(false)} />
          <div data-testid="contact-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-lg p-8">
            <button 
              onClick={() => setShowContactModal(false)} 
              data-testid="contact-modal-close"
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"
            >
              <X className="w-5 h-5 text-black" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <MessageCircle className="w-6 h-6 text-gold-500" />
              <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest">Contact Us</h2>
            </div>
            <p className="text-zinc-400 text-sm mb-6">Leave a message and our team will get back to you shortly.</p>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Name *</label>
                <input
                  data-testid="contact-name-input"
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Email *</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Company</label>
                <input
                  data-testid="contact-company-input"
                  type="text"
                  value={contactForm.company}
                  onChange={(e) => setContactForm(prev => ({ ...prev, company: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm"
                  placeholder="Company name (optional)"
                />
              </div>
              <div>
                <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Message *</label>
                <textarea
                  data-testid="contact-message-input"
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white px-4 py-3 outline-none text-sm min-h-[120px] resize-none"
                  placeholder="How can we help you?"
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={contactSending}
                className="w-full bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all"
              >
                {contactSending ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Share with Buyer Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowShareModal(false)} />
          <div data-testid="share-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-md p-8">
            <button 
              onClick={() => setShowShareModal(false)} 
              data-testid="share-modal-close"
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"
            >
              <X className="w-5 h-5 text-black" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <Share2 className="w-6 h-6 text-gold-500" />
              <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest">Share with Buyer</h2>
            </div>
            <p className="text-zinc-400 text-sm mb-6">
              Share this link with your buyer. When they register, they'll automatically be linked to your account.
            </p>
            {loadingReferral ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 text-gold-500 animate-spin" />
              </div>
            ) : myReferralCode ? (
              <div className="space-y-4">
                <div className="bg-zinc-900 border border-zinc-700 p-4 flex items-center justify-between gap-3">
                  <div className="overflow-hidden">
                    <p className="text-zinc-500 text-xs uppercase tracking-widest mb-1">Your Referral Link</p>
                    <p data-testid="referral-link" className="text-gold-500 font-mono text-sm truncate">
                      {window.location.origin}/?ref={myReferralCode}
                    </p>
                  </div>
                  <button
                    onClick={copyReferralLink}
                    data-testid="copy-referral-btn"
                    className="flex-shrink-0 bg-gold-500 hover:bg-gold-400 text-black px-4 py-2 font-bold uppercase tracking-widest text-xs transition-all flex items-center gap-2"
                  >
                    {referralCopied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {referralCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="bg-zinc-900/50 border border-zinc-800 p-4">
                  <p className="text-zinc-500 text-xs uppercase tracking-widest mb-1">Your Code</p>
                  <p className="text-white font-mono font-bold text-lg">{myReferralCode}</p>
                </div>
                <p className="text-zinc-600 text-xs mt-2">
                  When your buyer registers through this link, they will be connected to you as their referral contact.
                </p>
              </div>
            ) : (
              <p className="text-red-500 text-sm">Failed to generate referral link. Please try again.</p>
            )}
          </div>
        </div>
      )}

      {/* Save List Modal */}
      {showSaveListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowSaveListModal(false)} />
          <div data-testid="save-list-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-md p-8">
            <button 
              onClick={() => setShowSaveListModal(false)} 
              data-testid="save-list-modal-close"
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"
            >
              <X className="w-5 h-5 text-black" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <BookmarkPlus className="w-6 h-6 text-gold-500" />
              <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest">Save Current List</h2>
            </div>
            <p className="text-zinc-400 text-sm mb-2">
              Save the current view of <span className="text-white font-mono">{filteredStrains.length}</span> strains to your profile.
            </p>
            {Object.values(filters).some(v => v && v !== false) && (
              <div className="flex flex-wrap gap-1 mb-4">
                {filters.category && <span className="bg-gold-500/10 text-gold-500 text-[10px] px-2 py-0.5 border border-gold-500/30">{categoryLabel(filters.category)}</span>}
                {filters.type && <span className="bg-gold-500/10 text-gold-500 text-[10px] px-2 py-0.5 border border-gold-500/30">{filters.type}</span>}
                {filters.growingMethod && <span className="bg-gold-500/10 text-gold-500 text-[10px] px-2 py-0.5 border border-gold-500/30">{filters.growingMethod}</span>}
                {filters.minThc && <span className="bg-gold-500/10 text-gold-500 text-[10px] px-2 py-0.5 border border-gold-500/30">THC {filters.minThc}%+</span>}
                {(filters.selectedEffects || []).map(e => <span key={e} className="bg-gold-500/10 text-gold-500 text-[10px] px-2 py-0.5 border border-gold-500/30">{e}</span>)}
              </div>
            )}
            <div className="mb-6">
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">List Name *</label>
              <input
                data-testid="save-list-name-input"
                type="text"
                value={saveListName}
                onChange={(e) => setSaveListName(e.target.value)}
                className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-11 px-4 outline-none text-sm"
                placeholder="e.g., March 2026 Sativa Picks"
                autoFocus
              />
            </div>
            <button
              data-testid="save-list-confirm-btn"
              onClick={handleSaveList}
              disabled={savingList}
              className="w-full bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest px-6 py-3 text-sm transition-all disabled:opacity-50"
            >
              {savingList ? 'Saving...' : `Save ${filteredStrains.length} Strains`}
            </button>
          </div>
        </div>
      )}

      {/* My Lists Panel */}
      {showMyLists && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowMyLists(false)} />
          <div data-testid="my-lists-panel" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <Bookmark className="w-5 h-5 text-gold-500" />
                <h2 className="font-cinzel font-bold text-lg text-white uppercase tracking-widest">My Saved Lists</h2>
              </div>
              <button 
                onClick={() => setShowMyLists(false)}
                data-testid="my-lists-close"
                className="w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all"
              >
                <X className="w-5 h-5 text-black" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingLists ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-gold-500 animate-spin" />
                </div>
              ) : myLists.length === 0 ? (
                <div className="text-center py-12">
                  <Bookmark className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm">No saved lists yet</p>
                  <p className="text-zinc-600 text-xs mt-1">Use "Save List" to save your current catalog view</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {myLists.map(list => (
                    <div key={list.id} data-testid={`saved-list-${list.id}`} className="bg-zinc-900/50 border border-zinc-800 p-4 hover:border-gold-500/30 transition-all">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-white font-manrope font-bold text-sm">{list.list_name}</h3>
                          <p className="text-zinc-500 text-xs mt-0.5">{list.strain_count} strains · {new Date(list.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`delete-list-${list.id}`}
                            onClick={() => handleDeleteList(list.id, list.list_name)}
                            className="text-zinc-600 hover:text-red-400 transition-colors p-1"
                            title="Delete list"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      {list.strain_names && list.strain_names.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {list.strain_names.slice(0, 6).map((name, i) => (
                            <span key={i} className="bg-zinc-800 text-zinc-400 text-[10px] px-1.5 py-0.5">{name}</span>
                          ))}
                          {list.strain_names.length > 6 && (
                            <span className="text-zinc-600 text-[10px] px-1.5 py-0.5">+{list.strain_names.length - 6} more</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Inquiry Cart Drawer */}
      {showCartDrawer && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCartDrawer(false)} />
          <div data-testid="cart-drawer" className="relative bg-zinc-950 border-l border-zinc-800 w-full max-w-md overflow-y-auto">
            <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 p-6 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-5 h-5 text-gold-500" />
                <h2 className="font-cinzel font-bold text-lg text-white uppercase tracking-widest">Inquiry Cart</h2>
                <span className="bg-gold-500 text-black text-xs font-bold px-2 py-0.5">{inquiryCart.length}</span>
              </div>
              <button onClick={() => setShowCartDrawer(false)} data-testid="cart-close-btn" className="w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all">
                <X className="w-5 h-5 text-black" />
              </button>
            </div>
            
            {inquiryCart.length === 0 ? (
              <div className="p-8 text-center">
                <ShoppingCart className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm">Your inquiry cart is empty</p>
                <p className="text-zinc-600 text-xs mt-2">Add strains from the catalog to build a combined deal request</p>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {inquiryCart.map((item) => {
                  const strain = strains.find(s => s.id === item.strain_id);
                  return (
                    <div key={item.strain_id} data-testid={`cart-item-${item.strain_id}`} className="bg-zinc-900/50 border border-zinc-800 p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-white font-bold text-sm">{item.strain_name}</h3>
                          <p className="text-zinc-500 text-xs">{strain?.type} &middot; THC {getStrainThc(strain)} &middot; {strain?.lot_size}</p>
                        </div>
                        <button onClick={() => removeFromCart(item.strain_id)} data-testid={`cart-remove-${item.strain_id}`} className="text-zinc-500 hover:text-red-400 transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Desired KG</label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={item.desired_kg || ''}
                            onChange={(e) => updateCartItem(item.strain_id, parseFloat(e.target.value) || 0, item.target_price_per_gram)}
                            data-testid={`cart-kg-${item.strain_id}`}
                            className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm font-mono"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Target $/g</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.target_price_per_gram || ''}
                            onChange={(e) => updateCartItem(item.strain_id, item.desired_kg, parseFloat(e.target.value) || 0)}
                            data-testid={`cart-price-${item.strain_id}`}
                            className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm font-mono"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Cart Summary */}
                <div className="border-t border-zinc-800 pt-4 mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-400">Total Strains</span>
                    <span className="text-white font-mono font-bold">{inquiryCart.length}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-4">
                    <span className="text-zinc-400">Total Volume</span>
                    <span className="text-gold-500 font-mono font-bold">{inquiryCart.reduce((sum, i) => sum + (i.desired_kg || 0), 0)} kg</span>
                  </div>
                </div>

                {/* Cart submission form */}
                <div className="space-y-3">
                  <div>
                    <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Market of Interest</label>
                    <select
                      value={cartMarket}
                      onChange={(e) => setCartMarket(e.target.value)}
                      data-testid="cart-market-select"
                      className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white h-9 px-3 outline-none text-sm"
                    >
                      <option value="">Select market...</option>
                      <option value="Canada (Domestic)">Canada (Domestic)</option>
                      <option value="Germany">Germany</option>
                      <option value="Australia">Australia</option>
                      <option value="Israel">Israel</option>
                      <option value="UK">UK</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Message (Optional)</label>
                    <textarea
                      value={cartMessage}
                      onChange={(e) => setCartMessage(e.target.value)}
                      data-testid="cart-message"
                      className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white px-3 py-2 outline-none text-sm resize-none"
                      rows={3}
                      placeholder="Any additional requirements or notes..."
                    />
                  </div>
                </div>

                <button
                  onClick={submitCart}
                  disabled={cartSubmitting}
                  data-testid="cart-submit-btn"
                  className="w-full bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all disabled:opacity-50"
                >
                  {cartSubmitting ? 'Submitting...' : `Submit Deal Request (${inquiryCart.length} strains)`}
                </button>

                {/* My Deals Link */}
                <button
                  onClick={() => { fetchDeals(); setShowDealsModal(true); }}
                  data-testid="view-deals-btn"
                  className="w-full border border-zinc-700 text-zinc-400 hover:text-gold-500 hover:border-gold-500/50 font-bold uppercase tracking-widest py-2 text-xs transition-all"
                >
                  View My Deal History
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* My Deals Modal */}
      {showDealsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowDealsModal(false)} />
          <div data-testid="deals-modal" className="relative bg-zinc-950 border border-zinc-800 w-full max-w-2xl max-h-[80vh] overflow-y-auto p-8">
            <button onClick={() => setShowDealsModal(false)} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all">
              <X className="w-5 h-5 text-black" />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <Layers className="w-6 h-6 text-gold-500" />
              <h2 className="font-cinzel font-bold text-xl text-white uppercase tracking-widest">My Deals</h2>
            </div>
            {myDeals.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-8">No deal requests yet. Add strains to your cart and submit!</p>
            ) : (
              <div className="space-y-4">
                {myDeals.map(deal => (
                  <div key={deal.id} data-testid={`deal-${deal.id}`} className="bg-zinc-900/50 border border-zinc-800 p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${
                          deal.status === 'submitted' ? 'bg-blue-500/20 text-blue-400' :
                          deal.status === 'reviewing' ? 'bg-gold-500/20 text-gold-500' :
                          deal.status === 'negotiating' ? 'bg-purple-500/20 text-purple-400' :
                          deal.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' :
                          deal.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>{deal.status}</span>
                        <p className="text-zinc-500 text-xs mt-1">{new Date(deal.created_at).toLocaleDateString()}</p>
                      </div>
                      <p className="text-gold-500 font-mono font-bold text-sm">{deal.total_kg} kg</p>
                    </div>
                    <div className="space-y-1">
                      {deal.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-white">{item.strain_name}</span>
                          <span className="text-zinc-400 font-mono">{item.desired_kg}kg{item.target_price_per_gram ? ` @ $${item.target_price_per_gram}/g` : ''}</span>
                        </div>
                      ))}
                    </div>
                    {deal.market_of_interest && <p className="text-zinc-500 text-xs mt-2">Market: {deal.market_of_interest}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Strain Hunter Modal ── */}
      {showStrainHunter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowStrainHunter(false)} />
          <div data-testid="strain-hunter-modal" className="relative bg-zinc-950 border border-amber-500/30 w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-zinc-950 border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Crosshair className="w-6 h-6 text-amber-400" />
                <h2 className="font-cinzel font-bold text-lg text-white uppercase tracking-widest">Strain Hunter</h2>
              </div>
              <button onClick={() => setShowStrainHunter(false)} data-testid="strain-hunter-close" className="w-9 h-9 flex items-center justify-center bg-amber-500 hover:bg-amber-400 transition-all">
                <X className="w-5 h-5 text-black" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-zinc-800">
              {[
                { key: 'search', label: 'Search & Discover', icon: <Search className="w-3.5 h-3.5" /> },
                { key: 'request', label: 'I Am Seeking', icon: <MapPinned className="w-3.5 h-3.5" /> },
                { key: 'history', label: 'My Requests', icon: <Clock className="w-3.5 h-3.5" /> }
              ].map(tab => (
                <button
                  key={tab.key}
                  data-testid={`hunter-tab-${tab.key}`}
                  onClick={() => { setHunterTab(tab.key); if (tab.key === 'history') hunterFetchMyRequests(); }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-bold transition-all ${
                    hunterTab === tab.key ? 'text-amber-400 border-b-2 border-amber-400 bg-amber-500/5' : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  {tab.icon}{tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Search Tab */}
              {hunterTab === 'search' && (
                <div>
                  <p className="text-zinc-400 text-sm mb-4">Search for any cannabis strain. We'll show you matches from our catalog and recommend substitutes.</p>
                  <div className="space-y-4">
                    <div>
                      <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Strain Name *</label>
                      <input
                        data-testid="hunter-strain-input"
                        type="text"
                        placeholder="e.g. OG Kush, Blue Dream, Gelato..."
                        value={hunterQuery}
                        onChange={e => setHunterQuery(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && hunterSearch()}
                        className="w-full bg-zinc-900 border border-zinc-700 focus:border-amber-500 text-white px-4 py-3 outline-none text-sm transition-colors"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Type</label>
                        <select
                          data-testid="hunter-type-select"
                          value={hunterType}
                          onChange={e => setHunterType(e.target.value)}
                          className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 outline-none text-sm"
                        >
                          <option value="">Any Type</option>
                          <option value="Indica">Indica</option>
                          <option value="Sativa">Sativa</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Desired Potential Terpene Effects</label>
                        <EffectsMultiSelect
                          selected={hunterEffects}
                          onChange={(effects) => setHunterEffects(effects)}
                          testId="hunter-search-effects"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Desired Terpenes</label>
                      <div className="flex flex-wrap gap-1.5">
                        {ALL_TERPENES.map(terp => (
                          <button
                            key={terp}
                            onClick={() => setHunterTerpenes(prev => prev.includes(terp) ? prev.filter(t => t !== terp) : [...prev, terp])}
                            className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-all ${
                              hunterTerpenes.includes(terp) ? 'border-emerald-500 bg-emerald-500/15 text-emerald-400' : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'
                            }`}
                          >
                            {terp}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Use Case (optional)</label>
                        <select data-testid="hunter-search-usecase" value={hunterRequestForm.use_case} onChange={e => setHunterRequestForm(p => ({ ...p, use_case: e.target.value }))} className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 outline-none text-sm">
                          <option value="">Any</option>
                          <option value="flower">Whole Flower</option>
                          <option value="pre_roll">Pre-Rolls</option>
                          <option value="extraction">Extraction</option>
                          <option value="vape">Vape</option>
                          <option value="rosin">Rosin</option>
                          <option value="edible">Edible</option>
                        </select>
                      </div>
                    </div>
                    <button
                      data-testid="hunter-search-btn"
                      onClick={hunterSearch}
                      disabled={hunterSearching || !hunterQuery.trim()}
                      className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all flex items-center justify-center gap-2"
                    >
                      {hunterSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Binoculars className="w-4 h-4" />}
                      {hunterSearching ? 'Searching...' : 'Hunt Strain'}
                    </button>
                  </div>

                  {/* Results */}
                  {hunterResults && (
                    <div className="mt-6 space-y-6">
                      <div data-testid="hunter-results-summary" className={`p-4 border ${hunterResults.exact_in_catalog ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}>
                        {hunterResults.exact_in_catalog ? (
                          <p className="text-emerald-400 text-sm flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            <span><strong>{hunterResults.query}</strong> is in our catalog! Check the listings below.</span>
                          </p>
                        ) : hunterResults.fallback_mode ? (
                          <div>
                            <p className="text-amber-400 text-sm flex items-center gap-2">
                              <Crosshair className="w-4 h-4" />
                              <span><strong>{hunterResults.query}</strong> is not currently in our catalog.</span>
                            </p>
                            <p className="text-zinc-400 text-xs mt-1.5 leading-relaxed">We could not find a high-confidence direct comparable for this strain in the current FireGold database. The results below are the best available strains with the richest comparable profile data.</p>
                          </div>
                        ) : (
                          <p className="text-amber-400 text-sm flex items-center gap-2">
                            <Crosshair className="w-4 h-4" />
                            <span><strong>{hunterResults.query}</strong> is not currently in our catalog. Here are the closest substitutes.</span>
                          </p>
                        )}
                      </div>

                      {hunterResults.catalog_substitutes.length > 0 && (
                        <div>
                          <h3 className="text-white font-manrope text-sm font-bold uppercase tracking-widest mb-1 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-gold-500" />
                            {hunterResults.exact_in_catalog ? 'Catalog Listings' : hunterResults.fallback_mode ? 'Best Available Candidates' : 'Suggested Similar Strains Available Now'} ({hunterResults.catalog_substitutes.length})
                          </h3>
                          {hunterResults.reference_profile_used && (
                            <p data-testid="hunter-ref-match-note" className="text-zinc-500 text-[10px] mb-3 leading-relaxed">
                              Matched via reference profile: <span className="text-blue-400">{hunterResults.reference_profile_used}</span>
                            </p>
                          )}
                          <div className="space-y-2">
                            {hunterResults.catalog_substitutes.slice(0, 6).map((s, idx) => (
                              <div key={s.id || idx} data-testid={`hunter-catalog-${idx}`} className="bg-zinc-900/60 border border-zinc-800 p-3 hover:border-gold-500/30 transition-all">
                                <div className="flex items-center gap-4">
                                  {s.image_url && (
                                    <img src={`${BACKEND_URL}${s.image_url}`} alt={s.strain_name} className="w-12 h-12 object-cover rounded-sm flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-white font-bold text-sm truncate">{s.strain_name}</span>
                                      <span className="text-zinc-500 text-xs">{s.type}</span>
                                      {s.matched_via_reference && (
                                        <span className="text-blue-400 text-[8px] uppercase tracking-widest border border-blue-400/20 px-1">ref-matched</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                                      <span className="text-gold-500 text-xs font-mono font-bold">THC {getStrainThc(s)}</span>
                                      <span className="text-zinc-500 text-xs">{s.lot_size}</span>
                                      {s.target_price_per_gram ? <span className="text-emerald-400 text-xs font-mono">${s.target_price_per_gram}/g</span> : null}
                                      {s.province ? <span className="text-zinc-600 text-xs">{s.province}</span> : null}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <div className="text-right">
                                      <span className={`font-mono font-bold text-sm ${hunterResults.fallback_mode ? 'text-zinc-400' : s.similarity_score >= 50 ? 'text-emerald-400' : s.similarity_score >= 30 ? 'text-amber-400' : 'text-zinc-400'}`}>
                                        {s.similarity_score}%
                                      </span>
                                      <p className="text-zinc-600 text-[9px] uppercase tracking-widest">
                                        {hunterResults.fallback_mode ? 'profile score' : s.similarity_score >= 60 ? 'strong' : s.similarity_score >= 40 ? 'comparable' : 'related'}
                                      </p>
                                    </div>
                                    <button
                                      onClick={() => { const st = strains.find(x => x.id === s.id); if (st) setViewingStrain(st); }}
                                      data-testid={`hunter-view-${idx}`}
                                      className="text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-500 px-2 py-1 text-[10px] uppercase tracking-widest font-bold transition-all"
                                    >
                                      View
                                    </button>
                                    <button
                                      onClick={() => { addToCart(s.id); }}
                                      data-testid={`hunter-add-cart-${idx}`}
                                      className="text-gold-500 hover:text-gold-400 border border-gold-500/30 hover:border-gold-500 px-2 py-1 text-[10px] uppercase tracking-widest font-bold transition-all"
                                    >
                                      + Cart
                                    </button>
                                    <button
                                      onClick={() => toggleStrainAlert(s.strain_name)}
                                      data-testid={`hunter-alert-${idx}`}
                                      className={`border px-2 py-1 text-[10px] uppercase tracking-widest font-bold transition-all ${
                                        strainAlertSubs.some(sub => sub.strain_name_lower === s.strain_name.toLowerCase())
                                          ? 'text-amber-400 border-amber-500 bg-amber-500/10'
                                          : 'text-zinc-400 hover:text-amber-400 border-zinc-700 hover:border-amber-500'
                                      }`}
                                      title={strainAlertSubs.some(sub => sub.strain_name_lower === s.strain_name.toLowerCase()) ? 'Subscribed to alerts' : 'Get alerted for this strain'}
                                    >
                                      <Bell className="w-3 h-3 inline-block" />
                                    </button>
                                  </div>
                                </div>
                                {s.reasons && s.reasons.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 mt-2 ml-16" data-testid={`hunter-reasons-${idx}`}>
                                    {s.reasons.map((r, ri) => (
                                      <span key={ri} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-emerald-500/8 text-emerald-400/70 border border-emerald-500/15">{r}</span>
                                    ))}
                                  </div>
                                )}
                                {/* Factor Breakdown */}
                                {s.scoring_breakdown && (
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-2.5 ml-0 sm:ml-16" data-testid={`hunter-breakdown-${idx}`}>
                                    {Object.entries(s.scoring_breakdown).map(([factor, data]) => (
                                      <div key={factor} className="bg-zinc-950/60 border border-zinc-800/50 px-2 py-1.5">
                                        <div className="flex items-center justify-between mb-0.5">
                                          <span className="text-zinc-500 text-[8px] uppercase tracking-widest">{factor.replace(/_/g, ' ')}</span>
                                          <span className={`font-mono text-[10px] font-bold ${!data.data_available ? 'text-zinc-600' : data.score >= 60 ? 'text-emerald-400' : data.score >= 30 ? 'text-amber-400' : 'text-zinc-500'}`}>
                                            {data.data_available ? `${data.score}%` : 'N/A'}
                                          </span>
                                        </div>
                                        <div className="w-full bg-zinc-800 h-[3px] mb-1">
                                          <div className={`h-full transition-all ${!data.data_available ? 'bg-zinc-700' : data.score >= 60 ? 'bg-emerald-500' : data.score >= 30 ? 'bg-amber-500' : 'bg-zinc-600'}`} style={{width: `${data.data_available ? data.score : 0}%`}} />
                                        </div>
                                        <p className="text-zinc-600 text-[8px] leading-tight truncate" title={data.reason}>
                                          {data.data_available ? data.reason : (hunterResults.fallback_mode ? 'No direct overlap found' : 'Insufficient data')}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Chemical Match (COA Intelligence) */}
                                {s.chemical_match && (
                                  <div className="mt-3 ml-0 sm:ml-16" data-testid={`hunter-chem-${idx}`}>
                                    {s.chemical_match.available ? (
                                      <div className="border border-cyan-500/30 bg-cyan-950/20 rounded p-3">
                                        <div className="flex items-center gap-2 mb-3">
                                          <span className="text-cyan-400 text-[10px] uppercase tracking-widest font-bold">Chemical Match</span>
                                          <span className={`font-mono text-base font-bold ${
                                            s.chemical_match.overall_score >= 80 ? 'text-emerald-400' :
                                            s.chemical_match.overall_score >= 50 ? 'text-cyan-400' : 'text-amber-400'
                                          }`}>{s.chemical_match.overall_score}%</span>
                                          {s.chemical_match.compared_to && (
                                            <span className="text-zinc-500 text-[10px]">vs {s.chemical_match.compared_to}</span>
                                          )}
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                                          {[
                                            ['Terpene', s.chemical_match.terpene_score],
                                            ['Cannabinoid', s.chemical_match.cannabinoid_score],
                                            ['THC', s.chemical_match.total_thc_score],
                                            ['Total Terp', s.chemical_match.total_terpene_score],
                                            ['Total Canna', s.chemical_match.total_cannabinoid_score],
                                          ].map(([label, val]) => (
                                            <div key={label} className="bg-zinc-900/80 border border-zinc-700/40 rounded px-2 py-1.5">
                                              <span className="text-zinc-400 text-[10px] uppercase tracking-wider font-semibold block mb-1">{label}</span>
                                              <div className="flex items-center gap-2">
                                                <div className="flex-1 bg-zinc-800 rounded-full h-1.5">
                                                  <div className={`h-full rounded-full transition-all ${val == null ? 'bg-zinc-700' : val >= 70 ? 'bg-cyan-400' : val >= 40 ? 'bg-amber-400' : 'bg-zinc-600'}`} style={{width: `${val ?? 0}%`}} />
                                                </div>
                                                <span className={`font-mono text-xs font-bold min-w-[32px] text-right ${val == null ? 'text-zinc-600' : val >= 70 ? 'text-cyan-400' : val >= 40 ? 'text-amber-400' : 'text-zinc-500'}`}>
                                                  {val != null ? `${val}%` : 'N/A'}
                                                </span>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                        <p className="text-zinc-500 text-[10px] mt-2">{s.chemical_match.scoring_note}</p>
                                      </div>
                                    ) : (
                                      <div className="border border-amber-500/20 bg-amber-950/10 rounded px-3 py-2 flex items-center gap-2">
                                        <span className="text-amber-500/70 text-[10px] uppercase tracking-widest font-bold">Chemical Match:</span>
                                        <span className="text-zinc-400 text-xs">{s.chemical_match.scoring_note || 'Chemical profile unavailable'}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {hunterResults.reference_matches.length > 0 && (
                        <div>
                          <h3 className="text-white font-manrope text-sm font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
                            <Globe className="w-4 h-4 text-blue-400" />
                            Strain Reference Library ({hunterResults.reference_matches.length})
                          </h3>
                          <div className="space-y-2">
                            {hunterResults.reference_matches.slice(0, 5).map((r, idx) => (
                              <div key={idx} data-testid={`hunter-ref-${idx}`} className="bg-zinc-900/30 border border-zinc-800/50 p-3">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="text-white font-bold text-sm">{r.name}</span>
                                    <span className="text-zinc-500 text-xs ml-2">{r.type}</span>
                                    {r.confidence_level && (
                                      <span className={`ml-2 text-[8px] uppercase tracking-widest px-1 border ${r.confidence_level === 'high' ? 'border-emerald-500/30 text-emerald-500' : 'border-zinc-600 text-zinc-500'}`}>
                                        {r.confidence_level}
                                      </span>
                                    )}
                                  </div>
                                  <span className={`font-mono font-bold text-xs ${r.similarity_score >= 50 ? 'text-blue-400' : 'text-zinc-500'}`}>
                                    {r.similarity_score}% ref
                                  </span>
                                </div>
                                <p className="text-zinc-500 text-xs mt-1">{r.lineage} {r.lineage_confidence ? `(${r.lineage_confidence} confidence)` : ''}</p>
                                {r.thc_range && <p className="text-gold-500 text-xs font-mono mt-0.5">THC: {r.thc_range} {r.cbd_range ? `| CBD: ${r.cbd_range}` : ''}</p>}
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {(r.dominant_terpenes || []).map(t => (
                                    <span key={t} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{t}</span>
                                  ))}
                                </div>
                                {(r.potential_terpene_effects || []).length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {r.potential_terpene_effects.slice(0, 4).map((e, ei) => (
                                      <span key={ei} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-amber-500/8 text-amber-400/70 border border-amber-500/15">{e}</span>
                                    ))}
                                  </div>
                                )}
                                {(r.common_flavor_notes || []).length > 0 && (
                                  <p className="text-zinc-500 text-[10px] mt-1.5">Flavor: {r.common_flavor_notes.join(', ')}</p>
                                )}
                                {r.similar_strain_notes && (
                                  <p className="text-zinc-600 text-[10px] mt-1 italic">{r.similar_strain_notes}</p>
                                )}
                                {r.reasons && r.reasons.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {r.reasons.map((reason, ri) => (
                                      <span key={ri} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-blue-500/8 text-blue-400/70 border border-blue-500/15">{reason}</span>
                                    ))}
                                  </div>
                                )}
                                {/* Reference Match Factor Breakdown */}
                                {r.scoring_breakdown && (
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-2.5" data-testid={`hunter-ref-breakdown-${idx}`}>
                                    {Object.entries(r.scoring_breakdown).map(([factor, data]) => (
                                      <div key={factor} className="bg-zinc-950/60 border border-zinc-800/50 px-2 py-1.5">
                                        <div className="flex items-center justify-between mb-0.5">
                                          <span className="text-zinc-500 text-[8px] uppercase tracking-widest">{factor.replace(/_/g, ' ')}</span>
                                          <span className={`font-mono text-[10px] font-bold ${!data.data_available ? 'text-zinc-600' : data.score >= 60 ? 'text-emerald-400' : data.score >= 30 ? 'text-amber-400' : 'text-zinc-500'}`}>
                                            {data.data_available ? `${data.score}%` : 'N/A'}
                                          </span>
                                        </div>
                                        <div className="w-full bg-zinc-800 h-[3px] mb-1">
                                          <div className={`h-full transition-all ${!data.data_available ? 'bg-zinc-700' : data.score >= 60 ? 'bg-emerald-500' : data.score >= 30 ? 'bg-amber-500' : 'bg-zinc-600'}`} style={{width: `${data.data_available ? data.score : 0}%`}} />
                                        </div>
                                        <p className="text-zinc-600 text-[8px] leading-tight truncate" title={data.reason}>{data.data_available ? data.reason : 'Insufficient data'}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Inline Anonymous Marketplace Request */}
                      <div data-testid="hunter-inline-broadcast" className="border border-amber-500/20 bg-zinc-900/60 p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Send className="w-4 h-4 text-amber-400" />
                          <h4 className="text-white text-sm font-bold">Send Anonymous Marketplace Request</h4>
                        </div>
                        <p className="text-zinc-400 text-xs leading-relaxed">
                          {hunterResults.exact_in_catalog
                            ? 'Want to notify growers about your demand? Broadcast an anonymous request — only the strain name and specifications are shared.'
                            : "Can't find what you need? Broadcast an anonymous request to growers and suppliers. Your identity will not be shared — only the strain name and specifications."}
                        </p>
                        {!inlineBroadcastSent ? (
                          <div className="flex items-center gap-3">
                            <button
                              data-testid="hunter-inline-broadcast-yes"
                              onClick={hunterInlineBroadcast}
                              disabled={inlineBroadcastSending}
                              className="bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-bold uppercase tracking-widest px-5 py-2 text-xs transition-all inline-flex items-center gap-2"
                            >
                              {inlineBroadcastSending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                              {inlineBroadcastSending ? 'Sending...' : 'Yes, Send Request'}
                            </button>
                            <button
                              data-testid="hunter-inline-broadcast-no"
                              onClick={() => setInlineBroadcastSent(true)}
                              className="text-zinc-500 hover:text-zinc-300 border border-zinc-700 hover:border-zinc-500 px-5 py-2 text-xs uppercase tracking-widest font-bold transition-all"
                            >
                              No Thanks
                            </button>
                          </div>
                        ) : inlineBroadcastResult ? (
                          <div data-testid="hunter-inline-broadcast-result" className="border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-1.5">
                            <p className="text-emerald-400 text-sm font-bold flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Request Recorded</p>
                            <p className="text-zinc-400 text-xs">{inlineBroadcastResult.broadcast_message}</p>
                            <p className="text-zinc-400 text-xs">We've emailed you the top matches from our inventory.</p>
                            {inlineBroadcastResult.matched_strains?.length > 0 && (
                              <div className="mt-3 space-y-2">
                                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Your Top Matches</p>
                                {inlineBroadcastResult.matched_strains.slice(0, 3).map((m, i) => (
                                  <div key={m.id} className={`flex items-center justify-between p-2.5 bg-black/40 border ${i === 0 ? 'border-gold-500/40' : 'border-zinc-800'}`}>
                                    <div className="flex items-center gap-2">
                                      <span className={`text-[10px] font-bold uppercase tracking-widest ${i === 0 ? 'text-gold-500' : 'text-zinc-500'}`}>{i === 0 ? 'Best' : `#${i+1}`}</span>
                                      <span className="text-white text-sm font-medium">{m.strain_name}</span>
                                    </div>
                                    <span className="text-gold-500 text-xs font-bold">{m.similarity_score}%</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-zinc-500 text-xs">Noted — no request will be sent.</p>
                        )}
                        <p className="text-zinc-600 text-[10px]">Email broadcast may be pending activation. In-app visibility is enabled when the platform feature flag permits.</p>
                        {!inlineBroadcastSent && (
                          <p className="text-zinc-600 text-[10px]">Want to add more details? <button onClick={() => setHunterTab('request')} className="text-amber-400 hover:text-amber-300 underline">Use the full request form</button></p>
                        )}
                      </div>

                      {/* Knowledge Context */}
                      {hunterResults.knowledge_context && hunterResults.knowledge_context.length > 0 && (
                        <div data-testid="hunter-knowledge-context">
                          <h3 className="text-white font-manrope text-sm font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-cyan-400" />
                            Supporting Knowledge ({hunterResults.knowledge_context.length})
                          </h3>
                          <div className="space-y-2">
                            {hunterResults.knowledge_context.map((k, idx) => (
                              <div key={idx} data-testid={`hunter-knowledge-${idx}`} className="bg-cyan-500/5 border border-cyan-500/15 p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-cyan-400 text-xs font-bold">{k.title}</span>
                                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-zinc-800 text-zinc-400">{k.evidence_tier?.replace(/_/g, ' ')}</span>
                                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-zinc-800 text-zinc-500">{k.confidence_level}</span>
                                </div>
                                <p className="text-zinc-400 text-xs leading-relaxed">{k.answer_snippet}</p>
                                {k.source_name && <p className="text-zinc-600 text-[9px] mt-1">Source: {k.source_name}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Request Tab */}
              {hunterTab === 'request' && (
                <div>
                  <p className="text-zinc-400 text-sm mb-4">Tell us what you're looking for. Your request helps us understand market demand and source strains for the FireGold exchange.</p>
                  <div className="space-y-4">
                    <div>
                      <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Strain Name *</label>
                      <input data-testid="hunter-req-strain" type="text" placeholder="Strain you're seeking..." value={hunterQuery} onChange={e => setHunterQuery(e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 focus:border-amber-500 text-white px-4 py-3 outline-none text-sm transition-colors" />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Strain Type</label>
                        <select value={hunterType} onChange={e => setHunterType(e.target.value)} className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 outline-none text-sm">
                          <option value="">Any</option>
                          <option value="Indica">Indica</option>
                          <option value="Sativa">Sativa</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Min THC %</label>
                        <input data-testid="hunter-req-thc" type="number" placeholder="e.g. 25" value={hunterRequestForm.min_thc} onChange={e => setHunterRequestForm(p => ({ ...p, min_thc: e.target.value }))} className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-2.5 outline-none text-sm" />
                      </div>
                      <div>
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Volume Needed</label>
                        <input data-testid="hunter-req-kg" type="number" placeholder="e.g. 50" value={hunterRequestForm.desired_kg} onChange={e => setHunterRequestForm(p => ({ ...p, desired_kg: e.target.value }))} className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-2.5 outline-none text-sm" />
                      </div>
                      <div>
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Unit</label>
                        <select data-testid="hunter-req-unit" value={hunterRequestForm.quantity_unit} onChange={e => setHunterRequestForm(p => ({ ...p, quantity_unit: e.target.value }))} className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 outline-none text-sm">
                          <option value="kg">Kilograms (kg)</option>
                          <option value="lbs">Pounds (lbs)</option>
                          <option value="units">Units</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Supply Type</label>
                        <select data-testid="hunter-req-supply" value={hunterRequestForm.supply_type} onChange={e => setHunterRequestForm(p => ({ ...p, supply_type: e.target.value }))} className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 outline-none text-sm">
                          <option value="spot">Spot Buy</option>
                          <option value="repeatable">Repeatable</option>
                        </select>
                      </div>
                      {hunterRequestForm.supply_type === 'repeatable' && (
                        <div>
                          <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Repeat Period</label>
                          <select data-testid="hunter-req-period" value={hunterRequestForm.repeat_period} onChange={e => setHunterRequestForm(p => ({ ...p, repeat_period: e.target.value }))} className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 outline-none text-sm">
                            <option value="">Select...</option>
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Biweekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Use Case</label>
                        <select data-testid="hunter-req-usecase" value={hunterRequestForm.use_case} onChange={e => setHunterRequestForm(p => ({ ...p, use_case: e.target.value }))} className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 outline-none text-sm">
                          <option value="">Select...</option>
                          <option value="flower">Whole Flower</option>
                          <option value="pre_roll">Pre-Rolls</option>
                          <option value="extraction">Extraction</option>
                          <option value="vape">Vape</option>
                          <option value="rosin">Rosin</option>
                          <option value="edible">Edible</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Target Market</label>
                        <select data-testid="hunter-req-market" value={hunterRequestForm.target_market} onChange={e => setHunterRequestForm(p => ({ ...p, target_market: e.target.value }))} className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 outline-none text-sm">
                          <option value="">Select market...</option>
                          <option value="Canada">Canada (Domestic)</option>
                          <option value="Germany">Germany</option>
                          <option value="Australia">Australia</option>
                          <option value="Israel">Israel</option>
                          <option value="UK">United Kingdom</option>
                          <option value="Poland">Poland</option>
                          <option value="Czech Republic">Czech Republic</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Desired Characteristics</label>
                        <input data-testid="hunter-req-chars" type="text" placeholder="e.g. dense nugs, high terpene, frosty..." value={hunterRequestForm.desired_characteristics} onChange={e => setHunterRequestForm(p => ({ ...p, desired_characteristics: e.target.value }))} className="w-full bg-zinc-900 border border-zinc-700 focus:border-amber-500 text-white px-4 py-2.5 outline-none text-sm transition-colors" />
                      </div>
                    </div>
                    <div>
                      <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Desired Potential Terpene Effects</label>
                      <p className="text-zinc-600 text-[10px] mb-2">Reported terpene-associated experiences may vary between individuals.</p>
                      <EffectsMultiSelect
                        selected={hunterEffects}
                        onChange={(effects) => setHunterEffects(effects)}
                        testId="hunter-req-effects"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Desired Terpenes</label>
                      <div className="flex flex-wrap gap-1.5">
                        {ALL_TERPENES.map(terp => (
                          <button key={terp} onClick={() => setHunterTerpenes(prev => prev.includes(terp) ? prev.filter(t => t !== terp) : [...prev, terp])} className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-all ${hunterTerpenes.includes(terp) ? 'border-emerald-500 bg-emerald-500/15 text-emerald-400' : 'border-zinc-700 text-zinc-500 hover:border-zinc-500'}`}>
                            {terp}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-zinc-500 text-[10px] uppercase tracking-widest font-manrope block mb-1">Additional Notes</label>
                      <textarea data-testid="hunter-req-notes" placeholder="Any additional details about what you need..." value={hunterRequestForm.notes} onChange={e => setHunterRequestForm(p => ({ ...p, notes: e.target.value }))} rows={3} className="w-full bg-zinc-900 border border-zinc-700 focus:border-amber-500 text-white px-4 py-3 outline-none text-sm transition-colors resize-none" />
                    </div>

                    {/* Anonymous Broadcast Opt-In */}
                    <div className="border border-zinc-700 bg-zinc-900/50 p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <input
                          data-testid="hunter-broadcast-optin"
                          type="checkbox"
                          checked={hunterRequestForm.broadcast_opt_in}
                          onChange={e => setHunterRequestForm(p => ({ ...p, broadcast_opt_in: e.target.checked }))}
                          className="mt-0.5 w-4 h-4 accent-amber-500"
                        />
                        <div>
                          <p className="text-white text-sm font-bold">Send anonymous marketplace request</p>
                          <p className="text-zinc-500 text-xs mt-1 leading-relaxed">
                            Your identity will not be shared. Only the strain name, quantity, and specifications will be broadcast anonymously to growers and suppliers on the platform.
                          </p>
                          <p className="text-zinc-600 text-[10px] mt-1">
                            Email broadcast may be pending activation. In-app visibility will be enabled when the platform feature flag permits.
                          </p>
                        </div>
                      </div>
                    </div>

                    <button data-testid="hunter-submit-request-btn" onClick={hunterSubmitRequest} disabled={hunterSubmitting || !hunterQuery.trim()} className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all flex items-center justify-center gap-2">
                      {hunterSubmitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {hunterSubmitting ? 'Submitting...' : 'Submit Seeking Request'}
                    </button>

                    {/* Broadcast Result Confirmation */}
                    {hunterBroadcastResult && (
                      <div data-testid="hunter-broadcast-result" className="border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-2">
                        <p className="text-emerald-400 text-sm font-bold flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Request Submitted</p>
                        <p className="text-zinc-400 text-xs">{hunterBroadcastResult.broadcast_message}</p>
                        <p className="text-zinc-400 text-xs">We've emailed you the top matches from our inventory. Our team has been notified.</p>
                        {hunterBroadcastResult.matched_strains?.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Your Top Inventory Matches</p>
                            {hunterBroadcastResult.matched_strains.slice(0, 3).map((m, i) => (
                              <div key={m.id} className={`flex items-center justify-between p-3 bg-black/40 border ${i === 0 ? 'border-gold-500/40' : 'border-zinc-800'}`}>
                                <div className="flex items-center gap-3">
                                  <span className={`text-[10px] font-bold uppercase tracking-widest ${i === 0 ? 'text-gold-500' : 'text-zinc-500'}`}>{i === 0 ? 'Best Match' : `Alternative #${i}`}</span>
                                  <span className="text-white text-sm font-medium">{m.strain_name}</span>
                                  <span className="text-zinc-500 text-xs">{m.type || ''}</span>
                                </div>
                                <span className="text-gold-500 text-sm font-bold">{m.similarity_score}%</span>
                              </div>
                            ))}
                            <p className="text-zinc-600 text-[10px] mt-1">Click any strain in the catalog to view full details and COA data.</p>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-widest mt-2">
                          <span className="text-zinc-500">In Catalog: <span className={hunterBroadcastResult.exact_match_found ? 'text-emerald-400' : 'text-zinc-400'}>{hunterBroadcastResult.exact_match_found ? 'Yes' : 'No'}</span></span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* History Tab */}
              {hunterTab === 'history' && (
                <div>
                  <p className="text-zinc-400 text-sm mb-4">Track your seeking requests and their status.</p>
                  {hunterMyRequests.length === 0 ? (
                    <div className="text-center py-12">
                      <Crosshair className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                      <p className="text-zinc-500 text-sm">No seeking requests yet.</p>
                      <button onClick={() => setHunterTab('request')} className="text-amber-400 text-xs mt-2 hover:underline">Submit your first request</button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {hunterMyRequests.map(req => (
                        <div key={req.id} data-testid={`hunter-request-${req.id}`} className="bg-zinc-900/50 border border-zinc-800 p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <span className="text-white font-bold text-sm">{req.strain_name}</span>
                              {req.strain_type && <span className="text-zinc-500 text-xs ml-2">{req.strain_type}</span>}
                            </div>
                            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${req.status === 'open' ? 'bg-blue-500/20 text-blue-400' : req.status === 'sourcing' ? 'bg-amber-500/20 text-amber-400' : req.status === 'fulfilled' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700/50 text-zinc-400'}`}>{req.status}</span>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-400">
                            {req.desired_kg && <span>Volume: <span className="text-white font-mono">{req.desired_kg}kg</span></span>}
                            {req.min_thc && <span>Min THC: <span className="text-white font-mono">{req.min_thc}%</span></span>}
                            {req.target_market && <span>Market: <span className="text-white">{req.target_market}</span></span>}
                          </div>
                          {req.desired_effects?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {req.desired_effects.map(e => (<span key={e} className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-amber-500/10 text-amber-400/70 border border-amber-500/15">{e}</span>))}
                            </div>
                          )}
                          {req.admin_notes && (
                            <div className="mt-2 p-2 bg-gold-500/5 border border-gold-500/20">
                              <span className="text-gold-500 text-[10px] uppercase tracking-widest font-bold">Admin Note: </span>
                              <span className="text-zinc-300 text-xs">{req.admin_notes}</span>
                            </div>
                          )}
                          <p className="text-zinc-600 text-[10px] mt-2">{new Date(req.created_at).toLocaleDateString()}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff'
          }
        }}
      />
    </div>
  );
};

const ResetPasswordPage = () => {
  const [token] = useState(new URLSearchParams(window.location.search).get('token'));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
      <div className="bg-charcoal border border-white/10 p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="font-cinzel font-bold text-2xl text-gold-500 tracking-widest">FIREGOLD</h1>
          <p className="text-zinc-500 text-xs tracking-widest mt-1">MARKETPLACE</p>
        </div>
        {!token ? (
          <div className="text-center">
            <p className="text-red-500 mb-4">Invalid reset link. No token found.</p>
            <a href="/" className="text-gold-500 hover:text-gold-400 text-sm uppercase tracking-widest">Back to Login</a>
          </div>
        ) : success ? (
          <div className="text-center">
            <CheckCircle2 className="w-12 h-12 text-cannabis-green mx-auto mb-4" />
            <h2 className="text-white text-lg font-bold mb-2">Password Reset!</h2>
            <p className="text-zinc-400 text-sm mb-6">Your password has been updated. You can now log in.</p>
            <a href="/" className="bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest px-8 py-3 text-sm transition-all inline-block">Go to Login</a>
          </div>
        ) : (
          <form onSubmit={handleReset} className="space-y-4">
            <h2 className="font-cinzel font-bold text-lg text-white uppercase tracking-widest text-center mb-4">Set New Password</h2>
            {error && <p className="text-red-500 text-sm text-center">{error}</p>}
            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">New Password</label>
              <input data-testid="reset-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="New password" required />
            </div>
            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">Confirm Password</label>
              <input data-testid="reset-confirm-input" type="password" value={confirmPassword} onChange={e => setConfirmPw(e.target.value)} className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="Confirm password" required />
            </div>
            <button type="submit" disabled={loading} data-testid="reset-submit-btn" className="w-full bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-black font-bold uppercase tracking-widest py-3 text-sm transition-all">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const VerifyEmailPage = ({ onAuth }) => {
  const [token] = useState(new URLSearchParams(window.location.search).get('token'));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [tokenData, setTokenData] = useState(null);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/verify-email?token=${token}`);
        setTokenData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired verification link.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setVerifying(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/verify-email`, { token, password });
      setSuccess(true);
      // Auto-login
      if (res.data.token) {
        localStorage.setItem('userToken', res.data.token);
        localStorage.setItem('userData', JSON.stringify(res.data.user));
        setTimeout(() => {
          if (onAuth) onAuth(res.data.token, res.data.user);
          else window.location.href = '/';
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
      <div className="bg-charcoal border border-white/10 p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="font-cinzel font-bold text-2xl text-gold-500 tracking-widest">FIREGOLD</h1>
          <p className="text-zinc-500 text-xs tracking-widest mt-1">ACCOUNT VERIFICATION</p>
        </div>
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-gold-500 border-t-transparent animate-spin mx-auto mb-4" />
            <p className="text-zinc-400 text-sm">Verifying your link...</p>
          </div>
        ) : !token ? (
          <div className="text-center">
            <p data-testid="verify-no-token" className="text-red-500 mb-4">Invalid verification link. No token found.</p>
            <a href="/" className="text-gold-500 hover:text-gold-400 text-sm uppercase tracking-widest">Back to Register</a>
          </div>
        ) : error && !tokenData ? (
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p data-testid="verify-error" className="text-red-400 mb-4">{error}</p>
            <a href="/" className="text-gold-500 hover:text-gold-400 text-sm uppercase tracking-widest">Back to Register</a>
          </div>
        ) : success ? (
          <div data-testid="verify-success" className="text-center">
            <CheckCircle2 className="w-12 h-12 text-cannabis-green mx-auto mb-4" />
            <h2 className="text-white text-lg font-bold mb-2">Account Activated!</h2>
            <p className="text-zinc-400 text-sm mb-2">Welcome to FireGold, {tokenData?.first_name}!</p>
            <p className="text-zinc-500 text-xs mb-6">Redirecting you to the marketplace...</p>
            <a href="/" className="bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest px-8 py-3 text-sm transition-all inline-block">Enter Marketplace</a>
          </div>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="text-center mb-4">
              <Mail className="w-10 h-10 text-gold-500 mx-auto mb-3" />
              <h2 className="font-cinzel font-bold text-lg text-white uppercase tracking-widest">Create Your Password</h2>
              <p className="text-zinc-400 text-sm mt-2">Welcome, <span className="text-gold-500">{tokenData?.first_name}</span>! Set your password to activate your account.</p>
            </div>
            {error && <p className="text-red-500 text-sm text-center">{error}</p>}
            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <Lock className="w-4 h-4 inline mr-1" /> Password
              </label>
              <input data-testid="verify-password-input" type="password" value={password} onChange={e => { setPassword(e.target.value); setError(''); }} className="w-full bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white h-12 px-4 outline-none" placeholder="Choose a password (min 6 characters)" required minLength={6} />
            </div>
            <div>
              <label className="block font-manrope text-xs uppercase tracking-widest text-zinc-500 mb-2">
                <Lock className="w-4 h-4 inline mr-1" /> Confirm Password
              </label>
              <input data-testid="verify-confirm-input" type="password" value={confirmPassword} onChange={e => { setConfirmPw(e.target.value); setError(''); }} className={`w-full bg-zinc-900/50 border ${confirmPassword && confirmPassword !== password ? 'border-red-500' : 'border-zinc-800'} focus:border-gold-500 text-white h-12 px-4 outline-none`} placeholder="Confirm your password" required />
              {confirmPassword && confirmPassword !== password && (
                <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
              )}
            </div>
            <button type="submit" disabled={verifying} data-testid="verify-submit-btn" className="w-full bg-gold-500 hover:bg-gold-400 disabled:opacity-50 text-black font-bold uppercase tracking-widest py-4 text-sm transition-all mt-4">
              {verifying ? 'Activating...' : 'Activate Account'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// Chat Widget Component
const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: 'assistant', text: 'Welcome to Firegold. How can I help you today? Whether you\'re looking to buy premium wholesale cannabis flower or submit your product, I\'m here to assist.' }]);
    }
  }, [isOpen, messages.length]);

  const quickReplies = [
    { label: 'View Catalog', msg: 'How do I view your cannabis catalog?' },
    { label: 'Submit Product', msg: 'I\'m a grower and want to submit my product' },
    { label: 'Pricing Info', msg: 'How does your pricing work?' },
    { label: 'Export Markets', msg: 'What international markets do you support?' },
    { label: 'Contact Sales', msg: 'I\'d like to speak with someone from the Firegold team' }
  ];

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      const resp = await axios.post(`${API}/chat`, {
        message: userMsg,
        session_id: sessionId
      });
      setSessionId(resp.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', text: resp.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'I apologize, but I\'m having trouble connecting right now. Please try again or reach out to us at warren@firegold.ca.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="chat-widget">
      {/* Chat Window */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-[380px] h-[520px] bg-zinc-950 border border-zinc-800 shadow-2xl flex flex-col overflow-hidden"
          style={{ animation: 'fadeIn 0.2s ease-out' }} data-testid="chat-window">
          {/* Header */}
          <div className="bg-charcoal border-b border-zinc-800 px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gold-500/20 flex items-center justify-center">
                <Bot className="w-4 h-4 text-gold-500" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Firegold Assistant</p>
                <p className="text-cannabis-green text-[10px] uppercase tracking-widest">Online</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="w-7 h-7 flex items-center justify-center bg-gold-500 hover:bg-gold-400 transition-all" data-testid="chat-close-btn">
              <Minimize2 className="w-4 h-4 text-black" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                  msg.role === 'user' 
                    ? 'bg-gold-500/20 text-gold-100 border border-gold-500/30' 
                    : 'bg-zinc-900 text-zinc-300 border border-white/5'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-zinc-900 border border-white/5 px-4 py-3">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-zinc-800 flex-shrink-0">
            {/* Quick Replies */}
            {messages.length <= 1 && (
              <div className="px-3 pt-3 flex flex-wrap gap-1.5" data-testid="quick-replies">
                {quickReplies.map((qr, idx) => (
                  <button key={idx} data-testid={`quick-reply-${idx}`}
                    onClick={() => { setInput(qr.msg); }}
                    className="text-[11px] bg-gold-500/10 border border-gold-500/30 text-gold-400 hover:bg-gold-500/20 px-3 py-1.5 transition-all whitespace-nowrap">
                    {qr.label}
                  </button>
                ))}
              </div>
            )}
            <div className="p-3 flex gap-2">
            <input
              data-testid="chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Type your message..."
              className="flex-1 bg-zinc-900/50 border border-zinc-800 focus:border-gold-500 text-white text-sm px-4 py-3 outline-none"
            />
            <button
              data-testid="chat-send-btn"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="bg-gold-500 hover:bg-gold-400 disabled:bg-zinc-700 text-black px-4 py-3 transition-all"
            >
              <Send className="w-4 h-4" />
            </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 flex items-center justify-center shadow-lg transition-all ${
          isOpen ? 'bg-zinc-800 border border-zinc-700 text-white' : 'bg-gold-500 hover:bg-gold-400 text-black'
        }`}
      >
        {isOpen ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>
    </div>
  );
};

// Notification sound using Web Audio API
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1047, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  } catch (e) {
    // Audio not available
  }
};

// Affiliate Dashboard Component
const AffiliateDashboard = () => {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const resp = await axios.get(`${API}/affiliate/dashboard/${code}`);
        setData(resp.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Dashboard not found');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [code]);

  if (loading) return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center">
      <div className="text-gold-500 font-mono text-sm animate-pulse">Loading dashboard...</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
      <div className="bg-charcoal border border-white/10 p-12 text-center max-w-md">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="font-cinzel font-bold text-xl text-white mb-2">Not Found</h2>
        <p className="text-zinc-500 text-sm">{error}</p>
        <a href="/" className="inline-block mt-6 text-gold-500 text-sm hover:underline">← Back to Marketplace</a>
      </div>
    </div>
  );

  const { stats, recent_events } = data;

  const formatCommission = () => {
    if (data.commission_type === 'per_gram') return `$${data.commission_value}/gram`;
    if (data.commission_type === 'percentage') return `${data.commission_value}%`;
    return `$${data.commission_value} flat`;
  };

  return (
    <div className="min-h-screen bg-obsidian" data-testid="affiliate-dashboard">
      {/* Header */}
      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={FIREGOLD_LOGO} alt="Firegold" className="h-12 w-auto" />
            <div>
              <h1 className="font-cinzel font-bold text-xl text-white">Affiliate Dashboard</h1>
              <p className="text-zinc-500 text-sm font-mono">{data.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 ${data.is_active ? 'bg-cannabis-green/20 text-cannabis-green border border-cannabis-green/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
              {data.is_active ? 'Active' : 'Inactive'}
            </span>
            <span className="text-zinc-600 text-xs font-mono">CODE: {data.referral_code}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="bg-charcoal border border-white/5 p-6" data-testid="stat-referrals">
            <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Referrals</p>
            <p className="font-cinzel font-bold text-3xl text-white">{stats.total_referrals}</p>
          </div>
          <div className="bg-charcoal border border-white/5 p-6" data-testid="stat-conversions">
            <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Conversions</p>
            <p className="font-cinzel font-bold text-3xl text-cannabis-green">{stats.total_conversions}</p>
          </div>
          <div className="bg-charcoal border border-white/5 p-6" data-testid="stat-revenue">
            <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Revenue Generated</p>
            <p className="font-cinzel font-bold text-3xl text-white">${stats.total_revenue.toLocaleString()}</p>
          </div>
          <div className="bg-charcoal border border-white/5 p-6" data-testid="stat-commission">
            <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Total Commission</p>
            <p className="font-cinzel font-bold text-3xl text-gold-500">${stats.total_commission.toLocaleString()}</p>
          </div>
        </div>

        {/* Commission Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <div className="bg-charcoal border border-white/5 p-6">
            <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Commission Rate</p>
            <p className="text-white font-bold text-lg">{formatCommission()}</p>
          </div>
          <div className="bg-charcoal border border-white/5 p-6">
            <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Pending Commission</p>
            <p className="text-gold-500 font-bold text-lg">${stats.pending_commission.toLocaleString()}</p>
          </div>
          <div className="bg-charcoal border border-white/5 p-6">
            <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Paid Commission</p>
            <p className="text-cannabis-green font-bold text-lg">${stats.paid_commission.toLocaleString()}</p>
          </div>
        </div>

        {/* Your Referral Link */}
        <div className="bg-charcoal border border-gold-500/30 p-6 mb-10">
          <p className="text-gold-500 text-xs uppercase tracking-widest font-bold mb-3">Your Referral Link</p>
          <div className="flex items-center gap-3">
            <code className="flex-1 bg-black/40 border border-white/10 text-zinc-300 px-4 py-3 text-sm font-mono truncate" data-testid="referral-link">
              {window.location.origin}/?ref={data.referral_code}
            </code>
            <button
              data-testid="copy-referral-link-btn"
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/?ref=${data.referral_code}`); toast.success('Link copied!'); }}
              className="bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest px-6 py-3 text-sm transition-all whitespace-nowrap"
            >
              <Copy className="w-4 h-4 inline mr-2" />Copy
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h2 className="font-cinzel font-bold text-xl text-white mb-6">Recent Activity</h2>
          {recent_events.length === 0 ? (
            <div className="bg-zinc-900/30 border border-white/5 p-12 text-center">
              <Clock className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">No activity yet. Share your referral link to get started!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {recent_events.map((event, idx) => (
                <div key={idx} className="bg-charcoal border border-white/5 px-6 py-4 flex items-center justify-between" data-testid={`event-row-${idx}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 flex items-center justify-center ${event.type === 'conversion' ? 'bg-cannabis-green/20' : 'bg-gold-500/20'}`}>
                      {event.type === 'conversion' ? <DollarSign className="w-4 h-4 text-cannabis-green" /> : <UserPlus className="w-4 h-4 text-gold-500" />}
                    </div>
                    <div>
                      <p className="text-white text-sm font-bold capitalize">{event.type === 'registration' ? 'New Referral' : 'Sale Conversion'}</p>
                      {event.sale_amount && <p className="text-zinc-500 text-xs">Sale: ${event.sale_amount.toLocaleString()}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    {event.commission != null && (
                      <span className="text-cannabis-green font-bold text-sm">+${event.commission.toLocaleString()}</span>
                    )}
                    {event.status && (
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${event.status === 'paid' ? 'bg-cannabis-green/20 text-cannabis-green' : 'bg-gold-500/20 text-gold-500'}`}>
                        {event.status}
                      </span>
                    )}
                    <span className="text-zinc-600 text-xs">{event.date ? new Date(event.date).toLocaleDateString() : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-10 text-center">
          <a href="/" className="text-zinc-500 hover:text-gold-500 text-sm transition-colors">← Back to Firegold Marketplace</a>
        </div>
      </div>
      <Toaster position="top-right" theme="dark" richColors />
    </div>
  );
};

// COA Intelligence Display Component
const CoaParsedDisplay = ({ data, editMode, onSaveCorrections }) => {
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (editMode && data) {
      setEditData({
        cannabinoids: { ...data.cannabinoids },
        terpenes: { ...data.terpenes },
        metadata: { ...data.metadata },
        quality_metrics: { ...data.quality_metrics },
      });
    }
  }, [editMode, data]);

  if (!data) return null;

  const confidence = data.parse_confidence || {};
  const statusColor = data.parse_status === 'complete' ? 'emerald' : data.parse_status === 'partial' ? 'amber' : 'red';

  const cannabinoids = editMode ? (editData.cannabinoids || {}) : (data.cannabinoids || {});
  const terpenes = editMode ? (editData.terpenes || {}) : (data.terpenes || {});
  const metadata = editMode ? (editData.metadata || {}) : (data.metadata || {});
  const qualityMetrics = editMode ? (editData.quality_metrics || {}) : (data.quality_metrics || {});

  const detectedCannabinoids = Object.entries(cannabinoids).filter(([k, v]) => !k.startsWith('total') && !k.startsWith('_') && v?.value != null);
  const nonDetectCannabinoids = Object.entries(cannabinoids).filter(([k, v]) => !k.startsWith('total') && !k.startsWith('_') && v?.value == null && v?.status);
  const totalsCannabinoids = Object.entries(cannabinoids).filter(([k]) => k.startsWith('total'));
  const realTerpenes = Object.entries(terpenes).filter(([k]) => !k.startsWith('_')).sort((a, b) => (b[1]?.value || 0) - (a[1]?.value || 0));
  const totalTerpenes = terpenes._total_terpenes;

  return (
    <div className="space-y-4" data-testid="coa-parsed-display">
      {/* Status Bar */}
      <div className={`flex items-center justify-between p-3 border border-${statusColor}-500/30 bg-${statusColor}-500/10`}>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-mono font-bold uppercase px-2 py-0.5 bg-${statusColor}-500/20 text-${statusColor}-400`}>
            {data.parse_status}
          </span>
          <span className="text-zinc-400 text-xs">
            Confidence: <span className={`font-bold text-${statusColor}-400`}>{confidence.score}%</span>
          </span>
          <span className="text-zinc-500 text-xs">v{data.parse_version}</span>
        </div>
        <div className="text-zinc-500 text-xs">
          {data.parsed_at && `Parsed: ${new Date(data.parsed_at).toLocaleDateString()} ${new Date(data.parsed_at).toLocaleTimeString()}`}
          {data.lab_format && <span className="ml-2 text-zinc-600">({data.lab_format})</span>}
        </div>
      </div>

      {/* Missing fields warning */}
      {confidence.missing?.length > 0 && (
        <div className="p-2.5 bg-amber-500/10 border border-amber-500/20">
          <p className="text-amber-400 text-xs font-bold uppercase tracking-wider mb-1">Parse Notes</p>
          {confidence.missing.map((m, i) => (
            <p key={i} className="text-amber-400/70 text-xs">{m}</p>
          ))}
        </div>
      )}

      {/* Metadata */}
      <div className="border border-white/10 p-4">
        <h3 className="text-xs uppercase tracking-widest text-gold-500 font-bold mb-3">Metadata</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[['lab_name', 'Lab'], ['sample_name', 'Sample'], ['sample_id', 'Sample ID'],
            ['batch_id', 'Batch/Lot'], ['report_date', 'Report Date'], ['received_date', 'Received'],
            ['sampled_date', 'Sampled'], ['license_number', 'License'], ['sample_description', 'Description']
          ].map(([key, label]) => (
            metadata[key] ? (
              <div key={key}>
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider block">{label}</span>
                {editMode ? (
                  <input
                    value={editData.metadata?.[key] || ''}
                    onChange={e => setEditData(prev => ({ ...prev, metadata: { ...prev.metadata, [key]: e.target.value } }))}
                    className="w-full bg-zinc-900/50 border border-zinc-700 text-white text-xs h-7 px-2 outline-none focus:border-gold-500"
                  />
                ) : (
                  <span className="text-white text-xs font-mono">{metadata[key]}</span>
                )}
              </div>
            ) : null
          ))}
        </div>
      </div>

      {/* Cannabinoids */}
      <div className="border border-white/10 p-4">
        <h3 className="text-xs uppercase tracking-widest text-gold-500 font-bold mb-3">
          Cannabinoids
          <span className="text-zinc-500 font-normal ml-2">({detectedCannabinoids.length} detected, {nonDetectCannabinoids.length} below LOQ)</span>
        </h3>

        {/* Totals row */}
        {totalsCannabinoids.length > 0 && (
          <div className="flex gap-4 mb-3 pb-3 border-b border-white/5">
            {totalsCannabinoids.map(([key, val]) => (
              <div key={key} className="bg-zinc-900/50 px-3 py-2 border border-white/5">
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider block">
                  {key.replace('total_', 'Total ').replace('_', ' ')}
                </span>
                <span className="text-white text-sm font-bold font-mono">{val?.value != null ? `${val.value}%` : 'N/A'}</span>
              </div>
            ))}
          </div>
        )}

        {/* Detected cannabinoids table */}
        {detectedCannabinoids.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="cannabinoids-table">
              <thead>
                <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-white/5">
                  <th className="text-left py-1.5 pr-4">Cannabinoid</th>
                  <th className="text-right py-1.5 px-3">wt%</th>
                  <th className="text-right py-1.5 px-3">mg/g</th>
                  <th className="text-left py-1.5 px-3">Raw Label</th>
                </tr>
              </thead>
              <tbody>
                {detectedCannabinoids.sort((a, b) => (b[1].value || 0) - (a[1].value || 0)).map(([key, val]) => (
                  <tr key={key} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-1.5 pr-4 text-white font-mono font-bold">{key}</td>
                    <td className="py-1.5 px-3 text-right text-emerald-400 font-mono">
                      {editMode ? (
                        <input
                          value={editData.cannabinoids?.[key]?.value ?? ''}
                          onChange={e => {
                            const newVal = e.target.value === '' ? null : parseFloat(e.target.value);
                            setEditData(prev => ({ ...prev, cannabinoids: { ...prev.cannabinoids, [key]: { ...prev.cannabinoids[key], value: newVal } } }));
                          }}
                          className="w-20 bg-zinc-900 border border-zinc-700 text-emerald-400 text-xs text-right h-6 px-1 outline-none focus:border-gold-500 font-mono"
                        />
                      ) : (
                        `${val.value}%`
                      )}
                    </td>
                    <td className="py-1.5 px-3 text-right text-zinc-400 font-mono">{val.mg_per_g != null ? val.mg_per_g : '-'}</td>
                    <td className="py-1.5 px-3 text-zinc-600 text-[10px]">{val.raw_label || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Non-detected summary */}
        {nonDetectCannabinoids.length > 0 && (
          <div className="mt-2 pt-2 border-t border-white/5">
            <span className="text-zinc-600 text-[10px] uppercase tracking-wider">Below LOQ / Not Detected: </span>
            <span className="text-zinc-500 text-[10px] font-mono">
              {nonDetectCannabinoids.map(([k, v]) => `${k} (${v.status})`).join(', ')}
            </span>
          </div>
        )}
      </div>

      {/* Terpenes */}
      <div className="border border-white/10 p-4">
        <h3 className="text-xs uppercase tracking-widest text-gold-500 font-bold mb-3">
          Terpenes
          {totalTerpenes && <span className="text-zinc-500 font-normal ml-2">(Total: {totalTerpenes.value}%)</span>}
          <span className="text-zinc-500 font-normal ml-2">({realTerpenes.length} detected)</span>
        </h3>

        {realTerpenes.length > 0 ? (
          <div className="space-y-1">
            {realTerpenes.map(([key, val]) => {
              const maxVal = realTerpenes[0]?.[1]?.value || 1;
              const barWidth = ((val.value || 0) / maxVal) * 100;
              return (
                <div key={key} className="flex items-center gap-2 group">
                  <span className="text-zinc-400 text-xs w-36 truncate font-mono" title={key}>{key}</span>
                  <div className="flex-1 h-4 bg-zinc-900/50 relative">
                    <div
                      className="h-full bg-emerald-500/40 transition-all"
                      style={{ width: `${barWidth}%` }}
                    />
                    <span className="absolute right-1 top-0 text-[10px] text-zinc-400 font-mono leading-4">
                      {editMode ? (
                        <input
                          value={editData.terpenes?.[key]?.value ?? ''}
                          onChange={e => {
                            const newVal = e.target.value === '' ? null : parseFloat(e.target.value);
                            setEditData(prev => ({ ...prev, terpenes: { ...prev.terpenes, [key]: { ...prev.terpenes[key], value: newVal } } }));
                          }}
                          className="w-16 bg-zinc-900 border border-zinc-700 text-emerald-400 text-[10px] text-right h-4 px-1 outline-none focus:border-gold-500 font-mono"
                        />
                      ) : (
                        `${val.value}%`
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-zinc-600 text-xs">No terpene data extracted from this COA</p>
        )}
      </div>

      {/* Quality Metrics */}
      {Object.keys(qualityMetrics).length > 0 && (
        <div className="border border-white/10 p-4">
          <h3 className="text-xs uppercase tracking-widest text-gold-500 font-bold mb-3">Quality / Compliance</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(qualityMetrics).map(([key, val]) => (
              <div key={key} className="bg-zinc-900/50 px-3 py-2 border border-white/5">
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider block">{key.replace(/_/g, ' ')}</span>
                {val.status ? (
                  <span className={`text-xs font-bold font-mono ${val.status === 'PASS' ? 'text-emerald-400' : 'text-red-400'}`}>
                    {val.status}
                    {val.tests_count && <span className="text-zinc-500 font-normal ml-1">({val.tests_count} tests)</span>}
                  </span>
                ) : (
                  <span className="text-white text-xs font-mono">
                    {val.value != null ? `${val.value} ${val.unit || ''}` : 'N/A'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw Text Excerpt */}
      {data.raw_text_excerpt && (
        <details className="border border-white/10">
          <summary className="p-3 text-xs uppercase tracking-widest text-zinc-500 cursor-pointer hover:text-zinc-400">
            Raw Text Excerpt ({data.page_count} pages)
          </summary>
          <div className="p-3 pt-0">
            <pre className="text-zinc-600 text-[10px] font-mono whitespace-pre-wrap max-h-48 overflow-y-auto bg-zinc-900/50 p-3 border border-white/5">
              {data.raw_text_excerpt}
            </pre>
          </div>
        </details>
      )}

      {/* Edit Save Button */}
      {editMode && (
        <div className="flex justify-end pt-2">
          <button
            onClick={() => onSaveCorrections(editData)}
            data-testid="save-coa-corrections-btn"
            className="bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest px-6 py-3 text-sm transition-all"
          >
            Save Corrections
          </button>
        </div>
      )}

      {/* Correction history */}
      {data.last_corrected_at && (
        <div className="text-zinc-600 text-[10px] text-right">
          Last corrected: {new Date(data.last_corrected_at).toLocaleString()} by {data.last_corrected_by || 'admin'}
        </div>
      )}
    </div>
  );
};

// ── CRM Panel Component ──
const CRMPanel = ({ adminToken, API }) => {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [optInFilter, setOptInFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [editContact, setEditContact] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [blasting, setBlasting] = useState(false);
  const [newContact, setNewContact] = useState({ email: '', name: '', company: '', type: 'prospect', country: '', phone: '', notes: '' });
  const [importType, setImportType] = useState('cold_lead');
  const [importTag, setImportTag] = useState('');
  const [bulkTagName, setBulkTagName] = useState('');

  const headers = { Authorization: `Bearer ${adminToken}` };

  const fetchContacts = async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 50, search, type_filter: typeFilter, country: countryFilter, opt_in: optInFilter, tag: tagFilter });
      const res = await axios.get(`${API}/admin/crm/contacts?${params}`, { headers });
      setContacts(res.data.contacts);
      setTotalPages(res.data.pages);
      setTotalContacts(res.data.total);
    } catch (e) { toast.error('Failed to load contacts'); }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/crm/stats`, { headers });
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API}/admin/crm/tags`, { headers });
      setTags(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchStats(); fetchTags(); }, []);
  useEffect(() => { setPage(1); fetchContacts(1); }, [search, typeFilter, countryFilter, optInFilter, tagFilter]);
  useEffect(() => { fetchContacts(page); }, [page]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/crm/sync`, {}, { headers });
      toast.success(res.data.message);
      fetchContacts(1); fetchStats(); fetchTags();
    } catch (e) { toast.error('Sync failed'); }
    setSyncing(false);
  };

  const handleAddContact = async () => {
    try {
      await axios.post(`${API}/admin/crm/contacts`, newContact, { headers });
      toast.success('Contact added');
      setShowAddModal(false);
      setNewContact({ email: '', name: '', company: '', type: 'prospect', country: '', phone: '', notes: '' });
      fetchContacts(1); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add contact'); }
  };

  const handleUpdateContact = async () => {
    if (!editContact) return;
    try {
      await axios.put(`${API}/admin/crm/contacts/${editContact.id}`, editContact, { headers });
      toast.success('Contact updated');
      setEditContact(null);
      fetchContacts();
    } catch (e) { toast.error('Update failed'); }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API}/admin/crm/contacts/${id}`, { headers });
      toast.success('Contact deleted');
      fetchContacts(); fetchStats();
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('contact_type', importType);
    formData.append('tag', importTag);
    try {
      const res = await axios.post(`${API}/admin/crm/import`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      toast.success(res.data.message);
      fetchContacts(1); fetchStats(); fetchTags();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ type_filter: typeFilter, opt_in: optInFilter, tag: tagFilter });
      const res = await axios.get(`${API}/admin/crm/export?${params}`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'firegold_crm_contacts.csv'; a.click();
      toast.success('Exported');
    } catch (e) { toast.error('Export failed'); }
  };

  const handleBulkTag = async () => {
    if (!bulkTagName.trim() || selected.size === 0) return;
    try {
      await axios.post(`${API}/admin/crm/contacts/bulk-tag`, { contact_ids: [...selected], tag: bulkTagName.trim() }, { headers });
      toast.success(`Tagged ${selected.size} contacts`);
      setBulkTagName('');
      setSelected(new Set());
      fetchContacts(); fetchTags();
    } catch (e) { toast.error('Bulk tag failed'); }
  };

  const handleBlastSelected = async (template = 'permission_pass') => {
    if (selected.size === 0) { toast.error('Select contacts first'); return; }
    const subject = template === 'permission_pass'
      ? "FireGold at ICBC Berlin — Canadian Cannabis for the World"
      : "FireGold — Canadian Craft Cannabis Market";
    if (!window.confirm(`Send ${template === 'permission_pass' ? 'Permission Pass' : 'Full Promo'} email to ${selected.size} selected contacts?`)) return;
    setBlasting(true);
    try {
      const res = await axios.post(`${API}/admin/crm/blast-from-crm`, { contact_ids: [...selected], subject, template }, { headers });
      toast.success(res.data.message);
      setSelected(new Set());
      fetchContacts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Blast failed'); }
    setBlasting(false);
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === contacts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(contacts.map(c => c.id)));
    }
  };

  const typeColors = { buyer: 'text-blue-400 bg-blue-500/10', grower: 'text-emerald-400 bg-emerald-500/10', prospect: 'text-amber-400 bg-amber-500/10', cold_lead: 'text-zinc-400 bg-zinc-500/10' };

  return (
    <div className="space-y-6" data-testid="crm-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="font-cinzel font-bold text-2xl text-white mb-1">Contact CRM</h2>
          <p className="text-zinc-500 text-sm">Manage all your contacts in one place</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleSync} disabled={syncing} data-testid="crm-sync-btn"
            className="border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-400 font-bold uppercase tracking-widest text-xs px-4 py-2.5 transition-all flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} /> Sync Sources
          </button>
          <button onClick={() => setShowImportModal(true)} data-testid="crm-import-btn"
            className="border border-gold-500/40 hover:bg-gold-500/10 text-gold-500 font-bold uppercase tracking-widest text-xs px-4 py-2.5 transition-all flex items-center gap-1.5">
            <Upload className="w-3.5 h-3.5" /> Import CSV
          </button>
          <button onClick={handleExport} data-testid="crm-export-btn"
            className="border border-zinc-600 hover:bg-zinc-800 text-zinc-400 font-bold uppercase tracking-widest text-xs px-4 py-2.5 transition-all flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
          <button onClick={() => setShowAddModal(true)} data-testid="crm-add-contact-btn"
            className="bg-gold-500 hover:bg-gold-400 text-black font-bold uppercase tracking-widest text-xs px-4 py-2.5 transition-all flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add Contact
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="crm-stats">
          {[
            { label: 'Total', value: stats.total, color: 'text-white' },
            { label: 'Buyers', value: stats.buyers, color: 'text-blue-400' },
            { label: 'Growers', value: stats.growers, color: 'text-emerald-400' },
            { label: 'Prospects', value: stats.prospects, color: 'text-amber-400' },
            { label: 'Cold Leads', value: stats.cold_leads, color: 'text-zinc-400' },
            { label: 'Opted In', value: stats.opted_in, color: 'text-green-400' },
            { label: 'Countries', value: stats.countries, color: 'text-purple-400' },
          ].map(s => (
            <div key={s.label} className="bg-zinc-900 border border-zinc-800 p-3 text-center">
              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-zinc-500 text-[10px] uppercase tracking-widest mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <input value={search} onChange={e => setSearch(e.target.value)} data-testid="crm-search-input"
          placeholder="Search name, email, company..."
          className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white px-3 py-2 text-sm outline-none w-64 transition-all" />
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="crm-type-filter"
          className="bg-zinc-900 border border-zinc-700 text-zinc-300 px-3 py-2 text-sm outline-none">
          <option value="">All Types</option>
          <option value="buyer">Buyer</option>
          <option value="grower">Grower</option>
          <option value="prospect">Prospect</option>
          <option value="cold_lead">Cold Lead</option>
        </select>
        <select value={optInFilter} onChange={e => setOptInFilter(e.target.value)} data-testid="crm-optin-filter"
          className="bg-zinc-900 border border-zinc-700 text-zinc-300 px-3 py-2 text-sm outline-none">
          <option value="">Opt-in: All</option>
          <option value="yes">Opted In</option>
          <option value="no">Not Opted In</option>
        </select>
        {tags.length > 0 && (
          <select value={tagFilter} onChange={e => setTagFilter(e.target.value)} data-testid="crm-tag-filter"
            className="bg-zinc-900 border border-zinc-700 text-zinc-300 px-3 py-2 text-sm outline-none">
            <option value="">All Tags</option>
            {tags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <input value={countryFilter} onChange={e => setCountryFilter(e.target.value)} data-testid="crm-country-filter"
          placeholder="Country..." className="bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white px-3 py-2 text-sm outline-none w-32 transition-all" />
        <span className="text-zinc-500 text-xs ml-auto">{totalContacts} contacts</span>
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="bg-gold-500/5 border border-gold-500/20 p-3 flex flex-wrap items-center gap-3" data-testid="crm-bulk-actions">
          <span className="text-gold-500 text-xs font-bold">{selected.size} selected</span>
          <input value={bulkTagName} onChange={e => setBulkTagName(e.target.value)} placeholder="Tag name..."
            className="bg-zinc-900 border border-zinc-700 text-white px-2 py-1.5 text-xs outline-none w-36" />
          <button onClick={handleBulkTag} className="border border-gold-500/40 text-gold-500 font-bold text-[10px] uppercase tracking-widest px-3 py-1.5 hover:bg-gold-500/10 transition-all" data-testid="crm-bulk-tag-btn">Tag</button>
          <button onClick={() => handleBlastSelected('permission_pass')} disabled={blasting}
            className="border border-blue-500/40 text-blue-400 font-bold text-[10px] uppercase tracking-widest px-3 py-1.5 hover:bg-blue-500/10 transition-all" data-testid="crm-blast-permission-btn">
            {blasting ? 'Sending...' : 'Send Permission Pass'}
          </button>
          <button onClick={() => handleBlastSelected('full_promo')} disabled={blasting}
            className="border border-emerald-500/40 text-emerald-400 font-bold text-[10px] uppercase tracking-widest px-3 py-1.5 hover:bg-emerald-500/10 transition-all" data-testid="crm-blast-promo-btn">
            {blasting ? 'Sending...' : 'Send Full Promo'}
          </button>
          <button onClick={() => setSelected(new Set())} className="text-zinc-500 hover:text-white text-xs ml-auto">Clear Selection</button>
        </div>
      )}

      {/* Contact Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="crm-contacts-table">
          <thead>
            <tr className="border-b border-zinc-800 text-zinc-500 text-[10px] uppercase tracking-widest">
              <th className="py-2 px-2 text-left w-8">
                <input type="checkbox" checked={contacts.length > 0 && selected.size === contacts.length} onChange={toggleSelectAll}
                  className="accent-gold-500" data-testid="crm-select-all" />
              </th>
              <th className="py-2 px-2 text-left">Contact</th>
              <th className="py-2 px-2 text-left">Type</th>
              <th className="py-2 px-2 text-left hidden lg:table-cell">Company</th>
              <th className="py-2 px-2 text-left hidden md:table-cell">Country</th>
              <th className="py-2 px-2 text-left hidden lg:table-cell">Tags</th>
              <th className="py-2 px-2 text-center">Opt-in</th>
              <th className="py-2 px-2 text-left hidden xl:table-cell">Last Email</th>
              <th className="py-2 px-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="py-8 text-center text-zinc-500">Loading...</td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan={9} className="py-8 text-center text-zinc-500">No contacts found. Click "Sync Sources" to populate from existing data.</td></tr>
            ) : contacts.map(c => (
              <tr key={c.id} className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors ${c.unsubscribed ? 'opacity-50' : ''}`}>
                <td className="py-2.5 px-2">
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} className="accent-gold-500" data-testid={`crm-select-${c.email}`} />
                </td>
                <td className="py-2.5 px-2">
                  <div className="text-white text-sm font-medium">{c.name || '—'}</div>
                  <div className="text-zinc-500 text-xs">{c.email}</div>
                </td>
                <td className="py-2.5 px-2">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${typeColors[c.type] || 'text-zinc-400'}`}>
                    {c.type?.replace('_', ' ')}
                  </span>
                </td>
                <td className="py-2.5 px-2 text-zinc-400 text-xs hidden lg:table-cell">{c.company || '—'}</td>
                <td className="py-2.5 px-2 text-zinc-400 text-xs hidden md:table-cell">{c.country || '—'}</td>
                <td className="py-2.5 px-2 hidden lg:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {(c.tags || []).slice(0, 3).map(t => (
                      <span key={t} className="text-[9px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 border border-zinc-700">{t}</span>
                    ))}
                    {(c.tags || []).length > 3 && <span className="text-[9px] text-zinc-600">+{c.tags.length - 3}</span>}
                  </div>
                </td>
                <td className="py-2.5 px-2 text-center">
                  {c.unsubscribed ? (
                    <span className="text-red-400 text-[10px] font-bold">UNSUB</span>
                  ) : c.opt_in_status ? (
                    <CheckCircle className="w-4 h-4 text-green-400 mx-auto" />
                  ) : (
                    <span className="text-zinc-600">—</span>
                  )}
                </td>
                <td className="py-2.5 px-2 text-zinc-500 text-xs hidden xl:table-cell">
                  {c.last_email_sent ? new Date(c.last_email_sent).toLocaleDateString() : '—'}
                </td>
                <td className="py-2.5 px-2 text-right">
                  <div className="flex justify-end gap-1">
                    {c.email_history?.length > 0 && (
                      <button onClick={() => setShowHistoryModal(c)} title="Email History" className="text-zinc-500 hover:text-blue-400 p-1 transition-colors" data-testid={`crm-history-${c.email}`}>
                        <Mail className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => setEditContact({...c})} title="Edit" className="text-zinc-500 hover:text-gold-500 p-1 transition-colors" data-testid={`crm-edit-${c.email}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDeleteContact(c.id)} title="Delete" className="text-zinc-500 hover:text-red-400 p-1 transition-colors" data-testid={`crm-delete-${c.email}`}>
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3" data-testid="crm-pagination">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
            className="text-zinc-400 hover:text-white text-xs font-bold disabled:opacity-30 px-3 py-1.5 border border-zinc-700 hover:border-zinc-500 transition-all">Prev</button>
          <span className="text-zinc-500 text-xs">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
            className="text-zinc-400 hover:text-white text-xs font-bold disabled:opacity-30 px-3 py-1.5 border border-zinc-700 hover:border-zinc-500 transition-all">Next</button>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="crm-add-modal">
            <h3 className="text-white font-bold text-lg mb-4">Add Contact</h3>
            <div className="space-y-3">
              <input value={newContact.email} onChange={e => setNewContact({...newContact, email: e.target.value})} placeholder="Email *"
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" data-testid="crm-add-email" />
              <input value={newContact.name} onChange={e => setNewContact({...newContact, name: e.target.value})} placeholder="Name"
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" data-testid="crm-add-name" />
              <input value={newContact.company} onChange={e => setNewContact({...newContact, company: e.target.value})} placeholder="Company"
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" data-testid="crm-add-company" />
              <div className="grid grid-cols-2 gap-3">
                <select value={newContact.type} onChange={e => setNewContact({...newContact, type: e.target.value})}
                  className="bg-zinc-800 border border-zinc-700 text-zinc-300 px-3 py-2 text-sm outline-none" data-testid="crm-add-type">
                  <option value="buyer">Buyer</option>
                  <option value="grower">Grower</option>
                  <option value="prospect">Prospect</option>
                  <option value="cold_lead">Cold Lead</option>
                </select>
                <input value={newContact.country} onChange={e => setNewContact({...newContact, country: e.target.value})} placeholder="Country"
                  className="bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" data-testid="crm-add-country" />
              </div>
              <input value={newContact.phone} onChange={e => setNewContact({...newContact, phone: e.target.value})} placeholder="Phone"
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" />
              <textarea value={newContact.notes} onChange={e => setNewContact({...newContact, notes: e.target.value})} placeholder="Notes" rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" />
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowAddModal(false)} className="text-zinc-400 hover:text-white text-xs font-bold px-4 py-2 border border-zinc-700">Cancel</button>
              <button onClick={handleAddContact} className="bg-gold-500 hover:bg-gold-400 text-black text-xs font-bold px-4 py-2" data-testid="crm-add-submit">Add Contact</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contact Modal */}
      {editContact && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditContact(null)}>
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="crm-edit-modal">
            <h3 className="text-white font-bold text-lg mb-1">Edit Contact</h3>
            <p className="text-zinc-500 text-xs mb-4">{editContact.email}</p>
            <div className="space-y-3">
              <input value={editContact.name || ''} onChange={e => setEditContact({...editContact, name: e.target.value})} placeholder="Name"
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" data-testid="crm-edit-name" />
              <input value={editContact.company || ''} onChange={e => setEditContact({...editContact, company: e.target.value})} placeholder="Company"
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" />
              <div className="grid grid-cols-2 gap-3">
                <select value={editContact.type} onChange={e => setEditContact({...editContact, type: e.target.value})}
                  className="bg-zinc-800 border border-zinc-700 text-zinc-300 px-3 py-2 text-sm outline-none" data-testid="crm-edit-type">
                  <option value="buyer">Buyer</option>
                  <option value="grower">Grower</option>
                  <option value="prospect">Prospect</option>
                  <option value="cold_lead">Cold Lead</option>
                </select>
                <input value={editContact.country || ''} onChange={e => setEditContact({...editContact, country: e.target.value})} placeholder="Country"
                  className="bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" />
              </div>
              <input value={editContact.phone || ''} onChange={e => setEditContact({...editContact, phone: e.target.value})} placeholder="Phone"
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" />
              <textarea value={editContact.notes || ''} onChange={e => setEditContact({...editContact, notes: e.target.value})} placeholder="Notes" rows={2}
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" />
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setEditContact(null)} className="text-zinc-400 hover:text-white text-xs font-bold px-4 py-2 border border-zinc-700">Cancel</button>
              <button onClick={handleUpdateContact} className="bg-gold-500 hover:bg-gold-400 text-black text-xs font-bold px-4 py-2" data-testid="crm-edit-submit">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setShowImportModal(false); setImportResult(null); }}>
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-lg p-6" onClick={e => e.stopPropagation()} data-testid="crm-import-modal">
            <h3 className="text-white font-bold text-lg mb-4">Import Contacts</h3>
            <p className="text-zinc-400 text-sm mb-4">Upload a <strong>.csv</strong> or <strong>.xlsx</strong> file. The system auto-detects the email column. Optional columns: name, company, country, phone.</p>
            <div className="space-y-3 mb-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-500 text-[10px] uppercase tracking-widest mb-1">Contact Type</label>
                  <select value={importType} onChange={e => setImportType(e.target.value)} data-testid="crm-import-type"
                    className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 px-3 py-2 text-sm outline-none">
                    <option value="cold_lead">Cold Lead</option>
                    <option value="prospect">Prospect</option>
                    <option value="buyer">Buyer</option>
                    <option value="grower">Grower</option>
                  </select>
                </div>
                <div>
                  <label className="block text-zinc-500 text-[10px] uppercase tracking-widest mb-1">Tag (optional)</label>
                  <input value={importTag} onChange={e => setImportTag(e.target.value)} placeholder="e.g. icbc-berlin-2026" data-testid="crm-import-tag"
                    className="w-full bg-zinc-800 border border-zinc-700 text-white px-3 py-2 text-sm outline-none focus:border-gold-500" />
                </div>
              </div>
            </div>
            <label className="block border-2 border-dashed border-zinc-700 hover:border-gold-500/50 p-8 text-center cursor-pointer transition-all" data-testid="crm-import-dropzone">
              <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
              <span className="text-zinc-400 text-sm">Click to select a CSV or XLSX file</span>
              <input type="file" accept=".csv,.xlsx" onChange={handleImport} className="hidden" data-testid="crm-import-file-input" />
            </label>
            {importResult && (
              <div className="mt-4 bg-zinc-800 border border-zinc-700 p-3" data-testid="crm-import-result">
                <p className="text-emerald-400 text-sm font-bold">{importResult.imported} imported</p>
                <p className="text-zinc-400 text-xs mt-1">{importResult.skipped} duplicates skipped, {importResult.invalid} invalid emails</p>
                {importResult.tag && <p className="text-zinc-500 text-xs mt-1">Tagged as: {importResult.tag}</p>}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={() => { setShowImportModal(false); setImportResult(null); }} className="text-zinc-400 hover:text-white text-xs font-bold px-4 py-2 border border-zinc-700">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Email History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowHistoryModal(null)}>
          <div className="bg-zinc-900 border border-zinc-700 w-full max-w-lg p-6" onClick={e => e.stopPropagation()} data-testid="crm-history-modal">
            <h3 className="text-white font-bold text-lg mb-1">Email History</h3>
            <p className="text-zinc-500 text-xs mb-4">{showHistoryModal.email}</p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {(showHistoryModal.email_history || []).map((h, i) => (
                <div key={i} className="bg-zinc-800 border border-zinc-700 px-3 py-2 flex justify-between items-center text-xs">
                  <div>
                    <span className="text-white font-bold">{h.subject || h.template}</span>
                    <span className="text-zinc-500 ml-2">{h.template}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={h.status === 'sent' ? 'text-emerald-400' : 'text-red-400'}>{h.status}</span>
                    <span className="text-zinc-600">{new Date(h.sent_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowHistoryModal(null)} className="text-zinc-400 hover:text-white text-xs font-bold px-4 py-2 border border-zinc-700">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Email Blast Panel Component ──
const EmailBlastPanel = ({ adminToken, API }) => {
  const [emailList, setEmailList] = useState('');
  const [subject, setSubject] = useState("FireGold at ICBC Berlin — Canadian Cannabis for the World");
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [blastResult, setBlastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [template, setTemplate] = useState('permission_pass');

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-email/history`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setHistory(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchHistory(); }, []);

  const handlePreview = async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-email/preview?template=${template}`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch (e) { toast.error('Failed to load preview'); }
  };

  const handleValidate = async () => {
    if (!emailList.trim()) { toast.error('Paste your email list first'); return; }
    try {
      const res = await axios.post(`${API}/admin/promo-email/send`, { emails: emailList, subject, template, test_mode: true }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setValidationResult(res.data);
      toast.success(`${res.data.valid_count} valid emails found`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Validation failed'); }
  };

  const handleSend = async () => {
    if (!validationResult || validationResult.valid_count === 0) { toast.error('Validate your list first'); return; }
    if (!window.confirm(`Send promotional email to ${validationResult.valid_count} recipients? This cannot be undone.`)) return;
    setSending(true);
    setBlastResult(null);
    try {
      const res = await axios.post(`${API}/admin/promo-email/send`, { emails: emailList, subject, template, test_mode: false }, { headers: { Authorization: `Bearer ${adminToken}` } });
      setBlastResult(res.data);
      toast.success(res.data.message);
      fetchHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-cinzel font-bold text-2xl text-white mb-2">Promotional Email Blast</h2>
          <p className="text-zinc-500 text-sm">Send the FireGold promotional email to your contact list</p>
        </div>
        <button onClick={handlePreview} data-testid="preview-promo-email-btn" className="border border-gold-500/40 hover:bg-gold-500/10 text-gold-500 font-bold uppercase tracking-widest text-xs px-5 py-3 transition-all flex items-center gap-2">
          <Eye className="w-4 h-4" /> Preview Email
        </button>
      </div>

      {/* Template Selector */}
      <div>
        <label className="block text-zinc-400 text-xs uppercase tracking-widest mb-2">Email Template</label>
        <div className="flex gap-3">
          <button data-testid="template-permission-pass"
            onClick={() => { setTemplate('permission_pass'); setSubject("FireGold at ICBC Berlin — Canadian Cannabis for the World"); }}
            className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-widest transition-all border ${
              template === 'permission_pass' ? 'bg-gold-500 text-black border-gold-500' : 'text-zinc-400 border-zinc-700 hover:border-zinc-500'
            }`}>
            Permission Pass (Cold List)
          </button>
          <button data-testid="template-full-promo"
            onClick={() => { setTemplate('full_promo'); setSubject("FireGold — Canadian Craft Cannabis Market"); }}
            className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-widest transition-all border ${
              template === 'full_promo' ? 'bg-gold-500 text-black border-gold-500' : 'text-zinc-400 border-zinc-700 hover:border-zinc-500'
            }`}>
            Full Promo (Opted-In List)
          </button>
        </div>
        <p className="text-zinc-600 text-xs mt-2">{template === 'permission_pass' ? 'Shorter intro + opt-in CTA. Use for contacts who haven\'t consented yet.' : 'Full promotional email with all features. Only for opted-in contacts.'}</p>
      </div>

      {/* Subject Line */}
      <div>
        <label className="block text-zinc-400 text-xs uppercase tracking-widest mb-2">Subject Line</label>
        <input data-testid="promo-subject-input" type="text" value={subject} onChange={e => setSubject(e.target.value)}
          className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white px-4 py-3 text-sm outline-none transition-all" />
      </div>

      {/* Email List */}
      <div>
        <label className="block text-zinc-400 text-xs uppercase tracking-widest mb-2">Recipient Emails <span className="text-zinc-600">(paste list — comma, semicolon, or newline separated)</span></label>
        <textarea data-testid="promo-email-list-input" rows={8} value={emailList} onChange={e => { setEmailList(e.target.value); setValidationResult(null); setBlastResult(null); }}
          placeholder="buyer1@example.com&#10;buyer2@example.com&#10;seller@company.de"
          className="w-full bg-zinc-900 border border-zinc-700 focus:border-gold-500 text-white px-4 py-3 text-sm outline-none transition-all font-mono" />
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <button onClick={handleValidate} data-testid="validate-promo-list-btn"
          className="border border-blue-500/40 hover:bg-blue-500/10 text-blue-400 font-bold uppercase tracking-widest text-xs px-5 py-3 transition-all">
          Validate List
        </button>
        <button onClick={handleSend} disabled={sending || !validationResult} data-testid="send-promo-blast-btn"
          className={`font-bold uppercase tracking-widest text-xs px-6 py-3 transition-all flex items-center gap-2 ${
            sending || !validationResult ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed' : 'bg-gold-500 hover:bg-gold-400 text-black'
          }`}>
          <Send className="w-4 h-4" /> {sending ? 'Sending...' : 'Send Blast'}
        </button>
      </div>

      {/* Validation Result */}
      {validationResult && (
        <div data-testid="promo-validation-result" className="bg-zinc-900 border border-zinc-700 p-4">
          <p className="text-emerald-400 text-sm font-bold mb-1">{validationResult.valid_count} valid emails ready to send</p>
          {validationResult.invalid_count > 0 && (
            <p className="text-red-400 text-xs">{validationResult.invalid_count} invalid: {validationResult.invalid_emails?.join(', ')}</p>
          )}
        </div>
      )}

      {/* Send Result */}
      {blastResult && (
        <div data-testid="promo-blast-result" className={`border p-4 ${blastResult.failed > 0 ? 'bg-amber-500/5 border-amber-500/30' : 'bg-emerald-500/5 border-emerald-500/30'}`}>
          <p className="text-emerald-400 text-sm font-bold">{blastResult.message}</p>
          {blastResult.failed > 0 && <p className="text-amber-400 text-xs mt-1">{blastResult.failed} failed sends</p>}
        </div>
      )}

      {/* Email Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-zinc-900 border border-zinc-700 max-w-[700px] w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-zinc-900 border-b border-zinc-700 px-4 py-3 flex justify-between items-center">
              <span className="text-zinc-400 text-xs uppercase tracking-widest">Email Preview</span>
              <button onClick={() => setShowPreview(false)} className="text-zinc-500 hover:text-white text-xl">&times;</button>
            </div>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml, { ADD_ATTR: ['target'], ADD_TAGS: ['style'] }) }} />
          </div>
        </div>
      )}

      {/* Blast History */}
      {history.length > 0 && (
        <div>
          <h3 className="text-zinc-400 text-xs uppercase tracking-widest mb-3 mt-8">Send History</h3>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 px-4 py-3 flex justify-between items-center text-xs">
                <div>
                  <span className="text-white font-bold">{h.subject}</span>
                  <span className="text-zinc-500 ml-3">{new Date(h.created_at).toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="text-emerald-400">{h.sent} sent</span>
                  {h.failed > 0 && <span className="text-red-400 ml-2">{h.failed} failed</span>}
                  {h.skipped_unsubscribed > 0 && <span className="text-amber-400 ml-2">{h.skipped_unsubscribed} unsub</span>}
                  <span className="text-zinc-500 ml-2">/ {h.total_recipients} total</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════
// SUPPLY FORM PAGE — /supply
// ═══════════════════════════════════════════════════

// ═══════════════════════════════════════════════════
// ADMIN SUPPLY FORMS PANEL
// ═══════════════════════════════════════════════════

// ═══════════════════════════════════════════════════
// GROWER PIPELINE MANAGER
// ═══════════════════════════════════════════════════
const GrowerPipelinePanel = ({ adminToken, API }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedGrower, setExpandedGrower] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [sendingBulk, setSendingBulk] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/grower-pipeline`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setData(res.data);
    } catch (err) { toast.error("Failed to load grower data"); }
    setLoading(false);
  }, [adminToken, API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sendReminder = async (grower) => {
    setSendingReminder(grower.email);
    try {
      const res = await axios.post(`${API}/admin/grower-pipeline/send-reminder`, { email: grower.email, name: grower.name, company: grower.company }, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error("Failed to send reminder"); }
    setSendingReminder(null);
  };

  const sendBulkReminders = async () => {
    if (!data) return;
    const staleGrowers = data.growers.filter(g => g.staleness === "stale").map(g => ({ email: g.email, name: g.name, company: g.company }));
    if (staleGrowers.length === 0) { toast.info("No stale growers to remind"); return; }
    setSendingBulk(true);
    try {
      const res = await axios.post(`${API}/admin/grower-pipeline/send-bulk-reminders`, { emails: staleGrowers }, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success(res.data.message);
      fetchData();
    } catch (err) { toast.error("Failed to send bulk reminders"); }
    setSendingBulk(false);
  };

  if (loading) return <div className="text-zinc-500 text-center py-12">Loading grower pipeline...</div>;
  if (!data) return <div className="text-red-400 text-center py-12">Failed to load data</div>;

  const filtered = data.growers.filter(g => {
    if (filterStatus !== "all" && g.staleness !== filterStatus) return false;
    if (search) {
      const s = search.toLowerCase();
      return (g.company || "").toLowerCase().includes(s) || (g.name || "").toLowerCase().includes(s) || g.email.toLowerCase().includes(s);
    }
    return true;
  });

  const stalenessColors = { fresh: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10", aging: "text-amber-400 border-amber-400/30 bg-amber-400/10", stale: "text-red-400 border-red-400/30 bg-red-400/10" };
  const stalenessLabels = { fresh: "Fresh", aging: "Aging", stale: "Stale" };

  return (
    <div data-testid="grower-pipeline-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-cinzel font-bold text-2xl text-white uppercase tracking-widest">Manage Growers</h2>
          <p className="text-zinc-500 text-xs mt-1">Track grower submissions, inventory staleness, and send follow-up reminders</p>
        </div>
        <div className="flex gap-2">
          <button onClick={sendBulkReminders} disabled={sendingBulk || data.stale_count === 0} className="bg-red-500/20 border border-red-500/40 text-red-400 px-4 py-2 text-xs uppercase tracking-widest hover:bg-red-500/30 transition disabled:opacity-50" data-testid="grower-bulk-remind">
            {sendingBulk ? "Sending..." : `Remind All Stale (${data.stale_count})`}
          </button>
          <button onClick={fetchData} className="bg-gold-500 hover:bg-gold-400 text-black px-5 py-2 font-bold uppercase text-xs tracking-widest transition-all" data-testid="grower-refresh">
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-zinc-900/50 border border-white/10 p-4 text-center">
          <div className="text-2xl font-bold text-white">{data.total_growers}</div>
          <div className="text-zinc-500 text-xs uppercase tracking-widest mt-1">Total Growers</div>
        </div>
        <div className="bg-emerald-400/5 border border-emerald-400/20 p-4 text-center cursor-pointer" onClick={() => setFilterStatus(filterStatus === "fresh" ? "all" : "fresh")}>
          <div className="text-2xl font-bold text-emerald-400">{data.fresh_count}</div>
          <div className="text-emerald-400/60 text-xs uppercase tracking-widest mt-1">Fresh (&lt;14d)</div>
        </div>
        <div className="bg-amber-400/5 border border-amber-400/20 p-4 text-center cursor-pointer" onClick={() => setFilterStatus(filterStatus === "aging" ? "all" : "aging")}>
          <div className="text-2xl font-bold text-amber-400">{data.aging_count}</div>
          <div className="text-amber-400/60 text-xs uppercase tracking-widest mt-1">Aging (14-30d)</div>
        </div>
        <div className="bg-red-400/5 border border-red-400/20 p-4 text-center cursor-pointer" onClick={() => setFilterStatus(filterStatus === "stale" ? "all" : "stale")}>
          <div className="text-2xl font-bold text-red-400">{data.stale_count}</div>
          <div className="text-red-400/60 text-xs uppercase tracking-widest mt-1">Stale (30d+)</div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-3 mb-4">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, company, or email..." className="flex-1 bg-black border border-white/20 text-white px-4 py-2 text-sm focus:border-yellow-600 focus:outline-none" data-testid="grower-search" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-black border border-white/20 text-white px-4 py-2 text-sm" data-testid="grower-filter">
          <option value="all">All Statuses</option>
          <option value="stale">Stale (30d+)</option>
          <option value="aging">Aging (14-30d)</option>
          <option value="fresh">Fresh (&lt;14d)</option>
        </select>
      </div>

      {/* Grower List */}
      <div className="border border-white/10">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-zinc-600">No growers match the current filter.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map((grower, i) => (
              <div key={grower.email} className="hover:bg-white/[0.02] transition" data-testid={`grower-row-${i}`}>
                <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedGrower(expandedGrower === grower.email ? null : grower.email)}>
                  {/* Staleness Badge */}
                  <div className={`px-2 py-1 border text-[10px] uppercase tracking-widest font-bold whitespace-nowrap ${stalenessColors[grower.staleness]}`}>
                    {stalenessLabels[grower.staleness]} — {grower.days_since_update}d
                  </div>

                  {/* Grower Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium text-sm truncate">{grower.company || grower.name || grower.email}</span>
                      {grower.company && grower.name && <span className="text-zinc-600 text-xs">({grower.name})</span>}
                    </div>
                    <div className="text-zinc-500 text-xs mt-0.5">{grower.email}</div>
                  </div>

                  {/* Summary Stats */}
                  <div className="flex gap-6 text-xs text-zinc-400">
                    <div className="text-center">
                      <div className="text-white font-bold">{grower.strain_count}</div>
                      <div className="text-zinc-600">strains</div>
                    </div>
                    <div className="text-center">
                      <div className="text-white font-bold">{grower.total_kg.toFixed(1)}</div>
                      <div className="text-zinc-600">kg total</div>
                    </div>
                  </div>

                  {/* Actions */}
                  <button
                    onClick={(e) => { e.stopPropagation(); sendReminder(grower); }}
                    disabled={sendingReminder === grower.email}
                    className="bg-gold-500/15 border border-gold-500/40 text-gold-500 px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-gold-500/25 transition disabled:opacity-50 whitespace-nowrap"
                    data-testid={`grower-remind-${i}`}
                  >
                    {sendingReminder === grower.email ? "Sending..." : "Send Reminder"}
                  </button>
                </div>

                {/* Expanded Details */}
                {expandedGrower === grower.email && (
                  <div className="px-4 pb-4 border-t border-white/5 bg-zinc-900/30">
                    <div className="grid grid-cols-4 gap-4 py-3 text-xs">
                      <div><span className="text-zinc-600">Phone</span><p className="text-zinc-300">{grower.phone || "—"}</p></div>
                      <div><span className="text-zinc-600">License</span><p className="text-zinc-300">{grower.license_number || "On file"}</p></div>
                      <div><span className="text-zinc-600">First Submitted</span><p className="text-zinc-300">{grower.first_submitted ? new Date(grower.first_submitted).toLocaleDateString() : "—"}</p></div>
                      <div><span className="text-zinc-600">Last Reminder</span><p className="text-zinc-300">{grower.last_reminder_sent ? new Date(grower.last_reminder_sent).toLocaleDateString() : "Never"}</p></div>
                    </div>
                    <p className="text-zinc-600 text-xs uppercase tracking-widest mb-2 mt-2">Submissions ({grower.submissions.length})</p>
                    <div className="space-y-1">
                      {grower.submissions.map((sub, j) => (
                        <div key={sub.id || j} className="flex items-center justify-between py-1.5 px-3 bg-black/30 border border-white/5 text-xs">
                          <div className="flex items-center gap-3">
                            <span className="text-white font-medium">{sub.strain_name}</span>
                            <span className="text-zinc-500">{sub.category}</span>
                            {sub.thc_percentage && <span className="text-zinc-500">THC: {sub.thc_percentage}</span>}
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-zinc-400">{sub.lot_size_kg ? `${sub.lot_size_kg} kg` : ""}</span>
                            <span className={`px-1.5 py-0.5 border text-[10px] uppercase ${sub.status === "approved" ? "text-emerald-400 border-emerald-400/30" : sub.status === "pending" ? "text-amber-400 border-amber-400/30" : "text-zinc-500 border-zinc-500/30"}`}>{sub.status}</span>
                            <span className="text-zinc-600">{sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ""}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const SupplyFormsPanel = ({ adminToken, API }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendEmails, setSendEmails] = useState("");
  const [sending, setSending] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/supply-submissions`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setSubmissions(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [adminToken, API]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleSendLink = async () => {
    if (!sendEmails.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/send-supply-link`, { emails: sendEmails }, { headers: { Authorization: `Bearer ${adminToken}` } });
      toast.success(res.data.message);
      setSendEmails("");
    } catch (err) { toast.error("Failed to send"); }
    setSending(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText("https://app.firegold.ca/supply");
    toast.success("Supply form link copied!");
  };

  return (
    <div data-testid="supply-forms-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-cinzel font-bold text-2xl text-white uppercase tracking-widest">Supply Forms</h2>
          <p className="text-zinc-500 text-xs mt-1">Review supplier product submissions and send form invitations</p>
        </div>
        <div className="flex gap-2">
          <button onClick={copyLink} className="bg-white/10 border border-white/20 text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-white/20 transition" data-testid="supply-copy-link">
            Copy Form Link
          </button>
          <button onClick={fetchSubmissions} className="bg-gold-500 hover:bg-gold-400 text-black px-5 py-2 font-bold uppercase text-xs tracking-widest transition-all" data-testid="supply-refresh">
            Refresh
          </button>
        </div>
      </div>

      {/* Send Link Section */}
      <div className="bg-zinc-900/50 border border-white/10 p-5 mb-6" data-testid="supply-send-link-section">
        <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Send Supply Form Link</h3>
        <div className="flex gap-3">
          <input type="text" value={sendEmails} onChange={e => setSendEmails(e.target.value)} placeholder="Enter emails (comma separated)" className="flex-1 bg-black border border-white/20 text-white px-4 py-2 text-sm focus:border-yellow-600 focus:outline-none" data-testid="supply-send-emails-input" />
          <button onClick={handleSendLink} disabled={sending || !sendEmails.trim()} className="bg-gold-500 hover:bg-gold-400 text-black px-6 py-2 font-bold uppercase text-xs tracking-widest transition-all disabled:opacity-50" data-testid="supply-send-btn">
            {sending ? "Sending..." : "Send Invite"}
          </button>
        </div>
        <p className="text-zinc-600 text-xs mt-2">Recipients will receive an email with a link to <span className="text-zinc-400">app.firegold.ca/supply</span></p>
      </div>

      {/* Submissions Table */}
      <div className="border border-white/10">
        <div className="bg-zinc-900/30 px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500">Submissions ({submissions.length})</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-zinc-600">Loading...</div>
        ) : submissions.length === 0 ? (
          <div className="p-8 text-center text-zinc-600">No submissions yet. Send the supply form link to your suppliers to get started.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {submissions.map((sub, i) => (
              <div key={sub.id || i} className="p-4 hover:bg-white/5 transition" data-testid={`supply-submission-${i}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-white font-bold">{sub.strain_name}</span>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${sub.product_type === 'flower' ? 'text-emerald-400 border-emerald-400/30' : 'text-amber-400 border-amber-400/30'}`}>
                        {sub.product_type === 'flower' ? 'Dried Flower' : 'Concentrate'}
                      </span>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${sub.status === 'pending' ? 'text-yellow-400 border-yellow-400/30' : sub.status === 'approved' ? 'text-emerald-400 border-emerald-400/30' : 'text-red-400 border-red-400/30'}`}>
                        {sub.status}
                      </span>
                    </div>
                    <div className="text-zinc-500 text-xs space-x-4">
                      <span>{sub.company_name || sub.grower_name}</span>
                      <span>{sub.email}</span>
                      <span>Lot: {sub.lot_number}</span>
                      <span>{sub.lot_size_grams?.toLocaleString()}g ({sub.lot_size_kg} kg)</span>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs">
                      {sub.grade_a_confirmed && <span className="text-emerald-500">Grade A Confirmed</span>}
                      {sub.irradiation_acknowledged && <span className="text-emerald-500">Irradiation Ack</span>}
                      {sub.shipping_acknowledged && <span className="text-emerald-500">Shipping Ack</span>}
                      {sub.coa_path && <a href={`${API.replace('/api', '')}${sub.coa_path}`} target="_blank" rel="noreferrer" className="text-gold-500 hover:underline">View COA</a>}
                    </div>
                  </div>
                  <div className="text-zinc-600 text-xs text-right">
                    {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const SupplyFormPage = () => {
  const [userToken] = useState(() => localStorage.getItem("userToken"));
  const [prefill, setPrefill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [supplierInfo, setSupplierInfo] = useState({ company: "", name: "", email: "", phone: "", license_number: "" });
  const [form, setForm] = useState({
    product_type: "flower",
    strain_name: "",
    lot_number: "",
    lot_size_grams: "",
    grade_a_confirmed: false,
    irradiation_acknowledged: false,
    shipping_acknowledged: false,
    additional_notes: "",
  });
  const [coaFile, setCoaFile] = useState(null);
  const isLoggedIn = !!userToken;

  useEffect(() => {
    if (!userToken) { setLoading(false); return; }
    axios.get(`${API}/supply/prefill`, { headers: { Authorization: `Bearer ${userToken}` } })
      .then(res => { setPrefill(res.data); setSupplierInfo(res.data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [userToken]);

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><div className="text-zinc-500">Loading...</div></div>;
  }

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="max-w-lg w-full text-center bg-zinc-900 border border-white/10 p-10" data-testid="supply-success">
          <div className="text-4xl mb-4" style={{color:'#22C55E'}}>&#10003;</div>
          <h2 className="text-xl font-bold text-white mb-3">Submission Received</h2>
          <p className="text-zinc-400 mb-6">Thank you for your supply submission. Our team will review your product and be in touch shortly.</p>
          <button onClick={() => { setSuccess(false); setForm({ product_type: "flower", strain_name: "", lot_number: "", lot_size_grams: "", grade_a_confirmed: false, irradiation_acknowledged: false, shipping_acknowledged: false, additional_notes: "" }); setCoaFile(null); }} className="bg-white/10 border border-white/20 text-white px-6 py-2 text-sm uppercase tracking-widest hover:bg-white/20 transition" data-testid="supply-submit-another">Submit Another</button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLoggedIn && !supplierInfo.email.trim()) { setError("Email is required"); return; }
    if (!isLoggedIn && !supplierInfo.name.trim()) { setError("Contact name is required"); return; }
    if (!isLoggedIn && !supplierInfo.company.trim()) { setError("Company name is required"); return; }
    if (!form.strain_name.trim()) { setError("Strain name is required"); return; }
    if (!form.lot_number.trim()) { setError("Lot number is required"); return; }
    if (!form.lot_size_grams || parseFloat(form.lot_size_grams) <= 0) { setError("Lot size must be greater than 0"); return; }
    if (form.product_type === "flower" && !form.grade_a_confirmed) { setError("You must confirm the product meets Grade A specifications"); return; }
    if (!form.irradiation_acknowledged) { setError("You must acknowledge the irradiation requirement"); return; }
    if (!form.shipping_acknowledged) { setError("You must acknowledge the shipping responsibility"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("product_type", form.product_type);
      fd.append("strain_name", form.strain_name);
      fd.append("lot_number", form.lot_number);
      fd.append("lot_size_grams", form.lot_size_grams);
      fd.append("license_number", supplierInfo.license_number || "");
      fd.append("grade_a_confirmed", String(form.grade_a_confirmed));
      fd.append("irradiation_acknowledged", String(form.irradiation_acknowledged));
      fd.append("shipping_acknowledged", String(form.shipping_acknowledged));
      fd.append("additional_notes", form.additional_notes);
      fd.append("guest_company", supplierInfo.company || "");
      fd.append("guest_name", supplierInfo.name || "");
      fd.append("guest_email", supplierInfo.email || "");
      fd.append("guest_phone", supplierInfo.phone || "");
      if (coaFile) fd.append("coa_file", coaFile);
      const headers = { "Content-Type": "multipart/form-data" };
      if (userToken) headers["Authorization"] = `Bearer ${userToken}`;
      await axios.post(`${API}/supply/submit`, fd, { headers });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-yellow-600 focus:outline-none transition text-sm";
  const labelCls = "block text-zinc-400 text-xs uppercase tracking-widest mb-2";

  return (
    <div className="min-h-screen bg-black" data-testid="supply-form-page">
      <div className="border-b border-white/10 py-6 px-6">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <a href="/" className="text-xl font-bold tracking-[0.3em] hover:opacity-80 transition" style={{color:'#D4AF37'}}>FIREGOLD</a>
          {isLoggedIn && prefill && (
            <div className="text-right">
              <div className="text-white text-sm font-medium">{prefill.name}</div>
              <div className="text-zinc-500 text-xs">{prefill.company}</div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold text-white tracking-wide mb-2">Product Supply Form</h1>
        <p className="text-zinc-500 text-sm mb-8">Submit your product for consideration on the FireGold marketplace.</p>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="supply-form">
          {/* Supplier Info */}
          <div className="bg-zinc-900/50 border border-white/10 p-5 space-y-3">
            <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Supplier Information</h3>
            {isLoggedIn && prefill ? (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-zinc-600 text-xs">Company</span><p className="text-white">{prefill.company || '—'}</p></div>
                <div><span className="text-zinc-600 text-xs">Contact</span><p className="text-white">{prefill.name || '—'}</p></div>
                <div><span className="text-zinc-600 text-xs">Email</span><p className="text-white">{prefill.email || '—'}</p></div>
                <div><span className="text-zinc-600 text-xs">License</span><p className="text-white">{prefill.license_number || 'On file'}</p></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Company Name *</label>
                  <input type="text" value={supplierInfo.company} onChange={e => setSupplierInfo({...supplierInfo, company: e.target.value})} className={inputCls} placeholder="Your company" data-testid="supply-guest-company" required />
                </div>
                <div>
                  <label className={labelCls}>Contact Name *</label>
                  <input type="text" value={supplierInfo.name} onChange={e => setSupplierInfo({...supplierInfo, name: e.target.value})} className={inputCls} placeholder="Your name" data-testid="supply-guest-name" required />
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <input type="email" value={supplierInfo.email} onChange={e => setSupplierInfo({...supplierInfo, email: e.target.value})} className={inputCls} placeholder="your@email.com" data-testid="supply-guest-email" required />
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input type="tel" value={supplierInfo.phone} onChange={e => setSupplierInfo({...supplierInfo, phone: e.target.value})} className={inputCls} placeholder="+1 555 123 4567" data-testid="supply-guest-phone" />
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>License Number</label>
                  <input type="text" value={supplierInfo.license_number} onChange={e => setSupplierInfo({...supplierInfo, license_number: e.target.value})} className={inputCls} placeholder="e.g. LIC-HC-12345" data-testid="supply-guest-license" />
                </div>
              </div>
            )}
          </div>

          {/* Product Type */}
          <div>
            <label className={labelCls}>Product Type</label>
            <div className="flex gap-4">
              {["flower", "concentrate"].map(t => (
                <label key={t} className={`flex-1 border cursor-pointer text-center py-3 text-sm uppercase tracking-widest transition ${form.product_type === t ? 'border-yellow-600 text-white bg-yellow-600/10' : 'border-white/20 text-zinc-500 hover:border-white/40'}`} data-testid={`supply-type-${t}`}>
                  <input type="radio" name="product_type" value={t} checked={form.product_type === t} onChange={e => setForm({...form, product_type: e.target.value, grade_a_confirmed: false})} className="sr-only" />
                  {t === "flower" ? "Dried Flower" : "Concentrate"}
                </label>
              ))}
            </div>
          </div>

          {/* Strain Name */}
          <div>
            <label className={labelCls}>Strain Name</label>
            <input type="text" value={form.strain_name} onChange={e => setForm({...form, strain_name: e.target.value})} className={inputCls} placeholder="e.g. Jealousy, Cerberus, etc." data-testid="supply-strain-name" required />
          </div>

          {/* Lot Number */}
          <div>
            <label className={labelCls}>Lot Number</label>
            <input type="text" value={form.lot_number} onChange={e => setForm({...form, lot_number: e.target.value})} className={inputCls} placeholder="e.g. LOT-2026-001" data-testid="supply-lot-number" required />
          </div>

          {/* Lot Size */}
          <div>
            <label className={labelCls}>Lot Size (grams)</label>
            <input type="number" value={form.lot_size_grams} onChange={e => setForm({...form, lot_size_grams: e.target.value})} className={inputCls} placeholder="e.g. 50000" min="1" step="1" data-testid="supply-lot-size" required />
            {form.lot_size_grams > 0 && <p className="text-zinc-600 text-xs mt-1">{(parseFloat(form.lot_size_grams) / 1000).toFixed(2)} kg</p>}
          </div>

          {/* COA Upload */}
          <div>
            <label className={labelCls}>Certificate of Analysis (COA)</label>
            <label className="block border border-dashed border-white/20 p-4 text-center cursor-pointer hover:border-yellow-600/50 transition" data-testid="supply-coa-upload">
              <input type="file" accept=".pdf" onChange={e => setCoaFile(e.target.files[0])} className="sr-only" />
              {coaFile ? (
                <span className="text-white text-sm">{coaFile.name}</span>
              ) : (
                <span className="text-zinc-500 text-sm">Click to upload COA (PDF)</span>
              )}
            </label>
          </div>

          {/* Grade A Confirmation (flower only) */}
          {form.product_type === "flower" && (
            <div className="bg-zinc-900/50 border border-white/10 p-5" data-testid="supply-grade-a-section">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.grade_a_confirmed} onChange={e => setForm({...form, grade_a_confirmed: e.target.checked})} className="mt-1 accent-yellow-600 w-4 h-4" data-testid="supply-grade-a-checkbox" />
                <div>
                  <span className="text-white text-sm font-medium">I confirm this product is Grade A bud</span>
                  <p className="text-zinc-500 text-xs mt-1">Grade A is defined as cannabis flower that is <strong className="text-zinc-300">2.5 cm or greater in size</strong>, visually appealing, with intact trichomes and minimal leaf matter.</p>
                </div>
              </label>
            </div>
          )}

          {/* Irradiation Acknowledgement */}
          <div className="bg-zinc-900/50 border border-white/10 p-5" data-testid="supply-irradiation-section">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.irradiation_acknowledged} onChange={e => setForm({...form, irradiation_acknowledged: e.target.checked})} className="mt-1 accent-yellow-600 w-4 h-4" data-testid="supply-irradiation-checkbox" />
              <div>
                <span className="text-white text-sm font-medium">I acknowledge this product needs to be irradiated</span>
                <p className="text-zinc-500 text-xs mt-1">All product supplied to FireGold must be irradiated prior to sale. By checking this box, you confirm you understand and accept this requirement.</p>
              </div>
            </label>
          </div>

          {/* Shipping Acknowledgement */}
          <div className="bg-zinc-900/50 border border-white/10 p-5" data-testid="supply-shipping-section">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.shipping_acknowledged} onChange={e => setForm({...form, shipping_acknowledged: e.target.checked})} className="mt-1 accent-yellow-600 w-4 h-4" data-testid="supply-shipping-checkbox" />
              <div>
                <span className="text-white text-sm font-medium">I acknowledge shipping cost is my responsibility</span>
                <p className="text-zinc-500 text-xs mt-1">The cost of shipping product to FireGold is paid by the supplier.</p>
              </div>
            </label>
          </div>

          {/* Additional Notes */}
          <div>
            <label className={labelCls}>Additional Notes (optional)</label>
            <textarea value={form.additional_notes} onChange={e => setForm({...form, additional_notes: e.target.value})} className={`${inputCls} h-24 resize-none`} placeholder="Any additional information..." data-testid="supply-notes" />
          </div>

          {error && <div className="text-red-400 text-sm border border-red-400/30 bg-red-400/5 p-3" data-testid="supply-error">{error}</div>}

          <button type="submit" disabled={submitting} className="w-full py-4 font-bold uppercase tracking-widest text-sm transition disabled:opacity-50" style={{background:'#D4AF37', color:'#000'}} data-testid="supply-submit-btn">
            {submitting ? "Submitting..." : "Submit Product"}
          </button>
        </form>
      </div>
    </div>
  );
};


function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<CatalogPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/submit-strain" element={<GrowerSubmitPage />} />
          <Route path="/supply" element={<SupplyFormPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage onAuth={(token, user) => { localStorage.setItem('userToken', token); localStorage.setItem('userData', JSON.stringify(user)); }} />} />
          <Route path="/affiliate/:code" element={<AffiliateDashboard />} />
        </Routes>
        <ChatWidgetWrapper />
      </BrowserRouter>
    </div>
  );
}

// Only show chat widget on non-admin pages
const ChatWidgetWrapper = () => {
  const location = window.location.pathname;
  if (location === '/admin') return null;
  return <ChatWidget />;
};

export default App;
